import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { interpolateMessage } from "./i18n-format";

export type UiLang = "zh" | "en" | "ja";

/** 界面语言，按下拉里的顺序排 */
export const UI_LANGS: readonly UiLang[] = ["zh", "en", "ja"];

/**
 * 后台界面语言。
 *
 * 与「配置内容的多语言」是两回事：那个是给玩家看的（I18nField 管），
 * 存在数据里；这个只影响后台自身的按钮和标签，不进数据库。
 *
 * 用扁平 key 而不是嵌套对象：漏翻时 TypeScript 会直接报错，
 * 嵌套结构下漏一层只会在运行时显示成 undefined。
 *
 * ## 写界面文案的三条（2026-09-15 定，读者是运营，不懂实现）
 *
 * 1. **一条 tooltip 只回答一个问题：「这个字段填错了会怎样」。**
 *    不解释实现、不解释设计理由、不写文档出处——那些进代码注释。
 *    一条塞三五件事的下场是一件都没人读。
 * 2. **说后果，不说机制。** 「玩家的进度条会当场往回跳」而不是「upsert 会覆盖」。
 * 3. **一看就懂的字段不挂问号。** 问号是稀缺资源，一个字段一个问号地挂满，
 *    运营就学会一个也不点了。
 *
 * ## 术语只许用右边这一列（i18n-parity.test.ts 里有闸）
 *
 * | 别写 | 写 |
 * |---|---|
 * | 掉落项 / 道具项 / act_item | 活动道具格（与 spin 页既有的「格」一致） |
 * | 名词表 / 活动道具名词表 | 活动道具管理 |
 * | ref（界面上） | 道具编号 |
 * | 生效版本 / 当前版本 / 激活 | 线上版本 / 发布上线 |
 * | JST | 日本时间 |
 *
 * ⚠ 界面上说「格」是对的（运营看到的就是 spin 表里的一行），但**代码里别把它当位置**：
 * `cfg_spin_cell.code` 是编号不是棋盘格号，服务端没有棋盘概念。
 */
const ZH = {
  "auditChange.versionSettings": "版本金额设置",
  "auditChange.economyModel": "金币口径",
  "auditChange.moneyScale": "金币换算刻度",
  "auditChange.numerator": "分子",
  "auditChange.denominator": "分母",
  "auditChange.shownRange": "第 {from}–{to} 处 / 共 {total} 处变化",
  "auditChange.previousChanges": "上一页变化",
  "auditChange.nextChanges": "下一页变化",
  "auditChange.rivalCells": "竞品逐格造价",
  "auditChange.rivalStars": "竞品逐星造价",
  "auditChange.rivalAt": "{name} · 第 {level} 关 · 第 {building} 栋 · {star} 星",
  "auditChange.imageUnchanged": "图片未变",
  "auditChange.imageValue": "图片（展开查看）",
  "auditChange.changePage": "变化页码",
  "auditChange.checksumConflict": "当前能读取的配置未发现差异，但这两版的发布校验值不同，不能判定整版完全一致。早期配置迁移或未保留的旧字段可能影响历史校验值。",
  "auditChange.noVisibleDiff": "当前能读取的配置未发现差异。",
  "auditChange.whitespaceHint": "␠ 表示空格；\\n、\\t 等标出换行、制表等不可见字符。",
  "auditChange.cost": "造价",
  "auditChange.global": "全局配置",
  "auditChange.buildingCosts": "第 {n} 栋各星造价（从 1 星起）",
  "auditChange.fullCount": "{sections} 个配置模块 · {fields} 处变化",
  "auditChange.unknownActor": "未记录姓名",
  "auditChange.noHistory": "没有可用的字段修改记录，不能据此判断没有改动。可对比版本配置查看差异。",
  "module.count": "共 {ready} 项差异",
  "module.empty": "模块与来源版本一致，无需恢复。",
  "module.record": "恢复模块配置",
  "audit.route.mcpFeedbackResolve": "标记 MCP 反馈处理结果",
  "module.copyRecord": "从其他代理复制模块",
  "module.historySource": "将{module}恢复为 v{version} 的配置",
  "module.grouping": "修改记录分组",
  "module.byPerson": "按人",
  "module.byModule": "按模块",
  "module.select": "选择模块",
  "module.undoAll": "撤销本模块全部修改",
  "module.useActive": "使用当前生效版本配置",
  "module.scope": "按模块包含所有修改人的内容。撤销恢复到草稿来源版本；使用生效版本取本环境、本代理当前生效的配置。先预览，确认后只保存草稿。",
  "module.explain": "仅恢复所选模块，包含所有修改人的新增、删除和修改。确认后只保存草稿，不会发布，其他模块保留不动。",
  "module.baseSource": "草稿来源版本",
  "module.activeSource": "本代理当前生效版本",
  "module.title": "恢复模块 · v{version}",
  "module.apply": "确认恢复 {n} 项",
  "module.done": "已恢复 {n} 项。修改已保存到草稿。",
  "module.global": "全局参数（骰子与对抗）",
  "module.other": "其他玩法配置",
  "undo.crossTitle": "撤销 v{source} 的修改 → 草稿 v{target}",
  "undo.targetDraft": "撤销结果保存到草稿 v{version}，发布后才生效。",
  "undo.needDraft": "需要基于此版本的草稿才能撤销这些记录；也可先对比版本配置。",
  "undo.reason.identity": "两版中这个位置对应的关卡已不同，不能按编号直接恢复。",
  "undo.item": "撤销此项",
  "undo.session": "撤销这次修改",
  "undo.person": "撤销此人在本版的修改",
  "undo.personPage": "撤销此人在本页的修改",
  "undo.title": "撤销修改 · v{version}",
  "undo.explain": "只修改草稿，不会直接上线。仅恢复可安全撤销的项目，其余项目保留不动。",
  "undo.personRange": "按人撤销会检查此人在这一版的全部记录，不限于已加载的记录。",
  "undo.blocked": "当前页面还有未保存或未确认的修改，请先处理后再撤销。",
  "undo.count": "可撤销 {ready} 项 · 冲突 {conflict} 项 · 已恢复 {already} 项 · 无法自动撤销 {unavailable} 项",
  "undo.current": "当前值",
  "undo.restore": "恢复为",
  "undo.status": "处理结果",
  "undo.ready": "可以撤销",
  "undo.conflict": "有冲突，保留当前值",
  "undo.already": "已是原值，无需修改",
  "undo.unavailable": "无法自动撤销",
  "undo.reason.later": "之后还有其他修改，不能直接覆盖。",
  "undo.reason.different": "当前值与原记录不一致，或多次修改无法连续还原。",
  "undo.reason.missing": "这行已不存在。",
  "undo.reason.incomplete": "整行新增或删除需要完整记录，请选择撤销整次修改。",
  "undo.reason.legacy": "历史记录缺少完整的服务端改前、改后值。",
  "undo.reason.schema": "这条历史记录无法准确定位当前配置字段。",
  "undo.refresh": "重新预览",
  "undo.apply": "确认撤销 {n} 项",
  "undo.done": "已撤销 {n} 项。修改已保存到草稿。",
  "undo.record": "撤销修改",
  "undo.sources": "原记录：{ids}",
  "undo.empty": "所选记录没有可撤销的字段明细。",
  "undo.uncertain": "请求未完成确认，请先刷新预览查看当前配置，勿重复确认。",
  "auditChange.reverted": "这段时间有过修改，最终已恢复原值。下面保留每次保存的变化。",
  "auditChange.showSaves": "查看逐次保存",
  "auditChange.showNet": "只看最终变化",
  "auditChange.compareSource": "对比版本配置",
  "auditChange.leftVersion": "对比基准",
  "auditChange.rightVersion": "查看版本",
  "auditChange.incomplete": "{id} 的部分配置未能读取（{blocks}），无法完整对比，请重试。",
  "auditChange.contextChanged": "代理已切换，请重新打开对比。",
  "auditChange.imageAt": "{owner} · 第 {level} 关 · 第 {slot} 张",
  "auditChange.stageAt": "{owner} · 第 {level} 关",
  "auditChange.quantity": "数量",
  "auditChange.x": "横坐标",
  "auditChange.y": "纵坐标",
  "auditChange.main": "主建筑",
  "auditChange.slot": "图片序号",
  "auditChange.imageType": "图片类型",
  "auditChange.gridN": "拼图边长",
  "auditChange.source": "继承来源",
  "auditChange.nameReference": "缺少记录内名称时，位置名称取自该版本配置；编号始终保留。",
  "auditChange.loaded": "已加载 {loaded} / {total} 条记录",
  "talentPage.pageLog": "本页修改记录",
  "talentPage.pageCompare": "本页与线上对比 / 恢复",
  "talentPage.restore": "恢复本页为线上配置",
  "talentPage.hint": "将 {draft} 的天赋节点恢复为当前线上 {online} 的内容。其他页面保留，确认后自动保存到草稿；发布后才会影响玩家。",
  "talentPage.changed": "配置已发生变化，请关闭后重新对比。",
  "talentPage.error": "加载线上配置失败，请重新打开对比。",
  // 外壳
  "initcfg.sys.jigsaw.title": "新版拼图（五猫）",
  "initcfg.sys.jigsaw.desc": "每关五只猫升星，逐格揭开图片。与旧建筑版共用关卡、价格和进度，后台按拼图用途显示素材说明。",
  "level.col.puzzleImg": "25 格待揭图片",
  "hint.puzzleImg": "上传本关完整待揭图片（bg_img）。客户端切成 5×5，按解锁状态逐格显示；外围全屏背景和五猫形象使用客户端设计稿固定资源。",
  "level.presentation.jigsaw": "当前展示：新版拼图。封面图用于选关；25 格待揭图片用于中间拼图。外围全屏背景和五猫形象由客户端提供。",
  "level.presentation.buildings": "当前展示：旧建筑。封面图用于选关；建造底图用于场景背景，建筑素材在「建造」中配置。",
  "building.presentation.jigsaw": "新版拼图：建筑 1～5 从左到右对应五猫，逐星造价仍在这里配置。五猫形象和外围背景使用客户端固定资源，本页建筑图片、动画、损毁图及坐标不用于新版展示。",
  "app.title": "飞行棋运营后台",
  "app.welcome": "欢迎回来，{name}",
  "top.agent": "A0代理",
  "top.agentPlaceholder": "选择代理",
  "top.clear": "清除",
  "top.switchLang": "切换后台界面语言",
  "top.operationLog": "操作日志",
  "top.logout": "退出登录",
  "top.notLoggedIn": "未登录",
  "agent.searching": "搜索中…",
  "agent.loadFailed": "代理列表拉取失败",
  "agent.needSuperAdmin": " —— 本页需从 super-admin 进入才有登录凭据",
  "agent.noMatch": "没有匹配「{q}」的代理",
  "agent.empty": "无代理数据",
  "agent.inherited": "（还没有生效配置）",
  "agent.inheritedTip": "该代理还没有生效的配置版本（配置不跨代理继承）：玩家进游戏会被告知「还没有生效的配置版本」。到「初始化配置」建一版、填完发布即可。",
  "agent.degraded": "中台未拉通，以下为本地已配置的代理",

  // 通用
  "common.save": "保存",
  "common.next": "下一步",
  "common.cancel": "取消",
  "common.pick": "请选择",
  "common.apply": "应用",
  "common.reset": "还原",
  "common.fillDefaults": "填入参考值",
  "common.edit": "编辑",
  "common.view": "查看",
  "common.delete": "删除",
  "common.done": "完成",
  "common.add": "新增",
  "common.readonly": "只读",
  "common.editable": "可编辑",
  "common.pending": "待填",
  "common.none": "不发",
  "common.unset": "未选择",
  "common.boolUnset": "还没设置（勾选或取消一次即可）",
  "common.unfilledPublish": "还没填，发布前要填",
  "common.negativeNotSaved": "不能为负，存不进去",
  "common.invalidNotSaved": "取值不合规，存不进去（还是原来的值 {v}）",
  "common.clearedNotSaved": "清空不会存，原来的值（{v}）还在",
  "common.liveSaveHint": "改动自动保存；标红的项发布前要改好（负数和清空存不进去）。",
  "common.create": "创建",
  "tile.fightInvalid": "标红的项是空的或负数，这样存不进去（还是原来的值）；填好非负数就会存。",
  "common.invalid": "取值不合法",
  "common.retry": "重试",
  "common.search": "搜索…",
  "common.searchFailed": "没搜成——接口没应答，这不等于查无此人",
  "common.searching": "搜索中…",
  "common.createOption": "添加「{q}」",
  "common.noMatch": "没有匹配「{q}」的选项",

  // 菜单分组
  "navGroup.global": "全局配置",
  "navGroup.clear": "通关配置",
  "navGroup.board": "棋盘配置",
  "navGroup.card": "卡牌配置",
  "navGroup.act": "活动配置",
  "navGroup.shop": "商店管理",
  "navGroup.idol": "idol 中心",
  "navGroup.puzzle": "拼图配置",
  "spin.eb.title": "盘面经济对账",
  "spin.eb.live": "随表格内容实时重算",
  "spin.eb.ok": "经济可达：E={e} ∈ [{lo}, {hi}]",
  "spin.eb.bad": "E 出可达区间（E={e}，区间 [{lo}, {hi}]）：发布会被 SPIN_EB_RANGE 拦下——引擎只能在区间边界内纠偏",
  "spin.eb.none": "没有启用且权重大于 0 的格子，算不出可达区间。",
  "spin.eb.noE": "全局参数 E 还没填：发布校验会跳过经济对账，本面板同样不出结论。填上 E 后这里实时显示可达区间。",
  "spin.eb.e.none": "未填",
  "spin.eb.lo": "每骰期望 B 下沿 lo",
  "spin.eb.lo.sub": "金币取 min、承载取下限",
  "spin.eb.hi": "每骰期望 B 上沿 hi",
  "spin.eb.hi.sub": "金币取 max、承载取上限",
  "spin.eb.e": "全局每骰产出 E",
  "spin.eb.e.ok": "落在可达区间内",
  "spin.eb.e.bad": "需调权重或金额区间",
  "spin.eb.foot": "按启用掉落项的权重计算金币期望区间。骰子、盾牌和事件项不计金币；金额类玩法按「承载 × E」计入。",
  "spin.g.ok": "经济可达：每骰可达区间 [{lo}, {hi}] 骰，包含 1 骰",
  "spin.g.bad": "每骰可达区间不对：{why}。发布会被 SPIN_G_RANGE 拦下",
  "spin.g.lo": "每骰期望下沿（几骰）",
  "spin.g.hi": "每骰期望上沿（几骰）",
  "spin.g.target": "目标：每骰 1 骰",
  "spin.g.foot": "这个版本按单掷金币 g 算：每格金额填几倍单掷金币，金币类副玩法按承载（几骰）计入，骰子、盾牌和事件项不计金币。含区间的盘面每骰可达区间要严格包含 1，全定值的盘面每骰期望要恰为 1。",
  "p.fight.moved": "攻击 / 偷取 / 御守 / 福神的数值在各自副玩法页配置（棋盘配置 · 副玩法配置 → 雷云、抢金库（含福神）、御守）；本页只保留跨玩法共用的参数。",
  "p.atkSuccess": "攻击成功率",
  "p.atkSuccess.hint": "atk_success_bps：雷云攻击判定成功的概率。",
  "p.atkBlock": "被挡所得",
  "p.atkBlock.hint": "攻击被对方御守挡下时，攻击方仍能拿到的补偿。填几倍单掷金币。单掷金币 = 这一关掷一次平均能拿的金币，1 表示一掷的钱。右边括号是各关折合的金币。没配「每掷产出」E 时按 B 个数填。",
  "p.stealFloor": "单次保底",
  "p.stealFloor.hint": "一次至少偷到多少，对方余额很少也不会空手。填几倍单掷金币。单掷金币 = 这一关掷一次平均能拿的金币，1 表示一掷的钱。右边括号是各关折合的金币。没配「每掷产出」E 时按 B 个数填。",
  "p.protectNpc": "新手保护 · 仅 NPC 到",
  "p.protectNpc.hint": "protect_npc_level：第 1 关至该关只匹配 NPC，不进入真人对抗池。",
  "p.protectHalf": "新手保护 · 减半到",
  "p.protectHalf.hint": "protect_half_level：被偷取、被雷击的玩家关卡不超过这一关时，他损失的金币减半（被访次数不变）。",
  "p.fight.mapped": "全局参数 · 映射编辑",
  "p.fight.mappedHint": "底层仍是全局参数一份，格子页与「对抗与控骰」页改动同步，发布随版本走。",
  "tile.fightTitle": "对抗数值",
  "vs.unitHint": "金额单位是「掷」：当前关卡掷一次骰子的平均金币（1 级约 6,000，100 级约 18 万）。一套数全程通用，等级越高一掷越值钱。",
  "vs.grp.hit": "打中",
  "vs.grp.blocked": "被挡",
  "vs.grp.victim": "对手",
  "vs.grp.steal": "偷到",
  "vs.grp.clawback": "福神讨回",
  "vs.grp.protect": "保护",
  "vs.f.pct": "拿对手余额的",
  "vs.f.floor": "最少",
  "vs.f.cap": "最多",
  "vs.f.success": "打中概率",
  "vs.f.blockGain": "攻方拿",
  "vs.f.repair": "修楼花这一星造价的",
  "vs.f.daily": "每人每天最多被打",
  "vs.f.half": "这一级及以下被打减半",
  "vs.h.success": "没被御守挡下时打中的概率",
  "vs.h.block": "被御守挡下时的安慰，不会超过打中能拿的",
  "vs.h.daily": "雷云和抢金库合计",
  "vs.unit.roll": "掷",
  "vs.unit.level": "级",
  "vs.e.blank": "要填一个不小于 0 的数",
  "vs.e.floorCap": "不能比「最多」大",
  "vs.e.blockFloor": "不能比打中最少的 {n} 掷多",
  "vs.e.pct": "最多 {n}%",
  "vs.e.success": "最多 100%",
  "vs.e.daily": "填 {lo}～{hi}",
  "vs.preview": "效果预览",
  "vs.unitNote": "单位：掷",
  "vs.unitNoteB": "单位：B",
  "vs.halfNote": "（这一级在保护内，对手掉的已减半）",
  "vs.lv": "{n} 级",
  "vs.col.opp": "对手{name}（余额 {n}）",
  "vs.opp.poor": "穷",
  "vs.opp.mid": "普通",
  "vs.opp.rich": "富",
  "vs.row.hit": "打中，攻方拿",
  "vs.row.blocked": "被挡，攻方拿",
  "vs.row.gain": "攻方拿",
  "vs.row.lost": "对手掉",
  "vs.foot": "攻方拿的与对手掉的分开算，差额由系统出。开倍率只乘攻方拿的。",
  "tile.missingReadonly": "当前打开的是已发布版本（只读）。先在页头「基于此新建草稿」，再回到本页新建这一格。",
  "tile.missingUnavail": "玩法格数据暂时加载不可用，无法新建。请刷新页面重试；持续失败请查看连接状态。",
  "u.level": "关",
  "u.pct": "%",
  "u.permille": "‰",
  "p.giftCap": "每日赠卡上限",
  "p.giftCap.hint": "daily_gift_cap：每天最多赠出的物品卡张数。0 = 无封顶。",
  "p.friendLimit": "好友上限",
  "p.friendLimit.hint": "friend_limit：一个玩家最多能有多少好友，任一方到顶都拒。填 0 按默认 500 算；天赋树的好友位在它上面加。",
  "p.socialEventCap": "时间线上报上限",
  "p.socialEventCap.hint": "daily_social_event_cap：每天最多上报多少条时间线事件，防刷屏。0 = 关闭上报。",
  "p.avatarUploadCap": "头像上传上限",
  "p.avatarUploadCap.hint": "daily_avatar_upload_cap：每天最多传几张自定义头像，防止刷图打爆存储。0 = 关闭上传。⚠️ 2026-10-19 起服务端暂不执行这条上限（联调阶段），改了也不生效。",
  "p.puzzleHintCap": "拼图提示每日上限",
  "p.puzzleHintCap.hint": "puzzle_hint_daily_limit：每名玩家每天（UTC）能用几次拼图提示，所有 idol、所有拼图共用，UTC 0 点恢复。0 = 不给提示。",
  "pp.hint.title": "拼图提示次数（今天）",
  "pp.hint.limit": "每日上限",
  "pp.hint.used": "今天已用",
  "pp.hint.remaining": "剩余",
  "pp.hint.resetsAt": "{at} 自动恢复",
  "pp.hint.reset": "重置提示次数",
  "pp.hint.confirm": "把这名玩家今天已用的 {n} 次清零，剩余回到 {limit} 次。确定吗？",
  "audit.res.puzzleHint": "拼图提示次数",
  "p.uidQueryCap": "UID 查人上限",
  "p.uidQueryCap.hint": "daily_uid_query_cap：每天最多按好友代码查多少次，挡枚举脚本。0 = 关闭查询。",
  "p.askCap": "每日索要上限",
  "p.askCap.hint": "daily_ask_cap：每天最多发起的索要贴数。0 = 无封顶。",
  "spin.kind.item_card": "物品卡",
  "spin.pool": "三选一池子",
  "spin.pool.ATK_ONLY": "只出攻",
  "spin.pool.DEF_ONLY": "只出守",
  "spin.pool.MIXED": "攻守混",
  "spin.poolNote": "多人棋盘的道具格引用这一行时按它分池；改这里，引用它的格子全跟着改",
  "spin.kind.ability_item": "道具卡（多人对局）",
  "spin.kind.send_home": "遣回家（多人对局）",
  "spin.kind.warp": "穿越",
  "spin.kind.group": "副玩法组 {g}",
  "spin.itemCardNote": "掉落包与概率在卡牌配置里配",
  "spin.kind.free": "免费掷骰券",
  "spin.kind.wildcard": "万能卡",
  "spin.wildcardNote": "张；可兑换开放兑换的物品卡/idol 卡，对账计 0",
  "spin.kind.puzzle_frag": "拼图碎片",
  "spin.kind.gems": "钻石",
  "spin.gemsNote": "颗",
  "spin.puzzleFragNote": "片",
  "card.reupload": "替换",
  "card.f.coverImg": "封面图",
  "card.hint.coverImg": "可选。png / jpg / webp；上传即预览，保存后随卡包落库",
  "spin.freeNote": "张；用券掷骰不扣骰子、强制 x1",
  "card.dropStar": "完成 {id} 星",
  "card.levelRule": "同等级全部启用物品卡包的概率合计须为 100%。按该概率选包，再按包内权重抽卡；未配置的等级不掉卡。",
  "card.levelLegacy": "本包保留旧逐星数据，请按玩家等级重新填写掉落概率；星数不能直接当等级。保存后清除旧逐星配置。",
  "card.levelLegacyCount": "{n} 个启用包仍保留旧逐星配置，需要按等级重配后保存",
  "card.levelTotal": "物品卡概率 · {from}～{to} 级",
  "card.levelTotalValue": "{level} 级：{total}%",
  "card.prevLevels": "上一组等级",
  "card.nextLevels": "下一组等级",
  "card.f.drops": "掉落玩家等级与概率",
  "card.dropLevel": "玩家 {id} 级",
  "card.dropEmpty": "未配置则该卡包不会掉落；按玩家等级配置，同级各包概率合计 100%",
  "card.f.wildcardOk": "可被万能卡兑换",
  "card.hint.wildcardOk": "wildcard_ok：万能卡只能兑换开了此开关的包里的卡。物品包与 idol 包同一开关。",
  "nav.docLink": "查看规则文档",
  "shell.zhongtaiSunset": "从中台进入的方式将于 10 月 31 日关闭（还剩 {days} 天）。请改用账号密码或 Discord 登录：退出后在登录页选择。",
  "navGroup.g-player": "玩家管理",
  "navGroup.g-push": "推送管理",
  "nav.report-client-state": "客户端数据",
  "cs.player": "玩家",
  "cs.kwPh": "昵称 / 玩家 id（hex）",
  "cs.search": "查询",
  "cs.reset": "重置",
  "cs.refresh": "刷新",
  "cs.back": "← 返回列表",
  "cs.hint": "列表是本代理全部玩家，写过客户端数据的排前面。这张表是客户端自己存的键值（引导进度、UI 偏好、演出标记…），服务端不解释内容；本页给客服排障用，正常运营不需要动它。",
  "cs.listTitle": "玩家",
  "cs.listMeta": "共 {total} 人 · 第 {page}/{pages} 页",
  "cs.listEmpty": "没有匹配的玩家",
  "cs.col.player": "玩家",
  "cs.col.level": "关卡",
  "cs.col.keyCount": "客户端键数",
  "cs.col.lastWrite": "最近写入",
  "cs.col.lastActive": "最近活跃",
  "cs.never": "从未",
  "cs.prev": "上一页",
  "cs.next": "下一页",
  "cs.emptyPlayer": "玩家 {id} 在当前代理下没有任何客户端自留数据",
  "cs.summary": "{n} / {max} 键 · {bytes} 字节",
  "cs.summaryHint": "每玩家总键数上限 {max}（跨命名空间），超了客户端写入会被拒（60220）。",
  "cs.nsList": "命名空间",
  "cs.keysN": "{n} 个键",
  "cs.clearNs": "清空命名空间",
  "cs.col.key": "键",
  "cs.col.value": "值",
  "cs.col.valueHint": "客户端存什么显示什么，服务端不解析；超长只显示开头，点「查看」看全文。",
  "cs.col.bytes": "字节",
  "cs.col.ver": "ver",
  "cs.col.verHint": "值实际变化才 +1，同值重写不涨；客户端带 expect_ver 写入时以它判冲突。",
  "cs.col.updated": "更新时间",
  "cs.col.ops": "操作",
  "cs.view": "查看",
  "cs.delete": "删除",
  "cs.cancel": "取消",
  "cs.deleteKey": "删除此键",
  "cs.save": "保存",
  "cs.editNotice": "这是客户端自己写的数据，服务端不校验格式。改值只用于排障（比如把卡住的引导步骤拨回去）；保存后 ver +1，客户端下次带 expect_ver 写入会收到冲突并以服务端为准。",
  "cs.verLine": "ver {ver}",
  "cs.updatedAt": "更新于 {t}",
  "cs.bytesOf": "{n} / {max} 字节",
  "cs.confirmDeleteTitle": "删除键",
  "cs.confirmClearTitle": "清空命名空间",
  "cs.confirmDeleteBody": "将删除玩家 {id} 的 {ns} / {k}。客户端本地缓存不受影响，它下次写入会重新建行（ver 从 1 起）。",
  "cs.confirmClearBody": "将删除玩家 {id} 在 {ns} 下的全部 {n} 个键。不可恢复。",
  "cs.confirmDelete": "确认删除",
  "cs.confirmClear": "确认清空",
  "cs.savedToast": "已改写 {ns} / {k}，ver → {ver}",
  "cs.deletedToast": "已删除 {ns} / {k}",
  "cs.clearedToast": "已清空命名空间 {ns}，删除 {n} 个键",
  "cs.tooLong": "值超过 {max} 字节，未保存",
  "cs.loadFail": "读取失败：{msg}",
  "cs.rel.now": "刚刚",
  "cs.rel.min": "{n} 分钟前",
  "cs.rel.hour": "{n} 小时前",
  "cs.rel.day": "{n} 天前",
  "navGroup.g-report": "数据报表",
  "nav.report-spin": "spin 落点报表",
  "report.range": "时间范围",
  "report.range.today": "今天",
  "report.range.d7": "近 7 天",
  "report.range.d30": "近 30 天",
  "report.levels": "关卡",
  "report.levels.all": "全部关卡",
  "report.refresh": "刷新",
  "report.loading": "加载中…",
  "report.empty": "所选范围内还没有掷骰记录。",
  "report.noActive": "还没有生效的配置版本——理论概率无从算起。先在配置台发布并激活一版。",
  "report.dist": "按格命中分布",
  "report.meta": "共 {n} 次掷骰",
  "report.col.name": "名称",
  "report.col.rewards": "奖励类型",
  "report.col.theoretical": "理论概率",
  "report.col.theoretical.hint": "按当前生效版本的 weight ÷ 启用格权重之和计算。",
  "report.col.actual": "实际命中率",
  "report.col.hits": "命中次数",
  "report.col.dev": "偏差",
  "report.col.dist": "分布",
  "report.devNote": "偏差 = 实际命中率 − 理论概率，单位为百分点（pp）：超过 ±2pp 标黄、±5pp 标红。",
  "report.events": "保底 / 冷却观察",
  "report.events.only": "只看事件对抗格",
  "report.gapUnit": "骰 / 次",
  "report.never": "未命中",
  "report.hitN": "命中 {n} 次",
  "report.noEvents": "当前没有启用中的事件对抗格。",
  "report.foot": "数据来自 main_roll 结算记录，只读。理论概率按当前生效版本的权重算；改版期间新旧版本混在同一时间窗时以实际为准。平均间隔为 总骰数 ÷ 命中数 的近似口径。",
  "navGroup.g-tools": "工具",
  "navGroup.g-help": "教程",
  "nav.sim": "模拟器",
  "nav.debug-trace": "联调追踪",
  "nav.agents": "代理管理",
  "nav.hot-update": "热更新发布",
  "agents.libTitle": "头像 / 昵称库",
  "agents.libStats": "国家 {countries} · 头像 {avatars} · 头像国家匹配 {matches} · 昵称 {nicknames}",
  "agents.libUnknown": "统计读取失败",
  "agents.libHint": "游客建号时按国家随机取默认头像与昵称；数据从 portwind-avatar 同步，幂等可重跑。约 30 万行，全量一次几分钟。",
  "agents.libSync": "同步姓名库",
  "agents.libSyncing": "同步中…",
  "agents.libSynced": "头像 / 昵称库同步完成",
  "agents.notice": "这里是 A0（代理 / 租户）的唯一权威：顶栏下拉和玩家登录都按它判断。没登记或停用的 a0 登录会被拒（60503）。名称、状态、时区、备注都在这里手工改。",
  "agents.filterHint": "按 a0 或名称过滤",
  "agents.new": "新增代理",
  "agents.a0": "a0",
  "agents.name": "名称",
  "agents.status": "状态",
  "agents.tz": "时区（展示用）",
  "agents.note": "备注",
  "agents.enabled": "启用",
  "agents.disabled": "停用",
  "agents.ownConfig": "已有生效配置",
  "agents.inherited": "还没有生效配置",
  "agents.saved": "已保存 {a0}",
  "agents.empty": "没有匹配的代理",
  "agents.a0Hint": "1~64 位字母、数字、下划线或连字符；保存后不可改",
  "agents.tzHint": "IANA 时区名，如 Asia/Tokyo；服务端日切统一 UTC，这里只影响管端显示",
  "agents.filter.all": "全部 {n}",
  "agents.filter.on": "启用 {n}",
  "agents.filter.off": "停用 {n}",
  "agents.col.config": "数值配置",
  "agents.col.login": "三方登录",
  "agents.col.updated": "最后更新",
  "agents.col.act": "操作",
  "agents.login.own": "自有条目",
  "agents.login.default": "用默认条目",
  "agents.login.listHint": "「三方登录」列：没有自己条目（条目 + 密钥）的代理，玩家 Apple / Google 登录走服务端默认条目。",
  "agents.editTitle": "编辑代理 {a0}",
  "agents.saving": "保存中…",
  "agents.sec.basic": "基本信息",
  "agents.sec.flags": "功能开关",
  "agents.err.basicTab": "基本信息有误，请回到「基本信息」修改",
  "agents.sec.login": "三方登录",
  "agents.sec.notify": "变更通知",
  "agents.notify.hint": "填一个飞书群机器人的 webhook，这个代理发布、设为生效、删除或恢复草稿时往群里推一条，写清谁做的、这一版里谁改了什么。不按每次保存推。",
  "agents.notify.saveFirst": "先保存基本信息，建好代理后再回来配置通知。",
  "agents.notify.unavailable": "服务端暂不支持这个接口（配置变更通知尚未上线）",
  "agents.notify.webhook": "群机器人 webhook",
  "agents.notify.webhookPh": "https://open.larksuite.com/open-apis/bot/v2/hook/…（留空 = 不修改）",
  "agents.notify.bad": "要是飞书群机器人的地址：https://open.larksuite.com/open-apis/bot/v2/hook/…（或 open.feishu.cn）",
  "agents.notify.set": "已设置（{at} 由 {by} 更新）",
  "agents.notify.unset": "未设置，这个代理不发通知",
  "agents.notify.save": "保存",
  "agents.notify.test": "发送测试消息",
  "agents.notify.clear": "关闭通知",
  "agents.notify.saved": "已保存 {a0} 的通知群",
  "agents.notify.cleared": "已关闭 {a0} 的配置变更通知",
  "agents.notify.clearBody": "关闭后 {a0} 发布、删除草稿等操作不再往群里推消息。确定关闭？",
  "agents.notify.testOk": "测试消息已发出，去群里看一眼",
  "agents.notify.testFail": "测试消息没发出去：{msg}",
  "agents.err.basic": "基本信息保存失败：{msg}",
  "agents.err.login": "三方登录保存失败：{msg}",
  "agents.v.a0Empty": "请填写 a0",
  "agents.v.nameEmpty": "请填写名称",
  "agents.v.tz": "「{tz}」不是合法的 IANA 时区名",
  "agents.v.appIdForSecret": "填了密钥就必须填条目",
  "agents.col.ably": "实时推送",
  "agents.ably.label": "实时推送（Ably）",
  "agents.ably.hint": "开启后这个代理的新会话、新对局走 Ably 实时推送；关掉只影响之后新加入的。保存后最多一分钟生效",
  "agents.ably.on": "已开启",
  "agents.ably.off": "未开启",
  "agents.ably.envTag": "配置文件开启",
  "agents.ably.envOn": "配置文件已开启（ABLY_A0S），在这里关不掉",
  "agents.col.clientTa": "客户端埋点",
  "agents.col.serverTa": "服务端埋点",
  "agents.serverTa.label": "服务端埋点",
  "agents.serverTa.hint": "这个代理的服务端事件是否上报数数；默认关。还要服务端配置文件 TA_EVENTS_A0S 包含这个代理才会上报。关着期间的事件不补报，保存后最多一分钟生效",
  "agents.merge.label": "合成玩法入口",
  "agents.merge.hint": "开启后这个代理的玩家才看得到合成玩法入口；默认关。还要这个代理的线上版本配了合成才会显示，没配就算开着也没有入口。只管入口显示。服务端和客户端各缓存一分钟，保存后最多约两分钟、玩家下次打开入口页或回到前台时生效，一直停在入口页的玩家不会自己变",
  "agents.clientTa.label": "客户端埋点",
  "agents.clientTa.hint": "开启后这个代理的客户端才初始化数数 SDK、上报客户端埋点；默认关。客户端登录时取一次，保存后最多一分钟、玩家下次登录时生效。只管客户端，服务端埋点看下面的开关",
  "agents.statusHint": "停用后这个 a0 的玩家登录会被拒，顶栏默认不列出",
  "agents.login.hint": "没有自己条目的代理使用服务端默认条目。",
  "agents.login.saveFirst": "先保存基本信息，建好代理后再回来配置三方登录。",
  "agents.login.unavailable": "服务端暂不支持这个接口（三方登录录入尚未上线）",
  "agents.login.appId": "条目 app_id",
  "agents.login.secret": "密钥 app_secret",
  "agents.login.secretPh": "留空 = 不修改",
  "agents.login.secretSet": "已设置（{at} 由 {by} 更新）",
  "agents.login.secretUnset": "未设置",
  "agents.login.save": "保存三方登录",
  "agents.login.clear": "清除自有条目",
  "agents.login.saved": "已保存 {a0} 的三方登录",
  "agents.login.cleared": "已清除 {a0} 的自有条目",
  "agents.login.clearBody": "清除后 {a0} 的条目和密钥都清空，玩家三方登录改走服务端默认条目。确定清除？",
  "agents.col.iap": "内购",
  "agents.iap.own": "自有",
  "agents.iap.default": "默认",
  "agents.iap.listHint": "「内购」列：自有 = 至少一个平台在这里填了商店凭证；没填的平台验单用服务端默认凭证。",
  "agents.sec.iap": "内购凭证",
  "agents.iap.hint": "按平台填写。没填的平台使用服务端默认凭证。私钥只写不读：保存后不会回显，留空就是不改。",
  "agents.iap.saveFirst": "先保存基本信息，建好代理后再回来配置内购凭证。",
  "agents.err.iap": "内购凭证保存失败：{msg}",
  "agents.iap.unavailable": "服务端暂不支持这个接口（内购凭证录入尚未上线）",
  "agents.iap.ios": "iOS 内购（App Store Server API）",
  "agents.iap.android": "Android 内购（Google Play）",
  "agents.iap.issuerId": "Issuer ID",
  "agents.iap.keyId": "Key ID",
  "agents.iap.p8": "p8 私钥",
  "agents.iap.p8Ph": "粘贴 .p8 文件的全部内容（-----BEGIN PRIVATE KEY----- …）",
  "agents.iap.saJson": "服务账号 JSON",
  "agents.iap.saJsonPh": "粘贴 Google Cloud 下载的 service account JSON 文件全部内容",
  "agents.iap.pickFile": "从文件读取",
  "agents.iap.saParsed": "将保存：{email}",
  "agents.iap.saInvalid": "不是合法的 service account JSON（需要 client_email 与 private_key）",
  "agents.iap.rtdnAudience": "RTDN audience",
  "agents.iap.rtdnAudiencePh": "Pub/Sub 推送订阅的 audience，可选",
  "agents.iap.rtdnSa": "RTDN 服务账号邮箱",
  "agents.iap.rtdnSaPh": "推送订阅的身份验证服务账号，可选",
  "agents.iap.rtdnHint": "两项要么都填、要么都不填；不填则收不到 Google 退款通知。",
  "agents.iap.iosSet": "已设置：Key ID {keyId} · Issuer {issuer}（{at} 由 {by} 更新）",
  "agents.iap.androidSet": "已设置：{email} · RTDN {rtdn}（{at} 由 {by} 更新）",
  "agents.iap.rtdnNone": "未配置",
  "agents.iap.unset": "未设置 → 使用默认凭证",
  "agents.iap.v.iosIncomplete": "iOS 的 Issuer ID、Key ID、p8 私钥要一起填",
  "agents.iap.v.rtdnHalf": "RTDN 的两项要么都填、要么都不填",
  "agents.iap.v.rtdnNeedsSa": "填 RTDN 需要同时粘贴 service account JSON（Android 凭证整块替换）",
  "agents.iap.save": "保存内购凭证",
  "agents.iap.saved": "已保存 {a0} 的内购凭证",
  "agents.col.update": "版本更新",
  "agents.update.none": "未设",
  "agents.sec.update": "版本更新",
  "agents.update.hint": "按平台填 x.y.z。低于最低版本：强制更新，除启动检查与内购补单外的接口都回 60008，客户端挡在更新页；低于最新版本：提示更新，玩家可关掉。留空就是不设。最低代码版本：代码版本（热更新后的版本）低于它，要先热更新才能继续（60009）。保存后游戏端最多一分钟生效。",
  "agents.update.saveFirst": "先保存基本信息，建好代理后再回来配置版本更新。",
  "agents.err.update": "版本更新保存失败：{msg}",
  "agents.update.unavailable": "服务端暂不支持这个接口（版本更新尚未上线）",
  "agents.update.min": "最低版本",
  "agents.update.minHint": "低于它强制更新",
  "agents.update.latest": "最新版本",
  "agents.update.latestHint": "低于它提示更新",
  "agents.update.storeUrl": "商店地址",
  "agents.update.storeUrlPh": "https://… 留空用包里自带的地址",
  "agents.update.save": "保存版本更新",
  "agents.update.saved": "已保存 {a0} 的版本更新",
  "agents.update.last": "最后修改：{at} · {by}",
  "agents.update.v.version": "{platform} 的版本要写成 x.y.z，如 1.2.0",
  "agents.update.v.order": "{platform} 的最低版本不能高于最新版本",
  "agents.update.v.url": "{platform} 的商店地址必须以 https:// 开头",
  "agents.update.confirmTitle": "确认抬高最低版本",
  "agents.update.confirmBody": "最低版本将改成：{platform}。低于它的玩家一分钟内会被挡在更新页，只能去商店更新。确认这些版本已经在商店上架、玩家能下载到。",
  "agents.update.confirm": "确认保存",
  "agents.update.minCode": "最低代码版本",
  "agents.update.minCodeHint": "低于它要先热更新",
  "agents.update.notesVersion": "日志对应包版本",
  "agents.update.notes": "商店包更新日志",
  "agents.update.notesHint": "填写这份说明对应的 x.y.z；与最新包版本（未设时最低包版本）一致才下发。只显示玩家文案。",
  "agents.update.notesMismatch": "对应版本未匹配最新/最低包版本，暂不向客户端显示这些说明。",
  "agents.update.notesEnglish": "更新日志需填写英文，其余语言可选",
  "agents.update.notesLong": "更新日志每种语言最多 4000 字，合计不超过 16 KiB",
  "agents.update.codeTag": "代码≥",
  "agents.update.confirmCodeBody": "最低代码版本将改成：{platform}。代码版本低于它的玩家一分钟内会被要求先热更新，热更完才能继续。确认这一版热更新已经发布到 CDN、对应的商店包都能拿到。",
  "agents.iap.clear": "清除 {platform}",
  "agents.iap.cleared": "已清除 {a0} 的 {platform} 内购凭证",
  "agents.iap.clearBody": "清除后 {a0} 的 {platform} 验单改用服务端默认凭证。确定清除？",
  "nav.shop": "商城商品",
  "nav.asset-info": "物品资料",
  "nav.iap": "内购配置",
  "iap.title": "内购配置",
  "iap.intro": "现金商品走苹果 / 谷歌内购：先在这里配好每个平台的 App，再给现金商品填上商店商品 ID，C 端才能下单。保存即生效。",
  "iap.needA0": "先在右上角选一个代理——内购按代理各配各的。",
  "iap.apps.title": "App 配置",
  "iap.apps.hint": "每个平台一行。验单时小票里的 Bundle ID / 包名必须与这里一致，否则拒绝。",
  "iap.items.title": "内购商品",
  "iap.items.hint": "只列出定价方式为「现金」的商城商品；某个平台的商品 ID 空着 = 该平台不卖。",
  "iap.items.empty": "还没有现金商品。先到「商城商品」新建商品，定价方式选「现金」。",
  "iap.col.platform": "平台",
  "iap.col.bundle": "Bundle ID / 包名",
  "iap.col.appleAppId": "App Apple ID",
  "iap.col.sandbox": "沙盒购买",
  "iap.col.appStatus": "状态",
  "iap.col.updated": "最近修改",
  "iap.col.ops": "操作",
  "iap.col.item": "商品",
  "iap.col.kind": "类型",
  "iap.col.price": "展示价",
  "iap.col.shopStatus": "上架",
  "iap.col.ios": "App Store 商品 ID",
  "iap.col.android": "Google Play 商品 ID",
  "iap.platform.ios": "苹果 iOS · App Store",
  "iap.platform.android": "安卓 · Google Play",
  "iap.sandbox.on": "允许",
  "iap.sandbox.off": "不允许",
  "iap.status.on": "启用",
  "iap.status.off": "停用",
  "iap.unset": "未配置",
  "iap.action.setup": "配置",
  "iap.action.edit": "编辑",
  "iap.appDialog.title": "{platform} App 配置",
  "iap.itemDialog.title": "{name} · 内购商品 ID",
  "iap.f.bundle.ios": "Bundle ID",
  "iap.f.bundle.android": "包名",
  "iap.f.bundle.hint": "与 App Store Connect / Google Play Console 里的完全一致，例如 com.example.game",
  "iap.f.appleAppId": "App Apple ID",
  "iap.f.appleAppId.hint": "App Store Connect → App 信息里的 Apple ID（纯数字）；暂时不知道可以先填 0",
  "iap.f.sandbox": "沙盒购买",
  "iap.f.sandbox.hint": "允许后，测试账号与审核账号的沙盒购买也会发货。正式环境通常关闭。",
  "iap.f.status": "状态",
  "iap.f.status.hint": "停用后该平台不能再下单；已付款的订单照常验单发货，退款通知也照常处理。",
  "iap.f.ios": "App Store 商品 ID",
  "iap.f.android": "Google Play 商品 ID",
  "iap.f.productId.hint": "与商店后台建的商品 ID 完全一致；字母或数字开头，只含字母、数字、点、下划线、横线；留空 = 该平台不卖",
  "iap.f.productId.invalid": "商品 ID 格式不对",
  "iap.toast.appSaved": "App 配置已保存",
  "iap.toast.itemSaved": "商品 ID 已保存",
  "assetInfo.title": "物品资料",
  "assetInfo.intro": "商城礼包里奖励图标的详情（图标、名称、介绍、获取途径）。按代理分别配置，保存后立即生效。",
  "assetInfo.needA0": "先在右上角选一个代理——物品资料按代理各配各的。",
  "assetInfo.filter.all": "全部",
  "assetInfo.filter.missing": "有缺失",
  "assetInfo.empty": "没有符合条件的物品",
  "assetInfo.col.icon": "图标",
  "assetInfo.col.item": "物品",
  "assetInfo.col.i18n": "多语言",
  "assetInfo.col.obtain": "获取途径",
  "assetInfo.col.use": "去使用",
  "assetInfo.col.show": "C 端展示",
  "assetInfo.col.updated": "最后修改",
  "assetInfo.col.ops": "操作",
  "assetInfo.noIcon": "未配图标",
  "assetInfo.never": "未配置",
  "assetInfo.obtainCount": "{n} 条 · {j} 跳转",
  "assetInfo.show.on": "开",
  "assetInfo.show.off": "关",
  "assetInfo.edit": "编辑",
  "assetInfo.audit": "审计",
  "assetInfo.auditTitle": "{name} · 修改记录",
  "assetInfo.saved": "物品资料已保存",
  "assetInfo.dialog.title": "编辑物品资料 · {name}",
  "assetInfo.f.icon": "单个图标",
  "assetInfo.f.icon.hint": "建议 128×128 透明 PNG",
  "assetInfo.f.iconPlural": "复数图标",
  "assetInfo.f.iconPlural.hint": "数量大于 1 时客户端使用；不配则用单个图标。建议 128×128 透明 PNG",
  "assetInfo.f.name": "名称",
  "assetInfo.f.desc": "介绍",
  "assetInfo.f.desc.hint": "一两句话，说明它是什么、用来做什么",
  "assetInfo.f.obtain": "获取途径",
  "assetInfo.f.obtain.hint": "按顺序展示，最多 {n} 条；带跳转的条目玩家可以点进对应页面",
  "assetInfo.f.obtain.none": "还没有获取途径",
  "assetInfo.f.obtain.add": "+ 添加一条",
  "assetInfo.f.obtainRow": "获取途径第 {n} 条",
  "assetInfo.f.use": "去使用",
  "assetInfo.f.use.hint": "可不填。填了会在介绍下面显示「去使用 >」按钮",
  "assetInfo.f.show": "在 C 端展示详情",
  "assetInfo.f.show.hint": "关掉后，玩家点这个物品的图标不弹详情",
  "assetInfo.jump.none": "无",
  "assetInfo.jump.shop_section": "商城分区",
  "assetInfo.jump.task": "任务",
  "assetInfo.jump.puzzle": "拼图",
  "assetInfo.jump.idol": "Idol 页",
  "assetInfo.jump.sectionGone": "分区「{id}」已不可用",
  "assetInfo.tab.daily": "每日",
  "assetInfo.tab.weekly": "每周",
  "assetInfo.tab.achievement": "成就",
  "assetInfo.tab.newbie": "新手七日",
  "assetInfo.missingSave": "{langs} 未填写，玩家将看到英文",
  "assetInfo.preview.sample": "示例",
  "assetInfo.preview.hidden": "已关闭展示：玩家点图标不会弹出这个详情",
  "shop.title": "商城商品",
  "shop.intro": "上架的商品 C 端立刻可见，玩家按定价方式用钻石、金币或现金购买，免费商品直接领取；下架即时生效，已买的不受影响。内容只能是主线资产（骰子、金币、免费券、护盾、万能卡、拼图碎片），现金商品还可以放钻石。idol 卡包与骰子单买另有固定入口，不在此配。",
  "shop.needA0": "先在右上角选一个代理——商品按代理各配各的。",
  "shop.empty": "还没有商品，点右上「新建商品」。",
  "shop.free": "免费",
  "shop.unlimited": "不限",
  "shop.status.on": "上架中",
  "shop.status.off": "已下架",
  "shop.col.name": "商品",
  "shop.col.contents": "内容",
  "shop.col.price": "价格",
  "shop.col.limit": "每人限购",
  "shop.col.sort": "排序",
  "shop.col.status": "状态",
  "shop.col.ops": "操作",
  "shop.action.new": "新建商品",
  "shop.action.on": "上架",
  "shop.action.off": "下架",
  "shop.action.edit": "编辑",
  "shop.action.delete": "删除",
  "shop.confirm.delete": "删除「{name}」？已买的订单不受影响，但商品将从列表消失且不可恢复。",
  "shop.toast.on": "已上架",
  "shop.toast.off": "已下架",
  "shop.toast.saved": "已保存",
  "shop.toast.created": "已创建（默认下架，确认无误后上架）",
  "shop.toast.deleted": "已删除",
  "shop.dialog.title": "商品",
  "shop.f.name": "名称",
  "shop.f.cover": "商品图",
  "shop.f.cover.hint": "选填。客户端商城列表展示",
  "shop.img.upload": "上传",
  "shop.img.replace": "替换",
  "shop.img.clear": "清除",
  "shop.img.uploading": "上传中…",
  "shop.img.none": "未上传",
  "shop.f.limit": "每人限购",
  "shop.f.limit.hint": "次数；0 = 不限",
  "shop.f.sort": "排序",
  "shop.f.sort.hint": "小的在前",
  "shop.f.contents": "内容",
  "shop.f.contents.hint": "买到什么，最多 10 项，同一资产不重复",
  "shop.asset.dice": "骰子",
  "shop.asset.coins": "金币",
  "shop.asset.free_rolls": "免费券",
  "shop.asset.shields": "护盾",
  "shop.asset.wildcard": "万能卡",
  "shop.asset.puzzle_frag": "拼图碎片",
  "shop.asset.gems": "钻石",
  "nav.shop-sections": "商城分区",
  "shop.col.section": "分区",
  "shop.filter.allSections": "全部分区",
  "shop.noSection": "未归类",
  "shop.kind.once": "一次性",
  "shop.kind.subscription": "月卡",
  "shop.card.banner": "大横幅",
  "shop.card.grid": "格子卡",
  "shop.priceKind.gem": "钻石",
  "shop.priceKind.coin": "金币",
  "shop.priceKind.free": "免费",
  "shop.priceKind.cash": "现金（商店内购）",
  "shop.period.lifetime": "终身",
  "shop.period.daily": "每天",
  "shop.period.weekly": "每周",
  "shop.period.monthly": "每月",
  "shop.badge.none": "无",
  "shop.badge.value_pct": "超值 %",
  "shop.badge.text": "文字",
  "shop.f.section": "分区",
  "shop.f.section.hint": "未归类的商品不进整页，只在平铺列表里",
  "shop.f.card": "卡片形态",
  "shop.f.card.hint": "大横幅用商品图，格子卡用小图；格子列数在分区里设",
  "shop.f.icon": "小图",
  "shop.f.icon.hint": "格子卡展示用",
  "shop.f.priceKind": "定价方式",
  "shop.f.priceKind.hint": "钻石 / 金币商品扣对应余额；现金商品的标价只用于展示，玩家实付以 App Store / Google Play 后台价格为准，需要在「内购配置」里填好商店商品 ID 才能买",
  "shop.f.cashCurrency": "币种",
  "shop.f.firstBonus": "首次加赠",
  "shop.f.firstBonus.hint": "第一次购买多发一份；留空 = 无",
  "shop.f.badge": "角标",
  "shop.f.badgeValue": "超值百分比",
  "shop.f.badgeText": "角标文字",
  "shop.f.desc": "说明",
  "shop.f.desc.hint": "卡片下方一行说明（可选）；解锁提示会自动生成，不用写",
  "shop.f.limitPeriod.hint": "按周期限购必须填次数；日切按代理时区",
  "shop.progressRequired": "尚未读取到有效的生效建筑体系，请检查当前配置后重试。",
  "shop.f.unlockLevel.hintPettown": "玩家等级到 N 级后可买（萌宠小镇 = 小镇等级，与物品卡掉落同一个等级）；0 = 无条件。未解锁的照常下发并置灰",
  "shop.f.unlockLevel": "解锁玩家等级",
  "shop.f.unlockLevel.hint": "玩家等级到 N 级后可买（关卡制 = 升满的建筑数）；0 = 无条件。未解锁的照常下发并置灰",
  "shop.f.kind": "商品类型",
  "shop.f.kind.hint": "月卡：买时发「内容」，之后每天领「每日份」，持续 N 天；有效期内再买 = 续期",
  "shop.f.durationDays": "有效天数",
  "shop.f.dailyContents": "每日份",
  "shop.f.window": "售卖窗口",
  "shop.f.window.hint": "留空 = 不限",
  "shop.f.window.to": "至",
  "shop.f.contents.none": "无",
  "shop.f.contents.addOne": "+ 加一项",
  "shop.f.contents.invalidPricing": "当前定价方式不支持所选资产，请更换或移除后保存。",
  "shop.f.contents.subHint": "月卡可留空（只有每日份）",

  // ── 系统邮件（docs/sys-mail-plan.md）。活表，不进配置版本：点发送即生效 ──
  "mail.title": "系统邮件",
  "mail.liveHint": "活表 · 发送即生效",
  "mail.search": "搜标题…",
  "mail.new": "写邮件",
  "mail.empty": "还没发过邮件",
  "mail.loading": "加载中…",
  "mail.preview.title": "玩家会看到",
  "mail.preview.untitled": "（还没填标题）",
  "mail.preview.failed": "预览没出来——正文没转换成功，现在看到的不是玩家会看到的。别照这个发。",
  "mail.preview.loading": "生成中…",
  "mail.preview.empty": "正文还是空的",
  "mail.preview.claim": "领取",
  "mail.preview.note": "由服务端按真正会落库的内容生成。不支持的格式在这里就会消失——那说明玩家也看不到它。",
  "mail.confirm.title": "确认发送",
  "mail.confirm.allWarn": "这封会进入**全服所有玩家**的收件箱。发出后只能撤回（让它从收件箱消失），已经被领走的奖励收不回来。",
  "mail.confirm.userWarn": "发出后只能撤回，已经被领走的奖励收不回来。",
  "mail.confirm.andMore": "…等 {n} 人",
  "mail.confirm.audience": "收件范围",
  "mail.confirm.rewards": "附件奖励",
  "mail.confirm.noRewards": "无（纯公告）",
  "mail.confirm.expire": "过期时间",
  "mail.confirm.send": "确认发送",
  "mail.need.title": "还缺英文标题",
  "mail.need.body": "还缺英文正文",
  "mail.need.titleLen": "英文标题超过 64 字",
  "mail.need.expire": "过期时间要晚于现在",
  "mail.need.rewardQty": "有奖励没填数量",
  "mail.need.rewardRef": "有奖励没选具体是哪个",
  "mail.need.recipients": "还没选收件人",
  "player.select.search": "搜昵称或 id",
  "common.pickedN": "已选 {n} 项",
  "player.select.more": "还有 {n} 个没列出，打细一点",
  "mail.withdraw.title": "撤回邮件",
  "mail.page": "第 {from}–{to} 条 / 共 {total} 条",
  "mail.prev": "上一页",
  "mail.next": "下一页",
  "mail.col.title": "标题",
  "mail.col.audience": "收件范围",
  "mail.col.rewards": "奖励项",
  "mail.col.sent": "发送时间",
  "mail.col.expire": "过期时间",
  "mail.col.operator": "操作人",
  "mail.col.act": "操作",
  "mail.audience.all": "全服",
  "mail.audience.user": "指定玩家",
  "mail.audience.userN": "指定 {n} 人",
  "mail.expired": "已过期",
  "mail.withdraw": "撤回",
  "mail.withdraw.confirm": "撤回「{title}」？它会立刻从所有玩家的收件箱消失。已经领走的奖励不会收回。",
  "mail.withdraw.ok": "已撤回",
  "mail.dialog.title": "写邮件",
  "mail.send": "发送",
  "mail.sent.ok": "已发送（{n} 人）",
  "mail.sent.all": "已发送（全服）",
  "mail.sent.replayed": "这封刚才已经发过了，没有重复发送",
  "mail.f.title": "标题",
  "mail.f.title.hint": "英文必填；其余语言进抽屉里填，玩家按自己的语言看",
  "mail.f.body": "正文",
  "mail.f.body.hint": "英文必填。换行会原样显示给玩家",
  "rich.tb.styles": "段落样式（正文 / 标题）",
  "rich.tb.undo": "撤销",
  "rich.tb.redo": "重做",
  "rich.tb.bold": "加粗",
  "rich.tb.italic": "斜体",
  "rich.tb.underline": "下划线",
  "rich.tb.ol": "有序列表",
  "rich.tb.ul": "无序列表",
  "rich.tb.alignLeft": "左对齐",
  "rich.tb.alignCenter": "居中",
  "rich.tb.alignRight": "右对齐",
  "rich.tb.link": "插入链接（只能是 https://）",
  "rich.tb.clear": "清除格式",
  "rich.hint": "支持加粗 / 斜体 / 下划线 / 链接 / 列表 / 对齐；链接只能是 https",
  "mail.f.audience": "收件范围",
  "mail.f.audience.hint": "全服只发给「此刻之前已注册」的玩家，之后注册的新玩家看不到这封",
  "mail.rcp.pick": "搜索挑人",
  "mail.rcp.paste": "批量粘贴",
  "mail.rcp.parsed": "切出 {n} 个 id。格式和归属由服务端核对，有一个不对整封拒发，不会只发一部分",
  "mail.f.recipients": "收件人",
  "mail.f.recipients.hint": "零散补发用搜索挑人（看得见昵称）；整份名单用批量粘贴。单封最多 5000 人",
  "mail.f.expire": "过期时间",
  "mail.f.expire.hint": "过期后这封从收件箱消失，没领的奖励作废、不补发",
  "mail.f.rewards": "附件奖励",
  "mail.f.rewards.hint": "留空就是纯公告。金币按填的数发，所见即所得（不按关卡折算）",
  "reports.title": "举报管理",
  "reports.liveHint": "活表 · 玩家举报即时落表",
  "pushEvents.edit": "编辑文案",
  "pushEvents.save": "保存",
  "pushEvents.cancel": "取消",
  "pushEvents.reset": "恢复默认文案",
  "pushEvents.copyTitle": "标题",
  "pushEvents.copyBody": "正文",
  "pushEvents.saved": "已保存，下批发送生效",
  "pushEvents.pageHint": "按用户当前语言发送。按代理管理事件开关和五语文案，保存后下批发送生效。",
  "pushEvents.search": "搜索事件或模块",
  "pushEvents.copyEdited": "已编辑 · 5 种语言",
  "pushEvents.copyDefault": "默认文案 · 5 种语言",
  "pushEvents.language": "文案语言",
  "pushEvents.complete": "已填写",
  "pushEvents.incomplete": "待补全",
  "pushEvents.missing": "请补全{language}的标题和正文",
  "pushEvents.variables": "可用变量：{name} 对方昵称；{title} 邮件标题。",
  "pushEvents.preview": "通知预览",
  "pushEvents.now": "现在",
  "pushEvents.titlePlaceholder": "通知标题",
  "pushEvents.bodyPlaceholder": "通知正文",
  "pushEvents.previewHint": "仅示意通知内容，变量使用示例值，实际样式以用户设备为准。",
  "pushEvents.discardTitle": "放弃未保存的修改？",
  "pushEvents.discard": "放弃修改",
  "pushEvents.discardHint": "关闭后，本次尚未保存的五语文案修改会丢失。",
  "pushEvents.resetHint": "恢复全部五种语言的默认文案，当前编辑内容将被替换。事件开关保持不变。",
  "pushEvents.sampleName": "小林",
  "pushEvents.sampleTitle": "活动奖励",
  "pushEvents.col.actions": "操作",
  "pushEvents.title": "推送事件",
  "pushEvents.count": "共 {total} 种 · 已关闭 {off} 种",
  "pushEvents.liveHint": "五种语言 · 保存后下批发送生效",
  "pushEvents.col.module": "模块",
  "pushEvents.col.event": "事件",
  "pushEvents.col.desc": "说明",
  "pushEvents.col.text": "文案",
  "pushEvents.col.enabled": "开关",
  "pushEvents.col.updated": "最近修改",
  "pushEvents.on": "开",
  "pushEvents.off": "关",
  "pushEvents.updated.default": "未改过，按默认",
  "pushEvents.toggle.on": "已打开「{name}」，立即生效",
  "pushEvents.toggle.off": "已关闭「{name}」，立即生效",
  "pushEvents.loading": "加载中…",
  "pushEvents.empty": "还没有登记的推送事件",
  "hotUpdate.title": "热更新发布",
  "hotUpdate.upload": "上传热更新包",
  "hotUpdate.intro": "热更新包是一个 zip，按平台和商店包版本下发；玩家下载后重启生效。",
  "hotUpdate.introPick": "客户端拿到哪个包：同平台、已发布、商店包版本在适用范围内、代码版本比客户端高的包里，取代码版本最高的那个。",
  "hotUpdate.normalAdminHint": "你可以上传、发布给内部、下架内部包、维护设备名单；发布给全部、下架已发布的包、重新发布和改最低代码版本只有超级管理员能操作。",
  "hotUpdate.superOnly": "只有超级管理员能操作：发布、下架和最低代码版本会直接影响全部玩家",
  "hotUpdate.loading": "加载中…",
  "hotUpdate.copied": "已复制 sha256",
  "hotUpdate.copy": "复制",
  "hotUpdate.copyTitle": "复制完整 sha256",
  "hotUpdate.cancel": "取消",
  "hotUpdate.minCode": "当前最低代码版本：",
  "hotUpdate.minCodeNone": "未设",
  "hotUpdate.minCodeOn": "低于它的客户端必须先热更新",
  "hotUpdate.minCodeOff": "不强制热更新",
  "hotUpdate.minCodeTag": "最低代码版本",
  "hotUpdate.alreadyMin": "已经是当前最低代码版本",
  "hotUpdate.empty": "还没有 {platform} 的热更新包",
  "hotUpdate.emptyHint": "点右上「上传热更新包」上传第一个",
  "hotUpdate.col.code": "代码版本",
  "hotUpdate.col.kind": "类型",
  "hotUpdate.col.range": "适用商店包版本",
  "hotUpdate.col.size": "大小",
  "hotUpdate.col.status": "状态",
  "hotUpdate.col.who": "上传 / 发布",
  "hotUpdate.col.note": "备注",
  "hotUpdate.col.ops": "操作",
  "hotUpdate.kind.full": "全量",
  "hotUpdate.kind.diff": "差量",
  "hotUpdate.range.diff": "仅 {v}（差量）",
  "hotUpdate.status.draft": "草稿",
  "hotUpdate.status.live": "已发布",
  "hotUpdate.status.withdrawn": "已下架",
  "hotUpdate.who.upload": "上传 {by} · {at}",
  "hotUpdate.who.publish": "发布 {by} · {at}",
  "hotUpdate.who.withdraw": "下架 {by} · {at}",
  "hotUpdate.act.publish": "发布给全部",
  "hotUpdate.act.delete": "删除",
  "hotUpdate.act.minCode": "设为最低代码版本",
  "hotUpdate.act.withdraw": "下架",
  "hotUpdate.act.republish": "重新发布",
  "hotUpdate.saveDraft": "保存为草稿",
  "hotUpdate.saveAndPublish": "保存并发布",
  "hotUpdate.f.platform": "平台",
  "hotUpdate.f.kind": "包类型",
  "hotUpdate.f.kind.fullHint": "全量：包含全部脚本和要换的资源，可以发给一段商店包版本。",
  "hotUpdate.f.kind.diffHint": "差量：只包含相对某一个商店包变了的文件，只能发给那一个商店包版本。",
  "hotUpdate.f.file": "zip 文件",
  "hotUpdate.f.fileHint": "根目录要有 version.json：{\"code_version\":\"x.y.z\"}，和下面填的代码版本一致。最大 50 MB",
  "hotUpdate.f.pick": "选择 zip",
  "hotUpdate.f.repick": "重新选择",
  "hotUpdate.f.hashing": "正在计算 sha256…",
  "hotUpdate.f.size": "大小：{size}",
  "hotUpdate.f.uploading": "上传中 {pct}%",
  "hotUpdate.f.checking": "服务端核对中…",
  "hotUpdate.f.code": "代码版本",
  "hotUpdate.f.codeHint": "热更新之后客户端的代码版本，x.y.z",
  "hotUpdate.f.base": "基准商店包版本",
  "hotUpdate.f.baseHint": "差量包只能发给这一个商店包版本",
  "hotUpdate.f.min": "最低商店包版本",
  "hotUpdate.f.max": "最高商店包版本",
  "hotUpdate.f.maxHint": "留空表示不限",
  "hotUpdate.f.maxPlaceholder": "不限",
  "hotUpdate.f.note": "备注",
  "hotUpdate.f.notePlaceholder": "改了什么，给其他人看",
  "hotUpdate.f.normalHint": "只有超级管理员能发布。你可以先保存为草稿，再请超级管理员发布。",
  "hotUpdate.err.file": "请选择 zip 文件",
  "hotUpdate.err.notZip": "要选 .zip 文件",
  "hotUpdate.err.code": "请填写代码版本",
  "hotUpdate.err.ver": "格式是 x.y.z，例如 {eg}",
  "hotUpdate.err.dup": "这个平台已经有代码版本 {code} 的包",
  "hotUpdate.err.base": "请填写基准商店包版本",
  "hotUpdate.err.min": "请填写最低商店包版本",
  "hotUpdate.err.maxVer": "格式是 x.y.z，不限就留空",
  "hotUpdate.err.range": "最低商店包版本不能高于最高",
  "hotUpdate.publish.title": "发布给全部",
  "hotUpdate.publish.reTitle": "重新发布",
  "hotUpdate.publish.confirm": "确认发布给全部",
  "hotUpdate.publish.reConfirm": "确认重新发布",
  "hotUpdate.publish.range": "适用商店包版本：{range}",
  "hotUpdate.publish.effect": "发布后一分钟内生效：商店包版本在这个范围内、代码版本比它低的客户端，下次启动会下载这个包，重启后生效。",
  "hotUpdate.publish.shadow": "{list} 已覆盖这个区间且版本不低于它，新包不会被选中。仍可发布。",
  "hotUpdate.withdraw.title": "下架热更新包",
  "hotUpdate.withdraw.confirm": "确认下架",
  "hotUpdate.withdraw.what": "下架 {platform} 代码版本 {code}（{range}）。",
  "hotUpdate.withdraw.effect": "已经更新到它的玩家不受影响；一分钟后新进来的客户端不会再下到它，会改选其他已发布的包。下架后可以重新发布。如果它是最低代码版本唯一够得着的包，下架会被拦下。",
  "hotUpdate.delete.title": "删除草稿",
  "hotUpdate.delete.confirm": "确认删除",
  "hotUpdate.delete.what": "删除草稿 {code} 的登记。草稿没发布过，不影响任何玩家。已上传到 CDN 的 zip 删不掉，但不会再被引用。",
  "hotUpdate.minCodeDlg.title": "设为最低代码版本",
  "hotUpdate.minCodeDlg.confirm": "确认修改",
  "hotUpdate.minCodeDlg.change": "{platform} 最低代码版本：{from} → ",
  "hotUpdate.minCodeDlg.warn": "代码版本低于 {code} 的玩家一分钟内会被要求先热更新，热更完才能继续。",
  "hotUpdate.minCodeDlg.covered": "够得着这个版本的已发布包：{list}。",
  "hotUpdate.minCodeDlg.uncovered": "商店包版本不在这些范围里的玩家会被要求热更新却下不到包，只能等商店包强制更新。请先确认这些范围覆盖了线上还在用的商店包。",
  "hotUpdate.toast.draft": "已保存为草稿 {code}",
  "hotUpdate.toast.published": "已发布 {code}，一分钟内生效",
  "hotUpdate.toast.withdrawn": "已下架 {code}",
  "hotUpdate.toast.deleted": "已删除草稿 {code}",
  "hotUpdate.toast.minCode": "最低代码版本已改成 {code}，一分钟内生效",
  "hotUpdate.status.internal": "内部验证中",
  "hotUpdate.act.internal": "发布给内部",
  "hotUpdate.internal.title": "发布给内部",
  "hotUpdate.internal.confirm": "确认发布给内部",
  "hotUpdate.internal.effect": "只有「内部验证设备」里的 {n} 台设备能下到，玩家不受影响。验证没问题再点「发布给全部」，用的是同一个包。",
  "hotUpdate.internal.noDevice": "名单里还没有设备，发布给内部后没有人下得到。先在下面「内部验证设备」里添加。",
  "hotUpdate.toast.internal": "已发布给内部 {code}，一分钟内生效",
  "hotUpdate.dev.title": "内部验证设备",
  "hotUpdate.dev.hint": "只有这些设备能下到「内部验证中」的包。设备 ID 在客户端调试页里看",
  "hotUpdate.dev.col.id": "设备 ID",
  "hotUpdate.dev.col.added": "添加",
  "hotUpdate.dev.notePlaceholder": "备注，如谁的哪台手机",
  "hotUpdate.dev.add": "添加",
  "hotUpdate.dev.remove": "移除",
  "hotUpdate.dev.added": "已添加设备",
  "hotUpdate.dev.removed": "已移除设备",
  "hotUpdate.dev.empty": "还没有设备",
  "reports.hint": "只记录与标记，不做自动处罚；需要封号 / 禁言的先人工走中台。",
  "reports.search": "被举报人 UID / 昵称…",
  "reports.empty": "没有举报",
  "reports.loading": "加载中…",
  "reports.filter.all": "全部状态",
  "reports.status.open": "待处理",
  "reports.status.handled": "已处理",
  "reports.status.ignored": "已忽略",
  "reports.col.time": "时间",
  "reports.col.target": "被举报人",
  "reports.col.reporter": "举报人",
  "reports.col.reason": "原因",
  "reports.col.note": "说明",
  "reports.col.status": "状态",
  "reports.col.handled": "处理",
  "reports.col.act": "操作",
  "reports.reason.cheating": "作弊",
  "reports.reason.abuse": "辱骂 / 骚扰",
  "reports.reason.spam": "广告 / 刷屏",
  "reports.reason.inappropriate_name": "不当昵称",
  "reports.reason.inappropriate_avatar": "不当头像",
  "reports.reason.other": "其它",
  "reports.mark.handled": "标记已处理",
  "reports.mark.ignored": "忽略",
  "reports.mark.reopen": "重新打开",
  "reports.mark.title": "标记为「{status}」",
  "reports.mark.note": "处理备注（选填，≤200 字）",
  "reports.mark.ok": "已标记",
  "reports.page": "{from}–{to} / 共 {total}",
  "reports.prev": "上一页",
  "reports.next": "下一页",
  "nav.share-links": "分享链接",
  "nav.pay-chain": "付费链",
  "pc.title": "付费链活动",
  "pc.liveHint": "保存即生效，不进配置版本",
  "pc.new": "新建一期",
  "pc.hint": "一串格子按顺序解锁：免费格玩家点领取，付费格花真钱（内购）买，领完 / 买完一格才轮到下一格。开放时间外不能领、不能买，结束时没领的作废。同一时间只能开一期。",
  "pc.loading": "加载中…",
  "pc.empty": "还没有付费链",
  "pc.col.id": "ID",
  "pc.col.name": "名称",
  "pc.col.enabled": "状态",
  "pc.col.cells": "格子",
  "pc.col.when": "开放时间（东京）",
  "pc.col.act": "操作",
  "pc.on": "开启",
  "pc.off": "关闭",
  "pc.cellsSummary": "{n} 格（付费 {paid} 格）",
  "pc.edit.new": "新建付费链",
  "pc.edit.title": "编辑付费链",
  "pc.f.id": "ID",
  "pc.f.id.hint": "字母、数字、_ 或 -，最长 48；建好之后不能改，删掉的也不能再用",
  "pc.f.name": "名称",
  "pc.f.enabled": "开启",
  "pc.f.enabled.hint": "同一时间只能开一期；开着的不能删",
  "pc.f.start": "开始时间",
  "pc.f.end": "结束时间",
  "pc.f.end.hint": "到点后不能再领、不能再买，没领的作废",
  "pc.cells": "格子（按顺序解锁）",
  "pc.cellNo": "第 {n} 格",
  "pc.type.free": "免费",
  "pc.type.paid": "付费",
  "pc.addFree": "+ 免费格",
  "pc.addPaid": "+ 付费格",
  "pc.locked": "已经有玩家领过 / 买过：格子的数量、类型和内购商品不能再改，奖励、时间、名称可以改。要换结构请新建一期。",
  "pc.reward.add": "+ 奖励",
  "pc.randomItem": "随机物品卡",
  "pc.kind.money": "金币",
  "pc.kind.gems": "钻石",
  "pc.kind.puzzle": "拼图碎片",
  "pc.kind.spin": "骰子",
  "pc.kind.wildcard": "万能卡",
  "pc.kind.free": "免费掷骰",
  "pc.kind.shield": "盾牌",
  "pc.kind.itemCard": "物品卡",
  "pc.kind.pet": "宠物",
  "pc.kind.title": "称号",
  "pc.item.hint": "玩家买的内购商品（从「内购配置」里选，只列这个平台填了商品 ID 的；一件商品只能挂一格，且要是没卖过的新商品）。只配一个平台的，另一个平台的玩家这一格买不了。",
  "pc.item.none": "（不配）",
  "pc.item.gone": "已删除",
  "pc.item.usedBy": "已被 {id} 选走",
  "pc.err.name": "名称的英文必填",
  "pc.err.id": "ID 只能是字母、数字、_ 或 -，最长 48",
  "pc.saved": "已保存",
  "pc.deleted": "已删除",
  "pc.delete.title": "删除付费链",
  "pc.delete.body": "删除 {id}？玩家的进度不会清掉，但这个 ID 以后不能再用。",
  "nav.piggy": "存钱罐",
  "pg.f.extras": "敲开奖励（宠物 / 物品卡 / 称号）",
  "pg.f.extras.hint": "敲开时当场发，免费敲开也一样；付费敲开的退款不收回",
  "pg.extra.pet": "宠物",
  "pg.extra.itemCard": "物品卡",
  "pg.extra.title": "称号",
  "pg.extra.add": "加一项",
  "pg.liveHint": "保存即生效，不进配置版本",
  "pg.new": "新建一期",
  "pg.title": "存钱罐",
  "pg.hint": "玩家拿到「攒什么」那种资产时（单人掷骰、多人对局都算），拿到多少就存多少，存满为止；敲开拿到罐里攒的 + 敲开奖励。配了内购商品的要花钱买那件商品敲开，iOS、Android 各配各的；都没配的免费敲开。一期只能敲一次，同一时间只能开一期。",
  "pg.loading": "加载中…",
  "pg.empty": "还没有存钱罐",
  "pg.saved": "已保存",
  "pg.deleted": "已删除",
  "pg.on": "开启",
  "pg.off": "关闭",
  "pg.col.id": "ID",
  "pg.col.name": "名称",
  "pg.col.enabled": "状态",
  "pg.col.asset": "攒什么",
  "pg.col.cap": "满值",
  "pg.col.price": "敲开价格",
  "pg.col.when": "开放时间",
  "pg.col.act": "操作",
  "pg.whenLevel": "{level} 级开放，持续 {hours} 小时",
  "pg.notSet": "未配，这个平台的玩家敲不开",
  "pg.edit.new": "新建存钱罐",
  "pg.edit.title": "编辑存钱罐",
  "pg.f.id": "ID",
  "pg.f.id.hint": "建好后不能改",
  "pg.f.name": "名称",
  "pg.f.enabled": "开启",
  "pg.f.enabled.hint": "关着的玩家看不到。要开这一期，先把别的关掉",
  "pg.f.asset": "攒什么",
  "pg.f.cap": "满值",
  "pg.f.rewards": "敲开奖励",
  "pg.f.rewards.hint": "罐里攒的之外另给的",
  "pg.f.rewards.none": "不另给",
  "pg.f.item": "内购商品",
  "pg.f.item.hint": "iOS、Android 各从「内购配置」里选一件，玩家按自己的平台买那件来敲开；每个平台只列在这个平台填了商品 ID 的，两个平台可以选同一件。价格和商品 ID 要改去内购配置页。两个都不选 = 免费敲开；只选一个平台，另一个平台的玩家敲不开",
  "pg.f.item.none": "不配",
  "pg.f.item.gone": "商品已删除",
  "pg.f.item.usedBy": "已被 {id} 选",
  "pg.f.item.noPid": "没填 {platform} 商品 ID",
  "pg.f.item.detail": "价格 {price} · 商品 ID {pid}",
  "pg.free": "免费",
  "pg.f.mode": "开始方式",
  "pg.mode.time": "固定时间",
  "pg.mode.level": "玩家到某个等级",
  "pg.f.start": "开始时间",
  "pg.f.end": "结束时间",
  "pg.f.level": "开放等级",
  "pg.f.hours": "持续小时数",
  "pg.f.hours.hint": "玩家到级后第一次掷骰开始计时",
  "pg.err.name": "名称的英文必填",
  "pg.err.id": "ID 只能是字母、数字、_ 或 -，最长 48",
  "pg.delete.title": "删除存钱罐",
  "pg.delete.body": "删除「{id}」后玩家看不到它，罐里攒的也不再能敲开。",
  "sl.title": "分享链接",
  "sl.liveHint": "活表 · 保存即生效",
  "sl.hint": "分享页的外链，按类型分组：社交媒体 / 创作者中心 / Facebook，一类下可多条。客户端只拿启用的行、按排序号排；没传图标时客户端用本地默认图标。",
  "sl.empty": "还没有分享链接",
  "sl.loading": "加载中…",
  "sl.new": "新建链接",
  "sl.edit.new": "新建分享链接",
  "sl.edit.title": "编辑分享链接",
  "sl.delete.title": "删除这条链接？",
  "sl.delete.body": "「{title}」（{type}）删除后客户端立刻看不到。只是暂时下线的话请改用「停用」。",
  "sl.saved": "已保存",
  "sl.deleted": "已删除",
  "sl.col.type": "类型",
  "sl.col.icon": "图标",
  "sl.col.title": "标题",
  "sl.col.url": "地址",
  "sl.col.sort": "排序",
  "sl.col.enabled": "状态",
  "sl.col.act": "操作",
  "sl.f.type": "类型",
  "sl.f.title": "标题",
  "sl.f.title.hint": "英文必填；其余语言点「多语言」补，客户端按玩家语言取，缺则英文",
  "sl.f.url": "跳转地址",
  "sl.f.url.hint": "必须 https，http 会被 App 拦掉",
  "sl.f.icon": "图标",
  "sl.f.icon.hint": "选填；不传则客户端按类型用本地默认图标",
  "sl.f.sort": "排序号",
  "sl.f.sort.hint": "小的在前，三类合在一起排，客户端分组后保持这个顺序",
  "sl.f.enabled": "启用",
  "sl.type.social_media": "社交媒体",
  "sl.type.creator_center": "创作者中心",
  "sl.type.facebook": "Facebook",
  "sl.status.on": "启用",
  "sl.status.off": "停用",
  "sl.err.title": "启用的链接必须有英文标题",
  "sl.err.url": "跳转地址必须以 https:// 开头",
  "sl.set.title": "分享设置",
  "sl.set.hint": "好友页「Share」用：分享文案 + 邀请落地页参数。邀请链接本身服务端签发（带 UID，有效期可配），不做归因。保存即生效，落地页最多延迟 1 分钟。",
  "sl.set.text": "分享文案",
  "sl.set.text.hint": "占位符 {uid} = 好友码、{link} = 邀请链接；没写 {link} 会自动追加到末尾。英文没填时用客户端现在那句默认英文。",
  "sl.set.scheme": "拉起协议（scheme）",
  "sl.set.scheme.hint": "客户端注册的 URL scheme，不带 ://，如 idolludo → 落地页拉起 idolludo://invite?uid=…；留空则落地页只显示好友码",
  "sl.set.ios": "App Store 地址",
  "sl.set.android": "Google Play 地址",
  "sl.set.ttl": "邀请链接有效期（天）",
  "sl.set.ttl.hint": "1～365，默认 30；过期后点开显示「链接已过期」",
  "sl.set.appName": "游戏名",
  "sl.set.appName.hint": "落地页和分享卡片上显示的名字；不填用 Idol Ludo",
  "sl.set.logo": "落地页 logo",
  "sl.set.logo.hint": "透明底 PNG / WebP，宽 600 左右；不传就显示游戏名文字",
  "sl.set.hero": "落地页背景",
  "sl.set.hero.hint": "竖版图，宽 900 左右、控制在 300KB 内，可传 AVIF（再小四成；iOS 16 以下看不到背景，只显示底色）；角色放在画面中部，底部会被按钮卡片盖住",
  "sl.set.og": "分享卡片图",
  "sl.set.og.hint": "Facebook 等分享出去的预览图，1200×630，JPG 控制在 300KB 内；不传就用背景图",
  "sl.set.icon": "网站图标",
  "sl.set.icon.hint": "方形 PNG，180×180，一般用游戏的应用图标；浏览器标签页和飞书 / 微信链接卡片左上角显示它",
  "sl.set.err.ogAvif": "分享卡片图不能用 AVIF：Facebook 等平台不认，请传 JPG",
  "sl.set.save": "保存设置",
  "sl.set.saved": "分享设置已保存",
  "sl.set.err.url": "商店地址必须以 https:// 开头",
  "sl.set.err.scheme": "scheme 只能是小写字母开头的 a-z0-9+.-，不带 ://",
  "sl.inv.title": "邀请奖励",
  "sl.inv.hint": "邀请页「邀请 → 好友达级 → 累计大奖」的全部口径（sion 2026-10-26：三件事全部可配）。保存即生效。两项奖励都为空 = 邀请奖励关闭，客户端隐藏入口、新玩家也绑不了邀请人。",
  "sl.inv.on": "已开放",
  "sl.inv.off": "未开放（两项奖励都为空）",
  "sl.inv.level": "达标等级",
  "sl.inv.level.hint": "被邀请人的玩家等级到几级算「邀请成功」；0 = 绑定就算。设计稿是 Lv.5",
  "sl.inv.window": "绑定窗口（小时）",
  "sl.inv.window.hint": "新玩家注册后多少小时内还能填邀请人；默认 72，1～8760",
  "sl.inv.per": "每人奖励",
  "sl.inv.per.hint": "每成功邀请一人发给邀请人的奖励；金币是绝对数（不乘 B）。设计稿是骰子 +100",
  "sl.inv.roundTarget": "一轮人数",
  "sl.inv.roundTarget.hint": "每凑满几个成功邀请发一次满轮大奖；0 = 不发大奖。设计稿是 2",
  "sl.inv.roundRewards": "满轮大奖",
  "sl.inv.roundRewards.hint": "凑满一轮发的奖励；一轮人数 > 0 时必填。设计稿是金币 600M、骰子 1000、钻石 840",
  "sl.inv.maxRounds": "最多轮数",
  "sl.inv.maxRounds.hint": "大奖最多发几轮；0 = 不封顶（每凑满一轮都发）",
  "sl.inv.save": "保存邀请奖励",
  "sl.inv.saved": "邀请奖励已保存",
  "sl.inv.err.level": "达标等级须为 0～1000 的整数",
  "sl.inv.err.window": "绑定窗口须为 1～8760 的整数小时",
  "sl.inv.err.round": "一轮人数须为 0～1000 的整数",
  "sl.inv.err.roundRewards": "一轮人数大于 0 时要配满轮大奖",
  "sl.inv.err.qty": "奖励数量要大于 0",
  "sl.inv.err.ref": "卡包 / 活动道具 / 头像框要选具体对象",
  "sections.title": "商城分区",
  "sections.intro": "分区 = 商城页左侧一个锚点 + 右侧一段，按排序纵向排。下架的分区连同里面的商品整段不下发。分区、商品、价格、角标、限购、解锁全部在这里配，客户端不写死。",
  "sections.empty": "还没有分区。先建分区，再到「商城商品」里把商品归进去。",
  "sections.needA0": "先在顶栏选代理",
  "sections.col.name": "分区",
  "sections.col.cols": "格子列数",
  "sections.col.window": "售卖窗口",
  "sections.col.items": "商品数",
  "sections.dialog.title": "分区",
  "sections.f.name": "名称",
  "sections.f.icon": "锚点图标",
  "sections.f.cols": "格子列数",
  "sections.f.cols.hint": "格子卡一行几张；大横幅不受影响",
  "sections.action.new": "新建分区",
  "sections.confirm.delete": "删除分区「{name}」？里面的商品不会删，只是不再出现在整页里。",
  "sections.toast.created": "分区已创建（默认下架）",
  "sections.toast.saved": "已保存",
  "sections.toast.on": "分区已上架",
  "sections.toast.off": "分区已下架",
  "sections.toast.deleted": "分区已删除",
  "nav.dev-token": "玩家 token",
  "nav.dev-panel": "测试面板",
  "nav.gm": "GM 白名单",
  "sim.src": "数据源",
  "sim.src.formula": "公式造价",
  "sim.src.actual": "实际造价",
  "sim.src.hint": "用哪一份造价当作每关的金币目标：公式造价按公式算出；实际造价取建造页里逐格填的合计；竞品则用建造页里逐格填的竞品价反推。可多选，一次跑完并排对比。",
  "sim.scope": "关卡范围",
  "sim.scope.all": "全部关卡",
  "sim.scope.first10": "前 10 关",
  "sim.scope.breather": "仅喘息关",
  "sim.players": "虚拟玩家",
  "sim.seed": "随机种子",
  "sim.seed.hint": "同一种子跑出的结果完全一致，便于对照参数改动前后的差异；换种子相当于换一批玩家。",
  "sim.clear": "清空",
  "sim.run": "开始模拟",
  "sim.running": "模拟中…",
  "sim.caliber": "模拟口径",
  "sim.meta": "每骰产出 E {e} · 消耗系数 ρ {rho} · 自然恢复 {perDay} 骰/日",
  "sim.metaG": "单掷金币按每关 g · 消耗系数 ρ {rho} · 自然恢复 {perDay} 骰/日",
  "sim.caliber.body": "每个虚拟玩家从第 1 关起逐骰投掷（×1 倍率），与运行时同一套规则：按 spin 表的 weight 抽格；金额 = 配置的 B 数 × 本关运行时 B（与服务端同口径，含收入膨胀 K）；区间格与金币类副玩法按服务端同一个纠偏算式选点（落后取上沿、超前取下沿）；副玩法组按领取时服务端允许挑的算（领金币 = 全部启用金币类副玩法的承载并集）。每关量两件事：掷到目标造价要几骰（中位数与 p10 / p90），以及掷满目标骰次时金币够不够造价（p10 / p50 / p90）。骰子奖励回补骰次，副玩法按掉落权重出现。金币目标取自所选数据源——公式造价、实际造价（建筑配置逐格合计）或竞品总造价，多选时同一批玩家跑多轮并排对比。",
  "sim.caliber.bodyG": "每个虚拟玩家从第 1 关起逐骰投掷（×1 倍率），与运行时同一套规则：按 spin 表的 weight 抽格；金额 = 配置的几倍单掷金币 × 本关 g；区间格与金币类副玩法按服务端同一个纠偏算式选点（落后取上沿、超前取下沿）；副玩法组按领取时服务端允许挑的算（领金币 = 全部启用金币类副玩法的承载并集）。每关量两件事：掷到目标造价要几骰（中位数与 p10 / p90），以及掷满目标骰次时金币够不够造价（p10 / p50 / p90）。骰子奖励回补骰次，副玩法按掉落权重出现。金币目标取自所选数据源——公式造价、实际造价（建筑配置逐格合计）或竞品总造价，多选时同一批玩家跑多轮并排对比。",
  "sim.empty": "尚未运行。选好数据源、关卡范围与玩家数后点「开始模拟」。",
  "sim.noData": "所选数据源在这批关卡上没有可用数据（竞品价未填完或造价为空）。",
  "sim.compare": "数据源对比",
  "sim.compare.note": "同一批玩家、同一随机种子，只换金币目标",
  "sim.compare.gold": "金币目标合计",
  "sim.compare.rolls": "模拟总骰次",
  "sim.compare.target": "目标骰次合计",
  "sim.compare.dev": "偏差",
  "sim.compare.off": "超区间关数",
  "sim.compare.detail": "明细",
  "sim.compare.showing": "明细中",
  "sim.compare.view": "查看明细",
  "sim.compare.none": "无数据",
  "sim.compare.missing": "该源在所选关卡上没有填完数据",
  "sim.compare.foot": "竞品源某关某格未填完即无法反推，该关会被跳过；对比时注意关数差异。",
  "sim.verdict": "结论",
  "sim.pass": "模拟通过：各关骰次落在目标浮动区间内，金币发放均有玩法格承载，预算缩放系数量级合理，骰子回补未失控。",
  "sim.warn.off": "{n} 关模拟骰次落在目标浮动区间之外",
  "sim.warn.k": "{n} 关的预算缩放系数 k 偏离 1 超过一倍（spin 表的金额与该关 g 量级不匹配）",
  "sim.warn.regen": "spin 表的骰子回补率达 {r} 颗/骰，骰子接近自给自足，关卡节奏不再受骰次预算约束——需下调骰子格权重或发放量",
  "sim.warn.uncovered": "平均每关有 {n} 次金币发放的 X/g 落在所有启用玩法格的承载区间之外（无格可演）",
  "sim.card.rolls": "模拟总骰次",
  "sim.card.rolls.sub": "目标 {n}",
  "sim.card.dice": "骰子奖励回补",
  "sim.card.dice.sub": "回补率 {r} 颗/骰",
  "sim.card.dice.high": " · 偏高",
  "sim.card.days": "通关天数",
  "sim.card.days.unit": "天",
  "sim.card.days.sub": "净消耗 {net} 骰 · 恢复 {perDay} 骰/日",
  "sim.card.gold": "金币产出",
  "sim.card.gold.sub": "＝ 各关目标造价之和",
  "sim.card.dev": "骰次偏差",
  "sim.card.dev.off": "{n} 关超出区间",
  "sim.card.dev.ok": "全部在区间内",
  "sim.detail": "明细",
  "sim.tab.pace": "关卡节奏",
  "sim.tab.spin": "spin 落点",
  "sim.tab.tile": "玩法格触发",
  "sim.col.level": "关卡",
  "sim.col.city": "城市",
  "sim.col.band": "目标骰次区间",
  "sim.col.rolls": "模拟骰次（中位）",
  "sim.col.g.hint": "单掷金币：以 ×1 倍率掷一次这一关的目标收入 = E × 本关运行时 B。",
  "sim.col.g.hintG": "单掷金币：以 ×1 倍率掷一次这一关的目标收入，即本关配置的 g。",
  "sim.col.k": "缩放 k",
  "sim.col.k.hint": "k = 1 ÷ 盘面中性期望（各区间取中点时每骰发几骰的钱）。k 远离 1 说明 spin 表的量级与 g 不匹配，纠偏要一直顶着区间一端。",
  "sim.col.fill.hint": "掷满目标骰次（有限关长）那一刻累计金币 ÷ 本关目标造价，p10 / p50 / p90。低于 100% 说明这么多骰攒不够造价。",
  "sim.col.fill": "掷满目标骰次时",
  "sim.col.check": "校验",
  "sim.col.name": "名称",
  "sim.col.rewards": "奖励类型",
  "sim.col.theoretical": "理论概率",
  "sim.col.simulated": "模拟命中率",
  "sim.col.dist": "分布",
  "sim.col.tile": "玩法格",
  "sim.col.cat": "分类",
  "sim.col.hits": "模拟触发次数",
  "sim.col.gapShare": "平均间隔 / 承载占比",
  "sim.check.slow": "偏慢",
  "sim.check.fast": "偏快",
  "sim.check.kOff": "k 偏离，spin 表量级待调",
  "sim.check.pass": "通过",
  "sim.check.uncovered": "无格可演 {n} 次",
  "sim.cat.eco": "经济演出",
  "sim.cat.event": "事件对抗",
  "sim.unit.rolls": "骰",
  "sim.tile.weighted": "按掉落权重独立抽取，无保底或冷却限制",
  "sim.tile.never": "未触发",
  "sim.tile.carryOk": "承载 {lo} ~ {hi} ×g 有量",
  "sim.tile.carryEmpty": "承载区间内没有任何金币落点",
  "sim.foot": "模拟只验证参数是否自洽，不代表线上真实分布：付费补骰、活动加成、对抗损失都未计入。",

  "navGroup.auction": "竞拍配置",
  // 菜单项
  "nav.init-config": "初始化配置",
  "nav.match-mode": "匹配与房间",
  "nav.battle-stage": "战阶",
  "nav.ability-card": "道具卡",
  "nav.global-dice": "骰子参数",
  "nav.global-fight": "对抗与控骰",
  "nav.cost": "总造价配置",
  "nav.level": "关卡配置",
  "talent.col.icon": "节点图标",
  "nav.building": "建造",
  "nav.reward": "奖励配置",
  "nav.spin": "spin 奖励配置",
  "nav.subgame": "副玩法配置",
  "nav.subgame-slot777": "777 福引",
  "nav.subgame-jackpot": "大奖格",
  "nav.subgame-thunder": "雷云（大炮）",
  "nav.subgame-visit": "抢金库（贫乏神）",
  "adopt.note": "踩中这一格就会爆出一只宠物。下面配的是「爆出哪几只、各自多大概率」，与另一个领养点互不影响。改动自动保存；领养池和格子本身是两块数据，各存各的。",
  "adopt.decay": "已拥有衰减（bps）",
  "adopt.decay.hint": "10000 = 不衰减，3000 = 打 3 折。只影响同一品质档内部选谁，不改变抽到各档的概率",
  "adopt.oshiBoost": "本命加成（bps）",
  "adopt.oshiBoost.hint": "10000 = 不加成，15000 = ×1.5。同样只作用于同品质档内",
  "adopt.maxedDecay": "满级额外衰减",
  "adopt.maxedDecay.hint": "已经满级的宠物在本点被抽到的额外权重系数（bps，10000 = 不额外压）。叠加在「已拥有衰减」之上，用来把「抽到满级宠物改发金币」压成低频事件。⚠️ 当前不生效：升星要到养成版才开放，玩家的宠物恒为 1★。",
  "adopt.oshiRequiresUnlock": "本命加成需先解锁 idol",
  "adopt.oshiRequiresUnlock.hint": "开启后，只有玩家已解锁那位 idol 时她的本命宠物才享受加成",
  "adopt.pitySr": "保底档",
  "adopt.pitySr.hint": "连续这么多次从收养馆领养都没出 R 及以上（第 2 档），下一次强制出。0 = 不保底。草丛结构上没有保底，也不推进计数",
  "adopt.tiers": "品质档稀有度权重",
  "adopt.tiers.hint": "填相对值，右侧 % 是归一后的基准概率。「候选数」是这一档里权重大于 0 的成员数——配了权重却没有候选时整档抽不出来，发布会被拦。⚠️ 权重没有文档默认值（产品文档 1.4 §343：掉落权重全部管端配置），留空不预填。方向依据《宠物名册 V1.0》各档主要出处（2026-10-05 并为三档，10-06 显示名改为 N/R/SR）：N（第 1 档，44 只）草丛为主 / R（第 2 档，14 只）收养馆主出处 / SR（第 3 档，6 只）馆低概率。",
  "tile.adoptBlockMissing": "{what}没读到，所以这一块先不显示——空着和「真的没配」长得一样，容易看反。刷新页面重试；一直这样就是后端接口没就绪。",
  "tile.adoptBlock.pool": "领养池数据",
  "tile.adoptBlock.roster": "喵汪角色名册",
  "adopt.title": "宠物配置",
  "adopt.spawnMode": "爆出方式",
  "adopt.mode.specific": "指定方式",
  "adopt.mode.random": "系统随机",
  "adopt.mode.specific.hint": "挑几只、各给权重，归一后就是概率",
  "adopt.mode.random.hint": "按稀有度抽，档内从名册里随机挑一只",
  "adopt.keptMembers": "这一行还存着 {n} 只的指定名单（{names}），当前方式下**不生效**；切回指定方式就直接用上，不用重新挑。",
  "adopt.advanced": "高级修正（已拥有衰减 / 本命加成 / 保底）",
  "adopt.col.prob": "概率",
  "adopt.total.weight": "合计权重 {n}",
  "adopt.total.prob": "合计 {pct}%",
  "adopt.tiers.fromRoster": "候选来自本版本全部启用的喵汪角色，按品质自动归档，不用逐只配。要精确指定爆哪几只，用「指定方式」。",
  "adopt.point.shop": "收养馆",
  "adopt.point.grass": "草丛",
  "adopt.col.quality": "品质",
  "adopt.col.candidates": "候选数",
  "adopt.col.petId": "编号",
  "adopt.col.name": "名称",
  "adopt.notice": "概率公示文案",
  "adopt.notice.gen": "按当前配置生成",
  "adopt.notice.genNote": "覆盖中英日三种；生成后请自己过一遍措辞，其余语言客户端回退到英文",
  "adopt.notice.genBlocked": "品质档权重还没配，没有概率可公示",
  "adopt.notice.stale": "公示里的基准概率与当前品质档权重对不上——改完权重要重新生成，否则发出去的是一份写着旧概率的合规文件",
  "adopt.notice.hint": "合规必需，启用的池必须填。内容至少要覆盖四件事：各品质档基准概率、已拥有会降低同品质内再次抽到的相对概率、本命加成及其生效条件、本点的保底次数（草丛写明不参与）。",
  "adopt.members": "宠物池",
  "adopt.members.count": "已选 {n} / 名册共 {all} 只",
  "adopt.members.noRoster": "这一版还没有任何喵汪角色，成员无从配起。先去「喵汪角色」页建角色并启用，它们会自动出现在多选框里。",
  "adopt.members.hint": "先在多选框里勾出这个点要爆哪些宠物，再给勾出来的填权重。权重是相对值（5:3:2 与 50:30:20 等价），概率列是归一结果、合计恒 100%。权重 0 = 选中了但这一轮不爆，比取消勾选省事（切回来不用重新找）。",
  "adopt.col.weight": "权重",
  "adopt.col.candidates.n": "{n} 只",
  "adopt.members.pick": "未选任何宠物",
  "adopt.members.clear": "全部取消",
  "adopt.err.specificEmpty": "指定方式至少要挑一只宠物、并且权重大于 0——一只都没挑等于「全部启用角色权重都 1」，与「只爆这几只」正好相反",
  "adopt.err.notice": "缺概率公示文案（合规必需）",
  "adopt.err.allZero": "没有任何一档「既有候选又有正权重」——这个领养点抽不出任何东西",
  "adopt.err.pityPoint": "只有收养馆支持保底，这个点的保底档必须填 0（填了也不生效，保存会被拒）",
  "adopt.unknownPoint": "库里有不认识的领养点：{p}。本页锁成只读——保存会整表替换，那些行会被删掉。要改先找后端。",
  "adopt.off": "已停用",
  "adopt.err.numRange": "有数值超出取值范围或不是整数（衰减/本命 1~100000、保底 0~10000、成员权重 0~100000），保存会被拒",
  "adopt.err.noticeLong": "有语言的公示文案超过 2000 字，保存会被拒",
  "adopt.err.memberOff": "成员里有已停用的角色——放进池里也抽不到，保存会被拒，把它从多选框里取消掉",
  "adopt.err.tierNoCandidate": "有品质档配了权重却没有任何候选——公示会写着它有概率，玩家却永远抽不到",
  "nav.subgame-adopt_shop": "收养馆",
  "nav.subgame-adopt_grass": "草丛",
  "nav.subgame-omamori": "御守",
  "nav.subgame-coinrain": "金币喷泉",

  "nav.card-item": "物品卡",

  "nav.card-drop": "物品掉落配置",

  "drop.title": "物品掉落配置 · {from}～{to} 级",

  "drop.rule": "按玩家等级配置会掉落的物品卡包和概率：同一等级合计须为 100%，整行为空表示该等级不掉物品卡。先按概率选包，再按包内权重抽卡。只能选启用且有卡的物品卡包；一行即一个等级，单独保存，发布后生效。",

  "drop.col.level": "玩家等级",

  "drop.col.entries": "掉落卡包 / 掉落概率",

  "drop.col.total": "合计",

  "drop.col.ops": "操作",

  "drop.level": "{level} 级",

  "drop.none": "不掉落",

  "drop.total": "{total}%",

  "drop.pickPack": "选择卡包",

  "drop.ratePh": "概率",

  "drop.addEntry": "+ 添加卡包",

  "drop.edit": "编辑",

  "drop.save": "保存",

  "drop.cancel": "取消",

  "drop.clear": "清空",

  "drop.saved": "{level} 级掉落已保存（草稿，发布后生效）",

  "drop.saveFail": "保存失败：{msg}",

  "drop.reloadFail": "已保存，但刷新草稿失败，请刷新页面",

  "drop.err.pack": "有一条没选卡包",

  "drop.err.rate": "概率须大于 0 且不超过 100%",

  "drop.err.dup": "同一等级不能重复选同一个卡包",

  "drop.err.total": "合计 {total}%，须为 100%（或清空整行）",

  "drop.badTotal": "合计 {total}% ≠ 100%：上面列出的是当前计入合计的全部卡包，编辑这一行调平后再发布",

  "drop.ignored": "不计入（{why}）",

  "drop.ignored.off": "已停用",

  "drop.ignored.empty": "无卡",

  "drop.onlyBad": "只看合计不为 100% 的等级",

  "drop.badCount": "{n} 个等级合计不为 100%",

  "drop.noBad": "所有等级合计正常",

  "drop.prev": "上一组等级",

  "drop.next": "下一组等级",

  "drop.noPacks": "本版本还没有启用且有卡的物品卡包，先去「物品卡」页配包",


  "card.dropReadonly": "掉落概率改在「物品掉落配置」页按玩家等级统一编辑，这里只读展示。",

  "card.goDropPage": "去物品掉落配置",
  "nav.card-idol": "idol 卡",
  "nav.activity": "其他活动",
  "nav.idol": "idol 中心",
  "nav.puzzle": "拼图配置",
  "nav.auction": "竞拍配置",
  "nav.notStarted": "尚未开始",
  "search.trigger": "搜索页面或配置项",
  "search.placeholder": "输入页面名或配置项名，如「恢复时间」「偷取比例」",
  "search.kind.page": "页面",
  "search.kind.field": "配置项",
  "search.section.pages": "页面",
  "search.section.fields": "配置项",
  "search.section.recent": "最近打开",
  "search.section.hidden": "当前代理用不上的页",
  "search.kind.player": "玩家",
  "search.section.content": "内容",
  "search.section.players": "玩家",
  "search.loadingPlayers": "正在查玩家…",
  "search.playerId": "ID",
  "search.playerLevel": "第 {n} 关",
  "search.tag.level": "关卡",
  "search.tag.building": "建筑",
  "search.tag.reward": "奖励",
  "search.tag.pack": "卡包",
  "search.tag.card": "卡",
  "search.tag.spin": "spin",
  "search.tag.pet": "宠物",
  "search.tag.frame": "头像框",
  "search.tag.stage": "战阶",
  "search.tag.ability": "道具卡",
  "search.tag.channel": "渠道",
  "search.tag.talent": "天赋",
  "search.tag.slot": "副建筑",
  "search.tag.shopItem": "商品",
  "search.tag.shopSection": "商城分区",
  "search.tag.assetInfo": "物品",
  "search.tag.iap": "内购",
  "search.tag.idol": "idol",
  "search.tag.task": "任务",
  "search.tag.achievement": "成就",
  "search.tag.title": "称号",
  "search.tag.actItem": "活动道具",
  "search.tag.actEvent": "活动",
  "search.tag.piggy": "存钱罐",
  "search.tag.shareLink": "分享",
  "search.empty": "没有找到「{q}」，试试更短的词或换个叫法",
  "search.loadingFields": "配置项加载中…",
  "search.tip": "可以搜页面名，也可以搜页面里的配置项名；多个词用空格隔开",
  "search.keys": "↑↓ 选择 · Enter 打开 · Esc 关闭 · 任何页面按 ⌘K / Ctrl+K 或 / 打开搜索",
  "search.hidden.init": "先在「初始化配置」选好玩法体系才有这一页",
  "search.hidden.pettown": "萌宠小镇体系才有，当前代理不是",
  "search.hidden.levels": "关卡制体系才有，当前代理不是",
  "search.hidden.match": "多人匹配才有，当前代理是单人",
  "nav.todo": "待配置",
  "nav.todoIntro": "需求脑图里这一页要配的字段：",

  // 状态
  "status.draft": "草稿",
  "status.published": "已发布",
  "status.active": "生效中",

  // 后端不可达
  "health.down": "后端不可达：{reason}。数据无法读取或保存。",

  // 总造价配置
  "cost.title": "逐关对比",
  "cost.titleHint": "算法总造价只读；实际总造价手填，填了就以它为准，建造页逐格价向它对齐（手填模式下对不齐标红）。每行没被客户端用到的那个数显示为浅灰。竞品总造价由建造页里逐格手填的竞品价加出来，本页只看合计。一页到底不分页，「关卡」「城市」两列横向滚动时固定。",
  "cost.onlyFailed": "只看校验未通过",
  "p.targetBase": "算法起始骰次",
  "p.targetBase.hint": "填写后自动生成各关目标骰次：起始值×增长系数^(关号−1)，四舍五入。单关覆盖优先；清空起始值则恢复原逐关配置。",
  "p.targetBase.note": "填写启用曲线；留空沿用逐关值。",
  "p.targetBase.off": "未启用曲线",
  "p.targetGrowth": "算法骰次增长系数",
  "p.targetGrowth.hint": "每过一关乘一次，最多4位小数。填1保持固定目标；它与收入膨胀K分别控制目标与收入。",
  "p.targetGrowth.note": "留空按1计算。",
  "p.targetGrowth.default": "默认 1.00",
  "cost.rollsEstimate": "算法估算 {n} 骰",
  "cost.rollsEstimateHint": "所选造价合计÷（E×收入B×ρ），向上取整。只作数值估算，不保证玩家实际骰次，也不反写目标。",
  "cost.rollsEstimateHintG": "所选造价合计÷（本关单掷金币 g×ρ），向上取整。只作数值估算，不保证玩家实际骰次，也不反写目标。",
  "cost.curve.rollsTitle": "算法骰次曲线",
  "cost.curve.rollsPoint": "第 {n} 关 · {b} 骰",
  "cost.curve.rollsNote": "横轴为关卡，纵轴为目标骰次。起始值留空时展示现有逐关值。",
  "ver.f.targetRollsOverride": "单关目标骰次覆盖",
  "p.unifiedE.hint": "每次掷骰期望产出多少份B。收入期望目标为E×当前进度的B；算法造价也乘E。它不是单骰固定奖励，实际结果仍需奖励表、配置检查与玩法验证。",
  "p.unifiedRho.hint": "算法总价相对“目标骰次×E×B”的倍数。小于1更便宜、等于1相等、大于1更贵；不直接修改每骰期望收入。页面预算估算也用它归一化，不是实际投入比例的观测值。",
  "p.unifiedRollGrowth.hint": "目标骰次=起始骰次×本系数^(关号−1)，最后取整一次；留空按1。它设置节奏预算，K设置B的金币量级，两者共同参与算法造价。",
  "cost.unifiedB.algorithmHint": "由起始B与K生成的当关B，收入与算法造价共用。每关从起点计算，最后取整一次；不会再读取另一套逐关B算价。",
  "cost.unifiedB.incomeHint": "当前收入B与算法B相同。价格来源只决定最终价格，不反推B；已填价格保留，未填格可能随公式变化。",
  "cost.unifiedB.actualHint": "填写后覆盖本关目标总价；公式来源也优先用它。比例矩阵按该总额分配，旧逐格/竞品版本按格覆盖。不会反推收入B，也不会随B或E变化自动改写，预算估算须重新检查。",
  "cost.unifiedB.sourceHint": "统一B模式下所有造价来源共用同一B曲线；所选来源仅影响价格合计与预算估算。",
  "cost.unifiedB.note": "各价格来源共用统一B，期望收入目标为E×B。切换来源不反推B；只改变价格和预算估算。竞品整关未填仍显示“—”。预算估算=最终价格合计÷(E×B×ρ)，向上取整，不是实际通关保证。",
  "cost.unifiedB.label": "统一 B：收入与算法造价使用同一条 B 曲线",
  "cost.unifiedB.on": "起始 B × K^(关卡−1) 计算当关 B；收入和算法造价同时使用它。已填价格保留，空格走公式；比例矩阵按总价分配。预算估算随最终价格变化。",
  "cost.unifiedB.off": "此版本保留旧模型：K 只改变收入，算法造价仍用各进度原 B。勾选后本草稿切换统一模型，发布后生效。",
  "cost.unifiedB.curve": "请先清空分段总价曲线，再启用统一 B。",
  "p.unifiedAnchor": "起始 B",
  "p.unifiedAnchor.hint": "第1关每份金币的大小。留空用第1关原配置B，不从手填造价反推。收入与算法造价共用此B。",
  "p.unifiedAnchor.note": "留空沿用第1关原配置 B。",
  "p.unifiedK": "B 增长系数 K",
  "p.unifiedK.hint": "第n关B=起始B×K^(n−1)，最后四舍五入为整数。收入与算法造价同步变化；与目标骰次增长系数分别控制金币量级和关卡时长。",
  "p.unifiedK.note": "留空按1计算；填1表示每关B不增长。",
  "cost.unifiedB.group": "B 曲线（收入与算法造价共用）",
  "p.incomeAnchor": "算法起始收入 B",
  "p.incomeAnchor.default": "默认 {b}",
  "p.incomeAnchor.hint": "填写 K 后，收入曲线从此值开始；留空按当前造价来源的首关／首星 B 计算。不改变算法 B 和造价。",
  "p.incomeAnchor.note": "仅在填写 K 时生效。",
  "cost.curve.title": "收入曲线",
  "cost.curve.level": "第 {n} 关 · B {b}",
  "cost.curve.star": "第 {n} 星 · B {b}",
  "curve.formula": "按分段增长曲线计算，取整到百：{c} 金币；单关实际价不参与后续增长。",
  "curve.segments": "分段增长 · {n} 段",
  "curve.expand": "展开曲线",
  "curve.collapse": "收起曲线",
  "curve.enabled": "已启用分段",
  "curve.disabled": "未启用分段",
  "curve.needsBase": "未启用：先填首关总造价。仅填写分段不会保存或生效，刷新会丢失；当前仍用原算法。",
  "curve.base": "首关总造价",
  "curve.legacy": "留空沿用现有算法",
  "curve.from": "从第几关开始",
  "curve.growth": "每关增长",
  "curve.remove": "删除分段",
  "curve.add": "添加分段",
  "curve.preset": "参考 Piggy GO",
  "curve.presetNote": "采用4.43.0的区间平均增长率，平滑近似；仅替换分段，保留首关金额。",
  "curve.fill": "填入分段",
  "curve.invalid": "首关金额须为正整数，起始关不能重复；增长率大于-100%，最多两位小数。",
  "curve.incomplete": "新分段未填完整，暂不保存该行。",
  "curve.note": "分段只控制总造价，0%固定；收入仍由 B / K 控制，实际价只覆盖本关。",
  "curve.compare": "曲线对比",
  "curve.metric": "查看指标",
  "curve.metric.netDice": "预计主线净耗（骰子）",
  "curve.metric.visits": "预计回访次数",
  "curve.linearNote": "线性纵轴",
  "curve.pace.basis": "按本版本选定造价与每骰期望金币估算，盘面直接返骰 / 券约 {rate}%。",
  "curve.pace.visitsPerDay": "每天回访次数",
  "curve.pace.entryDice": "入关骰子",
  "curve.pace.extraDicePerDay": "额外每日骰子",
  "curve.pace.talents": "按入关前可解锁天赋已领取估算",
  "curve.pace.netNote": "净耗为总造价折算骰次，扣除盘面直接返骰和免费券。不含建造、通关、任务、签到、卡册、拼图等奖励，也不含对抗损失与付费；不代表竞品实际节奏或随机模拟结果。",
  "curve.pace.visitsNote": "各关独立估算：按所填入关骰子、等间隔回访且每次玩空余额计算，受恢复容量与速度限制。额外每日骰子按回访次数均分；金币结余和各类领奖未计入，不能当作真实玩家通关天数。",
  "curve.pace.invalidScenario": "回访次数须为1～24的整数，骰子数量须为非负整数。",
  "curve.pace.invalidReturns": "盘面没有有效权重、返骰配置不完整或直接返还率达到100%，暂不估算净耗。",
  "curve.metric.cost": "总造价",
  "curve.metric.rolls": "目标骰次",
  "curve.metric.income": "收入 B",
  "curve.metric.perRoll": "每骰期望金币",
  "curve.metric.E": "期望系数 E",
  "curve.metric.rho": "消耗系数 ρ",
  "curve.metric.dice": "初始骰子",
  "curve.algorithm": "算法",
  "curve.actual": "实际造价",
  "curve.relative": "相对首关",
  "curve.relativeNote": "首关为1倍 · 对数纵轴",
  "curve.logNote": "对数纵轴",
  "curve.overlay": "叠加指标",
  "curve.range": "关卡",
  "curve.rangeFrom": "起始关卡",
  "curve.rangeTo": "结束关卡",
  "curve.all": "全部",
  "curve.empty": "当前范围没有可比较的数据，请选择曲线或调整范围。",
  "curve.level": "第 {n} 关",
  "curve.chartNote": "空值不补线。收入按本项目参数推算，不代表竞品真实收入；多指标叠加时统一比较首关倍数。",
  "curve.view": "查看曲线",
  "cost.curve.empty": "参数未齐，暂时算不出曲线",
  "cost.curve.note": "横轴是进度，纵轴是收入 B。按本版本默认造价来源计算。",
  "cost.params.algorithm": "算法造价",
  "cost.params.rolls": "算法目标骰次",
  "cost.applyAlgoAll": "全部 →",
  "cost.applyAlgoHint": "将本关算法总造价填入实际总造价，覆盖已有值",
  "cost.applyAlgoAllHint": "将全部关卡可计算的算法总造价填入实际总造价，覆盖已有值；不受筛选影响",
  "cost.params.income": "算法金币收入",
  "cost.params.kNote": "留空沿用造价基准 B；填 1 表示收入 B 不增长。",
  "cost.b.algorithm": "算法 B",
  "cost.b.algorithmHint": "关卡原始 bValue，用于计算算法总造价。不会随 K 改变；这里只读展示。",
  "cost.b.income": "当前采用 B",
  "cost.b.incomeHint": "按上方选中的客户端造价来源计算，包含 K 的影响；切换来源可在 B 值明细中对照。",
  "cost.b.perRoll": "每骰期望金币",
  "cost.b.perRollHint": "当前列 B × E，展示期望值，不是每次掷骰固定发放的数量。",
  "cost.b.sourceTitle": "{source} B",
  "cost.b.sourceHint": "按「{source}」这套造价独立计算 B，包含 K 的影响；竞品整关未填造价时显示「—」；部分未填的格子按现有规则回退公式价。",
  "cost.b.formulaHint": "对应客户端「公式」来源，包含 K 的影响。新矩阵按算法总造价计算；旧版本填写实际总造价时以其为目标。",
  "cost.b.manualHint": "对应客户端「手填」来源，包含 K 的影响。新矩阵按实际总造价自动分配；旧版本按建造页逐格手填价计算，未填格子回退公式价。",
  "cost.b.active": "当前采用",
  "cost.b.total": "所用造价合计：{v}",
  "cost.b.baseValue": "应用 K 前的 B：{v}",
  "cost.b.note": "按客户端造价来源分别展示 B，高亮列为当前默认来源。各列独立计算，包含 K 的影响：起始 B 留空时，各自取该来源第 1 关的 B 为锚；填写起始 B 时共用起点，因此数值可能相同。竞品整关未填造价时显示「—」；部分缺失格子沿用现有公式回退规则，悬停数值可看所用造价和应用 K 前的 B。",
  "cost.view.cost": "造价配置",
  "cost.view.b": "B 值明细",
  "hv.linkLabel": "{title}？看视频",
  "hv.watch": "看视频：{title}",
  "hv.modalTitle": "{title}",
  "hv.prev": "← 上一课：{title}",
  "hv.next": "下一课：{title} →",
  "hv.b.title": "B 值是什么",
  "hv.b.summary": "B 值：这一关一份钱的大小",
  "hv.e.title": "E 是什么",
  "hv.e.summary": "算法每骰产出 E：掷一次平均拿几份钱",
  "hv.rho.title": "ρ 是什么",
  "hv.rho.summary": "算法消耗系数 ρ：楼价是掷骰赚到的钱的几倍",
  "hv.k.title": "K 是什么",
  "hv.k.summary": "算法收入膨胀系数 K 与算法起始收入 B：赚钱用的一份钱每过一关乘几倍",
  "hv.playbackSpeed": "倍速播放",
  "hv.fullscreen": "全屏播放",
  "hv.exitFullscreen": "退出全屏",
  "hv.fullscreenFailed": "无法进入全屏，请使用视频自带的全屏按钮。",
  "hv.finishedTitle": "本节学习完成",
  "hv.finishedNext": "下一课：{title}",
  "hv.rewatch": "再看一遍",
  "hv.continueNext": "学习下一课",
  "hv.pending": "视频制作中",
  "hv.pageCourse": "本页课程",
  "hv.copyLink": "复制链接",
  "hv.copied": "已复制",
  "hv.linkCopied": "已复制本节链接",
  "hv.copyManual": "无法自动复制，请手动复制链接",
  "hv.copyLinkLabel": "复制「{title}」的链接",
  "ht.operations.title": "运营课程",
  "ht.operations.summary": "从代理开通、配置发布到日常运营，按工作步骤学习。",
  "ht.client.title": "客户端课程",
  "ht.client.summary": "从工具准备到上线验收，帮助新人完成客户端接入。",
  "ht.server.title": "服务端课程",
  "ht.server.summary": "代码怎么开分支、提 PR、合进 test 和发到生产。",
  "ht.design.title": "数值与策划课程",
  "ht.design.summary": "理解造价参数和配置规则，学习如何检查数值方案。",
  "ht.qa.title": "测试验收课程",
  "ht.qa.summary": "准备测试环境，覆盖主要场景，记录可复现的问题。",
  "ht.helloLudo.title": "Hello ludo 课程",
  "ht.helloLudo.summary": "Hello ludo 飞行棋一期要用的后台：配置、萌宠小镇和服务端代码。",
  "hv.course.client.title": "客户端接入入门",
  "hv.course.client.summary": "先准备工具，再接协议、资源和实时事件，最后联调验收。",
  "hv.course.devFlow.title": "代码合并流程",
  "hv.course.devFlow.summary": "主仓库只读后怎么提 PR、机器人怎么检查合并、怎么发生产和紧急修复",
  "hv.devPr.title": "开分支到提 PR",
  "hv.devPr.summary": "主仓库只读，合并由机器人做",
  "hv.devMerge.title": "机器人怎么合进 test",
  "hv.devMerge.summary": "检查、合并、失败了怎么办、合进去之后",
  "hv.devRelease.title": "发生产与紧急修复",
  "hv.devRelease.summary": "test 预发、main 生产：怎么发版，生产出急问题怎么修",
  "hv.course.operations.title": "日常运营",
  "hv.course.operations.summary": "学习玩家查询、邮件奖励和发布检查。",
  "hv.course.qa.title": "测试与验收入门",
  "hv.course.qa.summary": "从环境和测试账号开始，学习场景检查、失败恢复和缺陷反馈。",
  "hv.clientOverview.title": "接入全貌与学习路线",
  "hv.clientOverview.summary": "认识客户端接入涉及的页面、请求和事件，了解学习顺序。",
  "hv.clientMcp.title": "MCP 工具配置",
  "hv.clientMcp.summary": "准备 MCP 工具，了解环境连接与基本检查。",
  "hv.clientAuth.title": "登录与加密协议",
  "hv.clientAuth.summary": "了解登录流程、请求鉴权和加密协议的接入范围。",
  "hv.clientResources.title": "资源块与多语言",
  "hv.clientResources.summary": "了解资源块加载、版本更新与多语言资源的接入步骤。",
  "hv.clientState.title": "核心流程与状态恢复",
  "hv.clientState.summary": "梳理核心玩法请求，以及重试和重新进入时的状态恢复。",
  "hv.clientAbly.title": "Ably 实时事件",
  "hv.clientAbly.summary": "了解实时连接、事件处理和断线恢复的接入范围。",
  "hv.clientDebug.title": "联调追踪与问题定位",
  "hv.clientDebug.summary": "学习如何关联请求与日志，整理可复现的联调问题。",
  "hv.clientRelease.title": "上线前验收",
  "hv.clientRelease.summary": "按清单检查正常操作、异常恢复和版本兼容。",
  "hv.opsPlayers.title": "玩家查询与问题排查",
  "hv.opsPlayers.summary": "学习按玩家信息查找记录，收集处理问题需要的上下文。",
  "hv.opsMail.title": "邮件与奖励发放",
  "hv.opsMail.summary": "了解邮件和奖励发放的操作入口、核对步骤与结果确认。",
  "hv.opsRelease.title": "发布前后检查",
  "hv.opsRelease.summary": "整理发布前需要核对的配置，以及发布后要确认的玩家表现。",
  "hv.qaSetup.title": "环境与测试账号",
  "hv.qaSetup.summary": "准备测试环境和账号，确认本次验收的版本与配置。",
  "hv.qaScenarios.title": "核心场景验收",
  "hv.qaScenarios.summary": "按玩家操作检查主流程，记录预期结果与实际表现。",
  "hv.qaRecovery.title": "失败重试与恢复",
  "hv.qaRecovery.summary": "检查请求失败、断线和重新进入后的恢复表现。",
  "hv.qaReport.title": "缺陷反馈与回归",
  "hv.qaReport.summary": "整理复现步骤、环境与证据，修复后按原场景回归。",
  "hv.course.global.title": "全局参数",
  "hv.course.global.summary": "骰子、对抗与新手保护、每日上限",
  "hv.course.board.title": "棋盘配置",
  "hv.course.board.summary": "spin 奖励、副玩法格、对抗数值、活动道具",
  "hv.course.clear.title": "通关与天赋树",
  "hv.course.clear.summary": "通关奖励、天赋树",
  "hv.course.pettown.title": "萌宠小镇",
  "hv.course.pettown.summary": "小镇参数、星级表与副建筑、天赋树",
  "hv.course.match.title": "多人对战",
  "hv.course.match.summary": "开房超时、规则版本、档位奖池、战阶道具、棋盘格神灯",
  "hv.course.content.title": "卡牌、拼图与喵汪",
  "hv.course.content.summary": "卡包与掉落、拼图价目、喵汪角色",
  "hv.dice.title": "骰子参数",
  "hv.dice.summary": "玩家的骰子从哪来、能存多少",
  "hv.fight.title": "对抗与新手保护",
  "hv.fight.summary": "谁会被匹配来打你、新手怎么保护",
  "hv.daily.title": "每日次数与社交上限",
  "hv.daily.summary": "每天能做几次、防刷的上限",
  "hv.spin.title": "spin 奖励配置",
  "hv.spin.summary": "掷一次骰子，会掉出什么",
  "hv.subgame.title": "副玩法格",
  "hv.subgame.summary": "棋盘上的特殊格子分两类",
  "hv.correct.title": "金额纠偏",
  "hv.correct.summary": "中奖金额按这一关发多发少往回调",
  "hv.fightval.title": "雷云、抢金库、御守的对抗数值",
  "hv.fightval.summary": "雷云打、贫乏神偷、御守防",
  "hv.actitem.title": "活动道具",
  "hv.actitem.summary": "限时活动：掷骰收集道具换奖励",
  "hv.reward.title": "通关奖励",
  "hv.reward.summary": "每一关通关时，额外发什么",
  "hv.talent.title": "天赋树",
  "hv.talent.summary": "升满建筑，一个个解锁永久权益",
  "hv.ptparams.title": "萌宠小镇参数",
  "hv.ptparams.summary": "一栋主建筑，一颗星一颗星往上升",
  "hv.ptstar.title": "主建筑星级表与副建筑",
  "hv.ptstar.summary": "每颗星多少钱，什么时候开新楼",
  "hv.pttalent.title": "天赋树与体力树",
  "hv.pttalent.summary": "按小镇等级解锁，骰子从体力树收",
  "hv.course.helloLudo.title": "Hello ludo 飞行棋后台上手",
  "hv.course.helloLudo.summary": "一期用到的后台配置、萌宠小镇，以及服务端从哪下手",
  "hv.hlRoute.title": "上手路线",
  "hv.hlRoute.summary": "一期用哪几块后台，按什么顺序看",
  "hv.hlRoll.title": "掷一次骰子，服务端返回什么",
  "hv.hlRoll.summary": "drop、detail、balances 各管什么",
  "hv.hlMap.title": "后台与代码对照",
  "hv.hlMap.summary": "每一页存在哪张表、哪段代码在读",
  "hv.hlNew.title": "一期新增从哪下手",
  "hv.hlNew.summary": "开局盾牌、金币加成、组内权重、进度条",
  "hv.hlEnter.title": "进飞行棋页面，要拿哪些数",
  "hv.hlEnter.summary": "三个接口加五块资源块；金币格数额现在不下发",
  "hv.hlGaps.title": "天赋缺口与删模块",
  "hv.hlGaps.summary": "天赋还缺哪些效果，删一个模块要改哪几处",
  "hv.mroom.title": "多人对战：开房与超时",
  "hv.mroom.summary": "几个人一房，等多久，掉线怎么办",
  "hv.mrules.title": "多人对战：棋盘规则与版本",
  "hv.mrules.summary": "一局怎么走，什么时候要客户端更新",
  "hv.mecon.title": "多人对战：体力档位与奖池",
  "hv.mecon.summary": "带多少体力进场，和谁一起打，赢了分多少",
  "hv.mstage.title": "多人对战：战阶与道具卡",
  "hv.mstage.summary": "赢得越多，能用的道具越多",
  "hv.mcells.title": "多人对战：棋盘格与神灯",
  "hv.mcells.summary": "56 格各是什么，神灯开出什么",
  "hv.cards.title": "卡牌：物品卡、掉落与 idol 卡",
  "hv.cards.summary": "卡按卡包管，集齐一包领奖",
  "hv.puzzle.title": "拼图与价目表",
  "hv.puzzle.summary": "每位 idol 的写真，用碎片兑换",
  "hv.pets.title": "喵汪角色与升星",
  "hv.pets.summary": "玩家领养的猫猫狗狗",
  "lc.back": "返回课程",
  "lc.catalog": "课程目录",
  "lc.lessons": "{n} 节课",
  "lc.duration": "{n} 分钟",
  "lc.viewOutline": "查看大纲",
  "lc.openCourse": "查看课程",
  "lc.available": "可立即学习",
  "lc.continueHint": "接着上次的位置继续",
  "lc.localShort": "续播位置保存在当前浏览器，完成情况会计入进度排名",
  "lc.preparingHint": "先了解学习内容，视频上线后即可学习",
  "lh.intro": "按工作需要选择学习路径，已有视频可以直接学习，其余课程先查看大纲。",
  "lh.localNote": "续播位置保存在当前浏览器，换设备后要从头找位置；看完的课会同步到服务端，计入进度排名。",
  "lh.resume": "继续学习",
  "lh.myLearning": "我的学习",
  "lh.all": "全部课程",
  "lh.planned": "筹备中",
  "lh.ready": "{n} 课可学习",
  "lh.completed": "已完成",
  "lh.progress": "已完成 {done} / {total} 课",
  "lh.emptyLearning": "还没有学习记录。选择一门已上线的课程开始学习。",
  "lh.outline": "课程大纲",
  "lh.search": "搜索课程或课题",
  "lh.emptySearch": "没有找到相关课程，试试其他关键词。",
  "lh.watchAt": "从 {time} 继续",
  "lh.start": "开始学习",
  "lh.review": "重新学习",
  "lh.storageUnavailable": "浏览器暂时无法保存学习进度，本次仍可正常观看。",
  "nav.help-learning": "我的学习",
  "nav.help-rank": "进度排名",
  "lr.intro": "按看完的课数排名，一样多再比学习时长。只算已经上线的课。",
  "lr.rank": "名次",
  "lr.member": "成员",
  "lr.done": "已完成",
  "lr.time": "学习时长",
  "lr.last": "最近学习",
  "lr.you": "（我）",
  "lr.empty": "还没有人开始学习。",
  "lr.filter": "学习路径",
  "nav.help-operations": "运营课程",
  "nav.help-client": "客户端课程",
  "nav.help-server": "服务端课程",
  "nav.help-design": "数值与策划课程",
  "nav.help-qa": "测试验收课程",
  "nav.help-hello-ludo": "Hello ludo 课程",
  "hv.course.cost.title": "总造价",
  "hv.course.cost.summary": "总造价页上的参数是什么、怎么算、最后客户端用哪套价。建议按顺序看：后一课会用到前一课的概念。",
  "hv.rolls.title": "目标骰次：要掷多少次",
  "hv.rolls.summary": "算法起始骰次与算法骰次增长系数：每关要掷多少次，单关覆盖怎么用，下方小字「算法估算骰次」怎么看",
  "hv.overview.title": "公式总览",
  "hv.overview.summary": "把前面几课串起来：算法总造价 = 目标骰次 × E × B × ρ",
  "hv.source.title": "实际总造价与造价来源",
  "hv.source.summary": "从算法价到实际价：「→ / 全部 →」怎么填，「客户端用哪套造价」选公式 / 手填 / 竞品",
  "hv.course.version.title": "配置版本",
  "hv.course.version.summary": "草稿、发布、生效时间、查看变更、回滚。每个配置页顶部的版本栏都按这套走。",
  "hv.verPublish.title": "草稿和发布",
  "hv.verPublish.summary": "先改草稿再发布：草稿自动保存、玩家看不到，配置检查通过后点发布才算数",
  "hv.verLive.title": "发布后多久生效",
  "hv.verLive.summary": "服务端最多半分钟换成新配置；客户端要重开游戏才拉新数据，商城单独一条",
  "hv.verChanges.title": "查看变更",
  "hv.verChanges.summary": "修改记录、与线上对比、版本历史、操作日志：各查什么、在哪里打开",
  "hv.verRollback.title": "回滚和多人一起改",
  "hv.verRollback.summary": "回滚是用旧版新建草稿再发布；草稿比线上旧时的提醒；多人同时改与清理多余草稿",
  "hv.course.tenant.title": "新代理上手",
  "hv.course.tenant.summary": "登记代理、做初始化配置。新代理先看这两课，再去配各页参数。",
  "hv.agents.title": "代理管理",
  "hv.agents.summary": "新增代理、启用与停用、时区、从中台导入名称",
  "hv.init.title": "初始化配置",
  "hv.init.summary": "复制已有代理做底稿，选玩法体系与建筑版本，有玩家以后哪些会锁定",
  "cost.showRivals": "竞品对比（{n}）",
  "cost.summary": "共 {total} 关，其中 {filled} 关已填实际总造价 · 首行为全关合计",
  "cost.addLevel": "+ 新增关卡",
  "cost.col.level": "关卡",
  "cost.col.city": "城市",
  "cost.col.algo": "算法总造价",
  "cost.col.actual": "实际总造价",
  "cost.col.check": "校验",
  "cost.col.op": "操作",
  "cost.algoHint": "按公式算出：目标骰次 × E × B 值 × ρ（E、ρ 取本页参数）。选「公式」时，没填实际总造价的关按它摊价；填了就按实际总造价。只读，不能在这里改。",
  "cost.algoFormula": "{c} = 目标骰次 {r} × E {e} × B {b} × ρ {rho}",
  "cost.algoFormulaG": "{c} = 目标骰次 {r} × 单掷金币 g {g} × ρ {rho}",
  "cost.algoRuntimeB": "运行时实际的 B 是 {b}：按客户端在用的那套价反推，掷骰产出与发奖按它折金币",
  "cost.actualHint": "手填这关的目标总造价。新矩阵仅在客户端采用「手填」来源时按它自动分配，各格合计严格一致；公式来源仍按算法总造价。未填时使用算法总造价。旧版本保留原公式目标和逐格覆盖规则。",
  "cost.rivalSub": "建造页里逐格手填 · 本页只看合计",
  "cost.built": "实配 {v}（{dev}）",
  "cost.hasPending": "有格子没填而公式价算不出来（目标骰次 / B 值 / E、ρ 没配齐）",
  "cost.hasPendingG": "有格子没填而公式价算不出来（目标骰次 / 单掷金币 g / ρ 没配齐）",
  "cost.total": "合计",
  "cost.allLevels": "全部 {n} 关",
  "cost.pendingLevels": "{n} 关待填",
  "cost.onlyN": "仅 {n} 关",
  // 口径已定（leo 2026-08-21）：关卡配置向总造价对齐——手填实际总造价后，
  // 调关卡的 B 值 / 目标骰次让算法造价贴上去；建筑逐格价由公式派生自动跟随

  // 关卡配置
  "level.title": "关卡列表",
  "level.col.id": "关卡编号",
  "level.imgClear": "清空",
  "level.imgFrom": "继承自第 {n} 关",
  "level.col.coverImg": "封面图",
  "level.col.bgImg": "建造底图",
  "hint.coverImg": "关卡封面（横版）。选关列表/关卡卡片用，客户端从 cities 资源块的 cover_img 取",
  "hint.bgImg": "建造页底图（竖版，不含建筑）。建筑素材另在建造页逐格上传，客户端叠在这张底图上",
  "level.col.name": "名称（国际化）",
  "level.col.N": "建筑数量",
  "level.col.M": "建筑星级",
  "level.col.rolls": "目标骰次",
  "level.col.range": "浮动区间 ±%",
  "level.notConfigured": "未配置",
  "level.count": "共 {n} 关",

  // 建筑配置
  "building.col.name": "建筑名称",
  "building.col.code": "建筑 code",
  "building.col.star": "星级",
  "building.col.algo": "算法造价",
  "building.col.actual": "实际造价",
  "building.col.asset": "素材",
  "building.assetSub": "图片 / 动画",
  "building.assetInherited": "继承",
  "building.assetDamagedInherited": "破坏图继承自上一星级",
  "building.assetAnimInherited": "动画继承自上一星级",
  "building.col.upload": "上传素材",
  "building.codeSub": "自动生成 · 只读",
  "building.matrixSeed": "建筑造价随机方案",
  "building.view.label": "建造视图",
  "building.view.detail": "造价明细",
  "building.view.matrix": "矩阵视图",
  "building.matrix.source": "查看来源",
  "building.matrix.viewOnly": "仅切换查看，不改变客户端造价来源",
  "building.matrix.building": "建筑 {n}",
  "building.matrix.dimensions": "{n} 栋建筑 × {m} 星级",
  "building.matrix.total": "升满合计",
  "building.matrix.cellPercentHint": "每格百分比为该次升级造价占本关全部建筑升满总造价的比例，25格合计100%。本关价格未填齐时暂不显示比例。",
  "building.matrix.totalPercentHint": "升满合计下的百分比为本栋占本关总造价的比例，各栋合计100%。本关价格未填齐时暂不显示比例。",
  "building.percent": "{v}%",
  "building.percentHint": "占本栋建筑升满总造价的比例，各星级合计100%。竞品本栋尚未填齐时暂不显示比例。",
  "building.reset": "重置",
  "building.resetHint": "恢复截图中的原始比例，不打乱；公式和实际价同时恢复。",
  "building.random": "随机",
  "building.randomHint": "按已定三轮规则重新打乱本关比例；总造价不变，结果随版本保存。",
  "v.matrix.invalid": "随机方案无效，请重置或重新随机。",
  "v.matrix.randomized": "按约定的有限随机规则生成，允许局部星级价格交换。",
  "building.actualSub": "按总造价自动分配",
  "building.grid": "{n} 建筑 × {m} 星级 ＝ {rows} 行",
  "building.sumLine": "实际造价合计",
  "building.goalLine": " / 目标总造价 {v}（{src}）",
  "building.passed": "本关校验通过",
  "building.addRival": "新增竞品",
  "building.removeRival": "删除这一列",
  "building.removeRivalTitle": "删除竞品列「{name}」",
  "building.removeRivalBody": "这一列已经填了 {n} 个价，会跟着一起删掉，保存之后找不回来。",
  "building.removeRivalEmpty": "这一列还没有填任何价，删掉不影响别的数据。",
  "building.removeRivalInUse": "客户端正在用这一列的价，删掉后改回「公式」。",
  "building.removeRivalOk": "删除",
  "building.removeRivalAssigned": "有 {n} 个玩家单独指定了这一列。指定不会跟着删，这版发布后他们退回版本默认。",
  "building.isMain": "主建筑",
  "building.notFilled": "未填",

  // 奖励
  "reward.star": "完成星数",
  "reward.milestone": "星数里程碑奖励",
  "reward.addMilestone": "添加里程碑",
  "reward.starRange": "可配 1～{max} 星",
  "reward.milestoneHint": "默认不配。完成指定星数时发一次，可与副建筑解锁奖并存。已错过的里程碑不补发。",
  "reward.starTitle": "完成 {star} 星 · 里程碑奖励",
  "reward.title": "通关奖励总览",
  "reward.configured": "已配置 {n} / {total} 关",
  "reward.moneySum": "money 合计",
  "reward.ofTotal": " · 占目标总造价 {p}",
  "reward.configure": "配置奖励",
  "reward.fixedCols": "列按脑图字段固定 · 某项不发就留「不发」",
  "reward.multiNote": "物品卡与 idol 卡可同时发多张不同品质的卡，候选来自卡牌配置。",

  // 卡牌
  "card.packs": "{kind}包",
  "card.itemCard": "物品卡",
  "card.idolCard": "idol 卡",
  "card.byName": "按名称搜索",
  "card.byCode": "按编号搜索",
  "card.addPack": "+ 新增{kind}包",
  "mcp.entry": "MCP",
  "mcp.title": "让 Claude 帮你做后台的活",
  "mcp.sub": "点下面的按钮，把复制好的话粘贴给 Claude Code 发送。卡包、关卡奖励、商城、任务成就、活动、翻译……重复的配置活都可以交给它。只改草稿，发布还是你在后台点。",
  "mcp.oneClick": "复制给 Claude",
  "mcp.oneClickDone": "已复制，去 Claude Code 粘贴发送",
  "mcp.showText": "看看复制了什么",
  "mcp.hideText": "收起",
  "mcp.copyBlocked": "浏览器没让自动复制。点文本框右边的「复制」再试一次，或在框里全选后按 Ctrl/⌘+C。",
  "mcp.a0Unknown": "（先在后台顶栏选好代理再点复制，或在这里写上代理编号）",
  "mcp.defaultNote": "Claude",
  "mcp.disabled": "这个环境还没开 MCP。",
  "mcp.copy": "复制",
  "mcp.copied": "已复制",
  "mcp.exTitle": "接上以后，可以这样跟它说",
  "mcp.ex.importT": "导入一批卡面",
  "mcp.ex.import": "把桌面上 ITEM_CARD 文件夹里的物品卡配进草稿：先给我看导入计划，我确认后再写，最后把名字翻成简中、繁中、日文、越南文。",
  "mcp.ex.translateT": "补齐翻译",
  "mcp.ex.translate": "找出草稿里所有缺翻译的物品卡和卡包，把译名列给我看，我确认后一次补上。",
  "mcp.ex.dropsT": "配掉落概率",
  "mcp.ex.drops": "看看哪些物品卡包还没配掉落，玩家 1–5 级平均掉这几个包，先给我看方案。",
  "mcp.ex.rewardsT": "批量改关卡奖励",
  "mcp.ex.rewards": "把草稿里第 11–20 关的通关奖励金币都提高 20%，先按「关卡：原值 → 新值」列给我看，我确认后再改。",
  "mcp.ex.shopT": "改商城价格",
  "mcp.ex.shop": "把商城里所有骰子礼包的钻石价打九折（取整），先给我看每个商品的原价和新价，我确认后再改。",
  "mcp.ex.reviewT": "发布前检查",
  "mcp.ex.review": "草稿和线上比改了什么、还有什么问题会拦住发布，用中文列给我。",
  "mcp.more": "其他接法（Claude 桌面版 / 手动命令）",
  "mcp.desktop": "Claude 桌面版 / claude.ai 网页版",
  "mcp.desktopSteps": "设置 → 连接器 → 添加自定义连接器，名称随意，URL 粘贴下面这一行（需要 Pro 及以上套餐）。这条地址带着令牌，等同密码。",
  "mcp.code": "Claude Code 手动接入",
  "mcp.codeSteps": "在终端执行这一行。",
  "mcp.tokenPh": "<你的令牌>",
  "mcp.placeholderTip": "先点上面的「复制给 Claude」，这里会换成你的令牌。",
  "mcp.tokens": "我的令牌（{n}）",
  "mcp.noTokens": "还没有令牌。点「复制给 Claude」会自动生成一枚。",
  "mcp.lastUsed": "最近使用 ",
  "mcp.expires": "到期 ",
  "mcp.revoke": "撤销",
  "mcp.safety": "令牌等于你的账号权限，30 天到期，只发给你自己的 Claude。电脑不用了就在「我的令牌」里撤销。操作日志记的是你的名字。",
  "mcp.clientSafety": "令牌只认你本人，30 天到期；反馈和调用记的是你的名字。账号停用或禁用后令牌当场失效。电脑不用了就在「我的令牌」里撤销。",
  "mcp.clientDisabled": "客户端联调 MCP 只在 test 环境有，这个环境生成不了。",
  "mcp.clientNoTokens": "还没有令牌。点「生成令牌」生成一枚。",
  "mcp.clientPlaceholderTip": "先点「生成令牌」，这里会换成你的令牌。",
  "mcp.clientCodex": "Codex：加进 ~/.codex/config.toml",
  "mcp.clientClaude": "Claude Code：在终端执行",
  "mcp.clientOnce": "令牌只显示这一次，关掉抽屉就看不到了。先复制下面的命令接好；丢了就撤销再生成一枚。",
  "mcp.clientCreated": "已生成，复制下面的命令",
  "mcp.clientCreate": "生成令牌",
  "mcp.clientSub": "生成你自己的令牌，接到 Claude Code 或 Codex。接上以后它能搜全部对接文档、看接口定义、用测试号真打 test，查不到会自己反馈给服务端。",
  "mcp.clientTitle": "让客户端的 AI 自己查接口、打 test",
  "mcp.tabClient": "客户端联调",
  "mcp.tabAdmin": "运营后台",
  "mcp.oneClickText": "请帮我接入 Idol Ludo 运营后台，然后替我做后台的配置活。我不懂技术，请一步步替我做，需要我动手时说清楚点哪里。\n\n一、接入（只做一次）\n这是{envLabel}的后台，MCP 名字叫 {name}。\n1. 在终端执行这一行：\n{cmd}\n2. {setup}\n3. 检查 {name} 连上没有。如果要重开 Claude Code 才生效，告诉我：输入 /exit 退出，再在终端输入 claude --continue 回到这段对话，你接着往下做。\n\n二、开工\n- 代理编号：{a0}\n- 先调 get_context 看草稿和现状，再按 {name} 的使用说明来。专用工具覆盖不到的配置，用 list_admin_apis 找接口、call_admin_api 读和改。\n- 我的卡面图在电脑桌面的 ITEM_CARD 文件夹里，结构是「78_PANTS/78-7_Ski pants.png」（卡 78-7，英文名 Ski pants），「78_PANTS_icon.png」是包的封面。要导入时请你自己找到它，用 curl 把整个文件夹传上去，先给我看导入计划。\n- 接上后先告诉我你能帮我做哪些事，然后等我说要做什么。\n\n三、规则\n- 只改草稿，不要发布。\n- 已经有的图、名字、封面不要动；同一张图不要重复上传。\n- 写入前先把「原值 → 新值」或导入计划给我看，等我确认。\n- 商城、任务成就这类存了马上生效的配置，一定先给我看预览。\n- 数字（概率、价格、奖励、稀有度）按我说的算，不要自己编。\n- 做完用 review_before_publish 给我一份清单，提醒我去后台点「配置检查」再发布。\n- 写入工具要带 env，和要改的环境一致；我没说改测试还是生产、你又同时连着两个时，先问我。",
  "mcp.envTest": "测试",
  "mcp.envProd": "生产",
  "mcp.envBannerTest": "当前是测试后台。接入后 MCP 名字是 {name}，可以放心试，工具调用不用每次确认。",
  "mcp.envBannerProd": "当前是生产后台：改动发布后玩家可见。接入后 MCP 名字是 {name}，每次写入 Claude 都会弹窗让你确认，请看清工具名里的 prod 再点同意。",
  "mcp.setupTest": "把 mcp__{name} 加进 ~/.claude/settings.json 的 permissions.allow（测试环境的工具不用每次问我）。以前装过名字叫 idol-admin 的，先执行 claude mcp remove idol-admin -s user，并把 permissions.allow 里的 mcp__idol-admin 删掉。",
  "mcp.setupProd": "这是生产：不要把 mcp__{name} 加进 permissions.allow，生产的每次写入都要弹窗让我确认；allow 里已经有 mcp__{name} 或 mcp__idol-admin 就删掉。以前装过名字叫 idol-admin 的，先执行 claude mcp remove idol-admin -s user。如果我开着跳过权限确认的模式（bypass permissions），提醒我改生产前先关掉。",
  "folder.entry": "按文件夹导入",
  "folder.title": "按文件夹导入卡包",
  "folder.intro": "选一个卡包文件夹（如 78_PANTS），或整个 ITEM_CARD。按文件名排卡：78-7_Ski pants.png 是卡 78-7、英文名 Ski pants；78_PANTS_icon.png 是封面。只补空缺，已有的图和名字不动，所以重复导入不会重复上传。",
  "folder.pick": "选择文件夹…",
  "folder.repick": "换一个文件夹…",
  "folder.empty": "这个文件夹里没有认得出的卡面。",
  "folder.col.name": "包名",
  "folder.col.action": "动作",
  "folder.col.add": "新加卡",
  "folder.col.fill": "补图",
  "folder.col.kept": "已有保留",
  "folder.act.new": "新建",
  "folder.act.update": "补充",
  "folder.act.none": "无变化",
  "folder.cover": "补封面",
  "folder.uploading": "上传中 {done}/{total}",
  "folder.apply": "导入 {packs} 个包（传 {files} 张图）",
  "folder.unrecognized": "{n} 个文件认不出，没有导入：",
  "folder.after": "导入后多语言名、稀有度、权重还要补：可以在抽屉里逐个改，也可以交给运营后台 MCP。",
  "card.packCount": "共 {n} 个卡包 · 点名称进包内编辑",
  "card.col.code": "编号",
  "card.col.count": "卡片数量",
  "card.col.rarity": "稀有度分布",
  "card.col.unlock": "解锁条件",
  "card.col.shopDrawCount": "商城张数",
  "card.col.shopPriceGem": "价格",
  "card.shopCount": "{n} 张",
  "card.shopPrice": "{n} 钻石",
  "card.f.shopDrawCount": "商城每次张数",
  "card.f.shopPriceGem": "价格（钻石）",
  "card.hint.shopDrawCount": "商城购买一次抽出的张数，按包内卡牌权重随机，可重复（重复只加张数）。",
  "card.hint.shopPriceGem": "买一次扣的钻石数；0 = 免费（只应出现在测试环境）。",
  "card.col.idol": "所属 idol",
  "card.f.idol": "所属 idol",
  "card.hint.idol": "这包 idol 卡属于「idol 中心 · idol 列表」里的哪一位。可选，不必填；按艺名或 ID 搜索，已下架的也能选——绑定说的是归属，不是是否在架。",
  "card.idolUnbound": "未绑定",
  "card.searchIdol": "搜索艺名或 ID",
  "card.idolInactive": "已下架",
  "card.idolGone": "{id}（已不在名册）",
  "card.idolGoneTip": "绑定的 idol 已从名册删除，发布会被拦下——换一位或清空绑定。",
  "u.sheet": "张",
  "u.gem": "钻石",
  "u.dice": "个骰子",
  "card.col.setReward": "集齐奖励",
  "card.col.state": "校验",
  "card.col.coverImg": "封面",
  "card.notSet": "未设置",
  "card.empty": "还没有{kind}包",
  "card.cardList": "卡牌明细",
  "card.addCard": "+ 添加卡牌",
  "card.noCards": "这一包还没有卡牌",
  "card.upload": "上传",

  // 对话框 / 组件
  "dlg.addLevel": "新增关卡",
  "dlg.insertAt": "插入位置",
  "dlg.nowTotal": "当前共 {n} 关",
  "dlg.shiftWarn": "插入后第 {at} 关及其之后的 {n} 关序号都会顺延一位。关卡内的建筑按 code 标识，不受影响；但按关卡序号挂的奖励配置需要复核。",
  "dlg.cityName": "城市名",
  "dlg.enRequired": "英文名是默认语言，必须填写",
  "dlg.prevLevel": "上一关 {v}",
  "dlg.firstLevel": "首关",
  "dlg.targetRolls": "目标骰次",
  "dlg.blankNote": "各栋初始值与成本网格留空待填，不自动生成——编出来的数字看着合理却是假的，比空着危险。",
  "dlg.names": "第 {n} 关 · 名称（国际化）",
  "dlg.i18nNote": "英文为默认语言，未配置其他语言时客户端回落到英文。",
  "dlg.rewardTitle": "第 {n} 关 · 通关奖励",
  "dlg.rewardName": "奖励名称",
  "dlg.select": "选择…",
  "dlg.qty": "数量",
  "dlg.remove": "移除",
  "dlg.addKind": "+ 添加{kind}",
  "dlg.moneyShare": "本关发放 money 折合 {v} 金币，占目标总造价 {p}。",
  "dlg.moneyHigh": "占比偏高，玩家可能靠通关奖励直接推进下一关。",
  "dlg.packTitle": "{kind}包 · {code} {name}",
  "dlg.newPackTitle": "新增{kind}包",
  "pack.new.needName": "英文名必填",
  "pack.new.badCode": "编号只能填数字",
  "pack.new.codeTaken": "编号 {code} 已被占用",
  "pack.new.needCount": "卡片数量请填大于 0 的整数",
  "pack.new.needUnlock": "请选择要集齐的物品卡包",
  "dlg.packName": "包名称",
  "dlg.code": "编号",
  "dlg.cardCount": "卡片数量",
  "dlg.nowHas": "当前已配 {n} 张",
  "dlg.setReward": "集齐奖励",
  "card.addSetReward": "+ 添加奖励",
  "card.hint.setRewards": "集齐这一包后玩家点「领取」才发；与通关奖励同一套类型与发放链。money 按领取时所在关的 B 值折金币。留空 = 集齐不发奖励（解锁 idol 卡包资格不受影响）。",
  "card.hint.setRewardsG": "集齐这一包后玩家点「领取」才发；与通关奖励同一套类型与发放链。money 填的是几倍单掷金币，按领取时所在关的单掷金币 g 折金币。留空 = 集齐不发奖励（解锁 idol 卡包资格不受影响）。",
  "dlg.unlockCond": "解锁条件",
  "dlg.unlockBy": "集齐 {code} · {name}",
  "dlg.image": "图片",
  "dlg.rate": "掉率",
  "card.weightSum": "权重合计 {n}",
  "dlg.weight": "掉落权重",
  "dlg.rarity": "稀有度",
  "levelPicker.label": "所属关卡",
  "levelPicker.item": "第 {id} 关 · {name}",
  "levelPicker.prev": "上一关",
  "levelPicker.next": "下一关",
  "table.empty": "暂无数据",
  "i18nField.enRequired": "English（必填）",
  "i18nField.enOptional": "English（选填）",
  "i18nField.localeEmpty": "{lang}（未填）",
  "i18nField.enMissing": "缺 EN",
  "tz.tip": "租户时区 {tz}",
  "tz.tipLong": "租户时区 {tz}——本页所有时间均按此时区显示与录入",
  "check.allPassed": "全部通过",
  "check.complete": "配置完整",
  "coming.tbd": "需求待细化。",
  "hint.N": "本关有几栋建筑（N）。每栋建筑的初始值各自配置；增加数量时新栋的初始值与逐格实际造价留空待填，不会替你外推。",
  "hint.M": "每栋建筑要升几级星（M，默认 5：1★→5★）。建造页的行数 = 建筑数量 × 建筑星级；减少星级会隐藏（不删除）多余级的数据，改回可恢复。",
  "hint.rolls": "期望玩家打完这一关要掷多少次骰子，决定关卡时长；也是算法总造价的乘数。所有玩家用同一个值。这一关要花多少钱不在这里配，去「总造价配置」填实际总造价。",
  "hint.range": "只给管端模拟器用：模拟出的骰次落在「目标骰次 ± X%」内算通过。不会给玩家分配不同的目标骰次，也不下发给客户端。",
  "hint.buildingCode": "建筑的唯一标识，前后端通讯与埋点都用它，不用名称。按「关卡编号-building-建筑序号-星级」自动生成；关卡顺延时会跟着重算，不可手改。",
  "rw.money": "money",
  "rw.spin": "spin",
  "rw.itemCard": "物品卡",
  "rw.idolCard": "idol 卡",
  "rw.puzzle": "拼图碎片",
  "rw.actItem": "活动道具",
  "rw.money.hint": "通关发放的金币，单位是 B：发奖时 × 本关运行时的 B。与本关目标总造价一起看回收率。",
  "rw.spin.hint": "通关发放的骰子数量。",
  "rw.itemCard.hint": "对应普通物品奖励，具体卡牌从「卡牌配置 · 物品卡」里选。",
  "rw.idolCard.hint": "对应 idol 卡奖励，具体卡牌从「卡牌配置 · idol 卡」里选。",
  "rw.puzzle.hint": "拼图碎片数量，主题在「拼图配置」里定。",
  "rw.actItem.hint": "可多选：一关可以同时发多个细分主题的活动道具。",
  "rw.theme1": "细分主题 1",
  "rw.theme2": "细分主题 2",
  "rw.theme3": "细分主题 3",
  // 活动道具的展示名（ref → 名字的映射在 lib/reward.ts 的 ACT_ITEM_LABEL）。
  // 这些都是《任务与成就配置表 V1.0》里还没定资产键的名词，先占位；定了改这里的名字即可
  "act.materialSmall": "建材小包",
  "act.materialLarge": "建材大包",
  "act.cardPack": "卡包",
  "act.cardPackPremium": "高级卡包",
  "act.clue": "线索",
  "act.glowstick": "荧光棒",
  "act.adoptTicketFrag": "指名领养券碎片",
  "act.bouquet": "花束",
  "act.letter": "手写信",
  "act.idolVoice": "idol 专属语音",
  "act.spinTreeBoost": "spin 树增产 1 天",
  "act.srPetBox": "R（第 2 档）宠物自选箱",
  "act.albumLimitedCard": "卡册限定卡",
  "act.urAdoptTicket": "最高档（SR）保底领养券",

  // ── 任务 / 成就（docs/achievement-requirements.md） ──
  "nav.achievement-tasks": "任务管理",
  "nav.achievement-achievements": "成就管理",
  "nav.achievement-rewards": "奖励配置管理",
  "navGroup.g-achievement": "Idol Ludo任务成就",
  "navGroup.g-achievement-erhe": "二合任务成就管理",
  "nav.erhe-achievement-tasks": "任务管理",
  "nav.erhe-achievement-achievements": "成就管理",
  "nav.erhe-achievement-rewards": "奖励配置管理",
  // 二合任务成就（2026-10-29，口径《idol二合-任务与成就配置.xlsx》）：四个子页 + 测试面板
  "nav.erhe-daily": "每日任务",
  "nav.erhe-chest": "每日宝箱",
  "nav.erhe-weekly": "每周任务",
  "nav.erhe-achievement": "成就",
  "nav.erhe-tabs": "标签管理",
  "nav.game-center": "游戏中心",
  "gc.note": "游戏中心：客户端首页的「好友房 / VIP 房」入口。开关关着时客户端藏入口、所有游戏中心接口回 60701；生产没配过就是关的。保存即生效，不走配置版本。",
  "gc.needA0": "先在顶部选择代理",
  "gc.saved": "已保存，立即生效",
  "gc.enabled": "开放游戏中心",
  "gc.games": "可建房的游戏",
  "gc.gamesHint": "本期只有 Ludo 能建房；Bingo / UNO 的开关留着，开了客户端也只会显示「暂未开放」",
  "gc.game.ludo": "Ludo",
  "gc.game.bingo": "Bingo（本期未开放）",
  "gc.game.uno": "UNO（本期未开放）",
  "gc.friendSeats": "好友房人数候选",
  "gc.vipSeats": "VIP 房人数候选",
  "gc.seatsHint": "房主建房时在这里面选一个。候选只能在 2 到匹配玩法 ludo 的 room_max（当前 {max}）之间",
  "gc.seatsNoLudo": "本代理还没配匹配玩法 ludo（匹配与房间页），候选上限按 4 校",
  "gc.waitingTimeout": "等待超时（秒）",
  "gc.waitingTimeoutHint": "等待中的房这么久没开局自动解散，邀请一并失效；不能短于 60 秒",
  "gc.roomNameMaxLen": "房间名长度上限",
  "gc.roomNameMaxLenHint": "房主填的房间名最多几个字（按字数，一个 emoji 算一个）",
  "mmon.center.friend": "好友房",
  "mmon.center.vip": "VIP 房",
  "erhe.note.daily": "每日任务：每天 7 条 = 固定池全部 + 随机池按权重抽几条（去重，条数在「每日宝箱」页配）；任务只给活跃度，奖励走每日宝箱。UTC 0 点重置。保存即生效。",
  "erhe.note.chest": "每日宝箱：活跃度每天 0 点重置、宝箱不累计；活跃度到档就能开那一档。领满最后一档会记一次「领取每日第3宝箱次数」。保存即生效。",
  "erhe.note.weekly": "每周任务：全部启用的都下发，每周一 0 点（UTC）重置，到目标领奖励。保存即生效。",
  "erhe.note.achievement": "成就：一次性、永久、按档位逐级领取（第 2 档要第 1 档领了才能领）；目标值是累计值。保存即生效。",
  "erhe.note.tabs": "标签管理：客户端二合任务弹窗底部的标签。客户端按这里的顺序显示；名称不填时客户端显示默认中文名；停用的标签客户端不显示，里面的任务也领不了。保存即生效。",
  "erhe.tabs.col.tab": "标签",
  "erhe.tabs.col.name": "名称",
  "erhe.tabs.col.order": "顺序",
  "erhe.tabs.kind.daily": "列表 + 活跃度 + 每日宝箱",
  "erhe.tabs.kind.list": "普通列表",
  "erhe.needA0": "先在顶部选择代理",
  "erhe.add": "新建",
  "erhe.empty": "还没有条目",
  "erhe.saved": "已保存，立即生效",
  "erhe.deleted": "已删除",
  "erhe.deleteConfirm": "删除 {id}？玩家已领的记录保留，列表里不再出现。",
  "erhe.col.op": "操作",
  "erhe.col.taskId": "任务ID",
  "erhe.col.achId": "成就ID",
  "erhe.col.category": "类别",
  "erhe.col.taskName": "任务名称",
  "erhe.col.achName": "成就名称",
  "erhe.col.conditionType": "条件类型",
  "erhe.col.conditionParam": "条件参数",
  "erhe.col.source": "进度来源",
  "erhe.col.enabled": "启用",
  "erhe.col.coin": "金币",
  "erhe.col.ticket": "idol券",
  "erhe.col.gem": "钻石",
  "erhe.col.target": "目标值",
  "erhe.col.activity": "活跃度",
  "erhe.col.pool": "池类型",
  "erhe.col.weight": "随机权重",
  "erhe.col.unlock": "解锁条件",
  "erhe.col.tiers": "档位目标",
  "erhe.col.tiersSub": "按档位 1 / 2 / 3…",
  "erhe.col.rewards": "各档奖励",
  "erhe.unit.coin": "金币",
  "erhe.unit.ticket": "券",
  "erhe.unit.gem": "钻",
  "erhe.settings.saved": "已保存，立即生效",
  "erhe.settings.randomCount": "随机抽几条",
  "erhe.settings.randomCountHint": "每天从随机池按权重抽的条数（配置表：3），加上固定池就是当天的全部任务",
  "erhe.settings.activityCap": "活跃度上限",
  "erhe.settings.activityCapHint": "当日活跃度封顶（配置表：100）；宝箱档位的所需活跃度不能超过它",
  "erhe.chest.col.tier": "宝箱",
  "erhe.chest.col.activity": "所需活跃度",
  "erhe.chest.tiers": "宝箱档位",
  "erhe.chest.addTier": "+ 一档",
  "erhe.chest.empty": "还没配宝箱档位，客户端不会显示宝箱",
  "erhe.f.idHint": "1~32 位字母 / 数字 / 下划线（照配置表：D01 / W01 / A01），建后不可改",
  "erhe.f.conditionRequired": "要选条件类型",
  "erhe.dlg.newAch": "新建成就",
  "erhe.dlg.newTask": "新建任务",
  "erhe.dlg.edit": "编辑 {id}",
  "erhe.f.desc": "描述",
  "erhe.f.descHint": "可写 {target} 占位，客户端按当前档目标值替换",
  "erhe.f.paramHint": "附加条件，如「等级 ≥ 5」「特别单 / 偶像单」；不同条件各算一条进度",
  "erhe.f.sourceInternalHint": "本系统有这项数据：由服务端自己记进度",
  "erhe.f.sourceExternalHint": "二合平台的数据：对接二合接口后由服务端查回来写进度；对接前在测试面板写",
  "erhe.f.activityHint": "领这条给多少活跃度（配置表：登录 10、其余 15）",
  "erhe.f.poolHint": "固定 = 每天都有；随机 = 按权重抽（权重 0 不进池）",
  "erhe.f.reward": "奖励",
  "erhe.f.tiers": "档位",
  "erhe.f.addTier": "+ 一档",
  "erhe.f.tiersHint": "目标值是累计值，严格递增；每档各配一份奖励（金币 / idol券 / 钻石）",
  "erhe.f.tierNo": "第 {n} 档",
  "erhe.f.unlockHint": "二合章节 ≥ N 才进池 / 可领；0 = 不设",
  "erhe.f.icon": "图标",
  "erhe.f.sort": "排序",
  "erhe.cond.energy_spend": "消耗体力点数",
  "erhe.cond.order_complete": "完成订单张数",
  "erhe.cond.merge_count": "合成次数",
  "erhe.cond.merge_max_level": "合出链条顶级物品个数",
  "erhe.cond.generator_tap": "点击生产器次数",
  "erhe.cond.generator_unlock": "解锁生产器次数",
  "erhe.cond.generator_upgrade": "升级生产器次数",
  "erhe.cond.item_sell": "卖出物品个数",
  "erhe.cond.chest_open": "领取产出/宝箱次数",
  "erhe.cond.puzzle_piece": "拼上拼图碎片数",
  "erhe.cond.puzzle_stage_clear": "完成关卡数",
  "erhe.cond.puzzle_stage_first_clear": "单关首通",
  "erhe.cond.ticket_spend": "使用idol券张数",
  "erhe.cond.coin_spend": "消耗金币",
  "erhe.cond.coin_earn_total": "累计获得金币",
  "erhe.cond.item_discover": "图鉴新物品种数",
  "erhe.cond.chain_complete": "完整合出过的链条数",
  "erhe.cond.login": "当日登录",
  "erhe.cond.login_days": "累计登录天数",
  "erhe.cond.daily_chest3_claim": "领取每日第3宝箱次数",
  "erhe.cond.chapter": "二合章节进度",
  "erhe.cat.merge": "合成",
  "erhe.cat.order": "订单",
  "erhe.cat.energy": "体力",
  "erhe.cat.generator": "生产器",
  "erhe.cat.puzzle": "拼图",
  "erhe.cat.collect": "收集",
  "erhe.cat.economy": "经济",
  "erhe.cat.active": "活跃",
  "erhe.pool.fixed": "固定",
  "erhe.pool.random": "随机",
  "erhe.source.internal": "本系统",
  "erhe.source.external": "二合平台",
  "erhe.param.min_level": "最低等级",
  "erhe.param.order_type": "订单类型",
  "erhe.param.stage_id": "关卡",
  "erhe.paramFmt.min_level": "等级 ≥ {v}",
  "erhe.paramFmt.order_type": "订单类型：{v}",
  "erhe.paramFmt.stage_id": "关卡：{v}",
  "erhe.orderType.special": "特别单 / 偶像单",
  "erhe.unlockFmt": "二合章节 ≥ {n}",
  "erhe.f.orderTypeHint": "填 special 表示特别单 / 偶像单",
  "devpanel.erhe": "二合任务成就进度",
  "devpanel.erheHint": "二合接口还没对接，进度只能在这里写：选进度项（带条件的在右边填，如等级 ≥ 5 填 min_level=5），设成或加上指定值。清空会删掉这个号的二合进度和当天抽到的任务（下次打开重抽）；已领过的档保持已领。要重测领取用「清空二合进度和领取记录」：连领取记录和回执一起删，当天 / 本周 / 成就都能重新领（只支持没迁入 DO 的玩家）。",
  "devpanel.erheMetric": "进度项",
  "devpanel.erheParam": "条件",
  "devpanel.erheOpSet": "设成",
  "devpanel.erheOpAdd": "加上",
  "devpanel.erheValue": "值",
  "devpanel.erheWindowAll": "终身 + 当日 + 当周",
  "devpanel.erheWindowLifetime": "只终身",
  "devpanel.erheWindowDaily": "只当日",
  "devpanel.erheWindowWeekly": "只当周",
  "devpanel.erheSet": "写入",
  "devpanel.erheReset": "清空二合进度",
  "devpanel.erheDone": "已写入 {metric}",
  "devpanel.erheResetDone": "已清空：进度 {counters} 行、当天抽取 {draws} 行",
  "devpanel.erheResetClaims": "清空二合进度和领取记录",
  "devpanel.erheResetClaimsDone": "已清空：进度 {counters} 行、领取记录 {claims} 行、领取回执 {receipts} 行，当天 / 本周 / 成就都能重新领",
  "rw.shield": "盾牌",
  "rw.free": "免费掷骰券",
  "rw.wildcard": "万能卡",
  "rw.shield.hint": "受持有上限 shield_max 限制，满了不补发。",
  // 任务 / 成就弹窗里的奖励类型下拉用中文；通关奖励页的列头（rw.money / rw.spin）刻意保留英文字段名，不动
  "ach.metric.roll_count": "累计掷骰",
  "ach.metric.roll_x3_count": "高倍率（×3 及以上）掷骰",
  "ach.metric.coins_earned": "累计获得金币",
  "ach.metric.subgame_trigger_count": "触发任意副玩法（四件套）",
  "ach.metric.upgrade_count": "累计升星",
  "ach.metric.city_complete_count": "累计通关",
  "ach.metric.city_level_max": "到达关卡",
  "ach.metric.pettown_step_count": "敲主建筑",
  "ach.metric.pettown_star_gain": "主建筑升星",
  "ach.metric.pettown_star": "主建筑星级达到",
  "ach.metric.fog_unlock_count": "解锁迷雾地块",
  "ach.metric.item_card_count": "获得物品卡",
  "ach.metric.pack_open_count": "开卡包",
  "ach.metric.pack_done_count": "集齐卡册套系",
  "ach.metric.idol_count": "获得 idol 卡",
  "ach.metric.idol_unlocked_count": "解锁 idol",
  "ach.metric.pokedex_lit_count": "图鉴点亮",
  "ach.metric.pokedex_view_count": "领取图鉴/卡册任意奖励或查看进度",
  "ach.metric.steal_win_count": "偷取成功（我偷到别人）",
  "ach.metric.shield_block_count": "御守挡下雷神",
  "ach.metric.thunder_hit_count": "被雷神劈中",
  "ach.metric.steal_hit_count": "被贫乏神偷走",
  "ach.metric.revenge_win_count": "复仇成功",
  "ach.metric.gift_count": "向好友赠送线索",
  "ach.metric.friend_count": "好友数达到",
  "ach.metric.friend_interact_count": "好友互动（赠送 + 收礼）",
  "ach.metric.auction_win_count": "竞拍获胜",
  "ach.metric.invite_success_count": "成功邀请好友",
  "ach.metric.adopt_count": "领养宠物（含重复）",
  "ach.metric.adopt_grass_count": "草丛领养",
  "ach.metric.adopt_sr_count": "领养 R 及以上（第 2 档起）宠物",
  "ach.metric.adopt_ur_count": "领养最高档（SR）宠物（含重复）",
  "ach.metric.adopt_spot_hit_count": "踩中任意领养点",
  "ach.metric.pet_touch_count": "与任意宠物互动（点击）",
  "ach.metric.home_pet_count": "家园同屏宠物达到",
  "ach.metric.spin_tree_harvest_count": "收取 spin 树",
  "ach.metric.idol_greet_count": "给任意 idol 打招呼",
  "ach.metric.idol_gift_count": "送出应援礼物",
  "ach.metric.idol_intimacy_max": "单个 idol 亲密度达到",
  "ach.metric.task_points": "当日活跃点达到",
  "ach.metric.newbie_day_claimed": "领取当天新手子任务",
  "ach.metric.login_streak": "连续登录（断了从头计）",
  "ach.metric.misfortune_same_day": "同一天里几件倒霉事都发生",
  "ach.metric.idol_greet_streak": "连续给同一位 idol 打招呼",
  "ach.metric.spin_tree_on_time_streak": "连续准点收取 spin 树",
  "ach.metric.adopt_count_in_hours": "指定时段内领养",
  "ach.metric.adopt_then_showcase": "领养后短时间内设为门面",
  "ach.metric.pet_rename_distinct": "给不同宠物改昵称",
  "ach.tab.daily": "每日任务池",
  "ach.tab.track": "活跃度宝箱",
  "ach.tab.weekly": "每周任务",
  "ach.tab.lifetime": "成就（累计多级）",
  "ach.dlg.newTask": "新增任务",
  "ach.dlg.newAch": "新增成就",
  "ach.f.taskType": "任务分类",
  "ach.f.achType": "成就分类",
  "ach.f.typeLocked": "分类决定周期与形状，建好之后不能改；要换分类就新建一条",
  "ach.f.taskName": "任务名",
  "ach.f.achName": "成就名",
  "ach.f.trigger": "触发条件",
  "ach.f.reward": "奖励",
  "ach.f.intentHint": "达成后客户端显示的文案；配置表里的「设计意图」写这里",
  "ach.f.topTitle": "最高档称号",
  "ach.f.topTitleNone": "不发称号",
  "ach.f.topTitleHint": "达到第 {n} 档时发；玩家佩戴后挂在好友列表头像旁。只有累计多级成就发得了称号",
  "ach.col.target": "目标值",
  "ach.cat.collector": "收集家",
  "ach.cat.adopt": "领养",
  "ach.cat.build": "建设",
  "ach.cat.board": "棋盘",
  "ach.cat.idol": "idol",
  "ach.cat.social": "社交",
  "ach.cat.album": "卡册",
  "ach.unit.times": "次",
  "ach.unit.pet": "只",
  "ach.unit.item": "个",
  "ach.unit.portion": "份",
  "ach.unit.tap": "下",
  "ach.unit.star": "星",
  "ach.unit.set": "套",
  "ach.unit.point": "点",
  "ach.unit.stage": "关",
  "ach.unit.card": "张",
  "ach.unit.tile": "块",
  "ach.unit.person": "人",
  "ach.unit.species": "种",
  "ach.unit.day": "天",
  "ach.tr.new": "新增档位",
  "ach.tr.edit": "编辑第 {n} 档",
  "ach.tr.deleted": "已删除第 {n} 档",
  "ach.tr.empty": "还没有配置活跃度宝箱，点「新增」加第一档",
  "ach.tr.needPoints": "所需活跃点必填",
  "ach.tr.orderHint": "档位按所需活跃点从低到高自动排序，先填哪档都行",
  "ach.tr.enabledTrack": "启用整条宝箱轨（三档一起）",
  "ach.nb.chestDlg": "第 {day} 天 · 收口大奖",
  "ach.nb.chestTargetHint": "= 当天启用的子任务条数，保存时自动算",
  "ach.tab.hidden": "趣味 / 隐藏成就",
  "ach.tab.newbie": "新手七日",
  "ach.tab.title": "称号",
  "ach.ai.new": "新增活动道具",
  "ach.ai.edit": "编辑活动道具 {ref}",
  "ach.ai.ref": "道具 ref",
  "ach.ai.refHint": "奖励项里填的那个 id；玩家钱包里是 act_<ref>。建立后不可改",
  "ach.ai.name": "名字",
  "ach.ai.desc": "说明",
  "ach.jump.home": "家园",
  "ach.jump.album": "卡册",
  "ach.jump.pets": "宠物",
  "ach.f.points": "活跃点",
  "ach.f.pointsHint": "领取这一条给多少活跃点，推进活跃度宝箱。只有每日任务和新手子任务能给",
  "ach.f.weight": "权重",
  "ach.f.weightHint": "数值越大在客户端列表里越靠前。每日池里同时是抽中概率：自身权重 ÷ 剩余候选权重之和，0 = 不进池",
  "ach.f.weightOrderHint": "数值越大在客户端列表里越靠前；相同权重按建的先后",
  "ach.f.mustShow": "必出",
  "ach.f.mustShowHint": "先占名额、不参与随机。必出的条数不能超过「每天抽几条」",
  "ach.f.hiddenHint": "达成之前完全不下发给客户端，所以只配一档",
  "ach.f.newbieDay": "第几天",
  "ach.f.newbieDayHint": "新手期第几天。收口大奖的目标值 = 当天启用的子任务条数，保存时服务端自动重算，不用手填",
  "ach.f.replace": "条件替换",
  "ach.f.replaceNone": "不替换",
  "ach.f.replaceWith": "替换成",
  "ach.f.replaceHint": "展示这一条时若条件成立，改成展示另一条。不级联：换过去的那条自己不能再配替换",
  "ach.f.params": "探测器参数",
  "ach.f.paramsHint": "阈值填在这里，代码里没有——同一个探测器可以被好几条不同阈值的成就复用",
  "ach.f.titleName": "称号名",
  "ach.col.points": "活跃点",
  "ach.col.weight": "权重",
  "ach.col.day": "天",
  "ach.dailyNote": "每天从这个池里按权重抽若干条给玩家，当天固定不变、跨 UTC 0 点重抽。必出的先占名额，其余按权重随机。",
  "ach.trackNote": "活跃度宝箱：进度是当日活跃点（只来自每日任务和新手子任务），每天清零。**只允许启用一条**，档数与门槛都在这里配。",
  "ach.hiddenNote": "趣味 / 隐藏成就：达成前完全不下发，客户端看不到。阈值（连续几天、单日几次、几秒内）都在配置里，不在代码里。",
  "ach.newbieNote": "新手七日活动按注册后 24 小时滚动解锁。有效期内可补做已解锁任务，奖励手动领取，每日任务照常开放。大奖目标等于当天启用的子任务数；点子任务或大奖编辑，点「＋」新增。",
  "ach.nb.theme": "主题",
  "ach.nb.goals": "目标（2–3 条）",
  "ach.nb.chest": "收口大奖",
  "ach.nb.systems": "涉及系统",
  "ach.nb.setChest": "配大奖",
  "ach.nb.editChest": "编辑大奖",
  "ach.nb.noTasks": "还没有子任务",
  "ach.nb.daySaved": "第 {day} 天已保存",
  "ach.nb.dayReward": "完成这些目标后的奖励",
  "ach.nb.goalsN": "目标（{n}）",
  "ach.nb.dupId": "{id} 在这一天里重复了",
  "ach.nb.needTask": "这一天至少要有一条子任务",
  "ach.nb.noChest": "这一天还没配收口大奖",
  "ach.nb.chestTargetAuto": "自动，等于当天启用的子任务条数，保存时服务端重算",
  "ach.nb.dayHint": "整天一次保存：目标与奖励一起提交，一个事务写完，不会出现改了一半的状态。目标本身不发奖励，一天只有下面这一份，集齐当天全部启用的目标才能领。",
  "ach.nb.addTask": "加一条目标",
  "ach.nb.untitled": "（未命名）",
  "ach.nb.count": "{n} 天 · {m} 条",
  "ach.titleNote": "称号：成就最高档发的东西，玩家佩戴后挂在好友列表头像旁。删除只删配置，玩家已经拿到的不会被收回。",
  "ach.metricAch.pokedex_lit_count": "图鉴点亮种类数",
  "ach.metricAch.adopt_ur_count": "累计获得最高档（SR）宠物（含重复）",
  "ach.metricAch.adopt_count": "累计领养次数（含重复）",
  "ach.metricAch.adopt_grass_count": "草丛领养累计",
  "ach.metricAch.pettown_star": "主建筑星级",
  "ach.metricAch.fog_unlock_count": "迷雾解锁地块数",
  "ach.metricAch.roll_count": "累计掷骰",
  "ach.metricAch.shield_block_count": "御守挡下雷神累计",
  "ach.metricAch.revenge_win_count": "复仇成功累计",
  "ach.metricAch.idol_unlocked_count": "解锁 idol 数",
  "ach.metricAch.idol_intimacy_max": "单 idol 最高亲密度等级",
  "ach.metricAch.idol_gift_count": "累计送出应援礼物",
  "ach.metricAch.friend_count": "好友数",
  "ach.metricAch.gift_count": "累计赠送线索",
  "ach.metricAch.invite_success_count": "成功邀请好友",
  "ach.metricAch.pack_done_count": "累计集齐套系",
  "ach.metricAch.pack_open_count": "累计开卡包",
  "ach.trigger.adopt_count_in_hours": "指定时段内领养 {n} 只（时段在下方参数里填）",
  "ach.trigger.login_streak": "连续 {n} 天登录",
  "ach.trigger.misfortune_same_day": "同一天内被雷神劈+被贫乏神偷",
  "ach.trigger.shield_block_count": "单日御守挡下 {n} 次雷神",
  "ach.trigger.idol_greet_streak": "连续 {n} 天给同一位 idol 打招呼",
  "ach.trigger.adopt_then_showcase": "领养后短时间内设为门面宠物（秒数在下方参数里填）",
  "ach.trigger.adopt_sr_count": "一天内领养 {n} 只 R 及以上（第 2 档起）",
  "ach.trigger.pettown_step_count": "累计敲主建筑第 {n} 下",
  "ach.trigger.spin_tree_on_time_streak": "连续 {n} 天准点收取 spin 树",
  "ach.trigger.pet_rename_distinct": "给 {n} 只宠物改过昵称",
  "ach.trigger.thunder_hit_count": "累计被雷神劈 {n} 次",
  "ach.trigger.home_pet_count": "家园同屏宠物数达到 {n} 只",
  "ach.metricOffline": "玩法未上线，配了计数恒为 0",
  "ach.metricOfflineTag": "未上线",
  "ach.savedWithWarn": "已保存 {id}，但有 {n} 处提醒",
  "ach.progressBands": "进度奖励分段",
  "ach.bandsStarHint": "按当前生效的萌宠小镇方案配置。分段起点是已完成星数，0 星也可配置；未到任何分段时使用默认奖励。",
  "ach.bandsLevelHint": "按当前生效的关卡方案配置。分段起点是当前关卡；未到任何分段时使用默认奖励。",
  "ach.convertStarMetric": "转换为星数目标",
  "ach.convertStarMetricHint": "旧指标仍按派生等级计数。转换为星数后，目标 L 变为 10×(L−1)；请同时检查任务名称和描述。",
  "ach.convertStarBands": "转换旧分段",
  "ach.useDefaultReward": "使用默认奖励",
  "ach.legacyBands": "星数分段尚未配置，当前保留原关卡分段。转换时起点 L 对应已完成 10×(L−1) 星，奖励内容保留。",
  "ach.minStar": "起始星数",
  "ach.minLevel": "起始关卡",
  "ach.addRewardBand": "添加奖励分段",
  "ach.invalidRewardBands": "分段起点必须在可达范围内，且按顺序严格递增。",
  "ach.settings": "模块设置",
  "ach.activity.settings": "活动设置",
  "ach.activity.enabled": "活动开启",
  "ach.activity.duration": "活动有效期（天）",
  "ach.activity.durationHint": "至少覆盖全部解锁日，最长 {max} 天",
  "ach.activity.unlockHint": "第 N 天解锁第 N 天任务",
  "ach.activity.rule": "从玩家注册完成时起，每 24 小时进入下一天。有效期内可补做所有已解锁任务，奖励手动领取；每日任务同时开放。",
  "ach.activity.invalid": "请填写有效天数，活动有效期不能早于最后一天任务解锁",
  "ach.activity.preview": "玩家视角预览",
  "ach.activity.previewDay": "注册后第几天",
  "ach.activity.dayState": "第 {day} 天：{state}",
  "ach.activity.expired": "活动已结束",
  "ach.activity.unlocked": "可补做、可领奖",
  "ach.activity.locked": "未解锁",
  "ach.settings.note": "保存即生效。新手活动设置在「活动配置 · 新手七日」中管理。",
  "ach.settings.dailyTaskCount": "每天抽几条",
  "ach.settings.dailyTaskCountHint": "每天从每日任务池里抽给玩家的条数。必出的条数不能超过它",
  "ach.settings.newbieDays": "任务解锁天数",
  "ach.settings.saved": "设置已保存",
  "ach.title.new": "新增称号",
  "ach.title.edit": "编辑称号 {id}",
  "ach.title.saved": "已保存称号 {id}",
  "ach.title.deleted": "已删除称号 {id}",
  "ach.title.empty": "还没有配置任何称号",
  "ach.title.count": "{n} 个",
  "ach.det.streak": "连续天数",
  "ach.det.same_day_all": "同日复合（同一天几件事都发生）",
  "ach.det.time_window": "时序窗口（前一个动作后 N 毫秒内）",
  "ach.det.hour_range": "时段：每天的这个时间段内发生才算（按租户时区）。选几点几分到几点几分，没有日期——它每天重复，不是某一天的活动窗口。跨午夜就把开始填得比结束晚，如 22:00 → 05:00。",
  "ach.det.distinct_count": "去重计数（同一个对象只算一次）",
  "ach.param.same_target": "必须是同一个目标",
  "ach.param.all_of": "同一天内都要发生的指标",
  "ach.param.within_ms": "间隔上限（毫秒）",
  "ach.param.hour_from": "起始小时（含）",
  "ach.param.hour_to": "结束小时（不含）",
  "ach.param.from_sec": "开始时刻（含）",
  "ach.param.to_sec": "结束时刻（不含）",
  "ach.cond.no_friends": "玩家一个好友都没有",
  "ach.cond.all_packs_done": "所有启用的物品卡包都已集齐",
  "rw.gems": "钻石",
  "rw.gems.hint": "与商城花的是同一个资产",
  "rw.title": "称号",
  "rw.title.hint": "发一个称号，没有数量；已经有了就不重复发",
  "rw.frame": "头像框",
  "rw.frame.hint": "发一个头像框（本版本「头像框」页里启用的），没有数量；已经有了就不重复发。玩家拿到后才能戴",
  "ach.kind.money": "金币",
  "ach.kind.spin": "骰子",
  "ach.count": "{n} 条",
  "ach.empty": "本周期还没有配置任何条目",
  "econ.shared.model2": "金币奖励填几骰：发放 = 几骰 × 玩家所在进度的单掷金币 g。",
  "econ.shared.qPlaceholder": "几骰",
  "econ.shared.dice": "骰",
  "ach.liveNote": "任务与成就暂不走配置版本：弹窗里点保存即落库、立即对玩家生效，不需要发布。保存时服务端会做完整校验，不合格的条目不会入库。",
  "ach.saved": "已保存 {id}",
  "ach.deleted": "已删除 {id}",
  "ach.saveRejected": "保存被拒 · 服务端校验发现 {n} 处问题，按位置修改后再保存。",
  "ach.col.id": "ID",
  "ach.col.metric": "达成指标",
  "ach.col.stages": "分档",
  "ach.col.status": "状态",
  "ach.col.sort": "排序",
  "ach.enabled": "启用",
  "ach.disabled": "停用",
  "ach.stageSummary": "{n} 档 · 目标 {targets}",
  "ach.dlg.edit": "编辑 {id}",
  "ach.f.id": "ID",
  "ach.f.idHint": "字母、数字、_、-；建立后不可改",
  "ach.f.sort": "排序",
  "ach.f.desc": "描述",
  "ach.f.descHint": "支持 {target} 占位，按当前档目标值替换，多档不用写多份",
  "ach.f.stageDesc": "档位描述",
  "ach.f.stageDescHint": "客户端在成就条目上画成这一档的标题（代替「第 N 档」）。留空就用成就描述、把 {target} 换成这一档的目标值",
  "ach.f.icon": "图标",
  "ach.f.upload": "上传",
  "ach.f.metric": "达成指标",
  "ach.f.noCategory": "不分类",
  "ach.f.category": "分类",
  "ach.f.metricCat": "指标大类",
  // 指标大类：与服务端指标表的 category 一一对应（src/game/achievement-metrics.ts）
  "ach.metricCat.main": "棋盘主线",
  "ach.metricCat.progress": "建筑进度",
  "ach.metricCat.collect": "收集",
  "ach.metricCat.versus": "对抗",
  "ach.metricCat.social": "社交",
  "ach.metricCat.pet": "宠物",
  "ach.metricCat.idol": "idol",
  "ach.metricCat.task": "任务体系",
  "ach.metricCat.hidden": "隐藏成就",
  "ach.f.jump": "跳转",
  "ach.f.enabled": "启用",
  "ach.f.stages": "分档",
  "ach.f.target": "目标值",
  "ach.addStage": "加一档",
  "ach.addItem": "加奖励项",
  "ach.stageN": "第 {n} 档",
  "ach.tr.stage": "档位",
  "ach.tr.points": "所需活跃点",
  "ach.tr.items": "宝箱内容",
  "ach.tr.desc": "说明",
  "ach.tr.count": "{n} 档",
  "ach.idInvalid": "ID 只能是字母、数字、_ 或 -，最长 64",
  "ach.idDup": "ID 已存在",
  "ach.uploadFail": "上传失败",
  "ach.jump.none": "不跳转",
  "ach.jump.board": "棋盘",
  "ach.jump.puzzle": "拼图",
  "ach.jump.social": "社区",
  "ach.jump.idol": "idol",
  "pack.noCount": "卡片数量未填",
  "pack.countMismatch": "已配 {have} 张，与卡片数量 {want} 不符",
  "pack.noName": "{n} 张缺名称",
  "pack.noWeight": "{n} 张缺掉落权重",
  "rivalDefault": "竞品{n}",
  "band.tip": "浮动 ±{p}% → {lo} ~ {hi}",
  "v.i18n": "英文名已配置（默认语言）",
  "v.i18n.noCity": "城市名（英文）为空",
  "v.i18n.ok": "{name} · {stage}",
  "v.params": "关卡参数完整（B 值 / 目标骰次）",
  "v.paramsG": "关卡参数完整（单掷金币 g / 目标骰次）",
  "v.params.missing": "B 值或目标骰次待填，算法总造价无法计算",
  "v.params.missingG": "单掷金币 g 或目标骰次待填，算法总造价无法计算",
  "v.params.ok": "B {b} × 骰次 {rolls} × E × ρ = {c}",
  "v.params.okG": "g {g} × 骰次 {rolls} × ρ = {c}",
  "v.empty": "逐格价齐全（没填的按公式价补）",
  "v.empty.some": "有 {n} 格没填，而公式价算不出来（目标骰次 / B 值 / E、ρ 没配齐）",
  "v.empty.someG": "有 {n} 格没填，而公式价算不出来（目标骰次 / 单掷金币 g / ρ 没配齐）",
  "v.empty.ok": "{n} 格全部有值（没填的按公式价）",
  "v.dev": "实际造价合计与目标总造价的偏差 < {limit}",
  "v.dev.na": "公式价算不出来或参数待填",
  "v.dev.ok": "实际造价合计 {total} / 目标总造价 {c}（{src}），偏差 {dev}{verdict}",
  "v.dev.pass": "，通过",
  "v.dev.over": "，超出阈值",
  "v.mono": "每栋内部金额随星级不减",
  "v.mono.bad": "建筑 {b} 的 {l}★ 低于 {prev}★",
  "v.mono.ok": "{n} 栋建筑内部均为升序",
  "v.first": "各栋初始值按建筑序号不降",
  "v.first.bad": "{names} 的 1★ 低于前一栋",
  "v.first.ok": "初始值 {values}",
  "h.timeout": "后端 {s}s 未响应",
  "h.http": "后端返回 HTTP {status}",
  "h.bizCode": "后端返回错误码 {code}",
  "h.unreachable": "无法连接后端",
  "log.title": "操作日志",
  "log.who": "操作人",
  "log.action": "动作",
  "log.target": "对象",
  "log.detail": "详情",
  "log.truncated": "……改动较多，只列前 40 条",
  "log.ip": "IP",
  "log.at": "时间",
  "log.agent": "代理",
  "log.searchActor": "按操作人筛选",
  "log.allActions": "全部动作",
  "log.empty": "还没有操作记录",
  "log.total": "共 {n} 条",
  "log.close": "关闭",
  "log.range": "第 {from}–{to} 条",
  "log.refresh": "刷新",
  "log.emptyHint": "运营在配置台里的每一次动作都会记在这里：切代理、保存、发布、回滚。",
  "audit.rival.add": "新增竞品",
  "audit.rival.remove": "移除竞品",
  "audit.card.save": "保存卡包",
  "audit.reward.save": "保存奖励",
  "audit.params.apply": "应用参数",
  "audit.session.logout": "退出登录",
  "audit.session.exchange": "换取会话",
  "audit.config.verPublish": "发布版本",
  "audit.config.verCreateFirst": "新建首个版本",
  "audit.config.verDraft": "新建草稿",
  "audit.config.verRollback": "回滚版本",
  "audit.config.aucDefaults": "保存竞拍默认值",
  "audit.config.aucAddPeriod": "新增竞拍期",
  "audit.config.removeRival": "移除竞品",
  "audit.config.batchGrid": "批量设置格数",
  "audit.agent.switch": "切换代理",
  "audit.player.costSource.set": "指定玩家造价来源",
  "audit.player.costSource.clear": "清除玩家造价来源",
  "audit.client_state.put": "修改客户端数据",
  "audit.client_state.delete": "删除客户端数据",
  "audit.client_state.clear": "清空客户端数据",
  "audit.recharge.view": "查看充值订单",
  "audit.gm.force_roll": "GM 指定掷骰",
  "audit.devToken.sign": "签发玩家 token",
  "audit.assetSign.backfill": "回填余额签名",
  "audit.devPanel.toggle": "开关测试面板",
  "audit.devPanel.setBalance": "测试面板 · 改余额",
  "audit.devPanel.talent": "测试面板 · 天赋造数",
  "audit.devPanel.resetRhythm": "测试面板 · 重置节奏",
  "audit.devPanel.resetDaily": "测试面板 · 重置每日",
  "audit.devPanel.resetBuildings": "测试面板 · 重置建筑",
  "audit.devPanel.wipe": "测试面板 · 清除数据",
  "audit.devPanel.cancelAccount": "测试面板 · 注销账号",
  "audit.devPanel.resetIdolPuzzle": "测试面板 · 拨回 idol 拼图状态",
  "audit.devPanel.repair": "测试面板 · 修复存档",
  "audit.devPanel.pets": "测试面板 · 改萌宠",
  "audit.devPanel.matchRig": "测试面板 · 对局控盘",
  "audit.devPanel.intimacy": "测试面板 · 改亲密度",
  "audit.devPanel.forceRoll": "测试面板 · 指定掷骰",
  "audit.devPanel.bootPets": "测试面板 · 开局萌宠",
  "audit.devPanel.achievement": "测试面板 · 改成就",
  "audit.route.verActivate": "激活版本",
  "audit.route.verUnfreeze": "解冻版本",
  "audit.route.verRestore": "从回收站恢复草稿",
  "audit.route.verDelete": "删除版本",
  "audit.route.draftsPrune": "清理多余草稿",
  "audit.route.login": "登录",
  "audit.route.password": "修改密码",
  "audit.route.upload": "上传图片",
  "audit.route.pushOfflineSend": "离线推送试发",
  "audit.route.streamUpload": "上传视频",
  "audit.res.common": "全局参数",
  "audit.res.adoptPools": "领养池",
  "audit.res.cardPacks": "卡包",
  "audit.res.rivals": "竞品列",
  "audit.res.idolPhotos": "idol 相册",
  "audit.res.stages": "关卡",
  "audit.res.stageImages": "关卡图片",
  "audit.res.idolVipPhotos": "VIP 照片",
  "audit.res.idolVipVideos": "VIP 视频",
  "audit.res.achievements": "任务成就",
  "audit.res.actEvents": "活动",
  "audit.res.titles": "称号",
  "audit.res.nameLibrary": "名字库",
  "audit.res.npcAccounts": "NPC 号",
  "audit.res.costSource": "玩家造价来源",
  "audit.res.econMigrate": "新建配置版本",
  "audit.res.econSwitch": "代理配置维护",
  "audit.res.erheSettings": "每日宝箱 / 标签管理",
  "audit.res.playerStorage": "玩家存储迁移",
  "audit.verb.post": "提交",
  "audit.verb.put": "保存",
  "audit.verb.patch": "修改",
  "audit.verb.delete": "删除",
  "audit.d.replaced": "替换线上 {v}",
  "audit.d.firstLive": "首次上线",
  "audit.d.from": "基于 {v}",
  "audit.d.rollbackTo": "回滚到 {v}",
  "audit.d.agentFrom": "原代理 {a}",
  "logout.title": "退出登录",
  "logout.confirm": "退出后将清除本机保存的登录凭据，并回到登录页。",
  "gate.expired": "登录已过期，请重新登录。",
  "gate.rejected": "登录失效（{msg}），请重新登录。",
  "gate.loggedOutElsewhere": "已在别的标签页退出登录。",
  "gate.switchedElsewhere": "别的标签页换了账号登录。刷新页面用那个账号继续，或在这里重新登录。",
  "gate.enter": "去 super-admin",
  "nav.accounts": "账号管理",
  "gate.loginTitle": "登录配置台",
  "gate.username": "用户名",
  "gate.password": "密码",
  "gate.login": "登录",
  "gate.loggingIn": "登录中…",
  "gate.loginMissing": "请输入用户名和密码",
  "gate.loginFailed": "登录失败",
  "nav.discord-bind": "Discord 绑定",
  "nav.help-videos": "课程中心",
  "dc.note": "绑定后就能在登录页用 Discord 一键登录。绑定关系存在 notify-hub 侧。",
  "dc.who": "当前账号：{who}",
  "dc.bind": "绑定 / 重新绑定",
  "dc.unbind": "解绑",
  "dc.unbound": "已解绑",
  "dc.viaDiscord": "你是用 Discord 登录进来的，说明已经绑定好了。要换绑或解绑，请先退出、用账号密码登录后再来这一页。",

  "gate.discordLogin": "用 Discord 登录",
  "gate.discordHint": "第一次用？先点「账号密码登录」进来绑定 Discord，之后都从这里一键进入。",
  "gate.discordExchanging": "正在用 Discord 凭证登录…",
  "gate.discordFailed": "Discord 登录没成功，请重新点一次",
  "gate.popupWaiting": "在弹出的窗口里完成 Discord 授权",
  "gate.popupWaitingHint": "授权完窗口会自动关掉，这里接着登录。",
  "gate.popupReopen": "没看到窗口？重新打开",
  "gate.popupCancel": "取消",
  "gate.popupBlocked": "浏览器拦住了弹出窗口，请允许本站弹窗后再点一次",
  "gate.popupDone": "Discord 授权完成",
  "gate.popupDoneHint": "已回到原来的页面登录，这个窗口可以关掉了。",
  "gate.subtitle": "用配置台账号登录。",
  "gate.subtitleDiscord": "点下面的按钮去 Discord 授权，授权完会自动回到这里。",
  "gate.otherWays": "其他登录方式",
  "gate.usePassword": "账号密码登录",
  "gate.fromSuperAdmin": "从中台进入？（10 月 31 日关闭）",
  "bind.title": "绑定 Discord",
  "bind.body": "{who}，点下面的按钮，到 Discord 里把命令粘贴发给机器人就好。",
  "bind.switchAccount": "换个账号",
  "bind.skip": "下次再说",
  "bindf.copyAndOpen": "复制命令并打开 Discord",
  "bindf.copiedOpen": "已复制，去 Discord 粘贴",
  "bindf.copyFailed": "复制没成功，请手动复制这条命令：",
  "bindf.pasteHint": "打开后加入服务器，找到机器人私聊，粘贴发送。绑上后这里会自动进入。",
  "bindf.copiedTitle": "命令已经复制到剪贴板了",
  "bindf.pasteNow": "去 Discord 和机器人的私聊对话框里粘贴（{key}），再按回车发送。发完这里会自动进入。",
  "bindf.waiting": "等你在 Discord 里发送…",
  "bindf.bound": "绑定成功",
  "bindf.boundAs": "已绑定 Discord：{name}",
  "bind.statusFailed": "没查到 Discord 绑定状态",
  "accounts.notice": "所有登录过管端的人都在这里：账号密码、Discord、中台登录的按用户名合成一行。禁用对所有登录方式生效，已登录的最迟 30 秒内被踢出。根超管配在环境变量 SUPER_ADMINS 里，改配置并部署才能变，这里不能禁用或撤销；超管名单里没有自建账号的人，只有根超管能加能删。",
  "accounts.create": "新建账号",
  "accounts.createBtn": "新建账号",
  "accounts.username": "用户名",
  "accounts.password": "密码",
  "accounts.isSuper": "超管",
  "accounts.list": "账号列表",
  "accounts.status": "状态",
  "accounts.you": "我",
  "accounts.enabled": "启用",
  "accounts.disabled": "已禁用",
  "accounts.yes": "是",
  "accounts.no": "否",
  "accounts.disable": "禁用",
  "accounts.enable": "启用",
  "accounts.setSuper": "设为超管",
  "accounts.unsetSuper": "取消超管",
  "accounts.resetPw": "重置密码",
  "accounts.deleteConfirm": "删除 {u} 的自建账号？他不能再用密码登录，不可恢复。如果还能用 Discord 登录，人仍会留在列表里；要彻底拦住请用禁用。",
  "accounts.empty": "还没有人登录过管端。",
  "accounts.resetFor": "为 {u} 设置新密码",
  "accounts.newPassword": "新密码",
  "accounts.confirm": "确定",
  "accounts.cancel": "取消",
  "accounts.created": "已新建 {u}",
  "accounts.updated": "已更新 {u}",
  "accounts.deleted": "已删除 {u}",
  "accounts.pwReset": "已重置 {u} 的密码",
  "accounts.stat": "共 {n} 个 · {e} 个启用 · {s} 个超管",
  "accounts.searchPh": "搜索用户名、显示名或备注",
  "accounts.filter.all": "全部",
  "accounts.role": "角色",
  "accounts.role.super": "超管",
  "accounts.role.member": "普通",
  "accounts.ops": "操作",
  "accounts.never": "从未登录",
  "accounts.noMatch": "没有匹配的账号",
  "accounts.retry": "重试",
  "accounts.selfLocked": "不能改自己的启用状态和超管标记，也不能删自己",
  "accounts.userRule": "3~32 位，只能是字母、数字、_ . -",
  "accounts.pwRule": "8~64 位。提交后再也看不到，请直接转告本人",
  "accounts.superHint": "超管能用联调追踪、测试面板和账号管理，会看到玩家数据",
  "accounts.show": "显示",
  "accounts.hide": "隐藏",
  "accounts.generate": "生成",
  "accounts.copy": "复制",
  "accounts.copied": "已复制",
  "accounts.disableConfirm": "禁用 {u}？他用任何方式都登不进来，已登录的最迟 30 秒内被踢出。之后可以再启用。",
  "accounts.enableConfirm": "启用 {u}？对方可以重新登录。",
  "accounts.superConfirm": "把 {u} 设为超管？对方将能用联调追踪、测试面板和账号管理，会看到玩家数据。",
  "accounts.unsuperConfirm": "取消 {u} 的超管？对方立即失去超管工具。",
  "accounts.user": "用户",
  "accounts.sources": "登录方式",
  "accounts.src.account": "账号密码",
  "accounts.src.discord": "Discord",
  "accounts.src.sso": "中台",
  "accounts.role.root": "根超管",
  "accounts.note": "备注",
  "accounts.lastSeen": "最近登录",
  "accounts.edit": "编辑",
  "accounts.more": "更多",
  "accounts.convert": "转成自建账号",
  "accounts.convertBadName": "用户名不符合自建账号规则（3~32 位字母、数字、_ . -），不能转",
  "accounts.convertFor": "把 {u} 转成自建账号",
  "accounts.convertHint": "建一个同名的账号密码账号，之后他可以直接用密码登录。原来的 Discord / 中台登录照常可用，资料、禁用和超管还是同一个人。",
  "accounts.deleteAccount": "删除自建账号",
  "accounts.rootLocked": "根超管配在环境变量 SUPER_ADMINS 里，这里改不了",
  "accounts.listLocked": "他在超管名单里，只有根超管能取消",
  "accounts.needRoot": "没有自建账号的人只能由根超管加进超管名单",
  "accounts.editFor": "编辑 {u}",
  "accounts.displayName": "显示名",
  "accounts.displayNameHint": "最多 {n} 字。只用来在列表里认人，登录和操作日志仍用用户名",
  "accounts.noteHint": "最多 {n} 字，例如岗位、所属团队",
  "accounts.profileUpdated": "上次由 {who} 修改于 {at}",
  "accounts.rootAccountLocked": "根超管的账号只有根超管能建、改密码和删除",
  "accounts.listOnlyHint": "只在超管名单里，对不上登录过的人",
  "accounts.rootReadonly": "环境变量 · 只读",
  "sim.incomeKError": "收入K在当前进度范围内无法计算，请调整K或首个进度的收入锚。",
  "p.incomeK": "算法收入膨胀系数 K",
  "p.incomeK.hint": "留空沿用现有B；填写正数（最多4位小数）后，以第1关/第1星有效B为起点，每关/每10星乘K。仅影响按B计价的收入，不修改总造价；通关速度可能变化。",
  "p.grp.diceMore": "其他骰子相关",
  "p.pDouble": "双倍概率",
  "p.pDouble.hint": "掷出双倍产出的概率，0~1 的小数（库里按万分数存）。",
  "p.capMax": "骰次上限封顶",
  "p.capMax.hint": "「基础上限 + 每关递增」算出来再高也不超过这个数。",
  "p.diceInit": "新号初始骰次",
  "p.diceInit.hint": "第一次进游戏送多少骰次。",
  "p.grp.relief": "救助金",
  "p.reliefQty": "每次领几颗",
  "p.reliefQty.hint": "玩家每领一次救助金得到几颗骰子。可以超过骰子上限。填 0 = 不开救助金。",
  "p.reliefTimes": "每天能领几次",
  "p.reliefTimes.hint": "每名玩家每天最多领几次，按代理时区换日。最多 999。填 0 = 不开救助金。",
  "p.reliefThreshold": "骰子不多于几颗才能领",
  "p.reliefThreshold.hint": "玩家的骰子不多于这个数才能领（攒着没到账的自动回复也算）。填 0 = 骰子用完才能领。",
  "p.grp.subgamePick": "副玩法由客户端选类型",
  "p.subgamePick": "客户端选副玩法",
  "p.subgamePick.hint": "填 1 开启（单人掷骰）：抽中副玩法时掷骰只告诉客户端「这一掷是副玩法」，由客户端挑哪一种、再调领取接口结算；挑哪种都按整关纠偏发同样的金币，各副玩法自己的保底 / 冷却不再生效，下一掷之前不领就作废。填 0 = 按掉落项引用的副玩法当场结算（原来的做法）。老客户端不认识这种返回，开之前先确认客户端已接好。",
  "p.grp.fightMore": "每日次数",
  "p.cardLimit": "每日抽卡上限",
  "p.cardLimit.hint": "一天最多抽几次卡。",
  "p.puzzleLimit": "每日拼图上限",
  "p.puzzleLimit.hint": "一天最多兑换几次拼图。",
  "p.adLimit": "每日广告上限",
  "p.adLimit.hint": "一天最多看几次激励广告。",
  "p.townRangeLink": "小镇范围设置",
  "p.townRangeHint": "小镇真人匹配、社交推荐的星数范围与小镇新手保护，在小镇参数页配置。",
  "p.grp.dice": "骰子参数",
  "cost.totalStillAll": "合计行仍为全关",
  "cost.actualPlaceholder": "未填 · 走算法总造价",
  "check.pass": "通过",
  "check.fail": "未通过",
  "cost.go.level": "关卡",
  "cost.go.building": "建造",
  "cost.go.reward": "奖励",
  "dlg.batchGrid": "批量设置 建筑数量 / 星级",
  "dlg.batchGrid.warn": "改建筑数量会改变「建造」页的行数：新增的栋位初始值与逐格实际造价留空待填，不替你外推；减少星级只隐藏多余级的数据，改回来能恢复。",
  "dlg.batchGrid.hit": "命中 {n} 关",
  "dlg.batchGrid.badRange": "区间无效",
  "dlg.batchGrid.keep": "不改",
  "dlg.batchGrid.keepHint": "留空表示这一项保持原值。",
  "level.footNote": "名称（含英文名）点末列「✎ 多语言」修改。目标骰次和单掷金币在「总造价配置」；建筑数量、星级和素材在「建筑配置」；浮动区间在「模拟器」。",
  "level.i18nBtn": "✎ 多语言",
  "building.renameRival": "重命名",
  "building.legend.readonly": "灰底 ＝ 只读（算法造价）",
  "building.legend.editable": "白底带框 ＝ 可输入",
  "building.footNote": "建筑 code 自动生成为「关卡编号-building-建筑序号-星级」（如 1-building-1-1），前后端通讯用它、不可手改。竞品逐格价格填完后，总造价配置页的竞品总造价才会显示（反推得来）。素材含图片与动画两类，按关卡 · 建筑 · 星级归档。",
  "dlg.rivalName": "列名",
  "dlg.rivalColor": "标记色",
  "dlg.rivalColorHint": "用在列头圆点与总造价页的对比色",
  "dlg.rivalNameEmpty": "列名不能为空",
  "devpanel.title": "测试面板",
  "devpanel.stateHint": "每次操作后自动刷新。判断操作有没有生效看这里的数（余额、计数、建筑），不要只看提示框：提示只说明服务端收下了请求。",
  "devpanel.cellsTitle": "落点（掉落项）",
  "devpanel.victimLabel": "对抗格打谁",
  "devpanel.victimRandom": "不指定（按正常规则选人）",
  "devpanel.turnOn": "打开面板按钮",
  "devpanel.pickFirst": "先在左边选一个玩家。右边的掷骰、发东西、重置都是对这个玩家操作的。",
  "devpanel.tabRoll": "掷骰",
  "devpanel.tabMatch": "多人对局",
  "devpanel.tabGrant": "发东西 / 改数值",
  "devpanel.tabReset": "拨回状态",
  "devpanel.global": "全局设置（对所有玩家生效，和左边选了谁无关）",
  "devpanel.save": "保存",
  "devpanel.notice": "这些操作会真的改动玩家数据：改余额会写账本，指定落点会真的掷一次骰（扣费、发奖）。仅 test 环境可用。",
  "devpanel.prodBlocked": "当前是 {env} 环境，测试面板不可用——它只在 test 环境开放。",
  "devpanel.switch": "启用测试面板",
  "devpanel.switchHint": "这个开关只管这一页的按钮；test 环境上接口本身一直可用（prod 恒不可用）",
  "devpanel.disabled": "面板按钮现在是关的，下面的操作按钮都点不了。",
  "devpanel.stateOn": "开",
  "devpanel.stateOff": "关",
  "devpanel.bootPets": "新手宠物",
  "devpanel.bootPetsHint": "打开后，新建档的玩家会从生效配置的名册里随机拿到几只（老号不补发）。改完最迟 30 秒生效。",
  "devpanel.bootPetsCount": "只数",
  "devpanel.bootPetsSaved": "新手宠物已保存：{state} / {count} 只",
  "devpanel.pickUser": "玩家",
  "devpanel.search": "搜索昵称或 ID",
  "devpanel.balance": "改余额",
  "devpanel.asset": "资产",
  "devpanel.target": "目标值",
  "devpanel.apply": "设为该值",
  "devpanel.intimacy": "idol 亲密度造数",
  "devpanel.pets": "宠物",
  "devpanel.petsSummary": "{kinds} 种 / 共 {total} 只",
  "devpanel.petGrant": "宠物造数",
  "devpanel.petGrantPet": "宠物",
  "devpanel.petGrantPick": "选一只…",
  "devpanel.petGrantQty": "加几只",
  "devpanel.petGrantStar": "星级",
  "devpanel.petGrantApply": "发放",
  "devpanel.petGrantNew": "（新获得）",
  "devpanel.petGrantDone": "{pet}：现在 {qty} 只、{star}★、可升星重复 {dup} 只{isNew}",
  "devpanel.petGrantHint": "一次发一种：只数加到这一种上（第一只是本体，多出来的是可用于升星的重复）；星级留空 = 不改，填了就直接设成这个星级（不超过星级上限，测满星 / 满级改发金币用）。只能发当前生效配置里启用的角色；不动展示顺序。已迁入 DO 的玩家，下面状态卡读的是投影、会晚几秒，以弹出的结果为准，别重复点。",
  "devpanel.battleWins": "调战阶胜场",
  "devpanel.battleWinsSet": "设为",
  "devpanel.battleWinsAdd": "或加减",
  "devpanel.battleWinsApply": "应用（都空 = 只查）",
  "devpanel.battleWinsDone": "战阶胜场 {wins}（真实 {real}，调整 {adjust}）",
  "devpanel.battleWinsHint": "测升阶、解锁道具卡用：「设为」直接设成多少胜，「或加减」在当前基础上加减（可填负数），二选一。只影响战阶 / 升阶 / 道具卡用的胜场，不伪造对局，战绩、玩家资料的胜场和成就统计都不变。调低不会退回已经升上去的阶。设回真实胜场即清掉调整。",
  "devpanel.talent": "天赋造数",
  "devpanel.talentTo": "领到第几个节点",
  "devpanel.talentDone": "天赋已领 {before} → {claimed} 个（本版本共 {total} 个）",
  "devpanel.talentHint": "测多人体力档位、单人倍率等由天赋开的东西用：按序号把第 1 个到第 N 个节点依次领上，不看天赋等级。和玩家自己领一样结算：礼包照发，体力上限、恢复间隔、体力树照改。只能往上加，要退回用「拨回状态」里的「重置等级与建造进度」。",
  "devpanel.intimacyIdol": "idol",
  "devpanel.intimacySource": "来源",
  "devpanel.intimacyUnits": "次数",
  "devpanel.intimacyMult": "倍率（万分比）",
  "devpanel.intimacyPoints": "点数",
  "devpanel.intimacyDone": "+{gained} 点 · 经验 {exp} · Lv{level} · 截断 {capped}",
  "devpanel.intimacyHint": "dev_panel 直接加点数、不看任何上限；选真实来源则走完整的次数 / 点数 / idol 每日上限链路（用来验上限本身）。按生效配置版本的规则算。",
  "devpanel.balanceDone": "{asset}：{before} → {after}",
  "devpanel.code": "掉落项 code",
  "devpanel.multiplier": "倍率",
  "devpanel.npcThunder": "NPC 雷击选中玩家",
  "devpanel.npcThunderHint": "左侧选择被雷击的玩家，NPC 自动分配。结算与真人被雷击一样：有御守先扣一个挡下；没有就按命中率抽，命中劈最高的一栋完好建筑并扣金币。新手保护内的玩家打不了。",
  "devpanel.npcThunderDone": "已击中 {player} 的第 {position} 处建筑",
  "devpanel.npcThunderBlocked": "御守挡下了 {player} 的这次雷击（扣 1 个御守）",
  "devpanel.npcThunderMissed": "{player} 没被劈中（按命中率落空）",
  "devpanel.npcThunderFailed": "没打成：{outcome}，请刷新玩家状态后重试",
  "devpanel.socialSeed": "给选中玩家造社交数据",
  "devpanel.socialSeedHint": "让本租户几个真实活跃号给他发好友申请，其中几条直接接受成好友。走的是客户端同一套发申请 / 接受，上限、拉黑、推送与真人一样；候选按「可能认识的人」的规则挑（有昵称、30 天内活跃、不是 NPC、和他没关系），重复点只会多出新的。每项最多 10。",
  "devpanel.socialSeedRequests": "待处理申请",
  "devpanel.socialSeedFriends": "直接成好友",
  "devpanel.socialSeedDone": "已给 {player} 造好：{friends} 个好友、{requests} 条待处理申请，{skipped} 个候选没发成",
  "devpanel.doRoll": "照这个落点掷一次",
  "devpanel.rollHint": "走的是真实 roll：扣骰、结算、入账、幂等序号全一样，只是跳过随机。",
  "devpanel.needA0": "先在顶部选择 A0 租户",
  "devpanel.loading": "载入中…",
  "devpanel.empty": "没有匹配的玩家",
  "devpanel.noName": "（无昵称）",
  "devpanel.roleVictim": "目标",
  "devpanel.noPlayerRow": "库里还没有这个玩家（没冷启动过）。第一次掷骰会把号建出来。（{why}）",
  "devpanel.level": "关卡",
  "devpanel.totalRolls": "累计掷骰",
  "devpanel.buildings": "建筑",
  "devpanel.dailyCounters": "当日计数",
  "devpanel.recentAttacks": "最近对抗",
  "devpanel.noActiveVersion": "这个租户还没有生效的配置版本，掉落项列不出来",
  "devpanel.cellsFallback": "掉落项列表拉不到，先按 code 手输：{why}",
  "devpanel.randomCell": "随机",
  "devpanel.times": "连掷",
  "devpanel.victimNone": "没指定目标：对抗格按正常规则选人（保护期、匹配带、当日上限都算数）",
  "devpanel.victimIgnored": "这一项不是对抗玩法，指定的目标不会生效",
  "devpanel.notTheTarget": "⚠不是指定的目标",
  "devpanel.rawJson": "完整响应",
  "devpanel.reset": "拨回可测状态",
  "devpanel.resetDaily": "清当日计数",
  "devpanel.fragMigrate": "老碎片按 idol 迁移",
  "devpanel.fragMigrateHint": "把本租户所有玩家不分 idol 的拼图碎片，整笔转到各自的当前 idol（没选过的转给展示顺序第一位）。可以重复点，已经转过的不会再转。",
  "devpanel.fragMigrateDone": "共 {players} 名玩家有老碎片：转好 {moved}，失败 {failed}（失败的稍后再点一次）",
  "devpanel.resetDailyDone": "已清 {n} 行当日计数",
  "devpanel.repair": "修好建筑 / 灭火",
  "devpanel.repairDone": "修好 {n} 栋，灭火 {fires} 处",
  "devpanel.resetRhythm": "清掉落节奏",
  "devpanel.resetRhythmDone": "已清 {n} 行节奏计数",
  "devpanel.resetBuildings": "重置等级与建造进度",
  "devpanel.resetBuildingsDone": "已拨回第 1 关、建筑全 0 星（清 {n} 行建筑状态、{logs} 条过关存档）",
  "devpanel.danger": "清除 / 注销",
  "devpanel.dangerHint": "都不可撤回。清除数据：清成刚注册的新号，账号留着、不用重新登录。注销账号：账号作废，这台设备下次游客登录就是新号，旧存档不删",
  "devpanel.wipe": "清除数据",
  "devpanel.wipeDone": "已清除，共删 {n} 行；玩家下一次请求按新号建档",
  "devpanel.wipeConfirm": "把玩家 {id} 清成刚注册的新号：余额、关卡与建筑、萌宠小镇、掷骰记录、卡、idol 与拼图、宠物、成就任务、商城购买、邮件领取、好友全部清掉。账号、登录、GM 权限留着。不可撤回。",
  "devpanel.cancelAccount": "注销账号",
  "devpanel.cancelAccountDone": "已注销；这台设备下次游客登录就是新号",
  "devpanel.cancelAccountAlready": "这个号之前已经注销过",
  "devpanel.cancelAccountConfirm": "注销玩家 {id}：撤销三方授权、解除绑定、作废登录。这台设备下次游客登录会建一个新号；旧存档不删，但再也登不进来。不可撤回。",
  "devpanel.resetIdolPuzzle": "拨回 idol 拼图状态",
  "devpanel.resetIdolPuzzleDone": "已清 {stages} 条关卡图记录、{puzzles} 条写真拼图记录、{intimacy} 位 idol 的亲密度（碎片与当前 idol 不动）",
  "devpanel.resetHint": "不发资产、不改余额。清当日计数、修复和清掉落节奏各自只处理对应状态；重置等级与建造进度会回到第 1 关、建筑 0 星，清过关记录、掉落节奏、萌宠进度（含天赋和体力树记录）。资产、库存和任务记录保留。重置后重新进入游戏，刷新进度。",
  "devpanel.match": "多人对局：指定下一手落点 / 点数 / 转盘扇区",
  "devpanel.matchLoad": "查他的对局",
  "devpanel.matchRoom": "房间 {room} · 座位 {seat} · 每手 {n} 枚骰子",
  "devpanel.matchMyTurn": "现在轮到他掷",
  "devpanel.matchNotTurn": "还没轮到他",
  "devpanel.matchProgress": "当前这一枚走到第 {p} 格（线性进度）· 已到家 {done} 枚",
  "devpanel.matchDice": "下一手点数",
  "devpanel.matchCell": "下一手跳到第几格",
  "devpanel.matchCellNow": "现在在第 {c} 格",
  "devpanel.matchCellTip": "填绝对格号（和棋盘资源 board:cells 的 index 一致），服务端自动算点数；和掉落行、格子类型、点数只能选一个。最远 18 步、不能越过他的终点入口，走不到这一手就照常掷",
  "devpanel.matchWheel": "下一次转盘扇区",
  "devpanel.matchWheelNone": "不指定",
  "devpanel.matchNoWheel": "本局棋盘没有转盘",
  "devpanel.matchSpin": "下一手落到掉落行",
  "devpanel.matchSpinTip": "落到他前方最近一个引用这一行的普通格（按钮名取自本局的掉落表），鼠标停在按钮上可看到对应格号；和格号、格子类型、点数只能选一个。最远 18 步，走不到这一手就照常掷",
  "devpanel.matchNoSpin": "本局棋盘没有普通格",
  "devpanel.matchType": "下一手落到格子类型",
  "devpanel.matchTypeTip": "落到他前方最近的神灯格 / 转盘格；和格号、掉落行、点数只能选一个。最远 18 步，走不到这一手就照常掷",
  "devpanel.matchCellsOf": "格号：{cells}",
  "devpanel.matchEmptyRow": "空行",
  "devpanel.matchApply": "挂上",
  "devpanel.matchClear": "清掉",
  "devpanel.matchRigNow": "当前挂着：{rig}",
  "devpanel.matchRigNone": "当前没有挂任何强制项",
  "devpanel.matchDone": "已挂上：{rig}",
  "devpanel.matchCleared": "已清掉",
  "devpanel.matchHint": "多人是实时回合制，面板替他出不了手：挂上之后，等他下一次自己掷骰（或超时被代打）时生效，各用一次自动清掉。要走到某一格，可以填格号，也可以按掉落行（金币、道具、金库、大炮、777 等）或格子类型（神灯、转盘）选。转盘扇区在他下一次踩中含这一行的转盘时生效。GM 白名单里的玩家也能在客户端调试面板里自己挂。",
  "devtoken.search": "搜索昵称或 ID",
  "devtoken.scope": "A0 {a0} 下的真实玩家",
  "devtoken.ttl": "有效期（小时）",
  "devtoken.refresh": "刷新",
  "devtoken.sign": "生成 token",
  "devtoken.loading": "载入中…",
  "devtoken.empty": "没有匹配的玩家",
  "devtoken.noName": "（无昵称）",
  "devtoken.expires": "有效期至 {at}",
  "devtoken.copyToken": "复制 token",
  "devtoken.copyCurl": "复制 curl",
  "devtoken.copiedToken": "token 已复制",
  "devtoken.copiedCurl": "curl 已复制",
  "devtoken.copyManual": "浏览器不允许自动复制，请手动选中复制",
  "devtoken.needA0": "请先在右上角选择一个 A0 代理。",
  "devtoken.notice": "生成的是**玩家身份票**：拿着它可以以该玩家身份调用全部 C 端接口，请勿外发。仅测试环境可用，默认 2 小时过期（最长 24 小时），每次生成都会记入操作日志。",
  "trace.search": "搜索接口路径",
  "trace.count": "已开启 {on} / 共 {all} 个接口",
  "trace.clearAll": "全部关闭",
  "trace.saving": "保存中…",
  "trace.loading": "载入中…",
  "trace.saved": "已保存，{n} 个接口开启中",
  "trace.by": "由 {who} 改",
  "trace.empty": "没有匹配的接口",
  "trace.playerFilter": "玩家过滤",
  "trace.playerFilterHint": "玩家 id，逗号分隔；留空 = 推全部流量",
  "trace.noPermission": "这一页只有超级管理员可见。需要开通请找服务端。",
  "trace.notice": "勾中的接口会把每次请求与响应推到飞书联调群（含请求体、响应体与玩家标识，票已打码）。仅测试环境生效；推送在响应返回之后进行，不影响接口本身。",
  "trace.noSpec": "暂时拉不到接口清单（游戏端 spec 读取失败）。已开启的仍可在下方查看，但列不出新接口——**此时保存会被拒绝**，因为清单不完整会漏登记新接口，导致它被别的模板误推。稍后重试即可。",
  "asset.title": "上传素材 · 第 {id} 关 · {name} {star}★",
  "asset.image": "图片资源",
  "asset.imageHint": "静态外观图，PNG / JPG",
  "asset.anim": "动画资源",
  "asset.damaged": "破坏图",
  "asset.damagedHint": "被雷云/抢金库打坏时显示的外观。不传则回退到静态图（客户端至少有东西画）；修好后自动换回静态图",
  "asset.animHint": "序列帧或 GIF / WebP 预览",
  "asset.upload": "上传",
  "asset.replace": "替换",
  "asset.clear": "清除",
  "asset.uploading": "上传中…",
  "asset.empty": "点击上传（PNG / JPG / WebP / GIF）",
  "asset.emptyReadonly": "未上传",

  "asset.notice": "素材按「关卡 · 建筑 · 星级」归档（{base}）。未上传的星级运行时回退到上一星级的资源；资源缺失不阻塞数值校验，只进发布前的美术检查。",
  "reward.spinSum": "spin 合计 {v}",
  "app.loadingConfig": "正在载入配置…",
  "app.noAgent": "还没有选中代理。顶栏选一个代理后才会读取配置；列表拉不到时顶栏会提示。",
  "app.blockFailedTitle": "有配置块没读上来，相关页面会是只读的",
  "app.blockFailedNoMsg": "（服务端没给错误信息）",
  "app.blockFailedHint": "最常见的原因是数据库少跑了迁移（表或列不存在）。没修好之前这些页面点不动是预期的，不是权限问题。",
  "app.loadFailedTitle": "配置读取失败",
  "app.loadFailedBody": "没能从后端读到这个代理的配置：{err}。页面不显示任何替代数据——顶一份假的上来，你会对着不存在的配置改半天。",
  "tile.missingTitle": "这一版还没配这个格子",
  "tile.missingBody": "菜单里列出全部玩法格，是为了让人看到整体范围；这一格在当前版本里还没有配置行。在草稿里打开这一页、改任何一项就会建上，填完随版本一起发布。",
  "app.emptyVersionTitle": "这一版还没有关卡",
  "app.emptyVersionBody": "新建的版本是空的，从第一关开始填。加完关卡后即可配置建筑、造价与奖励；全局参数（骰子、对抗）在左侧「全局配置」里填，全部填完才能发布。",
  "app.addFirstLevel": "新建第一关",
  "card.col.usedBy": "解锁的 idol 卡包",
  "card.col.enabled": "启用",
  "card.on": "启用中",
  "card.off": "已禁用",
  "card.stateAll": "全部",
  "card.enable": "启用",
  "card.disable": "禁用",
  "card.hit": " · 命中 {n}",
  "card.notReferenced": "未被引用",
  "card.collectPack": "集齐 {code} · {name}",
  "card.unlockStale": "{code}（已失效）",
  "card.unlockGone": "该物品卡包已不存在",
  "card.lockedHint": "不能删除：被 idol 卡包 {refs} 用作解锁条件。先到 idol 卡页把这些卡包的解锁条件改掉或清空，再回来删除。",
  "card.editInDialog": "卡牌明细在「编辑」里增删",
  "card.footItem": "物品卡按卡包管理：卡包内每张卡有自己的编号、图片、掉落权重与稀有度星级；集齐整包发放集齐奖励。奖励配置页的物品卡候选就是这里的卡包。",
  "card.footIdol": "idol 卡按卡包管理：解锁条件是集齐指定的物品卡包（下拉来自「物品卡」页的卡包编号）。奖励配置页的 idol 卡候选就是这里的卡包。",
  "auc.title": "各关卡 · 最新一期",
  "auc.latestOnly": "只展示最新一期 · 往期看历史",
  "auc.col.period": "期数",
  "auc.col.bid": "竞拍期",
  "auc.col.show": "展示期",
  "auc.col.minInc": "最低加价",
  "auc.col.spark": "中拍火花加成",
  "auc.periodN": "第 {n} 期",
  "auc.periodTotal": "共 {n} 期",
  "auc.span": "时长 {d} 天 {h} 小时",
  "auc.spanNote": "{s}；展示期与竞拍期同长，从竞拍结束时刻起算",
  "auc.underHour": "不足 1 小时",
  "auc.badWindow": "结束时间需晚于开始时间",
  "auc.sameLength": "与竞拍期同长",
  "auc.derived": "自动推出",
  "auc.minIncNote": "当前价 +{p}",
  "auc.sparkNote": "展示期内本城 +{p}",
  "auc.hint.bid": "本期竞价的开始与结束时间。竞拍的是下一期的地标舞台展示权。",
  "auc.hint.show": "展示的是这一期：从本期竞拍结束时刻起，与竞拍期同长。由竞拍期自动推出，不单独填。",
  "auc.hint.minInc": "下一次出价至少要比当前价高出的比例。0.1 即当前价 +10%。",
  "auc.hint.spark": "中拍后展示期内，本城火花产出的额外加成比例。",
  "auc.defaults": "默认值配置",
  "auc.applyDefaults": "应用默认值",
  "auc.defaults.notice": "这里配的是新增期数时套用到全部关卡的默认值；已开启的期次不受影响。",
  "auc.addPeriod": "新增期数",
  "auc.add.notice": "将为全部 {n} 关同时开启最新一期，已有的往期数据不受影响。下面的值默认取「默认值配置」，可直接改（只影响这次新增）。",
  "auc.add.confirm": "确认，全部关卡开启新一期",
  "auc.edit.notice": "只改这一关这一期；默认值不受影响。被超价即时全额退款为固定规则。",
  "auc.editTitle": "第 {id} 关 {name} · 第 {n} 期",
  "auc.history": "往期历史",
  "auc.historyTitle": "往期历史 · 第 {id} 关 {name}",
  "auc.historyFoot": "往期记录只读，用于回溯当期的窗口与参数。",
  "auc.pastCount": "{n} 期往期",
  "auc.noPast": "暂无往期",
  "auc.noPastBody": "这一关只有最新一期，还没有往期记录。",
  "auc.refund": "被超价退款",
  "auc.refundValue": "即时全额",
  "auc.refundNote": "落拍全退，参与零损失 · 固定规则，不可配",
  "auc.foot": "竞拍的是下一期、展示的是这一期，两者时长相同；被超价即时全额退款，落拍全退。",
  "spin.col.name": "name（国际化）",
  "spin.col.weight": "权重 %",
  "spin.col.relativeWeight": "相对权重",
  "spin.hint.relativeWeight": "多人抽组按权重占启用行总权重的比例抽取。例如组行合计30、全部启用行合计90，抽中组的概率是30/90。非组行代表普通掷骰；零权重行可供棋盘落点或转盘引用，不参加抽组。不要求总和100。",
  "spin.weightSum.relative": "启用行总权重 {n}（按比例抽组）",
  "spin.col.types": "奖励类型（可多选）",
  "spin.col.rewards": "奖励内容",
  "spin.hint.coin": "填的是 B 值，不是绝对金币——实发 = B 值 × 本关 bValue。选「区间」也不是掷骰随机：引擎按本关剩余预算在 min~max 里选点，总量收敛到关卡预算。",
  "spin.hint.coinG": "填的是几倍单掷金币，不是绝对金币——实发 = 填的数 × 本关单掷金币 g。选「区间」也不是掷骰随机：引擎按本关剩余预算在 min~max 里选点，总量收敛到关卡预算。",
  "spin.hint.dice": "直接发这么多颗骰子，不做预算收敛。计入通关目标骰次的骰子预算。",
  "spin.hint.gems": "付费货币，和商城花的是同一个资产。这是免费玩家唯一的棋盘钻石口子，给得太顺等于向他承诺「常有」——产品口径是小额 5-20、必须稀有。",
  "spin.hint.puzzleFrag": "idol 拼图碎片，是一份通用余额——兑哪幅由玩家在写真馆自己选，这里不配 idol。不进金币经济池。",
  "spin.hint.itemCard": "开关型，不配数量。掉哪个包由卡牌配置决定（按玩家等级直接概率选包，再按包内权重抽卡）。玩家当日卡片满了，这一骰就不发。",
  "spin.hint.subgameGroup": "抽中这一行时，掷骰只告诉客户端抽中了哪一组，客户端在组里挑离棋子最近的一种副玩法去领。组里有哪些副玩法在副玩法页配（每种可以属于多个组）；至少四种、棋盘四条边各一种由运营保证。金币不随组变，按全部金币类副玩法的承载区间算。",
  "spin.hint.subgameGroupMatch": "多人正权重组行只用于抽组。抽中后按前进方向找最近同组玩法格，在1～3颗骰子内反推点数；超过18格、需要越过自己的终点或位置/道具状态未确认时，回到普通掷骰。棋盘格引用具体玩法行，金币和道具配置在该行。",
  "spin.groupEmpty": "这一组还没有启用的副玩法，发布会被拦——去副玩法页给副玩法勾上这一组",
  "spin.warpRetired": "穿越不再是掉落项：取消勾选或删掉这一行，改在表格下面配「穿越概率」",
  "spin.warpProb": "穿越概率",
  "spin.hint.warpProb": "每一掷单独判一次，命中时掷骰多返回两个随机数（1–100、往前走 1–6 格）让客户端走一次传送。不占上面的权重，也不影响这一掷抽到的奖励。填 0 = 不出穿越。",
  "spin.warpProbMissing": "还没填，发布会被拦",
  "spin.warpProbRange": "只能填 0 ~ 100",
  "spin.hint.sendHome": "开关型，不配数量，只有多人代理能配，必须单独一条。转盘抽中时，踩中转盘的这一枚棋子退回出场区（与被撞一样）。",
  "spin.hint.abilityItem": "开关型，不配数量，只有多人代理能配。踩中从本局道具基池里三选一，通常与金币一起配。",
  "spin.hint.free": "发 N 张免费掷骰券（用券掷骰不扣骰子、强制 ×1）。2026-08-28 已撤下，只为清存量保留，别新配。",
  "spin.kind.coin": "金币",
  "spin.kind.dice": "骰子",
  "spin.kind.shield": "盾牌",
  "spin.kind.retired": "已撤下",
  "spin.kind.subgame": "玩法",
  "spin.mode.fixed": "定值",
  "spin.mode.range": "区间",
  "spin.badRange": "min 大于 max",
  "spin.noReward": "未选奖励类型",
  "spin.kind.blank": "空白格",
  "spin.blankNote": "踩中什么都不发，只占盘面概率",
  "spin.weightSum.ok": "启用格合计 100%",
  "spin.weightSum.bad": "启用格权重合计 {n}%，必须是 100%",
  "spin.subgameNote": "数值在它的玩法格页里改，点名字过去",
  "spin.fountainTotal": "总范围 {lo} ~ {hi} 倍单掷金币",
  "spin.fountainRange": "本项区间",
  "spin.fountainHint": "每条喷泉可独立命名并设置子区间，数量不限。子区间须在喷泉总范围内，客户端按 code 区分表现。",
  "spin.fountainInvalid": "请填写总范围内的子区间，下限不能大于上限",
  "spin.hint.weight": "填百分比：10 就是 10% 的概率踩中本格。所有启用格合计必须 100（停用格不计）。引擎还会按本关预算修正，所以这不是最终概率。",
  "spin.count": "共 {n} 格",
  "spin.hint.code": "掉落项的稳定编号，前后端通讯用它。不同喷泉各用一条记录，客户端按 code 区分表现，运营可自由改名。",
  "i18n.langCol": "语言",
  "i18n.base": "默认",
  "i18n.required": "必填",
  "i18n.fallback": "留空回退默认语言",
  "i18n.fallbackTo": "{v}（回退默认语言）",
  "i18n.baseUndeletable": "默认语言不可删除",
  "i18n.removeLang": "删除 {lang}",
  "i18n.addLang": "+ 添加语言",
  "i18n.allAdded": "+ 添加语言（已全部添加）",
  "i18n.pending": "共 {n} 种语言，待回填 {m} 种：{langs}",
  "i18n.complete": "共 {n} 种语言，已全部回填",
  "i18n.editBtn": "多语言",
  "i18n.filledOf": "已填 {filled}/{total}",
  "i18n.drawerTitle": "多语言 · {name}",
  "i18n.nameField": "名称",
  "i18n.notice": "默认语言（English）发布必填；其他语言留空时客户端回退默认语言。用不到的语言可以直接删掉，删除只影响这一条配置。",
  "tile.slot777": "777 福引",
  "tile.jackpot": "大奖格",
  "tile.thunder": "雷云（大炮）",
  "tile.visit": "抢金库（贫乏神）",
  "tile.omamori": "御守",
  "spin.kind.adoptNeedTile": "先启用该玩法格",
  "spin.kind.adopt_shop": "收养馆",
  "spin.kind.adopt_grass": "草丛",
  "tile.adopt_shop": "收养馆",
  "tile.adopt_grass": "草丛",
  "nav.subgame-act_item": "活动道具格",
  "tile.act_item": "活动道具格",
  "tile.note.act_item": "活动道具格：只经副玩法组到达。给它勾上组，组行抽中后客户端可以就近挑它；挑中时有生效中的活动发当期道具（基础值 × 倍数），空窗期或满档按回退发金币 / 骰子。本格没有出现节奏，不用配保底 / 冷却。",
  "tile.actItemTitle": "活动道具发放",
  "tile.f.actValue": "基础值",
  "tile.hint.actValue": "有生效中的活动时，实发当期道具 = 基础值 × 本次倍数。发哪种道具由当期活动决定",
  "tile.f.actFallback": "空窗期回退",
  "tile.hint.actFallback": "没有生效中的活动（或满档且活动选了回退）时改发什么。选金币：填几倍单掷金币（1 表示一掷的钱），再乘掷骰倍率，右边括号是各关折合的金币；没配「每掷产出」E 时按 B 个数填。选骰子：填个数，不乘倍率。",
  "tile.actItemNote": "与 spin 表里「活动道具」那一项同口径；那一项是直接掉落，这一格是组里挑中时发。",
  "tile.overview": "玩法格总览",
  "tile.count": "共 {n} 个玩法格 · 启用 {on} 个",
  "tile.cat.eco": "ECONOMY 经济演出格",
  "tile.cat.event": "EVENT 事件对抗格",
  "tile.col.sort": "顺序",
  "tile.col.name": "玩法格",
  "tile.col.cat": "分类",
  "tile.col.range": "承载范围",
  "tile.col.anim": "演出时长",
  "tile.col.groups": "所属组",
  "tile.col.desc": "说明",
  "tile.size": "喷泉大小（按金额）",
  "tile.sizeTag": "档位要连续盖满 1.0 ~ 20.0 倍单掷金币",
  "tile.hint.size": "看到手金额是单掷金币的几倍，落在哪档就演哪座喷泉，实发金额不变。没配时客户端按落在哪个角的格子演大小。",
  "tile.sizeOff": "还没配：客户端按落在哪个角的格子演大小，可能小喷泉发大钱。",
  "tile.sizeOn": "按金额分大小（默认三档）",
  "tile.col.size": "喷泉大小",
  "tile.size.small": "小喷泉",
  "tile.size.mid": "中喷泉",
  "tile.size.large": "大喷泉",
  "tile.err.size": "喷泉大小档位",
  "tile.col.gMin": "下限（倍单掷金币）",
  "tile.col.gMax": "上限（倍单掷金币）",
  "tile.col.combo": "转轮图案",
  "tile.col.animLevel": "动画等级",
  "tile.combo.cherry2": "樱桃 ×2",
  "tile.combo.cherry3": "樱桃 ×3",
  "tile.combo.bar1": "BAR",
  "tile.combo.bar2": "双 BAR",
  "tile.combo.jackpot777": "777 大奖",
  "tile.col.hit": "hit_count 命中",
  "tile.col.miss": "miss_count 落空",
  "tile.col.weight": "weight 权重 %",
  "tile.hint.range": "填的是倍数，不是金币数。单掷金币 = 这一关掷一次平均能拿的金币（课程里叫 g，等于 E × B）。例：填 3 ~ 20，某关单掷金币是 1,000，就发 3,000 ~ 20,000 金币。关卡越高单掷金币越多，一份配置所有关卡通用。出现几率看掉落表权重，和这里无关。",
  "tile.off": "未启用",
  "tile.offFallback": "未启用 · 退化为普通金币格",
  "tile.enter": "进入配置",
  "tile.coverOk": "发布校验通过：启用中的金币类玩法，承载范围连起来盖满了 {lo} ~ {hi} 倍单掷金币。",
  "tile.coverBad": "发布校验未通过：{from} ~ {to} 倍单掷金币这一段，没有启用中的金币类玩法承载，金额落到这里就没有格子可演。所有金币类玩法的范围连起来要盖满 {lo} ~ {hi} 倍。",
  "tile.foot1": "金额 X 由预算引擎每步反推，配置页没有「给多少钱」字段：玩法格只配 ① 承载范围 / 出现节奏，② 拿到 X 之后怎么演。",
  "tile.foot2": "未启用的玩法格在棋盘上自动退化为普通金币格。",
  "tile.basic": "基础信息",
  "tile.carry": "承载范围",
  "tile.fountainCarry": "喷泉总范围",
  "tile.fountainNote": "小、中、大等喷泉在 spin 奖励配置里逐条新增，每条配自己的子区间。调整总范围时，可同时调整各 spin 子区间；越界配置不能发布。",
  "tile.note.coinrain": "喷泉总范围在本页配置；各 spin 项独立命名并填写范围内的子区间，客户端按 code 区分表现。",
  "tile.pieces": "个",
  "tile.derived": "个 · 自动派生",
  "tile.f.id": "玩法格 ID",
  "tile.f.skin": "皮肤",
  "tile.f.enabled": "启用",
  "tile.f.minClient": "最低客户端版本",
  "tile.f.groups": "所属副玩法组",
  "tile.hint.groups": "掉落表行引用 A–E 组，每种副玩法可属于多个组。单人由客户端就近选；多人棋盘格引用具体副玩法行，服务端抽中组后按前进方向找最近的同组格。每组的成员要分布在棋盘各边，避免最近格仍然很远。",
  "tile.groups.none": "不属于任何组",
  "tile.group": "{g} 组",
  "tile.minClient.ph": "留空 = 不限",
  "tile.f.carryMin": "承载下限",
  "tile.f.carryMax": "承载上限",
  "tile.unit.g": "倍单掷金币",
  "tile.gSavedAs": "实际按 {x} 倍保存（最小 1 个 B）",
  "tile.rangeG": "{lo} ~ {hi} 倍单掷金币",
  "tile.f.boxTotal": "宝箱总数",
  "tile.f.pickCount": "可抢数",
  "tile.hint.id": "玩法格唯一标识（tile_type_id），前后端通讯用它，建立后不可修改。",
  "tile.hint.skin": "皮肤 ID（skin_id）：神明主题皮肤，活动可换。数值与美术分离：皮肤只换图标 / 动画 / 音效，数值一个不动。",
  "tile.hint.enabled": "停用（enabled=FALSE）后该格在棋盘上退化为普通金币格，预算包络不变。",
  "tile.hint.minClient": "min_client_version：低于这个版本的客户端不认识这个格子——服务端抽格时把引用它的 spin 格剔除、权重按剩下的归一，老客户端永远抽不到，不会报错。留空 = 所有版本都支持。格式 x.y.z，客户端请求头 X-Client-Version 里带。",
  "tile.hint.carry": "填的是倍数，不是金币数。单掷金币 = 这一关掷一次平均能拿的金币（课程里叫 g，等于 E × B）。例：下限 3、上限 20，某关单掷金币是 1,000，就在 3,000 ~ 20,000 金币之间发：这一关拿少了往高取，拿多了往低取；掷骰倍率另乘。右边括号是各关折合的金币。",
  "tile.hint.pickCount": "pick_count：定死 = 宝箱总数 - 1，自动派生，只读。",
  "tile.carryNote": "金额是单掷金币的 {lo} ~ {hi} 倍时，由本格来演。所有金币类玩法的范围连起来要盖满 {cl} ~ {ch} 倍，不能有缝。",
  "tile.reel": "reel_map 图案对照表",
  "tile.reelTag": "档位要连续盖满 1.0 ~ 20.0 倍单掷金币",
  "tile.tier": "tier_map 庆祝动画分级",
  "tile.tierTag": "档位要连续盖满 5.0 ~ 20.0 倍单掷金币",
  "tile.mapGap": "档位区间有缝隙 / 重叠",
  "tile.mapUnknown": "{v}（客户端不认识，会演成 cherry2）",
  "tile.mapCover": "档位没盖住 {from} ~ {to} 倍单掷金币",
  "tile.missing": "还差：{fields}——已先存着，发布前要填完",
  "tile.err.carryOrder": "承载下限要小于上限",
  "tile.err.reel": "转轮档位",
  "tile.err.tier": "动画档位",
  "tile.err.schemes": "开箱组合",
  "tile.hint.reel": "金额档位 → 转轮图案。看金额是单掷金币的几倍，落在哪档就演哪套图案，实发金额不变。combo_id 只能选客户端已有的五套图案，要新图案得先让客户端加。",
  "tile.hint.tier": "钱不分档，动画分档：看金额是单掷金币的几倍，选对应的庆祝动画，大钱要有大排面。",
  "tile.addTier": "新增档位",
  "tile.sumEqX": "命中箱金额之和恒等于 X",
  "tile.boxes": "宝箱",
  "tile.rules": "规则说明",
  "tile.schemes": "display_schemes 显示方案池",
  "tile.hint.schemes": "播哪个方案按权重抽；无论哪个方案，命中箱金额之和恒等于 X。命中 / 落空按点击顺序套用预生成结果包，与玩家选哪个箱子无关。",
  "tile.weightSum": "Σweight = {n}%",
  "tile.weightMust": " · 须为 100",
  "tile.schemeBad": "hit + miss 须 = {n} 且 hit ≥ 1",
  "tile.schemeOk": "命中 {n} 箱均分 X",
  "tile.addScheme": "新增方案",
  "tile.note.slot777": "图案只是金额的皮肤：看金额是单掷金币的几倍，落在哪档就演哪套图案，实发金额不变。",
  "tile.note.jackpot": "单次全额发放，无拆分。钱不分档、动画分档。",
  "tile.note.coin": "直接飘字发放，无子演出；承载下限 0.3 即全盘承载并集的下沿，务必保留。",
  "tile.note.thunder": "踩中后攻击别人：没被御守挡下就按打中概率判定，打中损对方最高一栋楼一星，对方修楼付这一星造价的一部分。攻方拿多少和对方掉多少分开算：攻方按对手余额的比例拿，夹在最少和最多之间；对方只掉自己余额的比例，扣不光。被御守挡下时攻方拿一笔安慰，不超过打中能拿的。数值在本页「对抗数值」区块配置。",
  "tile.note.visit": "攻方拿多少和对方掉多少分开算：攻方按对手余额的比例拿，夹在最少和最多之间；对方只掉自己余额的比例，扣不光。偷取不能被御守挡（御守只防雷云）。开箱按「显示方案池」抽一个方案演，命中箱金额之和就是攻方拿的。",
  "tile.note.adopt_shop": "收养馆是 R/SR（第2～3档）的主要出处，20次宠物品质保底只在这里计数。出现按掉落权重抽取；宠物种类由领养池决定。掉落项需配置本格、宠物(收养馆)和建材。",
  "tile.note.adopt_grass": "领养点（量）：N（第 1 档）为主、偶有 R（第 2 档）惊喜，不吃保底也不推进保底计数。其余同收养馆——本格只提供节奏，掉落项里必须同时配：本格 + 宠物(草丛) + 建材。",
  "tile.note.omamori": "御守仅防御雷云（雷神）攻击、挡下全部损坏；不防探班（贫乏神偷取）；持有上限在本页「对抗数值」区块配置；idol 回赠演出。",
  "tile.note.genericEconomy": "金额格：金额 X 由预算引擎按承载区间选点后全额发放，本页只配承载范围与演出参数；这个格子没有专门的规则说明，出现节奏由引擎调度。",
  "tile.note.genericEvent": "事件类玩法不计入金币期望，出现按掉落权重独立抽取。对抗数值（如有）在「对抗与控骰」页配置。",
  "tile.note.material": "首版默认不启用（enabled=FALSE，升星纯用应援资金）；预留给以后加材料系统：启用后材料实体经 extra_drop_channel_id=ch_material 掉落渠道发放，与金币预算解耦。",
  "flt.reset": "重置",
  "flt.range": "关卡范围",
  "flt.from": "起",
  "flt.to": "止",
  "flt.city": "城市名",
  "flt.cityPlaceholder": "关卡序号或城市名",
  "flt.filtered": "筛出 {n} / {total} 关",
  "flt.all": "显示全部 {n} 关",
  "p.grp.fight": "对抗与控骰",
  "p.grp.newbie": "新手保护",
  "p.foot.dice": "骰子参数只决定推进节奏与钻石缺口，不参与造价计算。",
  "p.E": "算法每骰产出 E",
  "p.E.hint": "玩家每掷一次骰子的期望金币产出（无量纲）。它把「目标骰次」换算成金币：目标造价 C = 目标骰次 × E × B值 × ρ。",
  "p.rho": "算法消耗系数 ρ",
  "p.rho.hint": "玩家产出的金币里，最终投到建筑升级上的比例系数——其余被存余、抢夺等消耗占去。不做取值限制。",
  "p.cap": "骰次上限",
  "p.cap.hint": "自然恢复的存量上限：存满后停止恢复，不影响购买或奖励发放获得的骰子。",
  "p.cap.note": "空仓存满约 {h} 小时",
  "p.capBase": "体力上限（基础值）",
  "p.capBase.hint": "萌宠小镇：收体力树时最多收到这个数，多的留在树上；天赋树「体力上限」在它上面加。购买与奖励发放获得的骰子不受它限制。",
  "p.capBase.note": "萌宠小镇下骰子不自动恢复：体力只从副建筑「体力树」领取（生长间隔、容量、棵数在「副建筑槽位」配）",
  "p.capStarStep": "每完成 1 星增加上限",
  "progress.multiplier": "倍率解锁",
  "issue.packStarDrops": "卡包 {id} · 按星掉落概率",
  "issue.packStarTotal": "第 {star} 星 · 各物品卡包概率合计",
  "p.capStep": "每过 1 关增加上限",
  "p.capStep.hint": "每通过 1 关，骰次上限增加的个数。默认 0，即全程共用同一上限。",
  "p.capStep.note": "第 50 关时上限 {v} 个",
  "p.capStep.zero": "默认 0：全程同一上限",
  "p.regen": "自动恢复时间",
  "p.regen.hint": "骰子的自然恢复速度。它决定玩家不付费时一天能推进多少关，是数值测试里「需补充骰次」的基准。",
  "p.regen.note": "整天自然恢复 {n} 个",
  "p.thunderPct": "雷神掉钱比例",
  "p.thunderPct.hint": "被雷神劈中时，从目标玩家余额里掠走的比例（定案④「掉钱不掉星」）。攻击方拿到的就是被打方实际损失的数，没有系统补贴。填 0 = 只劈楼不掉钱。",
  "p.thunderCap": "雷神掉钱上限",
  "p.thunderCap.hint": "雷神一次最多掠走多少。**填 0 等于关闭掉钱**，比例填了也不生效。填几倍单掷金币。单掷金币 = 这一关掷一次平均能拿的金币，1 表示一掷的钱。右边括号是各关折合的金币。没配「每掷产出」E 时按 B 个数填。",
  "p.thunderFloor": "雷神掉钱保底",
  "p.thunderFloor.hint": "雷神一次至少掠走多少；匹配不到真人时也按这个数发。填 0 = 不设下限。填几倍单掷金币。单掷金币 = 这一关掷一次平均能拿的金币，1 表示一掷的钱。右边括号是各关折合的金币。没配「每掷产出」E 时按 B 个数填。",
  "p.stealPct": "贫乏神偷取比例",
  "p.stealPct.hint": "被贫乏神踩中时，从目标玩家余额里偷走的比例。",
  "p.stealCap": "单次偷取上限",
  "p.stealCap.hint": "一次最多偷走多少，免得余额高的玩家被一次偷空。填几倍单掷金币。单掷金币 = 这一关掷一次平均能拿的金币，1 表示一掷的钱。右边括号是各关折合的金币。没配「每掷产出」E 时按 B 个数填。",
  "p.wardCap": "御守上限",
  "p.wardCap.hint": "玩家可同时持有的御守（防偷）数量上限。",
  "p.clawbackDays": "福神记录保留",
  "p.clawbackDays.hint": "福神可讨回的历史被偷记录保留时长。",
  "p.clawbackBonus": "福神收益加成",
  "p.clawbackBonus.hint": "讨回时在原被偷金额上的额外加成比例。",
  "p.repairPct": "雷神修缮费",
  "p.repairPct.hint": "建筑被损后修复的费用比例。",
  "p.visitCap": "每日被访上限",
  "p.visitCap.hint": "同一玩家每天最多被别人拜访/攻击的次数。",
  "p.matchRange": "匹配关卡范围",
  "p.matchRange.hint": "匹配对手的关卡区间：优先高余额、24h 内未被访满的活跃玩家；离线也可被触发。",
  "p.matchRange.note": "匹配关卡差 {n} 以内的玩家",
  "u.count": "个",
  "u.countPerLevel": "个 / 关",
  "u.minPerCount": "分钟 / 个",
  "u.B": "B",
  "bcoins.one": "（{v} 金币）",
  "bcoins.range": "（{v} 金币）",
  "bcoins.oneRolls": "（≈ {r} 掷的钱，{v} 金币）",
  "bcoins.rangeRolls": "（≈ {r} 掷的钱，{v} 金币）",
  "bcoins.head": "{q} B 在各关折合金币（按运行时实际的 B）",
  "bcoins.level": "第 {n} 关 {name}",
  "bcoins.star": "第 {n} 星",
  "bcoins.stars": "第 {a}–{b} 星",
  "u.day": "天",
  "u.times": "次",
  "u.person": "人",
  "u.plusMinusLevel": "± 关",

  // 版本生命周期（版本条 / 历史 / 对比 / 保存 / 发布 / 回滚）
  "ver.online": "线上生效：{id}",
  "initcfg.collapse": "选好了，收起",
  "initcfg.collapseTip": "选择已经自动保存；收起选项防止误点，要改再点「修改」",
  "initcfg.copyTitle": "以已有代理为底稿",
  "initcfg.copyBody": "把另一个代理现在生效的那一版整版复制过来，作为这个代理的第一版草稿。只复制配置，不带玩家与运营数据（商城、idol、分享链接）。",
  "initcfg.copyPick": "选一个代理…",
  "initcfg.copyGo": "复制它的生效版本",
  "initcfg.copyOr": "或者直接在下面选，从空白开始",
  "initcfg.copiedDropped": "已复制 a0={from} 的 v{v}。idol 名册按代理独立、不跟着复制，所以去掉了 {puzzles} 张拼图、{packs} 个卡包的所属 idol、{pets} 个角色的本命 idol",
  "initcfg.copied": "已复制 a0={from} 的 v{v}，作为本代理的第一版草稿",
  "initcfg.edit": "修改初始化配置",
  "initcfg.fixed": "已确定",
  "ver.workspace": "配置版本",
  "ver.editingLabel": "正在编辑",
  "ver.viewingLabel": "正在查看",
  "ver.more": "更多",
  "ver.draftHint": "改动会自动保存；发布后才对玩家生效。",
  "ver.staleDraftTitle": "这是旧草稿。",
  "ver.staleDraftBody": "{draft} 早于当前线上 {online}。发布整版配置会覆盖线上新增内容；继续前请先与 {online} 对比，确认保留哪些改动。",
  "ver.autoSaving": "保存中…",
  "ver.pendingN": "还差 {n} 项才能发布",
  "ver.pendingWarns": "另有 {n} 条提示",
  "ver.pendingOk": "可以发布",
  "ver.pendingTip": "点开看还差哪些，点行跳过去改",
  "ver.pendingOkTip": "配置检查没有问题，可以发布",
  "ver.autoSaved": "已保存 {at} · 作者：{who}",
  "ver.autoRetrying": "保存失败，正在重试：{msg}",
  "ver.autoFailed": "保存失败：{msg}。改正后会自动再存",
  "ver.autoConflict": "别人刚改过这一版，载入最新后再继续",
  "ver.autoConflictBy": "{who} 在 {at} 改过这一版，载入最新后再继续",
  "ver.autoReload": "载入最新",
  "ver.autoReconcileFail": "上一笔保存结果未知，正在读回对账",
  "collab.filling": "正在填 {label}",
  "collab.viewing": "在看",
  "collab.merged": "刚存了 {what}，已合进来",
  "collab.online": "在线 {n}",
  "collab.jumpTo": "跳到 {who} 那里",
  "collab.jumpHint": "点头像跳到他那里",
  "collab.follow": "跟随",
  "collab.unfollow": "停止跟随",
  "collab.following": "正在跟随 {who}：{where}",
  "collab.stop": "停止（Esc）",
  "collab.away": "离开（页面在后台）",
  "collab.offline": "已离线",
  "collab.hideCursors": "隐藏别人的鼠标",
  "collab.showCursors": "显示别人的鼠标",
  "collab.onlyYou": "只有你在线",
  "collab.party.highFive": "击掌！",
  "collab.party.bonk": "咚！",
  "collab.party.group": "{n} 人集合！",
  "collab.inAgent": "代理 {agent}",
  "collab.idle": "空闲 {n} 分钟",
  "collab.here": "{names} 在这一页",
  "collab.conflicts": "你和 {who} 都改了 {where}，以你的为准（修改记录里两笔都在）",
  "ver.autoReadBackPartial": "已保存，但这几块读回失败，页面上显示的是你刚存的内容：{blocks}",
  "ver.autoBusyAdd": "正在保存，存完再加关",
  "ver.autoPending": "还有改动没存上，存完才能检查或发布",
  "audit.config.autosave": "自动保存",
  "ver.readonlyHint": "当前版本只读。修改配置请先新建草稿。",
  "ver.switch": "切换版本",
  "ver.history": "版本历史",
  "ver.by": "由 {who} 建于 {at}",
  "ver.versionLog": "修改记录",
  "ver.multiDraft": "{n} 个草稿",
  "ver.pruneDrafts": "只留最新草稿",
  "ver.watchCourse": "看视频：配置版本怎么用",
  "ver.draftReused": "已有草稿 {id}（{who} 建于 {at}），你的改动写进了它",
  "ver.pruneDone": "清掉了 {n} 个多余草稿，保留 {id}",
  "ver.pruneNone": "没有多余草稿",
  "ver.prunePartial": "清掉 {n} 个，{m} 个没清掉：{why}",
  "ver.pruneConfirm": "除了最新的 {id}，其它草稿都会被删掉。已发布的版本不受影响。",
  "ver.versionLogTitle": "{id} 的修改记录",
  "vlog.f.name": "名称",
  "vlog.f.stage": "阶段",
  "vlog.f.i18n": "多语言名称",
  "vlog.f.B": "基准值 B",
  "vlog.f.actualC": "实际总造价",
  "vlog.f.buildings": "建筑",
  "vlog.f.bldInit": "各栋 1 星金额",
  "vlog.f.star": "星级倍率",
  "vlog.f.sys": "逐格造价",
  "vlog.f.assets": "逐格素材",
  "vlog.f.levelAssets": "关卡资源图",
  "vlog.f.weight": "权重",
  "vlog.f.enabled": "启用",
  "vlog.f.sort": "排序",
  "vlog.f.icon": "图标",
  "vlog.f.img": "图片",
  "vlog.f.items": "内容",
  "vlog.f.rewards": "奖励",
  "vlog.f.kind": "种类",
  "vlog.f.value": "数值",
  "vlog.addRow": "新增这一行",
  "vlog.removeRow": "删除这一行",
  "vlog.wholeRow": "整行",
  "vlog.empty": "空",
  "vlog.unset": "不再配置",
  "vlog.notSet": "未配置",
  "vlog.sameRest": "其余 {n} 项没变",
  "vlog.yes": "是",
  "vlog.no": "否",
  "vlog.count": "改了 {n} 处 · {saves} 条记录",
  "vlog.andMore": " 等 {n} 处",
  "vlog.where": "位置",
  "vlog.field": "字段",
  "vlog.before": "改前",
  "vlog.after": "改后",
  "vlog.loadFail": "修改记录没取到，稍后再开",
  "vlog.syncSource": "从代理 {a0} 的 v{version} 同步配置",
  "vlog.syncSummary": "同步配置",
  "vlog.writeSummary": "保存配置",
  "vlog.noFieldDetails": "这条记录未保存修改前后的值，无法还原字段明细。",
  "vlog.summaryCount": "{n} 条内容摘要 · {saves} 次记录",
  "vlog.noneLoaded": "已加载的日志中没有配置修改记录，可继续查看更早的记录。",
  "vlog.more": "更早的记录",
  "vlog.footer": "只列配置内容的改动；建版本、发布等操作在完整日志里。",
  "vlog.openFull": "看完整日志",
  "vlog.onlyWho": "只看：",
  "ver.people.created": "{who} 建",
  "ver.people.edited": "改过 {list}",
  "ver.people.more": " 等 {n} 人",
  "ver.people.published": "{who} 发布 {at}",
  "ver.pickFilter": "按人或备注筛，例如 sion",
  "ver.pickMine": "只看我参与的",
  "ver.pickNone": "没有符合的版本",
  "ver.noNote": "（未写备注）",
  "ver.h.people": "人",
  "ver.pubNote": "备注",
  "ver.pubNoteAuto": "按修改记录自动生成，可改",
  "vlog.scopeOne": "这一版",
  "vlog.scopeAll": "全部版本",
  "vlog.titleAll": "修改记录 · 全部版本",
  "vlog.titleWho": "{who} 的{title}",
  "vlog.auditWho": "在操作日志里看 {who} →",
  "vlog.autoNote": "{who} 改了 {n} 处：{items}{more}",
  "vlog.autoNoteMore": " 等",
  "ver.diffOnline": "与线上对比",
  "ver.publish": "发布版本",
  "ver.delete": "删除草稿",
  "ver.rowDelete": "删除该草稿",
  "upload.badType": "只支持 {kinds}，选中的是 {type}",
  "upload.unknownType": "未知类型",
  "upload.empty": "空文件",
  "upload.tooBig": "图片不能超过 {max}，选中的有 {size}MB",
  "upload.badName": "文件名「{name}」不合法：只能字母数字开头，由字母数字与 . _ - 组成",
  "upload.badExt": "文件「{name}」的后缀不受理，只收 {exts}",
  "level.move.drag": "拖到别的位置，改这一关的编号",
  "level.move.hint": "按住编号左边的 ⠿ 拖到别的位置，可以改关卡编号：整关（名称、图、造价、奖励、竞拍配置）跟着走，中间各关依次顺移。只改草稿，发布后生效。",
  "level.move.title": "把第 {from} 关移到第 {to} 关",
  "level.move.desc": "第 {lo}~{hi} 关的编号会变，下表是每个位置换上来的关。整关跟着走：名称、图、造价、奖励、竞拍配置；竞品价是竞品自己第几关的价，留在原位置。按曲线算的造价和目标骰次会按新位置重新算；沿用前面关的图会先固定成这一关自己的图，搬完看起来不变。",
  "level.move.col.pos": "新编号",
  "level.move.col.src": "换上来的关",
  "level.move.col.players": "现在这个编号上的玩家",
  "level.move.srcText": "原第 {n} 关 {name}",
  "level.move.shape": "栋数×星级 {a} → {b}",
  "level.move.playersErr": "读取失败",
  "level.move.playersNote": "玩家只记「在第几关」和这一关的建筑进度。发布后，站在这些编号上的玩家会直接到换上来的那一关，建筑进度原样套上去；栋数或星级变了的，超出部分升级会报错，玩家等级也会变。发布检查里还会再提醒一次。",
  "level.move.auction": "涉及的关里配了竞拍。竞拍的出价、结果和展示记录按关卡编号存，不会跟着搬：发布后已有的记录会对到别的关上。",
  "level.move.confirm": "确认移动",
  "level.move.busy": "移动中…",
  "level.move.unfixable": "这几张图搬之前是空的，搬完会沿用前面关的图，没法固定成空：{list}。需要的话在「关卡资源」、封面图或建造底图上传一张。",
  "level.move.unfixableItem": "第 {n} 关的{field}",
  "level.move.reloadFail": "已经移动好了，但读回最新草稿失败。请刷新页面后再继续改，否则改动可能落到别的关上",
  "level.move.waitSave": "还有改动没保存完，等右上角显示已保存后再拖动",
  "level.del.title": "删除第 {n} 关 {name}",
  "level.del.desc": "整关删掉：名称、图、造价、建筑、奖励、竞拍配置。第 {next}~{total} 关的编号各减 1，往前补上。沿用前面关的图会先固定成各关自己的图，删完看起来不变。只改草稿，发布后生效。",
  "level.del.descLast": "删掉最后一关（第 {n} 关）：名称、图、造价、建筑、奖励、竞拍配置一起删。只改草稿，发布后生效。",
  "level.del.players": "现在在这一关的玩家 {here} 人，在后面各关的 {after} 人。玩家只记「在第几关」，发布后他们会落到前移上来的那一关，建筑进度原样套上去。",
  "level.del.playersLast": "现在在这一关的玩家 {here} 人。发布后他们停在一个不存在的关上，先确认没人在这一关再删。",
  "level.del.busy": "删除中…",
  "level.del.reloadFail": "已经删除好了，但读回最新草稿失败。请刷新页面后再继续改，否则改动可能落到别的关上",
  "level.del.waitSave": "还有改动没保存完，等右上角显示已保存后再删除",
  "lvAsset.btn": "关卡资源",
  "lvAsset.title": "关卡资源 · 第 {n} 关",
  "lvAsset.notice": "未配置的槽位沿继承链向前取图，随资源包下发给客户端。",
  "lvAsset.slot.bg": "棋盘背景",
  "lvAsset.slot.blank": "棋盘空白格",
  "lvAsset.slot.mid": "中间建筑",
  "lvAsset.own": "本关上传",
  "lvAsset.inherited": "继承自第 {n} 关",
  "lvAsset.unset": "未配置",
  "lvAsset.upload": "上传 / 替换",
  "lvAsset.uploading": "上传中…",
  "lvAsset.inheritFrom": "继承自",
  "lvAsset.noPrev": "第 1 关没有上一关",
  "lvAsset.levelN": "第 {n} 关",
  "lvAsset.clearOwn": "清除本关上传",
  "ver.working": "处理中…",
  "ver.mergeConflictTitle": "发布前合并线上改动时有冲突",
  "ver.mergeKeepDraft": "保留草稿的值并发布",
  "ver.mergeKeepNote": "选「保留草稿的值」后，冲突的这几处按草稿发布，{online} 在这几处的改动不会进来；其余不冲突的改动照常合入。要用 {online} 的值，就关掉这个窗口，在草稿里改成和它一样再发布。",
  "ver.pubMerge": "这份草稿建的时候线上是 {base}，现在是 {online}。发布时会先把 {online} 相对 {base} 的改动合进草稿再发布；两边改了同一处的话不会发布，会列出是哪几处。下面的对比还没算这次合并，{online} 新加的改动在这里会显示成被改回去，发布后不会。",
  "ver.actBehind": "{id} 建的时候线上是 {base}，{online} 是之后才换上的，它的改动不在 {id} 里。设为生效会把这些改动退回去。不是有意回滚的话，请从 {online} 新建草稿，把要改的重新改一遍再发布。",
  "ver.mergedOnPublish": "发布前已把 {online} 的 {n} 处改动合进 {id}（草稿建的时候线上是 {base}）",
  "ver.pubStale": "⚠ 这一版基于 {base}，而线上是 {online}——发布并激活后，{base} 之后所有版本的改动都会被这一版覆盖掉。确认这就是你要的再继续。",
  "ver.driftBody": "线上生效版本已经从 {was} 变成 {now}（别人刚发布并激活）。你这一页的数据还是刷新前的，与线上对比、回滚、基于此新建都会按旧值判断。",
  "ver.driftBodyNone": "线上生效版本（原 {was}）已被取消激活。现在没有任何版本在线上生效。",
  "ver.driftGo": "切到 {id}",
  "ver.driftDismiss": "我知道了",
  "ver.staleBaseTitle": "这一版比生效版本旧",
  "ver.staleBaseBody": "你正基于 {base} 新建草稿，而当前生效的是 {online}。新草稿从 {base} 的内容开始；发布时会先把 {online} 在那之后的改动合进来，两边改了同一处会列出来让你选。",
  "ver.staleBaseHint": "中间隔着 {n} 个版本。要拿旧内容覆盖线上，请走「历史版本 → 回滚」，那条路会把回滚意图记进版本档案。",
  "ver.staleBaseUseOnline": "改为基于 {id} 新建",
  "ver.staleBaseGoOn": "仍然基于旧版本新建",
  "ver.delConfirmTitle": "删除草稿 {id}？",
  "ver.delConfirmBody": "这份草稿会移进回收站，7 天内可以恢复，之后内容被清理。线上生效的版本不受影响。草稿里有别人的改动时不能删除。",
  "ver.deleted": "草稿 {id} 已移进回收站，7 天内可在「更多 › 回收站」恢复",
  "ver.trash": "回收站",
  "ver.trashHint": "7 天内删掉的草稿都在这里，恢复后变回草稿、内容原样。已经有别的草稿也能恢复。",
  "ver.trashEmpty": "回收站是空的",
  "ver.trashBy": "{who} 删于 {at}",
  "ver.trashEditors": "改过的人：{list}",
  "ver.trashEditor": "{who} {n} 次",
  "ver.trashNoEdits": "没有人改过内容",
  "ver.trashPurgeAt": "{at} 后清理，不能再恢复",
  "ver.restore": "恢复",
  "ver.restored": "草稿 {id} 已恢复",
  "ver.publishedBanner": "此版本已发布，不可修改。所有输入项为只读。",
  "ver.newDraftFrom": "基于此新建草稿",
  "ver.rollbackFrom": "回滚自 {id}",
  "ver.draftCreated": "已创建新草稿 {id}",
  "ver.h.id": "版本号",
  "ver.h.status": "状态",
  "ver.h.time": "时间",
  "ver.h.note": "备注",
  "ver.h.from": "来源",
  "ver.h.actions": "操作",
  "ver.h.editing": "编辑中",
  "ver.h.viewing": "查看中",
  "ver.h.noNote": "（未填写）",
  "ver.h.view": "查看",
  "ver.h.compare": "与线上对比",
  "ver.h.rollback": "回滚到此版本",
  "ver.diff.pair": "版本对比",
  "ver.diff.none": "两个版本内容完全一致，没有差异。",
  "ver.diff.configTitle": "全局与业务配置",
  "ver.diff.fullSummary": "{sections} 个配置块 / {fields} 个字段变化 · {levels} 个关卡 / {cells} 项关卡变化",
  "ver.diff.section.dice": "骰子参数",
  "ver.diff.section.fight": "对抗与控骰",
  "ver.diff.section.rewards": "通关奖励",
  "ver.diff.section.itemPacks": "物品卡包",
  "ver.diff.section.idolPacks": "idol 卡包",
  "ver.diff.section.auctions": "竞拍配置",
  "ver.diff.section.tiles": "副玩法格",
  "ver.diff.section.adoptPools": "领养池",
  "ver.diff.section.spins": "spin 奖励",
  "ver.diff.section.puzzles": "拼图列表",
  "ver.diff.section.puzzlePrices": "拼图价目表",
  "ver.diff.section.dailyRewards": "每日奖励",
  "ver.diff.section.socialChannels": "加入我们",
  "ver.diff.section.socialEventTypes": "时间线事件",
  "ver.diff.section.frames": "头像框",
  "ver.diff.section.matchModes": "匹配与房间",
  "ver.diff.section.battleStages": "战阶",
  "ver.diff.section.abilityCards": "道具卡",
  "ver.diff.level": "第 {id} 关 · {name}",
  "ver.diff.changes": "{n} 处变化",
  "ver.diff.jump": "跳转到该关 →",
  "ver.diff.item": "项目",
  "ver.diff.derived": "（自动计算）",
  "ver.diff.cell": "建筑 {b} · {l}★ 实际造价",
  "ver.diff.total": "实际造价合计",
  "ver.diff.added": "新增关卡",
  "ver.diff.removed": "整关被移除",
  "ver.f.name": "城市名（英文）",
  "ver.f.stage": "主建筑（英文）",
  "ver.f.B": "B 值",
  "ver.f.targetRolls": "目标骰次",
  "ver.f.rollsRange": "骰次浮动区间(%)",
  "ver.f.actualC": "实际总造价",
  "ver.f.N": "建筑数 N",
  "ver.f.M": "星级数 M",
  "ver.f.bldInit": "建筑初始值",
  "ver.f.star": "星形状",
  "ver.pubTitle": "发布确认",
  "ver.pubBlocked": "有 {n} 关未通过校验，发布被阻止：{list}。",
  "ver.pubViewBad": "查看未通过的 {n} 关",
  "ver.pubConfirm": "确认发布",
  "ver.pubWarn": "将把 {id} 设为生效版本，当前生效的 {online} 成为历史。发布后立即影响线上玩家。",
  "ver.pubCount": "全量校验：{ok} / {total} 关通过",
  "ver.pubDiffTitle": "这次发布改了什么（和线上 {online} 比）",
  "ver.pubDiffFail": "线上 {online} 的配置没拉到，列不出改动。关掉重开试一次；也可以先用页头「对比线上」看。",
  "ver.checkTitle": "发布校验",
  "ver.checkPassTag": "{n} 关全部通过",
  "ver.checkFailTag": "{n} 关未通过",
  "ver.checkPassBody": "全部 {n} 关校验通过，可以发布。",
  "ver.checkFailBody": "以下 {n} 关未通过校验，发布被阻止。点击关卡可直接跳转修改。",
  "ver.checkStats": "目标造价 C {c} · 系统总造价 {total}",
  "ver.checkGroupN": "{n} 项",
  "ver.checkOther": "其他",
  "ver.checkJump": "跳转 →",
  "ver.levelShort": "第 {id} 关",
  "ver.levelEtc": "等",
  "ver.rbTitle": "回滚到 {id} · 差异预览",
  "ver.rbConfirm": "确认，以该版本内容创建新草稿",
  "ver.rbNote": "回滚不会修改历史版本，也不会直接把历史版本设为生效；确认后将以 {id} 的内容创建一份新草稿。",
  "ver.rbResultTitle": "已创建草稿 {id}（回滚自 {src}）",
  "ver.rbKeep": "继续编辑",
  "ver.rbFix": "去修第 {id} 关",
  "ver.rbPub": "发布该草稿",
  "ver.rbCheck": "全量校验完成：{ok} / {total} 关通过。",
  "ver.rbBad": "未通过：{list}，修完才能发布。",
  "ver.rbOk": "可以发布。",
  "ver.rbUntouched": "历史版本 {src} 未被修改，线上生效版本也未变化。",
  "ver.switchDirtyTitle": "有未保存改动",
  "ver.switchDirtyBody": "当前草稿还有刚改的内容没存上，切换到 {id} 会丢掉它们。等右上角显示「已保存」再切，或确认放弃。",
  "ver.switchDiscard": "放弃改动并切换",
  "ver.saveFirst": "还有改动没存上，存完再发布",
  "ver.syncFail": "同步失败：{msg}",
  "ver.saveFailedHere": "上次保存卡在这一页：",
  "common.dismiss": "知道了",
  "ver.snapshotLoadFail": "读取版本 {id} 的完整快照失败",
  // 只说「不可达」：代理没有配置是另一件事，由 unconfigured 空状态单独承担。
  // 两者的处置相反——前者该重试，后者该去建第一版。
  "ver.unconfiguredTitle": "该代理还没有配置",
  "ver.unconfiguredBody": "配置属于各个代理自己，没有可继承的公共配置，所以新代理是从空白开始的。建一版草稿后即可逐项填写，填完发布才会对玩家生效；在此之前该代理的游戏接口会明确报「还没有生效的配置版本」。",
  "ver.createFirst": "从 0 新建第一版",
  "ver.firstNote": "该代理的第一版配置",
  "ver.firstBuilding": "这个代理的第一版正在建，建好再切代理",
  "ver.firstPartial": "第一版已经建好，但全局参数没读回来，刚才的选择没存上：刷新页面后再选一次",
  "ver.firstCreated": "已创建 {id}，可以开始填写了",
  "ver.blockPending": "本页的后端读写接口尚未就绪：当前为只读展示，配置修改暂不可用。",
  "ver.noOnline": "尚无生效版本",
  "pack.countPending": "目标张数暂未接入后端，此处只读",
  "ver.check": "配置检查",
  "ver.checkOk": "{id} 全部通过，可以发布",
  "ver.checkErrTitle": "配置检查 · 待修改清单",
  "ver.checkErrHint": "发现 {n} 处问题。这跑的就是发布那一道闸，改完再发布不会再被这几条拦住。",
  "ver.pubErrTitle": "发布被拒 · 待修改清单",
  "ver.actErrTitle": "激活被拒 · 待修改清单",
  "ver.dropTitle": "切为生效：{id} 会拿走玩家的物品",
  "ver.dropBody": "生效版本 {from} 里有、{id} 里没有的合成物品共 {n} 种。切过去之后，玩家下次进合成页时，棋盘上和待领取区里的这些物品会消失，要它们的订单作废并补新单，不补偿。回滚再切回来也找不回。",
  "ver.dropGo": "确认切为生效",
  "ver.actErrHint": "{id} 已经发布成功，但激活时被拒（{n} 处）。版本内容已不可变，按下面的规则判断走哪条路：版本外的事实变了（idol 名册 / 授权批次，如 PUZZLE_IDOL_ACTIVE、PUZZLE_LICENSE_VALID）——处理完点「重试激活」；版本自身的问题（如 PUZZLE_ID_IDENTITY 与已发布版本冲突）——重试不会好，只能基于它新建草稿改掉。",
  "ver.actRetry": "重试激活",
  "ver.publishedNotLive": "{id} 已发布但**不是生效版本**：内容已锁定不可改，可以直接激活它，或基于它新建草稿。",
  "ver.activateThis": "激活这一版",
  "ver.refresh.title": "生效方式",
  "ver.refresh.normal": "正常",
  "ver.refresh.immediate": "立即刷新",
  "ver.refresh.force": "强制刷新",
  "ver.refresh.default": "（默认）",
  "ver.refresh.normalDesc": "玩家下次进游戏或回到前台时更新。大部分改动选这个。",
  "ver.refresh.immediateDesc": "正在玩的玩家下一次操作时就在后台换上新内容；在线没操作的也会收到通知去更新。适合价格、奖励这类显示和结算对不上会被投诉的改动。",
  "ver.refresh.forceDesc": "在立即刷新之外，还没换上这一版的玩家，掷骰、购买、升级会先被拦下，更新完自动重试，玩家会卡一两秒。适合旧内容会让结算出错的改动。",
  "ver.refresh.forceLocked": "只有超级管理员能选：它会让全部在线玩家卡一下。",
  "ver.refresh.rollbackNote": "回滚，或把更老的版本设为生效时，之前「强制刷新」设下的要求会自动解除。",
  "ver.refresh.forceWarn": "选了强制刷新：确认后，还没换上这一版的玩家掷骰、购买、升级会先被拦下，等更新完自动重试，全部在线玩家都会卡一两秒。",
  "ver.h.refreshMode": "上次生效方式",
  "ver.h.activate": "设为生效",
  "ver.actTitle": "设为生效 · {id}",
  "ver.actWarn": "将把已发布的 {id} 原样设为生效，不改动内容；当前生效版本 {online} 将成为历史。此操作影响线上玩家。",
  "ver.actWarnFirst": "将把已发布的 {id} 原样设为生效，不改动内容。此操作影响线上玩家。",
  "ver.actOlder": "{id} 比当前生效的 {online} 旧，这是回到旧版本。之前「强制刷新」设下的要求会一并解除，客户端不会再被拦。",
  "ver.actConfirm": "确认设为生效",
  "ver.activatedAs": "{id} 已设为生效（{mode}），{old} 转为历史版本",
  "ver.publishedAs": "{id} 已发布并设为生效（{mode}），{old} 转为历史版本",
  "ver.refreshFail": "版本状态刷新失败，界面可能不是最新的——请手动刷新页面",
  "ver.activateFail": "{id} 已发布，但激活失败：{msg}",
  "ver.pubErrHint": "还有 {n} 处配置要改，改完再点发布。",
  "ver.pubErr.jumpHint": "每条写了在哪、现在是什么、怎么改。点「去修改」直接打开那一页（通关奖励会直接打开那一关），改完保存，再回来发布。",
  "ver.pubErr.path": "位置",
  "ver.pubErr.rule": "规则",
  "ver.pubErr.change": "当前值 → 期望",
  "ver.pubErr.fix": "去修改",
  "ver.pubErr.now": "现在：",
  "ver.pubErr.how": "怎么改：",
  "ver.pubErr.missing": "没填：",
  "ver.pubErr.noJump": "定位不到具体页面，把「技术信息」发给技术同事。",
  "ver.pubErr.tech": "技术信息",
  "issue.rewardLevel": "第 {id} 关",
  "issue.rewardItem": "第 {n} 项奖励",
  "issue.rewardName": "英文名",
  "reward.randomItem": "随机物品（全部物品卡按权重抽一张）",
  "reward.refStale": "（失效·重选）",
  "cardPick.title": "选择{kind}",
  "cardPick.byCode": "编号（卡包 1 / 卡牌 1-3）",
  "cardPick.byName": "卡片名称",
  "cardPick.allPacks": "全部卡包",
  "cardPick.col.pack": "卡包",
  "cardPick.col.card": "卡片名称",
  "cardPick.empty": "没有符合条件的卡",
  "cardPick.hint": "点一行即选中并返回；共 {n} 张，命中 {m} 张",
  "cardPick.current": "当前",
  "ver.pubWarnFirst": "将把 {id} 发布并设为首个生效版本。此操作影响线上玩家。",
  // 玩家面板
  "nav.match-monitor": "对局监控",
  "nav.report-players": "玩家面板",
  "pp.overview": "总览",
  "nav.recharge-records": "充值记录",
  "pay.title": "充值记录",
  "pay.note": "只含现金内购（pay_order），沙盒单与正式单同等计入；付费 = 有过内购到账。时间范围最长 90 天。",
  "pay.all": "全部",
  "pay.f.user": "玩家",
  "pay.f.userPh": "昵称 / userId / 玩家号",
  "pay.f.orderId": "订单号",
  "pay.f.thirdOrderId": "第三方订单号",
  "pay.f.platform": "平台",
  "pay.f.env": "环境",
  "pay.env.production": "正式",
  "pay.env.sandbox": "沙盒",
  "pay.f.status": "状态",
  "pay.f.itemPh": "商品 ID（item_id）",
  "pay.f.time": "下单时间",
  "pay.query": "查询",
  "pay.col.time": "下单时间",
  "pay.col.player": "玩家",
  "pay.col.item": "商品",
  "pay.col.kind": "类型",
  "pay.col.amount": "实付金额",
  "pay.col.status": "状态",
  "pay.col.updated": "最近更新",
  "pay.status.pending": "待支付",
  "pay.status.paying": "处理中",
  "pay.status.success": "已到账",
  "pay.status.failed": "失败",
  "pay.status.expired": "已过期",
  "pay.status.refunding": "退款中",
  "pay.status.refunded": "已退款",
  "pay.stat.orders": "订单数",
  "pay.stat.success": "到账单数",
  "pay.stat.amount": "到账金额",
  "pay.stat.refund": "退款",
  "pay.stat.payers": "充值人数",
  "pay.stat.refundSub": "{n} 单",
  "pay.d.title": "订单 {id}",
  "pay.d.basic": "基本信息",
  "pay.d.productId": "商店商品 ID",
  "pay.d.opId": "发货单号",
  "pay.d.delivery": "发放内容",
  "pay.d.promised": "下单时承诺",
  "pay.d.granted": "实际发放",
  "pay.d.firstBonus": "首购加赠",
  "pay.d.days": "月卡 {n} 天",
  "pay.d.err": "失败原因",
  "pay.d.verify": "验单摘要",
  "pay.d.refund": "退款",
  "pay.d.refundRequested": "收到退款通知",
  "pay.d.reclaimGranted": "发出",
  "pay.d.reclaimDeducted": "扣回",
  "pay.d.debt": "退款欠款",
  "pay.d.debtTag": "欠款",
  "pay.d.audit": "打开详情会记一条操作日志",
  "pay.empty": "没有符合条件的充值记录",
  "pay.empty.sub": "试试放宽时间范围或清空筛选",
  "pay.empty.player": "该玩家没有充值记录",
  "pay.amount.unknown": "商店未回报实付金额",
  "pay.loadFail": "加载失败：{msg}",
  "pay.loadFailGeneric": "加载失败，请稍后重试",
  "pay.v.transactionId": "交易号",
  "pay.v.originalTransactionId": "原始交易号",
  "pay.v.environment": "环境",
  "pay.v.purchaseDate": "购买时间",
  "pay.v.recoveredBy": "补验提交方（交易回原账号）",
  "pay.v.recoveredOtherAgent": "由其他代理的会话补验",
  "pay.err.range": "时间范围最长 90 天",
  "pay.err.rangeOrder": "开始时间晚于结束时间",
  "pay.platform.short.ios": "iOS",
  "pay.platform.short.android": "Android",
  "pay.deleted": "(已删除)",
  "pay.copy": "复制",
  "pay.copied": "已复制",
  "pp.recharge": "充值记录",
  "pp.recharge.all": "查看全部",
  "pp.recharge.count": "到账笔数（全部时间）",
  "pp.recharge.recent": "近 90 天最近 5 单",
  "pp.recharge.amount90": "近 90 天到账金额",
  "pp.payNote": "付费 = 有过内购到账（pay_order 状态为已到账，含沙盒；退款中、已退款不算），不受充值记录页的时间范围限制。",
  "pp.overviewNote": "全库口径，不受上方筛选影响",
  "pp.total": "总玩家数",
  "pp.dau": "今日活跃",
  "pp.new": "今日新增",
  "pp.avgLevel": "平均关卡",
  "pp.maxLevel": "最高 {n} 关",
  "pp.rollsToday": "今日掷骰",
  "pp.sumCoins": "金币存量",
  "pp.sumGems": "钻石存量",
  "pp.sumDice": "骰子存量",
  "pp.payers": "付费玩家",
  "pp.ordersSub": "{n} 单",
  "pp.sumCards": "物品卡持有",
  "pp.levelDist": "关卡分布",
  "pp.bucketNote": "每 5 关一档",
  "pp.list": "玩家列表",
  "pp.listNote": "命中 {n} 人 · 第 {p}/{ps} 页 · 点行看详情",
  "pp.player": "玩家",
  "pp.noNickname": "未取到昵称",
  "pp.search": "昵称 / userId",
  "pp.sortBy": "排序",
  "pp.refresh": "刷新",
  "pp.cols": "显示列",
  "pp.colsReset": "恢复默认",
  "pp.selectAll": "全选",
  "pp.selectNone": "清空",
  "pp.prev": "上一页",
  "pp.next": "下一页",
  "pp.empty": "没有符合筛选条件的玩家",
  "pp.pay.all": "全部玩家",
  "pp.pay.payer": "付费玩家",
  "pp.pay.free": "未付费",
  "pp.sort.lastActive": "最近活跃",
  "pp.sort.level": "关卡",
  "pp.sort.coins": "金币",
  "pp.sort.gems": "钻石",
  "pp.sort.rolls": "掷骰",
  "pp.group.progress": "进度",
  "pp.group.asset": "资产",
  "pp.group.card": "卡牌",
  "pp.group.versus": "对抗",
  "pp.group.daily": "今日",
  "pp.group.spend": "消费",
  "pp.group.time": "时间",
  "pp.col.level": "当前关卡",
  "pp.col.cleared": "已通关",
  "pp.col.levelPair": "本关骰 / 已发",
  "pp.col.totalRolls": "累计掷骰",
  "pp.col.invested": "累计投入",
  "pp.col.coins": "金币",
  "pp.col.gems": "钻石",
  "pp.col.dice": "骰子",
  "pp.col.freeRolls": "免费骰",
  "pp.col.shields": "盾牌",
  "pp.col.wildcard": "万能卡",
  "pp.col.puzzleFrag": "拼图碎片",
  "pp.col.cards": "物品卡",
  "pp.col.packsDone": "集齐包",
  "pp.col.idols": "idol 卡",
  "pp.col.stealGain": "偷取所得",
  "pp.col.stealLoss": "被偷损失",
  "pp.col.thunderHit": "被雷击",
  "pp.col.shieldBlock": "盾牌抵挡",
  "pp.col.dCard": "今日抽卡",
  "pp.col.dPuzzle": "今日拼图",
  "pp.col.dSupply": "今日补给",
  "pp.col.dSlot": "今日 777",
  "pp.col.dAd": "今日广告",
  "pp.col.dStolen": "今日被偷",
  "pp.col.orders": "内购到账单",
  "pp.col.bids": "竞拍出价",
  "pp.col.lastActive": "最近活跃",
  "pp.col.created": "注册时间",
  "pp.hint.levelPair": "level_rolls / level_paid_coins：本关纠偏用的两个累计值，过关清零。",
  "pp.hint.invested": "main_city_log.invested 合计，不含本关未结算部分。",
  "pp.hint.gems": "user_asset · gems（付费货币，商城扣它）。",
  "pp.hint.cards": "user_card 持有总张数。",
  "pp.hint.packsDone": "user_pack_done 行数（集齐过的包）。",
  "pp.hint.idols": "user_idol 种数，括号内为总张数。",
  "pp.hint.stealGain": "main_attack_log 里作为发起方的 steal 金额合计。",
  "pp.hint.orders": "有过内购到账的订单数（不含已退款）。",
  "pp.rel.min": "{n} 分钟前",
  "pp.rel.hour": "{n} 小时前",
  "pp.rel.day": "{n} 天前",
  "pp.detailTitle": "玩家详情 · {who}",
  "pp.readOnly": "只读",
  "pp.tab.overview": "总览",
  "pp.tab.asset": "资产",
  "pp.tab.card": "卡牌与拼图",
  "pp.tab.clear": "通关记录",
  "pp.tab.versus": "对抗记录",
  "pp.tab.daily": "今日次数",
  "pp.tab.idol": "idol 亲密度",
  "pp.intimacy.exp": "累计经验",
  "pp.intimacy.level": "等级",
  "pp.intimacy.today": "今日已加",
  "pp.intimacy.stages": "关卡图已拼完",
  "pp.intimacy.updated": "最后更新",
  "pp.intimacy.none": "还没有任何 idol 的亲密度或关卡进度",
  "pp.clearedSub": "已通关 {n} 关",
  "pp.payer": "有内购到账",
  "pp.levelRolls": "本关掷骰",
  "pp.levelPaid": "本关已发金币",
  "pp.perRoll": "实际每骰",
  "pp.perRollSub": "对账 g = E × bValue",
  "pp.amount": "余额",
  "pp.count": "今日次数",
  "pp.ledgerNote": "掷骰的逐次记录看 main_roll（roll 的变动不进流水）；商城 / 发放 / 升级等低频变动在 asset_ledger。本面板不展开。",
  "pp.nRolls": "掷骰次数",
  "pp.completedAt": "通关时间",
  "pp.noClears": "还没有通关记录",
  "pp.detailRows": "明细",
  "pp.time": "时间",
  "pp.kind": "类型",
  "pp.role": "角色",
  "pp.building": "建筑",
  "pp.attacker": "发起方",
  "pp.victim": "受害方",
  "pp.noAttacks": "没有对抗记录",
  "pp.userType": "用户类型",
  "pp.userTypePlayer": "0（普通玩家）",
  "pp.group.profile": "账号资料",
  "pp.lang": "语言",
  "pp.status": "账号状态",
  "pp.statusOk": "正常",
  "pp.statusOther": "状态 {n}",
  "pp.lastLogin": "最后登录",
  "pp.lastLoginIp": "登录 IP / 地区",
  "pp.hint.lang": "中台的界面语言（en-US 这种），以前从中台补全资料时取到的，玩家票里没有。这个补全已下线，这一列不再更新。",
  "pp.name": "名称",
  "pp.profileAt": "资料更新",
  "pp.profileNever": "从未取到",
  "pp.costSource": "造价来源",
  "pp.costSource.default": "跟版本默认（{d}）",
  "pp.costSource.assigned": "单独指定",
  "pp.costSource.hint": "让这个玩家单独用另一套价，其余人不受影响；同一个版本里就能同时测公式和竞品。改完 30 秒内生效。正在建到一半的星，剩下的格按新价扣。",
  "pp.costSource.useDefault": "跟版本默认",
  "pp.costSource.missing": "生效版本没有这列，按版本默认",
  "pp.idolsSub": "idol {n} 种",
  "pp.buildings": "本关建筑",
  "pp.buildingsNote": "过关清零重建",
  "pp.notBuilt": "未建",
  "pp.starLevel": "{n} 星",
  "pp.damaged": "已损坏",
  "pp.noBuildings": "本关还没有建筑记录",
  "pp.cardRows": "持卡明细",
  "pp.packDone": "整包集齐",
  "pp.packDoneNote": "集齐是一次性事件，之后张数怎么变都不再触发",
  "pp.qty": "张数",
  "pp.source": "来源",
  "pp.obtainedAt": "获得时间",
  "pp.yes": "是",
  "pp.no": "否",
  "pp.noCards": "还没有物品卡",
  "pp.noIdols": "还没有 idol 卡",
  "pp.daily.card": "抽卡",
  "pp.daily.puzzle": "拼图",
  "pp.daily.supply": "补给",
  "pp.daily.slot": "777 免费",
  "pp.daily.ad": "广告",
  "pp.daily.stolen": "被偷",
  "pp.min": "最小",
  "pp.max": "最大",
  "pp.footer": "本页只读，不提供改数据的入口（发资产走专用流程）。列表按当前代理 A0 过滤；昵称与头像取 main_player 的资料快照，NULL 显示「未取到昵称」；名字后的 ◆ = 有过内购到账（付费）。「今日活跃」按当天有掷骰算。",


  // ── idol 名册 ──
  "nav.idol-list": "idol 列表",
  "idol.notVersioned": "idol 名册不随配置版本发布：这里改完立即生效，上下架也是即时的。",
  "idol.add": "新增 idol",
  "idol.idPending": "保存后分配",
  "idol.idHint": "由系统按 idol_001、idol_002 顺序分配。创建后不可修改，删除后也不会被复用——软删除保留行，编号不会退回去。",
  "idol.q.id": "按 ID 查",
  "idol.q.stageName": "按艺名查",
  "idol.q.agency": "按团体查",
  "idol.count": "共 {n} 位",
  "idol.empty": "还没有 idol。先新增一位，拼图配置才能选到人。",
  "idol.loadFail": "读取 idol 名册失败：{msg}",
  "idol.paged": "第 {page} 页：本页 {shown} 位，符合条件的一共 {total} 位。",
  "idol.prevPage": "上一页",
  "idol.nextPage": "下一页",
  "idol.status.all": "全部状态",
  "idol.status.active": "上架",
  "idol.status.inactive": "下架",
  "idol.col.id": "姓名 / ID",
  "idol.col.stageName": "艺名",
  "idol.col.gender": "性别",
  "idol.col.avatar": "头像",
  "idol.col.agency": "所属团体 / 事务所",
  "idol.col.cheerColor": "应援色",
  "idol.col.license": "授权批次",
  "idol.col.completeness": "素材完备度",
  "idol.col.birthday": "生日 / 星座",
  "idol.col.fans": "主推人数",
  "idol.col.sparks": "火花总数",
  "idol.col.subs": "订阅数",
  "idol.col.sort": "排序",
  "idol.notLaunched": "该功能尚未上线，此列恒为 0。",
  "idol.cheerColorPending": "应援色本期不出输入框（xmind 标「之后再说」），列先留着。",
  "idol.lic.none": "未配授权",
  "idol.lic.countN": "{n} 条",
  "idol.lic.expired": "有 {n} 个批次已过期",
  "idol.lic.allExpired": "全部已过期",
  "idol.lic.soon": "最近到期 {date}（30 天内）",
  "idol.lic.ok": "最近到期 {date}",
  "idol.complete.tip": "已填 {done}/{total}。还缺：{missing}",
  "idol.complete.all": "素材与信息已全部配齐",
  "idol.complete.block": "以下必填项还没填，填完才能保存：{missing}",
  "idol.live.confirmTitle": "素材还没配齐，确认上架？",
  "idol.live.confirmBody": "还差 {n} 项：{missing}。上架后玩家立刻能看到她，没配的部分在客户端会是空的。这些补齐之前不影响上架，随时可以回来补。",
  "idol.live.confirmOk": "仍然上架",
  "idol.f.stageName": "艺名（英文）",
  "idol.f.avatar": "头像",
  "idol.f.cover": "介绍页封面",
  "idol.f.intro": "个人介绍（英文）",
  "idol.f.birthday": "生日",
  "idol.f.agency": "所属团体",
  "idol.f.license": "有效的游戏内授权",
  "idol.f.puzzle": "至少一张拼图",
  "idol.f.card": "至少一张卡牌",
  "idol.op.on": "上架",
  "idol.op.off": "下架",
  "idol.deleteTitle": "删除 idol {id}",
  "idol.deleteBody": "这是软删除：数据不会真的从库里消失，但她会立即从名册和拼图下拉里消失，状态一并置为下架。ID 不会被复用。",
  "idol.deleteLocked": "该 idol 仍被拼图引用，删不掉——先去拼图列表把那些行删掉。",
  "idol.saved": "已保存 {id}",
  "idol.deleted": "已删除 {id}",
  "idol.statusOn": "{id} 已上架",
  "idol.statusOff": "{id} 已下架，她的拼图与画廊立即从客户端消失",
  "idol.failed": "操作失败：{msg}",
  "idol.step.1": "基本信息",
  "idol.step.2": "个人首页",
  "idol.step.3": "授权批次",
  "idol.step.back": "上一步",
  "idol.step.next": "下一步",
  "idol.step.create": "创建",
  "idol.step.done": "完成",
  "idol.step.licenseHint": "{id} 已创建，当前是下架状态。真人素材要挂在授权批次下，现在可以把合同录进来；也可以跳过，之后再补。",
  "idol.tab.basic": "基本信息",
  "idol.tab.home": "个人首页",
  "idol.tab.card": "卡牌",
  "idol.tab.license": "授权批次",
  "idol.dlgTitle": "idol · {name}",
  "idol.dlgNew": "新增 idol",
  "idol.fld.realName": "本名",
  "idol.fld.stageName": "艺名",
  "idol.fld.gender": "性别",
  "idol.fld.birthday": "生日",
  "idol.fld.zodiac": "星座",
  "idol.fld.zodiacHint": "由生日推导，不入库——存了就会有「生日改了星座没改」的脏数据。",
  "idol.fld.avatar": "头像缩略图",
  "idol.fld.agency": "所属团体 / 事务所",
  "idol.fld.height": "身高（cm）",
  "idol.fld.weight": "体重（kg）",
  "idol.fld.blood": "血型",
  "idol.fld.nationality": "国籍 / 地区",
  "idol.fld.sortOrder": "名册排序",
  "idol.fld.sortHint": "小的在前；同值按 ID 排。客户端拼图主页的轮播顺序也按它。",
  "idol.fld.recommended": "推荐",
  "idol.fld.recommendedHint": "勾上后，客户端「切换 idol」列表可以只看推荐的、或把推荐的排在前面。",
  "idol.col.recommended": "推荐",
  "idol.recommended.on": "推荐",
  "idol.fld.intro": "个人介绍",
  "idol.fld.cover": "介绍页封面",
  "idol.fld.qr": "二维码",
  "idol.fld.qrHint": "扫码关注用，由后端生成：码里是落地页链接（/q/idol_id），装了游戏会拉起并关注这位 idol，没装跳商店。新建的 idol 保存基本信息后才能生成；生成后记得保存。",
  "idol.fld.plan": "订阅价",
  "idol.fld.planHint": "纯展示：只画在 C 端未解锁的介绍页上，没有购买路径，填了也不会扣任何人的钱。金额填**最小货币单位**——日元 980 就是 ¥980，美元 699 是 $6.99。三格要么全空、要么填齐。",
  "idol.plan.none": "未配",
  "idol.plan.month": "按月",
  "idol.plan.year": "按年",
  "idol.gender.female": "女",
  "idol.gender.male": "男",
  "idol.gender.other": "其他",
  "idol.blood.empty": "未填",
  "idol.nation.empty": "未填",
  "idol.nation.search": "名字或 ISO 码",
  "idol.required": "必填",
  "idol.missingEn": "缺英文名",
  "idol.upload": "上传",
  "idol.replace": "替换",
  "idol.clearImg": "清除",
  "idol.qrGenerate": "生成二维码",
  "idol.qrRegenerate": "重新生成",
  "idol.qrGenerating": "生成中…",
  "idol.qrDownload": "下载",
  "idol.qrRegenAll": "全部重新生成二维码",
  "idol.qrRegenGo": "开始生成",
  "idol.qrRegenConfirm": "将为本代理下所有未删除的 idol 重新生成二维码，并直接替换现有二维码图（包括之前手动上传的）。码里是扫码关注的落地页链接。确定继续？",
  "idol.qrRegenDone": "完成：{updated} / {total} 位已替换。",
  "idol.uploading": "上传中…",
  "idol.noImg": "未上传",
  "idol.lic.add": "新增批次",
  "idol.lic.col.batchId": "批次 ID / 合同号",
  "idol.lic.col.name": "名称",
  "idol.lic.col.window": "生效 ~ 到期",
  "idol.lic.col.scope": "授权范围",
  "idol.lic.col.note": "备注",
  "idol.lic.scope.image": "肖像图片",
  "idol.lic.scope.audio": "音频",
  "idol.lic.scopeNone": "未标范围",
  "idol.lic.alreadyExpired": "这份授权的到期时间已经过了。补录历史合同是正常的，照样能存；如果是打错了，改一下再存。",
  "idol.lic.scopeHint": "按素材类型分别授权，可多选。管端只如实记录，不据此拦任何操作。",
  "idol.lic.revoke": "撤销",
  "idol.lic.revokedAt": "已于 {date} 撤销",
  "idol.lic.revokeTitle": "撤销授权批次 {id}",
  "idol.lic.revokeBody": "撤销不同于到期：到期是可预期的合约周期，撤销通常意味着法务出了问题。撤销后这条批次不可再编辑，只能新建一条。只要生效版本里还有拼图引用着它，本次撤销就会被拒——停用那些拼图或下架 idol 都不够，必须先在新草稿里删掉那些行并发布。\n\n注意：撤销只能停止继续售卖——**已经兑换过的玩家仍然看得到那张写真**（快照不随授权状态变化，CDN 地址也无法吊销）。",
  "idol.lic.empty": "还没有授权批次——没有批次就配不了拼图。",
  "idol.lic.expiredTag": "已过期",
  "idol.lic.soonTag": "临期",
  "idol.lic.notStarted": "未生效",
  "idol.lic.saved": "批次 {id} 已保存",
  "idol.lic.newId": "新批次 ID",
  "idol.lic.startsAt": "生效",
  "idol.lic.expiresAt": "到期",
  "idol.lic.needId": "填批次 ID / 合同号才能保存——它是这条授权对应哪份合同的唯一线索。",
  "idol.lic.idTaken": "这个批次 ID 已经用过了，换一个。",
  "idol.lic.needStart": "填生效时间。按合同上的日期填，不要拿今天顶替。",
  "idol.lic.needExpire": "填到期时间。按合同上的日期填，不要拿今天顶替。",
  "idol.lic.badWindow": "到期时间要晚于生效时间。",
  "idol.expiryNoBlock": "授权到期不阻断发布、也不自动下架——临期与过期只在这里高亮，要下架请手动改状态。",

  // ── 喵汪角色 ──
  "navGroup.pet": "喵汪角色",
  "nav.pet-list": "角色列表",
  "pet.add": "新增角色",
  "pet.empty": "本版本还没有角色",
  "pet.count": "共 {n} 个",
  "pet.idPending": "未填编号",
  "pet.q.kw": "编号或名称",
  "pet.q.speciesAll": "全部品种",
  "pet.col.id": "编号",
  "pet.col.name": "名称",
  "pet.col.species": "品种",
  "pet.col.family": "大类",
  "pet.col.color": "颜色",
  "pet.col.quality": "初始品质",
  "pet.col.static": "静态",
  "pet.col.anim": "动画资源",
  "pet.col.animSub": "齐 {max} 个才可用",
  "pet.dialog.new": "新增喵汪角色",
  "pet.dialog.edit": "编辑喵汪角色",
  "pet.delTitle": "删除角色 {id}",
  "pet.delBody": "只删本版本的这一行配置。已发布的版本不受影响。",
  "pet.f.id": "编号",
  "pet.f.id.hint": "自己定，字母数字下划线短横线；同一版本内不能重复",
  "pet.f.id.locked": "编号是身份，存过之后不能改——要改就删掉重建",
  "pet.f.name": "名称",
  "pet.f.name.hint": "英文必填：客户端语言缺失时回退到它",
  "pet.f.species": "品种",
  "pet.f.color": "颜色",
  "pet.f.color.hint": "自由文本，还没定成枚举",
  "pet.f.quality": "初始品质",
  "pet.f.quality.hint": "1 ~ {max} 的整数",
  "pet.f.enabled.hint": "停用的角色不下发给客户端；改完要发布新版本才生效",
  "pet.f.static": "静态资源",
  "pet.f.static.hint": "必填，{max} 个，格式不限",
  "pet.f.anim": "动画资源",
  "pet.f.anim.hint": "必填，一组 Spine 共 {max} 个文件（.skel / .atlas / .png 各一），可一次全选",
  "pet.f.species.hint": "品种名。自由文本，跟名称一样按语言填；右边可以从本版本已有品种里拷一份多语言过来",
  "pet.f.species.pick": "从已有品种拷一份",
  "pet.f.family": "猫狗大类",
  "pet.f.family.hint": "必填。喵汪页顶部的 ALL / 猫 / 狗 筛选按它分——不能让客户端按品种硬编码归类",
  "pet.f.size": "体型",
  "pet.f.size.hint": "必填。服务端只存与下发，不参与玩法判断；客户端要不要按它缩放家园里走动的宠物，由客户端决定",
  "pet.f.oshi": "本命 idol",
  "pet.f.oshi.hint": "选填。填了这只在领养池里会对该 idol 的粉丝小幅加权；一只至多绑一位，留空 = 非本命",
  "pet.family.cat": "猫",
  "pet.family.dog": "狗",
  "pet.size.small": "小",
  "pet.size.medium": "中",
  "pet.size.large": "大",
  "pet.asset.pick": "选文件上传（可多选）",
  "pet.asset.open": "查看",
  "pet.asset.missing": "缺",
  "pet.asset.dupList": "这些后缀传了不止一个，客户端不知道该用哪个：{exts}",
  "pet.asset.mixedBase": "这几个文件不是同一套导出（{bases}），装在一起加载不起来",
  "pet.asset.replaced": "这些被同后缀的新文件顶掉了：{names}",
  "pet.err.longId": "编号超长（上限 64 字符）",
  "pet.err.longName": "名称超长（单个语言上限 64 字符）",
  "pet.err.longColor": "颜色超长（上限 32 字符）",
  "pet.err.noId": "编号必填",
  "pet.err.badId": "编号只能用字母、数字、下划线和短横线",
  "pet.err.dupId": "这个编号本版本里已经有了",
  "pet.err.noStatic": "静态资源必填，至少传 1 个",
  "pet.warn.tag": "待补动画",
  "pet.warn.animPending": "已启用但还没配动画资源。现在能存，但发布会被拦——要么把三个后缀补齐，要么先停用它。",
  "pet.err.animMissing": "动画资源要三个后缀齐全，还差没传的",
  "pet.err.animDup": "动画资源有后缀传了不止一个，客户端不知道该用哪个",
  "pet.err.noEn": "名称至少要填英文（en）",
  "pet.publishedLocked": "这只已经发布过了：品种、品质、启用三项锁死，也不能删除——玩家手里那只存的是裸编号，配置侧动了它就会失去名字与素材。要下线请把它在领养池里的权重调成 0。名称、颜色、体型、猫狗大类、本命 idol、素材照常可改。",
  "pet.publishedDrift": "这一行与它首发时（v{v}）不一致：首发的品种是「{species}」、品质 {quality}、且是启用的。发布后这三项不可改，所以**必须先改回去**，否则这一行存不下、这一版也发不出去。要上一只不一样的角色，请换一个编号新建。",
  "pet.err.noSpecies": "缺品种（至少要填英文——客户端语言缺失时回退到它）",
  "pet.err.longSpecies": "品种超长（上限 32 字符）",
  "pet.err.badFamily": "没选猫狗大类——发布闸要求启用的角色必须是猫或狗",
  "pet.err.badSize": "没选体型",
  "pet.err.badQuality": "初始品质超出范围",

  // ── 写真拼图 ──
  "nav.puzzle-list": "拼图列表",
  "nav.puzzle-price": "拼图价目表",
  "pz.add": "新增拼图",
  "pz.idPending": "保存后分配",
  "pz.searchIdol": "打艺名或 ID",
  "pz.idHint": "由系统分配、创建后不可修改。**首次发布过、或被任何玩家兑换过之后，这个 ID 就永不复用**——否则老玩家的已兑记录会被判成「已完成」那张全新的图。",
  "pz.count": "共 {n} 张",
  "pz.empty": "还没有拼图。先在价目表里配好序号档位，再来这里加图。",
  "pz.q.idol": "按 idol 查",
  "pz.q.seq": "按序号查",
  "pz.col.idol": "idol",
  "pz.col.seq": "序号",
  "pz.col.imgLocked": "未拼图",
  "pz.col.imgUnlocked": "拼成后",
  "pz.col.photoType": "写真类型",
  "pz.col.grid": "拼块数",
  "pz.col.price": "碎片价",
  "pz.col.reward": "集齐奖励",
  "pz.fromPriceTable": "价目表带出",
  "pz.priceReadonly": "拼块数与碎片价由序号查价目表带出，不能在单张上改——价格不看 idol 人气",
  "pz.goFix": "去补这一档",
  "pz.goIdol": "去录 idol",
  "pz.noIdol": "还没有已上架的 idol。拼图必须挂在某位 idol 名下，先去 idol 列表录入并上架。",
  "pz.seqGap": "这位 idol 的启用序号断在第 {seq} 号：后面那些图玩家永远解锁不了，发布会被拦下",
  "pz.seqNth": "第 {n} 张",
  "pz.seqLockedHint": "建完不可改。可选的是「价目表里有、且不会造成断号」的那几档。",
  "pz.noSeqAvail": "价目表里没有第 {n} 档的价格，这位 idol 的下一张开不了。去价目表补一行 seq={n} 再回来。",
  "pz.goPrice": "去价目表",
  "pz.needIdol": "先选这张写真属于哪位 idol。",
  "pz.delTitle": "删除拼图 {id}",
  "pz.delBody": "只删本版本的配置行，玩家已兑换的记录不受影响（它存的是兑换时刻的快照）。如果这个 ID 已经发布过、或有玩家兑换过，它从此不可再用于别的写真；只在草稿里存在过的话可以再用。",
  "pz.dlgTitle": "拼图 {id}",
  "pz.dlgNew": "新增拼图",
  "pz.idolLocked": "所属 idol 在新增第一步选定，之后不能改 —— 换人等于换一张写真",
  "pz.lockedHint": "已保存的拼图在管端不给改绑 idol / 改序号——这两个值在兑换时会进玩家快照，改了两边就对不上。服务端的硬约束是「已发布过或被兑换过」才锁死，这里取更保守的一档：要改就删掉重建。",
  "pz.fld.idol": "所属 idol",
  "pz.fld.seq": "序号",
  "pz.fld.grid": "拼块数",
  "pz.fld.price": "碎片价",
  "pz.fld.imgLocked": "未拼图",
  "pz.fld.imgUnlocked": "拼成后图",
  "pz.fld.photoType": "写真类型",
  "pz.fld.reward": "集齐奖励",
  "pz.gridIsCosmetic": "纯表现：只决定揭图动画的网格切法",
  "pz.fld.levels": "使用关卡",
  "pz.levelsHint": "不填 = 不限。纯编号（大关-小关），客户端按编号决定这张在哪用；服务端不据此拦兑换",
  "pz.levelsAll": "全部关卡",
  "pz.codePlaceholder": "如 1-1, 1-3, 4-1~4-5",
  "pz.codeAdd": "加入",
  "pz.code.bad": "「{part}」格式不对：应形如 1-1、4-5（大关、小关都是 1 起的整数，各最多 3 位）",
  "pz.code.crossRange": "「{part}」跨了大关：范围只能在同一大关内，请分开填",
  "pz.code.reversed": "「{part}」起点比终点大",
  "pz.code.tooMany": "编号超过 200 个了（加到「{part}」时）",
  "pz.col.levels": "使用关卡",
  "pz.gridFollow": "跟价目表（{v}）",
  "pz.gridTagOverride": "覆盖",
  "pz.gridTagPrice": "价目表",
  "pz.gridOverrideHint": "只决定拼几块；碎片价仍按序号查价目表，不跟着变",
  "pz.priceIsEconomy": "唯一的经济量：兑换只看它",
  "pz.imgLockedHint": "这是玩家实际要拼的原图。",
  "pz.imgUnlockedHint": "拼完弹出的图，可以与未拼图不同。接口只在兑换成功后才下发它，配置接口里绝不出现。",
  "pz.rewardHint": "本期只有骰子一种。不复用通用奖励弹窗——那会开出「花碎片奖碎片」这种自循环。",
  "pz.fld.intimacyLv": "亲密度门槛",
  "pz.intimacyUnit": "级",
  "pz.fld.intimacyPoints": "拼完给亲密度",
  "pz.intimacyPointsUnit": "点",
  "pz.intimacyPointsHint": "玩家第一次拼完这张时，给这位 idol 加多少亲密度。0 = 不给。重复拼不再给；受亲密度配置里「拼完拼图」来源的每日上限与 idol 每日总上限截断，截掉的不补。",
  "pz.intimacyHint": "玩家与这位 idol 的亲密度到几级才在画廊里露出这张。0 = 无门槛，谁都能看见。与碎片价并存：够等级只是「看得见」，拿到手仍要花碎片兑换。⚠️ 本期服务端不校验它，只影响客户端展示。",
  "pz.noPriceRow": "价目表里没有序号 {seq}，去补一行再回来",
  "pz.idolTruncated": "只加载了 {n} 位 idol（共 {total} 位）——下拉里可能选不到人，请联系开发",
  "pz.disableHint": "停用等同断号：序号 1 停用而 2 启用时，玩家永远兑不了 1，于是 2 也永远解锁不了。发布会拦。",
  "pz.iss.badType": "写真类型不是约定取值（普通/舞台/限定），发布会被拦下",
  "pz.iss.noPrice": "序号在价目表里没有对应行",
  "pz.iss.noImg": "两张图没传齐",
  "pz.iss.noReward": "集齐奖励还是 0",
  "pz.type.unknown": "未知取值：{v}",
  "pz.type.normal": "普通写真",
  "pz.type.stage": "舞台照",
  "pz.type.limited": "限定大片",
  "pz.price.grid": "拼块边长",
  "pz.price.gridSub": "n × n",
  "pz.price.frag": "碎片价",
  "pz.price.fragSub": "兑换所需",
  "pz.price.delta": "与拼块数之差",
  "pz.price.same": "相等",
  "pz.price.used": "在用张数",
  "pz.price.usedN": "{n} 张",
  "pz.price.add": "新增第 {seq} 档",
  "pz.price.empty": "价目表是空的。至少配第 1 档，第一张拼图才有价格可查。",
  "pz.price.gap": "序号断在第 {seq} 档：价目表必须从 1 起连续，发布会被拦下。",
  "pz.price.orphan": "这些序号有拼图在用、价目表里却没有对应行：{seqs}",
  // 三条错误话术都直说「该填什么」，不说「格式错误」——后者等于让人自己猜边界
  "pz.price.badFrag": "碎片价要填大于 0 的整数",
  "pz.price.fragTooBig": "碎片价超上限（最大 {max}）",
  "pz.price.badGrid": "拼块边长要填 2 ~ {max} 的整数",
  "pz.price.goList": "去看拼图列表",
  "pz.price.onlyTail": "只能从末尾删：删中间会立刻造成断号，而断号在发布闸上是硬阻断。",
  "pz.price.inUse": "这一档还有 {n} 张拼图在用，删了它们就查不到价、发布会被拦下。先去拼图列表删掉那些拼图。",
  "pz.price.note": "改一行影响全体 idol 的同序号拼图——价格跨 idol 完全一致。",
  "pz.price.foot": "拼块数与碎片价是两个独立配置项，默认相等但允许配成不等：碎片价是唯一的经济量，拼块数只决定揭图动画的网格。",

  // 每日奖励（登录签到）
  "nav.daily-reward": "每日奖励",
  "dr.days.title": "周期档",
  "dr.days.sub": "第 1…N 天各发什么",
  "dr.days.note": "领一次进一格，错过一天不重置；领完最后一天回到第 1 天。行数就是周期长度。",
  "dr.days.add": "新增第 {seq} 天",
  "dr.days.empty": "还没配周期档。不配 = 本版不开每日奖励；配上第 1 天玩家就能领。",
  "dr.days.gap": "第 {seq} 天断号：周期档必须从第 1 天起连续，发布会被 DAILY_DAY_CONTINUOUS 拦下。",
  "dr.days.onlyTail": "只能删最后一天——删中间会断号",
  "dr.ms.title": "累计档",
  "dr.ms.sub": "当月累计领取天数到阈值时加发",
  "dr.ms.note": "到阈值那一次领取自动一并加发，没有单独的开礼盒动作；跨月清零。阈值 1~31。",
  "dr.ms.add": "新增一档",
  "dr.ms.empty": "没有累计档也能发布，只是进度条上没有礼盒。",
  "dr.ms.needDays": "配了累计档但没有周期档：累计天数只由领周期档产生，发布会被 DAILY_DAYS_REQUIRED 拦下。",
  "dr.col.day": "第几天",
  "dr.col.days": "累计天数",
  "dr.dlg.day": "第 {n} 天 · 每日奖励",
  "dr.dlg.ms": "累计 {n} 天 · 加发奖励",
  "dr.issue.empty": "至少一项奖励",
  "dr.issue.range": "阈值须在 1~31",
  "dr.issue.dup": "阈值重复",
  "dr.tz": "日切按租户时区；未登记的租户按 UTC。money 按玩家领取时所在关的 B 值折算。",
  "dr.tz.g": "日切按租户时区；未登记的租户按 UTC。money 填的是几倍单掷金币，按玩家领取时所在关的单掷金币 g 折算。",

  // 加入我们（个人中心的渠道弹窗）
  "nav.social-channel": "加入我们",
  "sc.col.name": "名称",
  "sc.col.icon": "图标",
  "sc.col.url": "跳转地址",
  "sc.col.rewards": "奖励",
  "sc.col.sort": "排序",
  "sc.col.sortSub": "小的在前",
  "sc.hint.code": "渠道标识，客户端领奖时传它（tiktok / discord）。小写字母开头，可用 a-z0-9_-。改它等于换一个渠道：老 code 的领取记录不会跟过来，已领过的人能再领一次。",
  "sc.hint.url": "玩家点卡片后打开的地址，必须是 https —— http 会被 App 的 ATS 直接拦掉，点了什么也不会发生。",
  "sc.add": "新增渠道",
  "sc.empty": "还没配渠道。不配 = 个人中心不显示「加入我们」；配上一条玩家就能领。",
  "sc.noReward": "没配奖励",
  "sc.note": "点 Claim 就发奖，不做外部验证（验证不了玩家是否真关注了）。防刷靠一人一渠道只能领一次。",
  "sc.foot": "奖励与通关奖励同一套发放链与发布校验。图标走上传管线，外链会在保存时被拒。",
  "sc.dlg.title": "{code} · 渠道奖励",
  "sc.issue.code": "code 要小写字母开头，只能用 a-z0-9_-",
  "sc.issue.dup": "code 与别的渠道重复",
  "sc.issue.url": "要填完整的 https 地址",

  // 多人匹配棋盘格位表
  "nav.board-cells": "棋盘格配置",
  "nav.lamp-drops": "神灯掉落表",
  "lamp.note": "踩中神灯格（棋盘上 0 / 14 / 28 / 42）按相对权重开出 1 行，即时到账。权重之和不必是 100。金币按这里填的数直接发（《棋盘格位表 v4.2》）。",
  "lamp.later": "五种奖励都会即时到账。万能卡保底：连续 N 次神灯没出万能卡，第 N 次强制开出「计入万能卡保底」的那一行（LAMP_001），计数存在玩家账号上、跨对局；自然开出万能卡也清零。",
  "lamp.pity.threshold": "万能卡保底次数",
  "lamp.pity.threshold.hint": "连续这么多次神灯没出万能卡，这一次强制出。产品默认 10，觉得慢压到 6–8（《棋盘格位表 v4.2》规则 12）。0 = 关闭保底。开局时随局冻结。",
  "lamp.pity.threshold.invalid": "要是 0 到 1000 的整数（0 = 关闭）",
  "lamp.empty": "还没配神灯掉落。不配 = 神灯格只记落点、什么都不发。",
  "lamp.col.id": "编号",
  "lamp.col.kind": "奖励类型",
  "lamp.col.value": "数量",
  "lamp.col.ref": "卡包 / 道具",
  "lamp.col.weight": "权重",
  "lamp.col.share": "占比",
  "lamp.col.pity": "计入万能卡保底",
  "lamp.col.fallback": "空窗期改发",
  "lamp.kind.coin": "金币",
  "lamp.kind.dice": "体力",
  "lamp.kind.wildcard": "万能卡",
  "lamp.kind.item_card": "物品卡",
  "lamp.kind.act_item": "活动道具",
  "lamp.fb.coin": "金币",
  "lamp.fb.dice": "体力",
  "lamp.issue.value": "数量要是 1 以上的整数",
  "lamp.issue.ref": "要选卡包 / 填道具",
  "lamp.issue.cards": "物品卡一次最多 64 张",
  "lamp.foot": "开局时整张表随局冻结：局中改了，只影响之后新开的局。",
  "bc.note": "《Fast Ludo 棋盘格位表 v4.0》：赛道 56 格，index 0–55 四色共用（0 = 绿色赛道第 1 格，方向 绿→黄→红→青）。经资源块 board:cells 下发给客户端：类型 / 分池 / 漩涡色 / 转盘 id 和固定发放量（体力数、卡张数等）会下发，占预算的百分比和领养掉落留服务端。",
  "bc.fill": "按 v4.0 模板填充 56 格",
  "bc.fillConfirm": "会把当前全部格子替换成 v4.0 默认模板，确定？",
  "bc.fill53": "按 v5.3 模板填充 56 格",
  "bc.fillConfirm53": "会把当前全部格子替换成《棋盘格位表 v5.3》的默认模板（顺时针重排、漩涡在 10 / 24 / 38 / 52）。代理的「棋盘规则版本」也要切到 v5.3、并抬最低客户端版本，否则客户端按旧棋盘走。确定？",
  "bc.empty": "还没配棋盘格。点右上「按 v4.0 模板填充」生成 56 格，再逐格改类型和参数。不配 = 客户端拿不到 board:cells 块。",
  "bc.foot": "改表纪律（v4.0）：任何一格的类型或 index 变了，客户端要重画棋盘，发布前必须同步抬高「匹配与房间」里的最低客户端版本；百分比、发放数量这些数值改了不用提版本。同一局里两个客户端拿到的必须是同一版棋盘。",
  "bc.notMatch": "这一版的玩法体系不是「多人匹配」，棋盘格位表不会下发。先在初始化配置里选多人匹配。",
  "bc.col.pos": "位置",
  "bc.col.type": "类型",
  "bc.col.params": "参数",
  "bc.col.desc": "效果说明",
  "bc.col.note": "备注",
  "bc.color.green": "绿",
  "bc.color.yellow": "黄",
  "bc.color.red": "红",
  "bc.color.cyan": "青",
  "bc.typeLegacy": "{type}（已退役，请重选）",
  "bc.shortLegacy": "老类型",
  "bc.legacyHint": "这一格还是 2026-09-18 之前的类型「{type}」，服务端已经不认它，踩上去不发东西。改成「引用 spin 奖励行」并选一行，或改成神灯 / 漩涡 / 转盘。",
  "bc.legacyBanner": "这一版里有 {n} 格还是老类型（发什么配在格子上那一套），服务端已经不认。最快的做法是按 v4.0 模板重新填充，再逐格选 spin 行。",
  "bc.param.vortex_color": "漩涡颜色",
  "bc.param.wheel_id": "转盘 id",
  "spin.kind.pet": "喵汪（领养点）",
  "bc.type.SPIN": "引用 spin 奖励行",
  "bc.desc.SPIN": "踩中就发所引用的那一行 spin 奖励：发什么、发多少都配在「spin 奖励配置」里，一行可以被多个格子引用，改一行全部生效。",
  "bc.short.SPIN": "未选行",
  "bc.col.spin": "发什么（spin 行）",
  "bc.col.sectors": "转盘扇区",
  "bc.sectorsNone": "还没选扇区（没选时按上面那一行固定发）",
  "bc.sectorsMissing": "这些行不在本版或已停用：{codes}",
  "bc.sectorsHint": "每勾一行就是转盘上的一个扇区，勾的顺序 = 扇区顺序，按行权重抽；最多 12 个。空扇区（BUST）就勾一行不发奖励的行。",
  "bc.col.spinSub": "金额在 spin 奖励配置里配",
  "bc.spinUnset": "还没选行",
  "bc.spinUnsetHint": "没选行的格子踩了不发东西，发布时会被拦下",
  "bc.spinMissing": "#{code}（已删除或已停用）",
  "bc.spinMissingHint": "这一行在本版本里不存在或已停用，发布会被拦下，重选一行",
  "bc.spinNoReward": "没配奖励",
  "bc.roleHint": "v4.0 布局里这一格是{role}",
  "bc.role.lamp": "神灯（起步格）",
  "bc.role.item_atk": "道具格·攻",
  "bc.role.item_def": "道具格·守",
  "bc.role.item_both": "道具格·攻守",
  "bc.role.subgame": "副玩法格（金库 / 大炮 / 777 / 盾牌）",
  "bc.role.wheel": "转盘",
  "bc.role.coin": "金币格",
  "bc.role.adopt": "领养馆",
  "bc.role.event": "活动道具格",
  "bc.role.stamina": "体力格",
  "bc.role.card": "物品卡格",
  "bc.role.vortex": "漩涡",
  "bc.type.MAGIC_LAMP": "神灯（起步格）",
  "bc.type.WHEEL": "转盘",
  "bc.type.VORTEX": "漩涡",
  "bc.desc.MAGIC_LAMP": "阿拉丁神灯：从后台掉落表按权重开出 1 份奖励。四色各自的赛道第 1 格，踩中照常触发。掉落表另配。",
  "bc.desc.WHEEL": "小转盘 6 扇区，有正有负；全场唯一的负面来源，负面是直接被遣回家。",
  "bc.desc.VORTEX": "本色 = 到达（进入本垒道）；他色 = 随机传送到另外两个漩涡之一。落上即结算离开，不停留。",
  "bc.view.board": "棋盘",
  "bc.view.table": "列表",
  "bc.pickHint": "点棋盘上的格子，在右边改类型和参数。格子颜色照客户端 UI 稿；小字是这一格的数值。",
  "bc.legend": "图例（格数）",
  "bc.cellTitle": "第 {index} 格",
  "bc.cellPos": "{color}色赛道 · 臂内第 {off} 格",
  "bc.prev": "上一格",
  "bc.next": "下一格",
  "bc.camp": "{color}色出生营地 → 起步格 {start}",
  "bc.notDrawable": "当前格子不是 index 0–55 各一格，画不成棋盘，先用列表看。可以按 v4.0 模板整盘填充后再画。",
  "bc.emptyBoard": "还没配棋盘格",
  "bc.short.MAGIC_LAMP": "神灯",
  "bc.short.WHEEL": "转盘",
  "bc.short.VORTEX": "漩涡",

  // 头像框名册
  "nav.frame": "头像框",
  "fr.col.image": "框图",
  "fr.hint.id": "框标识，客户端设置头像框时传它。小写字母开头，可用 a-z0-9_-。改它等于换一个框：戴着旧 id 的玩家会变成没戴。",
  "fr.add": "新增头像框",
  "fr.empty": "还没配头像框。不配 = 客户端没有可选的框；配上并启用，玩家就能戴。",
  "fr.note": "这一页只是名册。玩家要在成就 / 邮件 / 通关 / 集齐等奖励里配「头像框」奖励项拿到后才能戴（产品 2026-09-15 定）。",
  "fr.foot": "启用的框发布时必须有图。框图走上传管线，外链会在保存时被拒。停用一个框，戴着它的玩家展示为没戴。",
  "fr.issue.id": "frame_id 要小写字母开头，只能用 a-z0-9_-",
  "fr.issue.dup": "frame_id 与别的框重复",

  // 时间线事件类型（同时是 C 端上报白名单）
  "nav.social-event-type": "时间线事件",
  "se.col.icon": "图标",
  "se.col.text": "文案模板",
  "se.col.visibility": "可见性",
  "se.col.reportable": "允许上报",
  "se.col.sort": "排序",
  "se.col.sortSub": "小的在前",
  "se.hint.type": "事件类型标识，服务端与客户端按它对表。小写字母开头，可用 a-z0-9_。",
  "se.hint.text": "客户端按这份模板渲染，改文案不发版。可用占位符：{vars}",
  "se.hint.visibility": "self = 落在承受者自己的时间线（当前唯一用到的）；friends = 扇出到好友动态（预留，还没做）。",
  "se.hint.reportable": "允许客户端通过 POST /v1/social/events 上报这种事件。这张表就是那个接口的白名单：关掉之后客户端再发会被拒。",
  "se.vis.self": "自己可见",
  "se.vis.friends": "好友可见",
  "se.serverOnly": "「{type}」由服务端结算产生，开了上报等于让客户端往自己时间线里塞任意金额，接口会直接拒。",
  "se.add": "新增事件类型",
  "se.empty": "还没配事件类型。不配 = 时间线是空的，客户端也一条都上报不进来。",
  "se.vars": "文案里的占位符由服务端按事件填：{vars}。写错的占位符不会报错，会原样显示给玩家。",
  "se.note": "服务端只给类型和参数，文案在客户端按这里的模板渲染——改文案不发版，但要发布配置版本才生效。",
  "se.foot": "启用且允许上报的类型，客户端才能上报。steal / thunder_hit / thunder_block 由服务端结算产生，只能改文案图标。",
  "se.issue.type": "type 要小写字母开头，只能用 a-z0-9_",
  "se.issue.dup": "type 与别的事件重复",

  // idol 详情 · 画廊（拼图配置的只读投影）
  "idol.tab.gallery": "画廊",
  "idol.card.note": "卡牌是卡包配置的只读投影，不在这里编。取的是生效版本——与素材完备度同一口径，草稿里的改动要发布后才出现在这里。",
  "idol.card.emptyIn": "生效版本 v{v} 里她名下没有卡包，或包里还没放卡。刚在草稿里配过的话，要发布之后才会出现在这里。",
  "idol.card.noActive": "还没有生效的配置版本，卡牌要等第一版发布之后才有内容。",
  "idol.card.goPack": "去卡包配置",
  "idol.card.packOf": "{name}（{n} 张）",
  "idol.gallery.note": "画廊是拼图配置的只读投影，不单独建一套配置。取的是生效版本——与素材完备度同一口径，草稿里的改动要发布后才出现在这里。",
  "idol.gallery.emptyIn": "生效版本 v{v} 里她还没有拼图。刚在草稿里加过的话，要发布之后才会出现在这里。",
  "idol.gallery.noActive": "还没有生效的配置版本，画廊要等第一版发布之后才有内容。",
  "idol.gallery.goPuzzle": "去拼图列表",

  // 星座（生日的纯函数，不入库）
  "zodiac.aries": "白羊座",
  "zodiac.taurus": "金牛座",
  "zodiac.gemini": "双子座",
  "zodiac.cancer": "巨蟹座",
  "zodiac.leo": "狮子座",
  "zodiac.virgo": "处女座",
  "zodiac.libra": "天秤座",
  "zodiac.scorpio": "天蝎座",
  "zodiac.sagittarius": "射手座",
  "zodiac.capricorn": "摩羯座",
  "zodiac.aquarius": "水瓶座",
  "zodiac.pisces": "双鱼座",

  // GM 白名单页
  "gm.title": "GM 白名单",
  "gm.intro": "名单里的账号才能用 roll 的 force_code 指定落点。不在名单的账号会收到 60223。客户端可以在调试面板里自助申请（落「待审批」，批过才生效）；这里也可以直接添加，直接添加即已批准。",
  "gm.unavailable": "GM 白名单只在 test 环境可用（当前：{env}）。正式环境不存在玩家侧调试能力，所以也没有白名单可管。",
  "gm.env.unknown": "未知",
  "gm.col.userId": "玩家 id",
  "gm.col.developer": "开发",
  "gm.col.note": "备注",
  "gm.col.status": "状态",
  "gm.col.updated": "最后修改",
  "gm.col.ops": "操作",
  "gm.status.pending": "待审批",
  "gm.status.rejected": "已驳回",
  "gm.status.approved": "已批准",
  "gm.status.disabled": "已停用",
  "gm.action.approve": "批准",
  "gm.action.reject": "驳回",
  "gm.action.enable": "启用",
  "gm.action.disable": "停用",
  "gm.action.delete": "删除",
  "gm.field.userId": "玩家 id",
  "gm.field.userId.ph": "如 990233",
  "gm.field.developer": "开发",
  "gm.field.developer.ph": "谁在用",
  "gm.field.note": "备注",
  "gm.field.note.ph": "干什么用的",
  "gm.action.add": "加入白名单",
  "gm.empty": "白名单还是空的。等客户端自助申请，或在上面直接添加一个。",
  "gm.err.needUserId": "先填玩家 id",
  "gm.toast.added": "已加入白名单：{id}",
  "gm.toast.approved": "已批准 {id}",
  "gm.toast.rejected": "已驳回",
  "gm.toast.enabled": "已启用",
  "gm.toast.disabled": "已停用",
  "gm.toast.deleted": "已删除",
  "gm.confirm.delete": "彻底删除 {id}？停用更适合留痕。",

  // ── 萌宠小镇（pettown）──
  "navGroup.pettown": "萌宠小镇",
  "navGroup.match": "多人对战",
  "nav.pettown-params": "小镇参数",
  "nav.pettown-star": "主建筑星级表",
  "nav.pettown-slot": "副建筑槽位",
  "nav.pettown-talent": "天赋树",
  "nav.level-talent": "天赋树",
  "nav.idle-income": "挂机收益",
  "idle.grp": "挂机收益",
  "idle.pct": "每下最少 = 每投金币的",
  "idle.pct.hint": "玩家点投掷后、骰子动画还没结束时点屏幕，每点一下在本关每投金币的「最少」到「最多」千分之几之间随机给。最多一位小数，如 2.5。填 0 = 不开挂机收益。",
  "idle.max": "每下最多 = 每投金币的",
  "idle.max.hint": "每下随机的上限，千分之几，不能小于最少。和最少填一样就是固定值。",
  "idle.cap": "一次动画最多算几下",
  "idle.cap.hint": "一投的动画里报上来的点击超过这个数，超出的不给。只用来挡改包一次报出天文数字，不是收益上限：按真人手速 × 动画时长给，宽一点没关系。最多 10000。填 0 = 不开挂机收益。",
  "idle.on": "已开启",
  "idle.off": "未开启",
  "idle.note": "玩家点投掷后、骰子动画还没结束时点屏幕，每点一下收一点金币。这是额外奖励，不影响投掷本身给多少钱。每下金币 = 本关每投金币 × 上面区间里随机的千分比，所以越往后的关每下给得越多，但通关要点的次数也跟着骰次变多。倍率不参与，开 ×10 时点击金币不变。两个数都大于 0 才开启，随版本发布生效。",
  "idle.avg": "估算：平均每次点几下",
  "idle.avg.hint": "只用于本页估算，不保存、不下发、不影响玩家。上线后可按埋点里的真实点击数改。",
  "idle.u.taps": "下",
  "idle.sum.perRoll": "点 {n} 下抵一次投掷",
  "idle.sum.avg": "平均每次点 {n} 下：挂机占总收入 {v}",
  "idle.sum.cap": "每次都点满 {n} 下：挂机占总收入 {v}",
  "idle.sum.taps": "只靠点击通关：第 1 关 {a} 下，第 {n} 关 {b} 下",
  "idle.preview": "逐关预览",
  "idle.col.level": "关卡",
  "idle.col.total": "本关总造价",
  "idle.col.g": "每投金币",
  "idle.col.gHint": "×1 投掷的目标金币收入（与总造价页同一口径），不是某一投实际拿到的数。",
  "idle.col.perTap": "每下金币（最少~最多）",
  "idle.col.taps": "只靠点击通关要点几下",
  "idle.col.tapsHint": "本关总造价 ÷ 每下金币，给运营感受量级用。",
  "u.star": "星",
  "u.plusMinusStar": "± 星",
  "pt.params.note": "这几项只在萌宠小镇下生效。体系本身在「初始化配置」里选，跟玩法体系一起。",
  "pt.params.wrongSystem": "当前版本的建筑体系不是萌宠小镇，这几项配了也不生效。",
  "pt.params.goInit": "去初始化配置里选体系",
  // ── 宠物升星档（cfg_pet_star + cfg_main_common 五列）。升星功能是 V2，V1 只配不用 ──
  "nav.pet-star": "升星档",
  "petStar.grp.global": "升星 · 全局参数",
  "petStar.notice.v1": "本页配的东西当前都不生效：升星功能要到养成版才开放，玩家的宠物恒为 1★。现在配是为了客户端能显示「升星功能即将开放」的预告，上线时不用再改一遍。",
  "petStar.max": "星级上限",
  "petStar.max.hint": "满级线。玩家把某只宠物升到这个星数就算满级，之后再领到同一只会改发金币。",
  "petStar.maxedCoin": "满级替代金币",
  "petStar.maxedCoin.hint": "抽到已满级宠物时改发的金币。0表示不发金币；宠物品质保底仍按领养规则处理，不控制下次出现。",
  "petStar.maxedMultiplier": "替代金币吃倍数",
  "petStar.maxedMultiplier.hint": "勾上则替代金币按本次掷骰的倍数放大，与棋盘其他金币奖励一致。",
  "petStar.maxedCountsAsAdopt": "算一次领养",
  "petStar.maxedCountsAsAdopt.hint": "替代金币是否视为一次完整领养，用于处理宠物品质保底计数；不影响玩法出现概率。",
  "nav.idol-intimacy": "亲密度配置",
  "nav.merge": "合成与订单",
  "nav.merge-item": "物品",
  "nav.merge-generator": "生成器",
  "nav.merge-board": "棋盘",
  "nav.merge-order": "订单配置",
  "nav.merge-energy": "体力与倍率",
  "nav.merge-warehouse": "仓库",
  "nav.merge-dex": "图鉴",
  "nav.merge-intimacy-reward": "亲密度物品奖励",
  "navGroup.g-merge": "合成与订单",
  "pp.tab.merge": "合成与订单",
  "merge.notice.empty": "这一版还没有配合成玩法：七块全空时发布也合法",
  "merge.cancel": "取消",
  "merge.save": "保存",
  "merge.close": "关闭",
  "merge.items.new": "新建物品",
  "merge.items.search": "搜索 id / 名称",
  "merge.filter.all": "全部",
  "merge.filter.merge": "可合成",
  "merge.filter.produce": "生成器",
  "merge.filter.collect": "可领取",
  "merge.filter.sell": "可售",
  "merge.col.item": "物品",
  "merge.col.level": "等级",
  "merge.col.abilities": "能力",
  "merge.col.ops": "操作",
  "merge.chain.loose": "无合成关系",
  "merge.cap.merge": "合成 → {to}",
  "merge.cap.produce": "生成器 {kind} · 容量 {cap} · 冷却 {cd}",
  "merge.cap.collect": "领取 {kind} ×{n}",
  "merge.cap.sell": "售 碎片 {n}",
  "merge.kind.unlimited": "无限",
  "merge.kind.cooldown": "有 CD",
  "merge.kind.limited": "有限",
  "merge.reward.energy": "体力",
  "merge.reward.fragment": "碎片",
  "merge.reward.gems": "钻石",
  "merge.op.edit": "编辑",
  "merge.op.copy": "复制",
  "merge.op.delete": "删除",
  "merge.op.published": "已发布过，不能删除",
  "merge.op.deletePublished": "物品 {id} 已经发布过。删除后这一版生效时，所有玩家手里的 {id} 都会消失，要它的订单作废，不补偿。确定删除？",
  "merge.dlg.item.new": "新建物品",
  "merge.dlg.item.edit": "编辑物品 {id}",
  "merge.dlg.item.view": "查看物品 {id}",
  "merge.f.id": "id",
  "merge.f.name": "名称",
  "merge.f.desc": "简介",
  "merge.f.descHint": "底栏信息卡只有一行：每种语言最多 {max}，全角（汉字、假名、全角标点）算 2、其余算 1，约 {cjk} 个汉字或 {max} 个字母",
  "merge.f.icon": "图标",
  "merge.f.chain": "合成链 · 等级",
  "merge.f.byMerge": "按合成关系计算",
  "merge.f.mergeTo": "合成为",
  "merge.f.produceInGen": "在生成器页配置",
  "merge.f.collectGet": "领取得到",
  "merge.f.amount": "数量",
  "merge.f.sellPrice": "售价（碎片）",
  "merge.f.difficulty": "难度分值",
  "merge.f.difficultyHint": "订单引擎用：车道窗口是需求难度的和。留空按默认 2^(等级−1)",
  "merge.f.orderable": "可下单",
  "merge.f.orderable.defaultYes": "默认（可以，链首除外）",
  "merge.f.orderable.defaultNo": "默认（链首不可）",
  "merge.f.orderable.yes": "可以",
  "merge.f.orderable.no": "不可以",
  "merge.refs.title": "被引用",
  "merge.refs.generator": "生成器产出",
  "merge.refs.order": "订单模板需求",
  "merge.refs.board": "棋盘初始物品",
  "merge.refs.intimacy": "亲密度奖励",
  "merge.refs.none": "没有被引用",
  "merge.err.id": "id 只能是字母、数字、下划线、连字符，不超过 64 位",
  "merge.err.idDup": "id 已存在",
  "merge.err.nameEn": "英文名称必填",
  "merge.err.descEn": "简介填了就得有英文",
  "merge.err.descLen": "简介（{lang}）超长：{n} / {max}（全角算 2）",
  "merge.err.sellCollect": "售卖与领取互斥，只能开一个",
  "merge.gen.add": "添加生成器链",
  "merge.col.chain": "合成链",
  "merge.col.produceLevels": "生产等级",
  "merge.col.dropCount": "产出物品数",
  "merge.gen.none": "还没有配生产的链",
  "merge.gen.noChain": "没有未配生产的链",
  "merge.gen.produce": "生产",
  "merge.gen.kind": "生成器类型",
  "merge.gen.energyCost": "每次耗体力",
  "merge.gen.capacity": "容量",
  "merge.gen.cooldownSec": "冷却（秒）",
  "merge.gen.multiplier": "可用倍率",
  "merge.gen.drops": "产出权重（两位小数，发布时乘 100 存整数）",
  "merge.gen.addDrop": "添加产出物品",
  "merge.gen.total": "合计",
  "merge.gen.remove": "移除",
  "merge.gen.overTotal": "总权重超过 {max}",
  "merge.board.title": "棋盘",
  "merge.board.fixed": "棋盘固定 {cols} × {rows}，格号行优先",
  "merge.board.slots": "订单槽数量",
  "merge.board.cell": "格 {n}",
  "merge.board.empty": "空",
  "merge.board.notConfigured": "这一版还没配棋盘",
  "merge.board.create": "配置",
  "merge.board.coverAll": "全部遮住",
  "merge.board.uncoverAll": "全部揭开",
  "merge.board.locked": "锁定",
  "merge.board.stats": "开放 {open} 格 · 遮住 {hidden} 格 · 锁定 {locked} 件",
  "merge.board.setup": "棋盘设置",
  "merge.board.state.normal": "普通",
  "merge.board.state.cover": "遮住",
  "merge.board.state.lock": "锁定",
  "merge.board.lockNeedsItem": "格里有物品才能锁定",
  "merge.board.removeItem": "移除物品",
  "merge.board.dropHere": "拖物品到这里",
  "merge.board.pool": "物品候选区",
  "merge.board.poolHint": "拖进右边棋盘的格子；棋盘上的物品拖回这里就是移除",
  "merge.board.poolReadOnly": "这一版不是草稿，只能看",
  "merge.board.poolDrop": "松手移除这件物品",
  "merge.board.search": "搜索名称或 id",
  "merge.board.loose": "散件",
  "merge.board.noItems": "这一版还没有物品，先到「物品」页添加",
  "merge.board.noMatch": "没有匹配的物品",
  "merge.npc.new": "新建 NPC",
  "merge.oc.tab.lanes": "车道",
  "merge.oc.tab.trees": "介绍订单",
  "merge.oc.tab.cleanup": "清理订单",
  "merge.oc.tab.npcs": "NPC",
  "merge.oc.init": "用默认起点初始化",
  "merge.oc.notConfigured": "这一版还没有订单配置文档。初始化后按本版本的物品与 NPC 调整；没配不能发布。",
  "merge.oc.notConfiguredLocked": "这一版还没有订单配置文档，发布过的版本改不了；要配先建草稿版本。",
  "merge.oc.preview.needVersion": "保存成版本后才有实时预览（命中率、池子按已存的物品与 NPC 算）。",
  "merge.oc.preview.title": "预览",
  "merge.oc.preview.pending": "重算中…",
  "merge.oc.preview.unavailable": "预览不可用：{msg}",
  "merge.oc.preview.pool": "可抽物品 {n} 件",
  "merge.oc.preview.hit": "命中率 {p}",
  "merge.oc.problems.structural": "结构问题（发布会拦）",
  "merge.oc.problems.catalog": "引用问题（发布会拦）",
  "merge.oc.problems.sampling": "抽样问题（发布会拦）",
  "merge.oc.lane.easy": "简单",
  "merge.oc.lane.medium": "中等",
  "merge.oc.lane.hard": "困难",
  "merge.oc.minScore": "难度下限",
  "merge.oc.maxScore": "难度上限",
  "merge.oc.err.window": "上限要大于下限",
  "merge.oc.slots": "槽",
  "merge.oc.slot": "槽 {n}",
  "merge.oc.addSlot": "加一槽",
  "merge.oc.slotTotal": "三条车道共 {n} / {max} 槽",
  "merge.oc.addCharacter": "加角色…",
  "merge.oc.noCharacter": "至少选一个 NPC",
  "merge.oc.groups.title": "奖励组",
  "merge.oc.groups.add": "加一组",
  "merge.oc.groups.hint": "碎片按难度分算：每分 × 难度分 × 等级倍率，不足补到最少值，再向上凑到凑整单位的倍数。物品奖励进待领取区。被引用的组不能删、代号不能改。",
  "merge.oc.groups.refNone": "未引用",
  "merge.oc.col.code": "代号",
  "merge.oc.col.label": "名称",
  "merge.oc.col.kind": "类型",
  "merge.oc.col.params": "参数",
  "merge.oc.col.refBy": "被引用",
  "merge.oc.kind.currency": "碎片",
  "merge.oc.kind.item": "物品",
  "merge.oc.perScore": "每分",
  "merge.oc.min": "最少",
  "merge.oc.roundTo": "凑整到",
  "merge.oc.itemToPending": "进待领取区",
  "merge.oc.tiers.title": "等级倍率",
  "merge.oc.tiers.hint": "按棋盘 idol 的亲密度等级取最后一个「从等级 ≤ 等级」的档；第一档从 1 级起。",
  "merge.oc.tiers.fromLevel": "从等级",
  "merge.oc.tiers.multiplier": "倍率",
  "merge.oc.tiers.first": "1 起",
  "merge.oc.tiers.add": "加一档",
  "merge.oc.gen.title": "抽取参数",
  "merge.oc.gen.maxItemLevel": "最高物品级（引擎 0 起）",
  "merge.oc.gen.ourLevel": "即我们的第 {n} 级",
  "merge.oc.gen.min": "每单最少几种",
  "merge.oc.gen.max": "每单最多几种（≤ {max}）",
  "merge.oc.gen.attempts": "落窗尝试次数",
  "merge.oc.gen.hint": "只有链上有生成器、玩家已发现、不超过最高级且可下单的物品进池子。尝试用完取最接近窗口的那一组。",
  "merge.oc.trees.title": "介绍订单",
  "merge.oc.trees.new": "新建树",
  "merge.oc.trees.hint": "树是手配的订单链，按触发条件挂到棋盘上，订单靠前置排先后。新手树只有一棵、新玩家触发。",
  "merge.oc.tree.none": "还没有树",
  "merge.oc.tree.notSelected": "点一棵树编辑",
  "merge.oc.tree.editing": "编辑树 {id}",
  "merge.oc.tree.id": "树 id",
  "merge.oc.tree.name": "名称",
  "merge.oc.tree.kind": "类型",
  "merge.oc.tree.kind.tutorial": "新手",
  "merge.oc.tree.kind.intro": "引导",
  "merge.oc.tree.trigger": "触发",
  "merge.oc.tree.until": "到等级为止",
  "merge.oc.tree.untilHint": "空 = 不限；等级超过后没完成的介绍订单撤下",
  "merge.oc.tree.orders": "订单",
  "merge.oc.tree.addOrder": "加一单",
  "merge.oc.err.treeId": "只能小写字母、数字、- 和 _，1～32 位",
  "merge.oc.col.trigger": "触发",
  "merge.oc.col.until": "到等级",
  "merge.oc.col.orders": "订单数",
  "merge.oc.col.orderId": "订单 id",
  "merge.oc.col.character": "角色",
  "merge.oc.col.objectives": "需求",
  "merge.oc.col.rewardTree": "奖励",
  "merge.oc.col.minLevel": "最低等级",
  "merge.oc.col.lockedBy": "前置",
  "merge.oc.trigger.newPlayer": "新玩家",
  "merge.oc.trigger.producerUnlocked": "发现生成器链",
  "merge.oc.trigger.level": "到达等级",
  "merge.oc.dlg.treeOrder.new": "新建介绍订单",
  "merge.oc.dlg.treeOrder.edit": "编辑介绍订单 {id}",
  "merge.oc.dlg.treeOrder.view": "查看介绍订单 {id}",
  "merge.oc.treeOrder.addObjective": "加需求",
  "merge.oc.treeOrder.coins": "碎片奖励",
  "merge.oc.treeOrder.items": "物品奖励",
  "merge.oc.treeOrder.addItem": "加物品",
  "merge.oc.treeOrder.lockedBy": "前置订单",
  "merge.oc.treeOrder.noOthers": "这棵树还没有别的订单",
  "merge.oc.err.orderId": "订单 id 只能字母、数字、- 和 _，1～32 位",
  "merge.oc.err.orderIdDup": "订单 id 重复",
  "merge.oc.err.character": "要选角色",
  "merge.oc.err.objectives": "至少一条需求",
  "merge.oc.cleanup.title": "清理订单",
  "merge.oc.cleanup.enabled": "启用",
  "merge.oc.cleanup.minLevel": "最低等级",
  "merge.oc.cleanup.group": "奖励组（碎片）",
  "merge.oc.cleanup.mult": "奖励倍率",
  "merge.oc.cleanup.max": "单笔上限",
  "merge.oc.cleanup.hint": "棋盘上堆着池子里某件顶级物品够数时派一单清理。每条成功命令后查一次。",
  "merge.oc.cleanup.rules": "数量规则",
  "merge.oc.cleanup.maxDifficulty": "难度 ≤",
  "merge.oc.cleanup.amount": "要几件",
  "merge.oc.cleanup.unlimited": "其余",
  "merge.oc.cleanup.addRule": "加一条",
  "merge.oc.cleanup.rulesHint": "按顶级物品的难度取第一条满足的规则；最后一条兜底，不能删。",
  "merge.oc.cleanup.exclude": "排除的链",
  "merge.oc.cleanup.addChain": "加一条链…",
  "merge.oc.cleanup.pool": "清理池预览",
  "merge.oc.cleanup.poolSummary": "清理池 {n} 件 · 最高 {max} 碎片",
  "merge.oc.cleanup.poolEmpty": "清理开着但池子是空的，发布会拦。看看排除的链和物品的可下单。",
  "merge.oc.cleanup.poolNone": "池子为空",
  "merge.oc.cleanup.poolHint": "非生成器链的顶级物品进池；奖励 = 奖励组按难度分算 × 倍率，截到单笔上限。",
  "merge.oc.col.topItem": "顶级物品",
  "merge.oc.col.difficulty": "难度",
  "merge.oc.col.amount": "要几件",
  "merge.oc.col.cleanupReward": "奖励",
  "merge.oc.npc.hint": "NPC 是车道槽和介绍订单的角色来源；被引用或已发布过的不能删。",
  "merge.oc.fragments": "碎片 {n}",
  "merge.col.avatar": "头像",
  "merge.col.refBy": "被引用",
  "merge.col.name": "名称",
  "merge.npc.missing": "未填",
  "merge.unlimited": "不限",
  "merge.dlg.npc.new": "新建 NPC",
  "merge.dlg.npc.edit": "编辑 NPC {id}",
  "merge.dlg.npc.view": "查看 NPC {id}",
  "merge.energy.title": "体力",
  "merge.energy.initial": "新账号初始体力",
  "merge.energy.cap": "自然恢复停止线",
  "merge.energy.interval": "恢复 1 点间隔（毫秒）",
  "merge.energy.eq": "= {n}",
  "merge.energy.notConfigured": "这一版还没配",
  "merge.energy.create": "配置",
  "merge.mult.title": "生产倍率",
  "merge.mult.max": "倍率上限",
  "merge.mult.col.multiplier": "倍率",
  "merge.mult.col.level": "解锁亲密度等级",
  "merge.mult.always": "始终可用",
  "merge.wh.title": "解锁与免费格",
  "merge.wh.unlock": "解锁亲密度等级",
  "merge.wh.free": "免费格数",
  "merge.wh.unit.level": "级",
  "merge.wh.unit.slot": "格",
  "merge.wh.max": "每块棋盘最多",
  "merge.wh.total": "全部买齐",
  "merge.wh.slots": "{n} 格",
  "merge.wh.gems": "{n} 钻石",
  "merge.wh.prices": "付费格价格",
  "merge.wh.col.paid": "付费格",
  "merge.wh.col.slot": "仓库格号",
  "merge.wh.col.gems": "钻石",
  "merge.wh.nth": "第 {n} 格",
  "merge.wh.add": "加一格",
  "merge.pwh.title": "生成器仓库",
  "merge.pwh.count": "已配格数",
  "merge.pwh.allOpen": "全部开齐",
  "merge.pwh.level": "亲密度 {n} 级",
  "merge.pwh.col.order": "顺序",
  "merge.pwh.col.chain": "生成器链",
  "merge.pwh.col.top": "能放的（链顶）",
  "merge.pwh.col.level": "开格亲密度等级",
  "merge.pwh.early": "随仓库一起开",
  "merge.pwh.badChain": "不是生成器链的链首",
  "merge.pwh.pick": "选生成器链",
  "merge.pwh.noChain": "生成器链都配上了",
  "merge.pwh.up": "上移",
  "merge.pwh.down": "下移",
  "merge.dex.col": "图鉴奖励",
  "merge.dex.reward": "{kind} {n}",
  "merge.dex.kind.none": "没有",
  "merge.dex.f.config": "在「图鉴」页配置",
  "merge.dex.f.afterSave": "保存后在「图鉴」页配",
  "merge.dex.f.leave": "弹窗里的修改还没保存，跳去图鉴页会丢掉。确定跳过去？",
  "merge.dex.empty": "这一版还没有物品，先在「物品」页建好再来配图鉴奖励",
  "merge.dex.intro": "玩家在任一棋盘上见过的物品就算解锁。这里按合成链给每一级配解锁奖励（体力或钻石），改哪一格都按整条链写回各物品；不配就没有奖励。图鉴的页签与卡片顺序在下面的「分组」里配；不分组时按合成关系自动排。",
  "merge.dex.sum.count": "配了奖励的物品",
  "merge.dex.sum.countValue": "{n} 件",
  "merge.dex.sum.energy": "全部领完 · 体力",
  "merge.dex.sum.gems": "全部领完 · 钻石",
  "merge.dex.gen": "生成器链",
  "merge.dex.summary": "{n} 级 · {m} 级有奖励",
  "merge.dex.series": "系列名",
  "merge.dex.seriesHint": "图鉴卡片的标题，只写在链首上；填了任一语言就得有英文",
  "merge.dex.seriesPh": "没填用链首物品名：{name}",
  "merge.dex.stray": "{ids} 不是链首，上面的系列名不会显示",
  "merge.dex.strayClear": "清掉",
  "merge.dex.head": "链首",
  "merge.dex.kind": "奖励",
  "merge.dex.amount": "数量",
  "merge.dex.amountPh": "正整数",
  "merge.dex.amountBad": "要填正整数",
  "merge.dexg.title": "分组",
  "merge.dexg.count": "{n} 组 · 共 {m} 条链",
  "merge.dexg.intro": "图鉴底部一个页签一组，从上到下就是页签从左到右的顺序；组里链的顺序就是卡片顺序。链用链首物品认。配了分组就得把每条链恰好分进一组，不然发布会被拦；一组都不配 = 图鉴不分组，一张列表、没有页签。组名只给管理端看。",
  "merge.dexg.none": "还没分组：玩家看到的图鉴是一张列表，没有页签。",
  "merge.dexg.add": "加一组",
  "merge.dexg.tab": "第 {n} 个页签",
  "merge.dexg.id": "组 id",
  "merge.dexg.idBad": "只能用字母、数字、_ 和 -，最长 64 个字符",
  "merge.dexg.idDup": "和别的组重复了",
  "merge.dexg.name": "组名",
  "merge.dexg.namePh": "只给管理端认组用，玩家看不到",
  "merge.dexg.noIcon": "没传图标",
  "merge.dexg.noChain": "还没有链",
  "merge.dexg.up": "上移",
  "merge.dexg.down": "下移",
  "merge.dexg.remove": "移出",
  "merge.dexg.removeGroup": "删除这组",
  "merge.dexg.chains": "组里的链（{n} 条）",
  "merge.dexg.levels": "{n} 级",
  "merge.dexg.stale": "不是现在的链首",
  "merge.dexg.dup": "分进了不止一组",
  "merge.dexg.pick": "选一条还没分组的链加进来",
  "merge.dexg.allPlaced": "链都分完了",
  "merge.dexg.unassignedWarn": "还有 {n} 条链没分进任何组，发布会被拦",
  "merge.dexg.allDone": "每条链都分进了一组",
  "merge.dexg.unassigned": "没分组的链（{n} 条）",
  "merge.dexg.unassignedPlain": "全部的链（{n} 条，不分组时都在同一张列表里）",
  "merge.dex.pp.title": "图鉴（账号级，所有棋盘共用）",
  "merge.dex.pp.unlocked": "已解锁 {n} / {total} 件",
  "merge.dex.pp.pending": "待领取 {n} 件",
  "merge.dex.pp.claimed": "已领 {n} 件 · 体力 {energy} · 钻石 {gems}",
  "merge.dex.pp.col.chain": "链",
  "merge.dex.pp.col.unlock": "解锁",
  "merge.dex.pp.yes": "已解锁",
  "merge.dex.pp.no": "没解锁",
  "merge.dex.pp.claimedTag": "已领",
  "merge.dex.pp.noReward": "无奖励",
  "merge.dex.pp.note": "解锁读存档的 discovered（物品在任一棋盘上出现过就算），已领读 dexClaimed；待领取 = 已解锁、配置有奖励、还没领，现算不存。奖励一栏按存档的会话配置版本 #{v} 算，领过的以最近命令里记的实领为准。",
  "merge.dex.pp.noConfig": "读不到存档的会话配置版本 #{v}（{msg}），图鉴奖励和状态没法算",
  "merge.dex.pp.seen": "见过的物品 {n} 件：{ids}",
  "merge.dex.pp.claimAction": "领取图鉴奖励 {item} · {reward}",
  "merge.dex.pp.energyCredit": "体力 +{n}",
  "merge.bind.title": "奖励绑定（只读）",
  "merge.bind.logical": "逻辑奖励",
  "merge.bind.owner": "资产拥有方",
  "merge.bind.asset": "assetId",
  "merge.bind.how": "入账方式",
  "merge.bind.note": "由服务端固定，这里只看不改",
  "merge.intimacy.new": "新建规则",
  "merge.col.rule": "ruleId",
  "merge.col.threshold": "门槛等级",
  "merge.col.items": "奖励物品",
  "merge.dlg.rule.new": "新建规则",
  "merge.dlg.rule.edit": "编辑规则 {id}",
  "merge.dlg.rule.view": "查看规则 {id}",
  "merge.f.threshold": "门槛：亲密度达到",
  "merge.f.addItem": "添加物品",
  "merge.err.items": "至少一件物品",
  "nav.merge-removal": "物品删除",
  "merge.pp.fromMail": "邮件 {id}",
  "merge.pp.mail": "邮件发放",
  "merge.pp.mail.scanFrom": "扫描起点 {at}",
  "merge.pp.col.mail": "邮件",
  "merge.pp.col.sentAt": "发出时间",
  "merge.pp.col.state": "状态",
  "merge.pp.mail.done": "已发完",
  "merge.pp.mail.void": "过期没领，作废",
  "merge.pp.mail.open": "进行中 {done}/{all} 块，还差 {stuck}",
  "merge.pp.removals": "运营删除",
  "merge.pp.removals.cursor": "已处理到序号 {n}",
  "merge.pp.removals.allIdols": "所有 idol",
  "merge.pp.col.seq": "序号",
  "merge.pp.col.removedAt": "发出时间",
  "merge.pp.col.removalIdol": "idol",
  "mergeRm.title": "物品删除",
  "mergeRm.intro": "把玩家身上的合成物品拿走：棋盘上的、待领取区里的，以及需求里有它的订单（空出的订单槽当场补新单）。不补偿，发出就不能撤回。玩家下次开合成会话时生效，删的是发出这一刻他身上有的。物品还在配置里，生成器照样能再产出；想让它彻底不出现，去配置里删物品或停用模板。没领的邮件里有它的，删除之后再领照样会发，要收回还得去邮件页撤回那封。",
  "mergeRm.send": "发送删除",
  "mergeRm.f.scope": "对谁",
  "mergeRm.scope.user": "一名玩家",
  "mergeRm.scope.all": "全服",
  "mergeRm.f.user": "玩家",
  "mergeRm.f.user.hint": "只选一个人；选第二个会替换掉第一个",
  "mergeRm.f.idol": "idol",
  "mergeRm.idol.all": "所有 idol",
  "mergeRm.f.items": "物品",
  "mergeRm.f.items.hint": "当前生效版本里的合成物品，一次最多 20 个",
  "mergeRm.f.items.ph": "选物品",
  "mergeRm.f.reason": "原因",
  "mergeRm.f.reason.ph": "200 字以内，进历史和审计",
  "mergeRm.send.btn": "发送删除",
  "mergeRm.need.user": "选一名玩家",
  "mergeRm.need.items": "至少选一个物品",
  "mergeRm.need.reason": "填原因",
  "mergeRm.history": "历史",
  "mergeRm.empty": "还没有发过删除",
  "mergeRm.col.seq": "序号",
  "mergeRm.col.at": "发出时间",
  "mergeRm.col.scope": "对谁",
  "mergeRm.col.idol": "idol",
  "mergeRm.col.items": "物品",
  "mergeRm.col.reason": "原因",
  "mergeRm.col.operator": "操作人",
  "mergeRm.confirm.title": "确认删除",
  "mergeRm.confirm.ok": "确定删除",
  "mergeRm.confirm.allWarn": "这是全服删除：发出时已经注册的每个玩家，下次进合成都会被拿走这些物品。不能撤回，不补偿。",
  "mergeRm.confirm.userWarn": "这名玩家下次进合成时会被拿走这些物品。不能撤回，不补偿。",
  "mergeRm.confirm.orders": "需求里有这些物品的订单也会一起删掉，空出的订单槽当场补新单。",
  "mergeRm.confirm.type": "全服删除要手动输入一遍物品 id 才能确定（用逗号或空格隔开）：{ids}",
  "mergeRm.toast.sent": "已发出，序号 {seq}",
  "mergeRm.toast.replayed": "这条之前已经发出过（序号 {seq}），没有重复发",
  "merge.pp.none": "这名玩家还没有合成存档",
  "merge.pp.version": "配置版本",
  "merge.pp.energy": "独立体力",
  "merge.pp.session": "会话",
  "merge.pp.updated": "存档时间",
  "merge.pp.idol": "idol 棋盘",
  "merge.pp.notCreated": "还没创建任何棋盘",
  "merge.pp.intimacyLevel": "亲密度 {n}",
  "merge.pp.rewards": "待领取区",
  "merge.pp.grantedTo": "奖励已发放到 {n} 级",
  "merge.pp.col.unit": "来源规则",
  "merge.pp.col.item": "物品",
  "merge.pp.col.claimFrom": "可领取起",
  "merge.pp.col.status": "状态",
  "merge.pp.col.slot": "格",
  "merge.pp.pending": "待领取",
  "merge.pp.claimed": "已领取",
  "merge.pp.events": "亲密度升级事件",
  "merge.pp.col.receivedAt": "时间",
  "merge.pp.col.eventId": "事件 id",
  "merge.pp.board": "棋盘（{n} 格）",
  "merge.pp.hidden": "未揭开",
  "merge.pp.wh.title": "仓库",
  "merge.pp.wh.slots": "免费 {base} 格 · 买了 {bought} 格",
  "merge.pp.wh.used": "已存 {n} / {cap} 件",
  "merge.pp.wh.col.seq": "顺序",
  "merge.pp.wh.col.boughtAt": "购买时间",
  "merge.pp.wh.col.paidSlot": "付费格",
  "merge.pp.wh.col.gems": "钻石",
  "merge.pp.wh.done": "已扣款 · 已加格",
  "merge.pp.wh.void": "钻石不够 · 未扣",
  "merge.pp.wh.charging": "处理中",
  "merge.pp.pwh.title": "生成器仓库",
  "merge.pp.pwh.open": "已开 {n} 格",
  "merge.pp.pwh.used": "存了 {n} 件",
  "merge.pp.pwh.col.slot": "格（按配置顺序）",
  "merge.pp.pwh.col.chain": "生成器链",
  "merge.pp.pwh.col.stored": "存着的",
  "merge.pp.pwh.col.note": "说明",
  "merge.pp.pwh.empty": "空",
  "merge.pp.pwh.removed": "配置里已删这格",
  "merge.pp.pwh.removedNote": "取出后不再显示",
  "merge.pp.locked": "锁定",
  "merge.pp.col.instance": "实例",
  "merge.pp.col.createdAt": "创建时间",
  "merge.pp.col.producer": "生成器",
  "merge.pp.producer.cooling": "冷却中，{at} 恢复",
  "merge.pp.producer.charges": "剩 {n} · 已产 {m}",
  "merge.pp.orders": "订单",
  "merge.pp.col.kind": "类型",
  "merge.pp.col.where": "车道 / 槽 · 树 / 订单",
  "merge.pp.kind.lane": "车道",
  "merge.pp.kind.tree": "树",
  "merge.pp.kind.cleanup": "清理",
  "merge.pp.fallback": "兜底",
  "merge.pp.reward.fragment": "碎片 {n}",
  "merge.pp.trees": "已开树 {opened} · 已完成介绍订单 {done} 张",
  "merge.pp.col.order": "订单",
  "merge.pp.col.npc": "NPC",
  "merge.pp.col.requirements": "需求",
  "merge.pp.col.reward": "奖励",
  "merge.pp.col.issuedAt": "下发时间",
  "merge.pp.col.canFulfill": "现在能交吗",
  "merge.pp.enough": "材料够",
  "merge.pp.short": "材料不够",
  "merge.pp.recent": "最近命令",
  "merge.pp.col.action": "动作",
  "merge.pp.col.result": "结果",
  "merge.pp.col.credit": "奖励到账",
  "merge.pp.ok": "成功",
  "merge.pp.rejected": "拒绝 {code}",
  "merge.pp.act.StoreItem": "存入仓库",
  "merge.pp.act.RetrieveItem": "从仓库取出",
  "merge.pp.act.StoreProducer": "存入生成器仓库",
  "merge.pp.act.RetrieveProducer": "从生成器仓库取出",
  "merge.pp.credit.credited": "已到账",
  "merge.pp.credit.pending": "待到账",
  "merge.pp.credit.blocked": "到账受阻 · 自动重试",
  "nav.idol-photos": "关卡相册",
  "nav.idol-vip-photos": "VIP 照片",
  "nav.idol-vip-videos": "VIP 视频",
  "album.iss.badRule": "解锁规则不在可选范围内",
  "album.iss.badLevel": "亲密度等级必须是 ≥ 0 的整数",
  "album.iss.needLevel": "规则为「亲密度等级」时，等级必须 ≥ 1",
  "album.iss.levelMustBeZero": "只有「亲密度等级」规则看等级，其它规则等级必须是 0",
  "album.iss.noPuzzle": "所属拼图在本版本里不存在（拼图删了，这张照片也要删或改挂）",
  "album.iss.badSlot": "序位必须是 ≥ 2 的整数（第 1 张是拼图完成照，自动生成）",
  "album.iss.noImg": "缩略图和原图都要上传",
  "album.iss.noIdol": "请选择 idol（名册里要有这个人）",
  "album.iss.noCover": "请上传封面",
  "album.iss.badPublishAt": "发布时间不合法",
  "media.hint.thumbBig": "缩略图偏大，列表会加载慢",
  "media.hint.originalBig": "图片超过 10MB，玩家打开会很慢",
  "media.hint.aspectPortrait": "比例不是 3:4 竖图，展示时会被裁切",
  "media.hint.aspectCover": "比例不是 16:9，封面展示时会被裁切",
  "album.idolInactive": "（未上架）",
  "album.rule.stage_redeemed": "兑换拼图后解锁",
  "album.rule.stage_assembled": "拼完拼图后解锁",
  "album.rule.intimacy_lv": "亲密度等级",
  "album.rule.vip": "VIP 可见",
  "album.rule.free": "免费可见",
  "album.rule.unknown": "未知规则：{v}",
  "album.ruleHint": "决定玩家什么时候能看到原图；没解锁时只能看到缩略图",
  "album.fld.level": "亲密度等级",
  "album.levelHint": "达到这个亲密度等级才解锁，至少 1 级",
  "album.levelOffHint": "只有「亲密度等级」规则才看等级，当前规则固定为 0",
  "album.fld.publishAt": "发布时间",
  "album.publishAtHint": "按所选时区填写；留空 = 版本生效后立即可见。没到时间玩家看不到这一条",
  "album.publishNow": "立即",
  "album.video.upload": "上传视频",
  "album.video.uploaded": "已上传",
  "album.video.none": "未上传",
  "album.video.reupload": "重新上传",
  "upload.mp4.corrupt": "文件结构损坏，读不下去，请重新导出后再传",
  "upload.mp4.duration": "读不出视频时长，请重新导出后再传",
  "upload.mp4.no_video": "文件里没有视频画面",
  "upload.mp4.codec": "视频需要 H.264 编码、音频需要 AAC 编码，请用下面的命令转码后再传",
  "upload.mp4.encrypted": "不支持加密的 mp4",
  "upload.mp4.fragmented": "不支持分片 mp4，请用下面的命令转码后再传",
  "upload.mp4.moov_not_first": "视频索引不在文件开头（faststart），玩家要下载完整个文件才能开始播放。请用下面的命令转码后再传",
  "upload.mp4.not_mp4": "这不是 mp4 文件",
  "upload.video.tooBig": "视频不能超过 {max}，选中的有 {size}MB",
  "upload.video.badType": "视频只支持 mp4，选中的是 {type}",
  "album.video.formatHint": "只收 mp4：视频 H.264、音频 AAC、索引在文件开头，不超过 50MB。不符合的文件可用下面的命令转码：",
  "album.video.preview": "预览",
  "album.video.uploading": "上传中…（文件大时需要一点时间）",
  "album.photo.dlgNew": "新建相册照片",
  "album.photo.dlgTitle": "相册照片 {id}",
  "album.fld.puzzle": "所属拼图",
  "album.puzzleGone": "拼图 {id} 已不在本版本",
  "album.fld.slot": "序位",
  "album.slotHint": "这一关里的第几张，从 {min} 起（第 1 张是拼图完成照，自动生成）。可以随时对调",
  "album.slotDup": "同一关里有启用的照片序位相同，发布前要错开",
  "album.fld.thumb": "缩略图",
  "album.thumbHint": "未解锁玩家可见，请上传模糊图或打码图",
  "album.fld.img": "原图",
  "album.imgHint": "解锁后玩家看到的完整照片，建议 3:4 竖图",
  "album.thumbSame": "缩略图和原图是同一张：没解锁的玩家也能看到原图",
  "album.fld.unlockRule": "解锁规则",
  "album.vipPhoto.dlgNew": "新建 VIP 照片",
  "album.vipPhoto.dlgTitle": "VIP 照片 {id}",
  "album.fld.idol": "所属 idol",
  "album.fld.lockRule": "可见规则",
  "stageImg.tagsHint": "可多选；下拉里没有的直接输入后回车添加，存下后下次新增 / 编辑图片时就能选",
  "stageImg.tagsPh": "选择或输入标签",
  "album.fld.sort": "排序",
  "album.sortHint": "数字小的排前面；相同时按 ID",
  "album.vipVideo.dlgNew": "新建 VIP 视频",
  "album.vipVideo.dlgTitle": "VIP 视频 {id}",
  "album.fld.cover": "封面",
  "album.coverHint": "建议 16:9 横图；没权限的玩家也能看到封面",
  "album.fld.video": "视频",
  "album.videoHint": "上传后自动带回时长，不需要手填",
  "album.fld.duration": "时长",
  "album.durationHint": "随视频上传自动得出，不能修改；没传视频时显示「—」",
  "album.col.puzzle": "拼图（关卡）",
  "album.col.slot": "序位",
  "album.dupTag": "重复",
  "album.coverTag": "完成照·自动",
  "album.col.thumb": "缩略图",
  "album.col.img": "原图",
  "album.col.rule": "规则",
  "album.coverRule": "拼完自动解锁",
  "album.col.level": "等级",
  "album.photo.add": "添加照片",
  "album.count": "共 {n} 条",
  "album.goPuzzle": "去拼图列表",
  "album.noPuzzle": "本版本还没有已保存的拼图，相册照片要挂在拼图下",
  "album.slotDupNotice": "有关卡里启用照片的序位重复（标「重复」的行）。草稿里可以先这样，发布前要错开",
  "album.photo.notice": "每关第 1 张是拼图完成照，由拼图的完成图自动生成，不在这里配置；这里从第 2 张起。",
  "album.empty": "还没有内容",
  "album.delTitle": "删除 {id}",
  "album.delBody": "删除后保存即从本草稿里移除；已发布的版本不受影响。",
  "album.col.idol": "idol",
  "stageImg.col.tags": "标签",
  "album.col.sort": "排序",
  "album.col.publishAt": "发布时间",
  "album.vipPhoto.add": "添加 VIP 照片",
  "album.iss.badImageType": "图片类型不在可选范围内（免费 / 碎片拼图 / VIP 拼图）",
  "album.iss.badPriceFrag": "碎片价必须是 ≥ 0 的整数",
  "album.iss.priceOnlyFrag": "只有碎片拼图能填碎片价，其它类型必须是 0",
  "nav.stage-images": "关卡图片",
  "stageImg.col.stageImg": "关卡主图",
  "stageImg.col.count": "图片数",
  "stageImg.col.types": "免费 / 碎片 / VIP",
  "stageImg.typeCounts": "{free} / {frag} / {vip}",
  "stageImg.pageNotice": "先选一位 idol，再列出她的关卡。关卡就是编号（1、2、3…），与拼图列表无关；新增关卡会自动带出 6 张图。图片数含第 1 张和停用的，上限 12。",
  "stageImg.dlgTitle": "关卡图片 · {idol} 第 {seq} 关",
  "stageImg.pickIdol": "先在上面选一位 idol，再列出她的关卡。",
  "stageImg.noHead": "这一关缺少第 1 张关卡图（发布会被拦）。删掉重建这一关会自动带出。",
  "stageImg.headCost": "拼完才开放后面的图",
  "stage.col.code": "关卡编号",
  "stage.col.reward": "关卡奖励",
  "stage.noReward": "无奖励",
  "stage.empty": "这位 idol 还没有关卡，点右上角「新增关卡」",
  "stage.add": "新增关卡",
  "stage.dlgNew": "新增关卡 · {idol}",
  "stage.codeHint": "从 1 起的整数，如 1、2、3。同一位 idol 下不能重复（发布时检查）",
  "stage.codeTaken": "这位 idol 已有同编号的关卡，发布前要改掉",
  "stage.newHint": "创建后自动带出 6 张图：关卡图、免费 ×3、碎片拼图、VIP 拼图，图片需要再上传。",
  "stage.disableHint": "停用后没拿到图的玩家看不到这一关；已经拿到图的玩家照样能看",
  "stage.rewardHint": "玩家第一次拼完第 1 张时发一次；之后再改奖励不补发",
  "stage.delTitle": "删除关卡 {code}",
  "stage.delBody": "这一关和它下面的 {n} 张图会一起删除。保存后从本草稿移除，已发布的版本不受影响；已经拿到图的玩家，发布后照样能看。",
  "stage.iss.noIdol": "请先选择 idol",
  "stage.iss.badCode": "编号应为从 1 起的整数，如 1、2、3",
  "stage.iss.rewardKind": "奖励只能是骰子、金币、拼图碎片、钻石、免费掷骰、万能卡",
  "stage.iss.rewardQty": "奖励数量必须是 ≥ 1 的整数",
  "stage.iss.unlockKind": "解锁花费只能是金币、拼图碎片，每种只能配一项",
  "stage.discardNew": "放弃新建",
  "stage.addCost": "加花费项",
  "stage.iss.unlockQty": "解锁花费必须是 ≥ 1 的整数（不花就清空）",
  "stage.col.unlock": "解锁条件",
  "stage.unlockNone": "仅需上一关拼完",
  "stage.unlockHint": "上一关第 1 张拼完是固定前提；这里的条件叠加，全部满足才开放。金币 / 碎片在玩家点解锁时一次扣完，空着 = 不花",
  "stage.unlockFree": "不花",
  "stage.unlockVip": "需要 VIP",
  "stage.unlockVipHint": "目前没有 VIP 开通入口：勾了以后这一关对所有玩家都锁着（后面的关也会被它挡住）",
  "stage.iss.badSlot": "序号必须是 ≥ 1 的整数",
  "stage.iss.badGridN": "规格必须选 4×4 / 5×5 / 6×6",
  "stageImg.generateThumb": "生成小图",
  "stageImg.thumbLoadError": "无法读取原图，请重试；若仍失败，请重新上传原图后裁剪。",
  "crop.title": "截取拼图缩略图",
  "crop.hint": "在原图上拖动方框选一块正方形，拖右下角调整大小。确认后它会作为这张图的缩略图（拼图用）单独保存，原图不变；点「跳过」保留原来的缩略图。",
  "crop.size": "选区 {size}×{size} 像素，保存为 {out}×{out}",
  "crop.skip": "跳过",
  "crop.confirm": "保存缩略图",
  "crop.saving": "保存中…",
  "crop.err.canvas": "浏览器没能导出裁剪结果，请换一张图或换个浏览器再试",
  "stageImg.title": "关卡图片",
  "stageImg.count": "{n}/{max}（含第 1 张和停用的）",
  "stageImg.hint": "第 1 张是关卡图，玩家到了这一关就能拼；拼完第 1 张后第 2 张起才开放（免费、碎片拼图直接拼，都不花碎片；VIP 锁定）。拖动或点 ↑↓ 调整第 2 张起的顺序，客户端按这个顺序展示。",
  "stageImg.add": "添加一张",
  "stageImg.col.type": "类型",
  "stageImg.col.grid": "规格",
  "stageImg.col.cost": "花费",
  "stageImg.col.intimacy": "亲密度",
  "stageImg.intimacyHint": "第一次拼完这张，给这一关的 idol 加多少亲密度；0 = 不加。受亲密度配置里「拼完拼图」的每日上限约束",
  "stageImg.type.stage": "关卡图",
  "stageImg.type.free": "免费",
  "stageImg.type.frag": "碎片拼图",
  "stageImg.type.vip": "VIP 拼图",
  "stageImg.type.unknown": "未知类型：{v}",
  "stageImg.stageRow": "固定",
  "stageImg.drag": "拖动调整顺序",
  "stageImg.up": "上移",
  "stageImg.down": "下移",
  "stageImg.vipLocked": "VIP（暂未开放，一律锁定）",
  "stageImg.delBody": "删除后保存即从本草稿里移除，后面的图自动往前挪；已发布的版本不受影响。已经拼完这张的玩家，发布后照样能看到。",
  "album.vip.notice": "按 idol 分组、组内按排序升序。没到发布时间的条目玩家看不到。",
  "album.vipVideo.add": "添加 VIP 视频",
  "album.col.cover": "封面",
  "album.col.duration": "时长",
  "album.col.video": "视频",
  "issue.album.puzzle": "{id} · 所属拼图",
  "issue.stage.code": "{id} · 编号",
  "issue.stage.reward": "{id} · 奖励",
  "issue.stage.type": "{id} · 类型",
  "issue.stage.grid": "{id} · 规格",
  "issue.stage.price": "{id} · 碎片价",
  "issue.stage.images": "{id} · 图片",
  "issue.album.slot": "{id} · 序位",
  "issue.album.thumb": "{id} · 缩略图",
  "issue.album.img": "{id} · 原图",
  "issue.album.rule": "{id} · 规则与等级",
  "issue.album.idol": "{id} · 所属 idol",
  "issue.album.cover": "{id} · 封面",
  "issue.album.duration": "{id} · 时长",
  "issue.album.video": "{id} · 视频",
  "intimacy.grp.daily": "每日上限",
  "intimacy.idolDailyCap": "每位 idol 每日上限",
  "intimacy.idolDailyCap.hint": "所有来源加起来的封顶，每天按租户本地时间零点重置。0 = 不限",
  "intimacy.u.point": "点",
  "intimacy.levels.title": "等级曲线",
  "intimacy.levels.notice": "玩家等级由经验按生效的曲线实时算出。改了曲线一发布，所有玩家的等级立刻跟着变：门槛调低会直接升级，调高会掉级。",
  "intimacy.col.level": "等级",
  "intimacy.col.expTotal": "累计所需经验",
  "intimacy.col.delta": "本级需要",
  "intimacy.col.desc": "解锁内容（展示文案）",
  "intimacy.levels.add": "添加一级（Lv{n}）",
  "intimacy.levels.total": "共 {n} 级 · 满级累计 {exp}",
  "intimacy.levels.totalBad": "共 {n} 级 · {bad} 级累计经验没有递增，保存会被拒",
  "intimacy.levels.notIncreasing": "须大于 Lv{lv}（{exp}）",
  "intimacy.sources.title": "亲密度来源",
  "intimacy.sources.notice": "标「未上线」的玩法还没做完，配了暂时不会给玩家加亲密度。",
  "intimacy.sources.hint": "上限填 0 = 不限；同一来源只能配一行。",
  "intimacy.sources.add": "添加来源",
  "intimacy.sources.allUsed": "所有来源都已配置",
  "intimacy.col.source": "来源",
  "intimacy.col.status": "状态",
  "intimacy.col.points": "单次点数",
  "intimacy.col.dailyPointsCap": "每日点数上限",
  "intimacy.col.dailyTimesCap": "每日次数上限",
  "intimacy.status.live": "已上线",
  "intimacy.status.pending": "未上线",
  "intimacy.status.cheer": "仅主推 idol",
  "intimacy.source.greet": "打招呼",
  "intimacy.source.gift": "送礼",
  "intimacy.source.cheer_dice": "应援·掷骰",
  "intimacy.source.cheer_adopt": "应援·领养",
  "intimacy.source.cheer_oshi_pet": "应援·本命宠物",
  "intimacy.source.cheer_build_star": "应援·建筑升星",
  "intimacy.source.puzzle_assemble": "拼完拼图",
  "intimacy.points.caller": "逐张配",
  "intimacy.points.callerHint": "点数在拼图配置里逐张配（「拼完给亲密度」），这里只配每日上限。不加这一行也照给，只是不受来源上限约束。",
  "petStar.title": "升星档位",
  "petStar.notice.unit": "消耗数的是「重复」只数，不含本体 —— 玩家第一次领到某只宠物时它就是 1★。所以「升 2★ 要 3 只」= 这只宠物他总共要有 4 只。",
  "petStar.col.star": "目标星级",
  "petStar.col.dupCost": "消耗重复只数",
  "petStar.col.cumulative": "累计消耗",
  "petStar.col.fx": "里程碑外观资源",
  "petStar.fx.placeholder": "留空 = 这一档无外观变化",
  "petStar.add": "加一档（{n}★）",
  "petStar.total": "配满 {star}★ 累计需要 {n} 只重复",
  "petStar.notice.tuning": "这个累计数要和领养池的「已拥有种类衰减」一起调：衰减压得越狠，玩家攒重复越慢，而满级要上百只 —— 两者是对着干的。定稿前请与数值一起核。",
  "petStar.notice.overflow": "第 {from}★ 起的档位超过了上限 {max}★，玩家永远升不到，这几行不会生效。",
  "pt.grp.main": "萌宠小镇 · 主建筑",
  "pt.notice.units": "点 1 次升 1 星，一颗星就是一格，不用配。造价一颗星一个数，在「主建筑星级表」里配。玩家等级按「几星一级」从已拿到的星数算出来。",
  "pt.maxStar": "累计星数上限",
  "pt.maxStar.hint": "主建筑能拿到的最多星数。到顶后主线不再增长；解锁星数超过它的副建筑永远开不出来。",
  "pt.maxStar.note": "星级表共 {n} 行",
  "pt.starsPerLevel": "几星一级",
  "pt.starsPerLevel.hint": "每拿到这么多颗星，玩家升一级。玩家等级 = 已拿到的星数 ÷ 本值（向下取整）+ 1。匹配段位、新手保护、骰子上限、天赋解锁都按这个等级算。随时可以改：改完后，老玩家的等级在他下一次升星时按新值重算；已经升满的玩家不再升星，等级停在改之前的值。",
  "pt.starsPerLevel.note": "累计星数上限 {s} 星 = 最高 {l} 级",
  "pt.grp.damage": "萌宠小镇 · 被破坏与修复",
  "pt.foot.damage": "被打只点着主建筑的部位，已经拿到的星和级不会掉；破坏方拿到的金币由系统出，受害者不掉金币。",
  "pt.repairPct": "修复费比例",
  "pt.repairPct.hint": "主建筑被打坏后修复的费用 = 当前这颗星的总价 × 本比例。一次修完清掉全部着火点。",
  "pt.attackerPct": "破坏者收益比例",
  "pt.attackerPct.hint": "打坏别人主建筑的一方获得的金币 = 受害者当前这颗星的总价 × 本比例。这笔钱由系统出，受害者不掉金币。",
  "pt.grp.match": "萌宠小镇 · 对抗匹配",
  "pt.protectStar": "新手保护（星）",
  "pt.protectStar.hint": "已拿到的星数 ≤ 本值的玩家只会遇到 NPC，也不会被真人打。萌宠小镇不看对抗页的新手保护关数，只按累计星数判。",
  "pt.matchRangeStars": "匹配范围",
  "pt.matchRangeStars.hint": "雷云 / 抢金库只打星数在自己 ± 本值以内的真人，0 = 不限星数。当日被打上限仍按抢金库页的每日被访上限。",
  "pt.matchRangeStars.note": "星数差 {n} 以内",
  "pt.matchRangeStars.none": "不限星数",
  "pt.col.star": "星",
  "pt.starNo": "第 {n} 星",
  "pt.col.skin": "外观图",
  "pt.skinMissing": "第 1 星必须配图",
  "pt.skinInherit": "沿用第 {n} 星",
  "pt.col.unlocks": "解锁的副建筑",
  "pt.econ.growth": "每星增长",
  "pt.econ.needsBase": "未启用：先填首星总造价。只填写分段不会保存或生效。",
  "pt.econ.invalid": "首星金额须为正整数，起始星号不能重复；增长率大于-100%，最多两位小数。",
  "pt.econ.relative": "相对首星",
  "pt.econ.relativeNote": "首星为1倍 · 对数纵轴",
  "pt.econ.range": "星号",
  "pt.econ.rangeFrom": "起始星号",
  "pt.econ.rangeTo": "结束星号",
  "pt.econ.chartNote": "空值不补线。收入按本项目参数推算，不代表竞品真实收入；多指标叠加时统一比较首星倍数。",
  "pt.econ.rollBaseNote": "填写启用逐星曲线，留空沿用逐星值。",
  "pt.econ.presetNote": "仅替换增长分段、保留首星金额；这是关卡参考曲线，请按小镇需要调整。",
  "pt.econ.rollBase": "起始目标投资次数",
  "pt.econ.rollBaseHint": "第1星的目标投资次数。留空沿用逐星配置；填写后按增长系数生成，单星覆盖优先。",
  "pt.econ.rollGrowth": "每星目标次数增长系数",
  "pt.econ.rollGrowthHint": "目标次数按星增长：起始次数×增长系数^(星号−1)，最后四舍五入。留空按1计算。它控制预算次数，B的增长系数控制经济尺度。",
  "pt.econ.legacyKHint": "原模型只改变收入B，每10星应用一次K，不改变星价。开启统一B后改为每星增长，同时影响公式星价与收入。",
  "pt.econ.anchorHint": "第1星的统一B。留空取第1星原配置B；同一个B用于金币收入和公式星价。",
  "pt.econ.anchorNote": "留空取首星B，填写后作为整条B曲线的起点。",
  "pt.econ.kHint": "每星B=起始B×K^(星号−1)，从起点计算后四舍五入。同一个B用于收入和公式星价，手填/竞品星价不反推B。",
  "pt.econ.kNote": "留空或1表示B不增长；每颗星增长一次。",
  "pt.econ.curveBase": "首星总造价",
  "pt.econ.curveFrom": "起始星号",
  "pt.econ.curveNote": "分段从第2星开始，按增长率连续复利，最后按百取整；手填价不改变曲线。分段星价与统一B不能同时启用。",
  "pt.econ.unifiedOn": "按星统一B：起始B与K同时决定金币收入和公式星价。目标投资次数单独生成，单星覆盖优先；手填与竞品星价不反推B。",
  "pt.econ.applyOne": "将第{n}星公式价填入手填价",
  "pt.econ.from": "起",
  "pt.econ.to": "止",
  "pt.econ.reset": "重置",
  "pt.econ.algorithmB": "公式使用的B",
  "pt.econ.runtimeB": "当前采用B",
  "pt.econ.estimate": "预算估算投资次数",
  "pt.jump": "跳到星",
  "pt.range": "共 {n} 颗，一张表不翻页 · 首行为全部合计",
  "pt.badRows": "有 {n} 行填得不对，发布会被拦下",
  "pt.star.note": "每星可覆盖目标投资次数和手填价；未覆盖时使用上方曲线或原默认值。公式、手填、竞品三套价格随版本选择，手填与竞品缺价时使用公式。统一B模式下，收入和公式星价使用同一个B，手填/竞品不反推B；原模型保留原有的收入规则。外观留空继承之前最近的资源，点一次升一星。",
  "pt.star.noteG": "每星可覆盖目标投资次数和手填价；未覆盖时使用上方曲线或原默认值。公式、手填、竞品三套价格随版本选择，手填与竞品缺价时使用公式。外观留空继承之前最近的资源，点一次升一星。",
  "pt.star.empty": "累计星数上限还没填，星级表没有行",
  "pt.star.foot": "星是唯一的配置单位，这页没有任何分组；玩家等级按全局参数「几星一级」从星数算出来，这页不配它。客户端在用的那列没填的星按公式价扣；可以填也可以不填，发布闸不要求填满。竞品列可在此新增 / 重命名 / 删除，随版本保存；切换「客户端用哪套造价」不会动任何一套的数值。",
  "pt.col.rolls": "目标骰次",
  "pt.col.rolls.hint": "这一颗星的投资次数预算。填写上方起始值和增长系数后按星生成，本格填写值优先，清空恢复曲线；未启用曲线时使用原默认值。不是实际通关次数保证。",
  "pt.calc.bad": "目标骰次要是正数，E 与 ρ 要填好，才算得出来",
  "pt.col.slot": "槽位",
  "pt.col.slot.hint": "写成 r<环号>_<两位环内位号>，例如 r1_03。它是身份，玩家的解锁进度存的就是它，发布后不可改。",
  "pt.col.img": "图",
  "pt.col.name": "名称",
  "pt.col.pos": "环 · 位",
  "pt.col.pos.hint": "环号与环内位号决定位置。每一环都从右下方位起、顺时针数。同一个位置不能放两栋。",
  "pt.col.unlockStar": "解锁星数",
  "pt.col.unlockStar.hint": "主建筑累计拿到这么多颗星时这一栋散云。可跳着配，与环号无关——内圈开到一半先放一个外圈地标是合法配法。",
  "pt.col.reward": "解锁奖励",
  "pt.reward.none": "不给",
  "pt.reward.item": "一次性条目",
  "pt.reward.buff": "持续加成",
  "pt.reward.buffNote": "本版只存不生效",
  "pt.slot.note": "槽位号是身份，永不变更；内容、名字、奖励都是配置，随时可换。图进资源块下发，换楼不用发客户端版本。",
  "pt.addSlot": "新增槽位",
  "pt.slot.count": "共 {n} 个槽位",
  "pt.slot.rule": "解锁顺序只看「解锁星数」那一列，与环号无关。喷泉一类的景观跟地块美术一起画，不占槽位。",
  "pt.slot.empty": "还没有配任何副建筑",
  "pt.layout": "布局示意",
  "pt.layout.note": "中间是主建筑，外圈按环号与位号排列，每环从右下起顺时针。它只回答一个问题：有没有两栋楼配到同一个位置。真实坐标由客户端按美术稿摆。",
  "pt.dlg.reward": "{slot} 的解锁奖励",
  "pt.err.rolls": "目标骰次必须大于 0",
  "pt.err.slotId": "格式应为 r1_01",
  "pt.err.slotDup": "槽位号重复",
  "pt.err.slotPos": "这个位置已经有别的槽位了",
  "pt.err.unlockStar": "解锁星数必须 ≥ 1",
  "pt.err.unreachable": "超出星数上限，这栋永远开不出来",
  "pt.err.slotImg": "必须配图",
  "pt.err.rewardEmpty": "选了条目却没配内容",

  // ── 活动道具收集玩法（《棋盘活动道具收集玩法 V1.1》）──────────────
  // 长说明一律进 tip（问号 tooltip），不写成行内文案：这一页每条规则都要解释
  // 「为什么」，摊在输入框旁边会把每一行挤成两行
  "nav.act-item": "活动道具",
  "spin.kind.act_item": "活动道具（收集）",
  "spin.kind.actItemShort": "活动道具",
  "spin.actItem.goAct": "去活动道具 →",
  "spin.actItem.base": "每次发",
  "spin.actItem.perRoll": "个 × 本次掷骰倍数",
  "spin.actItem.fallback": "空窗期发",
  "spin.hint.actItem": "单人棋盘的活动道具收集，与「道具卡（多人对局）」不是一回事。发哪个道具由「副玩法配置 → 活动道具」里当期生效的那一期决定，这里只配发多少。",
  "act.st.draft": "未排期",
  "act.st.scheduled": "已排期",
  "act.st.running": "进行中",
  "act.st.ended": "已结束",


  "act.liveHint": "这一页保存即生效。棋盘上的活动道具格不在这儿——在「spin 奖励配置」加减，那边改完要发布上线才算数。",
  "act.goSpin": "去 spin 奖励配置 →",
  "act.tileTitle": "副玩法组",
  "act.tileTag": "随配置版本",
  "act.tileNote": "让活动道具进副玩法组：组行抽中后客户端可以就近挑它，挑中时有生效中的活动发当期道具，空窗期按回退发。这一块跟配置版本走——在草稿里改（自动保存），在顶上发布并激活才生效；下面两块照旧保存即生效。",
  "act.tileMissing": "这一版还没有活动道具格。",
  "act.item.title": "活动道具管理",
  "act.item.used": "被几期使用",
  "act.item.usedN": "{n} 期",
  "act.item.unused": "未使用",
  "act.item.inUse": "有 {n} 期活动在用这个道具，不能删除",
  "act.item.tip": "先在这里把道具建出来，下面排活动时才选得到。名字和图标是玩家看到的，不配的话他钱包里只显示一串编号。",
  "act.item.delConfirm": "删除活动道具「{ref}」？玩家手里已经发出去的不会消失，但以后只显示成一串编号。",
  "act.item.new": "+ 新增道具",
  "act.item.empty": "还没有活动道具。先建一个，下面排活动时才选得到。",
  "act.itemSaved": "已保存活动道具 {ref}",
  "act.itemDeleted": "已删除活动道具 {ref}",
  "act.schedule": "活动排期",
  "act.scheduleTip": "一期一行，按排好的开始 / 结束时间自动开始、自动结束（日本时间，约 30 秒内对玩家生效）。同一时刻最多一期进行中；要提前结束就把结束时间改成现在；已结束的不能再改，要继续请「复制为下期」。空窗期各格改发它自己配的金币或骰子。",
  "act.new": "+ 新建活动",
  "act.empty": "还没有配置任何活动，点「新建活动」加第一期",
  "act.clashNotice": "有 {n} 期活动时间重叠（标红行）。同一时刻两期并存，玩家攒的道具不知道算哪一期——请先结束其中一期。",
  "act.col.status": "状态",
  "act.col.name": "活动名称",
  "act.col.item": "当期道具",
  "act.noName": "未命名",
  "act.overSpan": "超出活动期",
  "act.copyNext": "复制为下期",
  "act.noItem": "未选",
  "act.col.span": "开始 / 结束时间",
  "act.col.spanTip": "保存时排好的活动周期：到开始时间自动开始，到结束时间自动结束（约 30 秒内对玩家生效）",
  "act.noSpan": "未排期",
  "act.openedAt": "开启于",
  "act.col.days": "满档预估",
  "act.days": "天",
  "act.view": "查看",
  "act.delConfirm": "删除活动《{name}》？档位与奖励一并删除，不可恢复。",
  "act.deleted": "已删除活动 {id}",
  "act.saved": "已保存活动 {id}",
  "act.foot2": "活动结束时未领的奖励自动补发邮件（保留 30 天）；未满档的剩余道具清零。",

  "act.copiedFrom": "这是从《{name}》复制出来的新一期，还没存过。时间已顺延到它结束之后——记得改名字、换道具。",
  "act.editTitle": "编辑活动《{name}》",
  "act.f.start": "开始时间",
  "act.f.end": "结束时间",
  "act.tip.start": "到点自动开始。活动开始后不能再改",
  "act.tip.end": "到点自动结束。进行中可以改：改成现在 = 立即结束；活动结束后不能再改",
  "act.spanDays": "共 {n} 天",
  "act.noEnd": "没有结束时间",
  "act.err.span": "结束时间不得早于或等于开始时间",
  "act.err.spanBoth": "开始时间和结束时间要一起填（都不填 = 先存成草稿）",
  "act.viewTitle": "查看活动《{name}》",
  "act.endedNotice": "活动已结束，只能查看：未领的奖励已补发邮件、剩余道具已清零。要继续请回列表点「复制为下期」。",
  "act.closeView": "关闭",
  "act.startLbl": "开始时间：",
  "act.endLbl": "结束时间：",
  "act.err.spanNeedsItem": "排时间之前请先选当期道具",
  "act.err.spanPast": "结束时间已经过了，请重新选择活动周期",
  "act.lockedNotice": "活动进行中，档位、当期道具和开始时间已锁定——改了玩家的进度条会当场往回跳。名称、皮肤、公示文案、奖励图片和结束时间仍可改（结束时间改成现在 = 立即结束）。",
  "act.moreFields": "展示与规则（皮肤 · 满档后行为 · 公示文案）",
  "act.basic": "基础信息",
  "act.idAuto": "保存后自动分配",
  "act.f.name": "活动名称",
  "act.f.item": "当期道具",
  "act.tip.item": "只列上方「活动道具管理」里启用中的道具。没有想要的先到上面建一个。",
  "act.f.item.pick": "搜名字或编号",
  "act.f.skin": "主题皮 ID",
  "act.tip.skin": "换皮资源包编号，不填走默认皮。",
  "act.skinPh": "不填 = 默认皮",
  "act.f.full": "满档后行为",
  "act.tip.full": "玩家把所有档位都打满之后，活动道具格改发什么。「回退值」就是这一格在「spin 奖励配置」里配的那个「空窗期发」——金币或骰子，每格可以不一样。建议选它：继续发道具他拿了也用不掉。累计量照记，结算还能显示本期共获得多少。",
  "act.full.fallback": "切回回退值（金币/骰子）",
  "act.full.keep": "继续发道具",
  "act.f.notice": "公示文案",
  "act.tip.notice": "玩家在玩法说明里看到的正文。必须写明「结束时剩余道具清零、已达成未领的会补发邮件」，不写就是差评。",

  "act.tierMode": "按",
  "act.tip.tierNeed": "从上一档满格算起，本档还要再攒多少。攒多了自动顺延到下一档。",
  "act.tiers": "档位表",
  "act.noTiers": "还没有档位。至少配 1 档，否则活动到点开始、玩家攒一个月什么都拿不到。",
  "act.addTier": "加一档",
  "act.col.tier": "#",
  "act.col.tierNeed": "本档需要量",
  "act.col.cum": "累计门槛",
  "act.col.rewards": "奖励",
  "act.col.tierIcon": "奖励图片",
  "act.tip.tierIcon": "这一档奖励在客户端显示的图片。不传 = 客户端按奖励类型用默认图。活动开始后和档位一起锁定",
  "act.tierIcon.none": "默认图",
  "act.tierIcon.clear": "清除",
  "act.tierSum": "累计需求合计 {n}",

  "act.previewMatch": "多人匹配代理的活动道具来自棋盘格位表引用的掉落行（玩家走到那一格才拿到），不是按每天掷骰次数和掉落权重抽的，所以这里不估算满档天数。要确认能不能掉：去「棋盘格配置」看有没有格子引用了带「活动道具」奖励的掉落行。",
  "act.preview": "保存前预览",
  "act.previewTag": "防呆展示，不拦截",
  "act.tip.preview": "按棋盘上的活动道具格反推：满档要多少天、×1 倍玩家要踩多少次。",
  "act.f.dicePerDay": "日均骰子数",
  "act.tip.dicePerDay": "只影响这个预览，不会保存。默认按每天 40 骰估。",
  "act.dice": "骰",
  "act.pv.ladder": "各档落在活动期的哪一天",
  "act.pv.allReachable": "{n} 档都够得到",
  "act.pv.unreachable": "{total} 档里有 {n} 档攒不到",
  "act.pv.tierDay": "第 {tier} 档 · 累计 {need} · 约第 {n} 天",
  "act.pv.days": "满档需要",
  "act.pv.ofSpan": "天 / 活动期 {n} 天",
  "act.pv.overHint": "比活动期还长，这期没人打得完",
  "act.pv.hits": "×1 倍玩家满档要踩",
  "act.pv.hits10": "×10 倍约 {n} 次（倍数只改节奏，不改总量）",
  "act.pv.times": "次",
  "act.pace1": "{d} 骰 × 道具格权重合计 {w} × 平均每格 {b} 个 = 日均 {per} 个",
  "act.pace2none": "当前配置掉不出道具，或还没有档位——算不出满档天数",
  "act.cumList": "累计门槛：{list}",

  "act.err.id": "活动编号格式不对：只能是字母、数字、_ 或 -，最长 48",
  "act.err.name": "活动名称的英文必填",
  "act.err.itemMissing": "当期道具 {ref} 已不在「活动道具管理」里，或已停用——请先到上面建好",
  "act.err.noTier": "活动已开启，至少要配 1 档",
  "act.err.seq": "第 {n} 档缺失，序号必须从 1 起连续无缺号",
  "act.err.need": "第 {n} 档要攒的数量必须大于 0（按累计填时：这一档要大于上一档）",
  "act.err.cumTooSmall": "要大于 {n}",
  "act.err.reward": "第 {n} 档未配置奖励",
  "act.err.ref": "第 {n} 档的 {kind} 奖励没选对象",
  "act.err.qty": "第 {n} 档的 {kind} 数量必须是大于 0 的整数",
  "act.time.choose": "选择时间",
  "act.time.disabledHint": "灰色日期和时间不可选，已避开活动冲突和必要间隔",
  "act.time.unavailable": "没有可选时间，请选择其他日期",
  "act.time.previousMonth": "上个月",
  "act.time.nextMonth": "下个月",
  "act.time.month": "月份",
  "act.time.hour": "小时",
  "act.time.minute": "分钟",
  "act.time.cannotSave": "暂时无法保存，请先处理以下问题：",
  "act.time.rules": "同一时间只能有一期活动；同一道具至少间隔 5 分钟，不同道具至少间隔 30 秒。",
  "act.time.startFirst": "请先选择开始时间，再选择同一空档内的结束时间。",
  "act.time.endWindow": "结束时间可选：{from} 至 {to}",
  "act.time.noLimit": "不限",
  "act.time.endCleared": "开始时间已调整，请重新选择结束时间；原结束时间不在同一可用区间内。",
  "act.time.endNow": "结束时间设为现在（保存后生效）",
  "act.time.noWindow": "没有可用档期，请先调整其他活动的结束时间，再重新打开本活动。",
  "act.time.clear": "清空排期，先存为草稿",
  "act.time.sameItemClash": "与《{name}》使用同一道具，两期重叠或间隔不足 5 分钟，请调整起止时间。",
  "act.err.clash": "与《{name}》的活动周期重叠或间隔不足 30 秒，请调整起止时间。",
  // ── 造价来源（2026-09-16）：客户端用哪套 / 竞品列手填 / 萌宠等级表按总造价页的形状 ──
  "cost.src.title": "客户端用哪套造价",
  "cost.src.hint": "三套造价平级，都能发布：公式 = 以实际总造价为目标（没填按算法）按系统内置的星级曲线 1 / 1.2 / 1.44 / 2.5 / 3.12 摊到每级、各栋同价；手填 = 建造页里逐格填的实际造价；竞品 = 建造页里逐格填的那一列竞品价，原值照搬。选哪套，发布时客户端就拿哪套的价；发布闸只校验选中的那套。随版本保存、发布、回滚。",
  "cost.src.hintNote": "\n\n当前：{note}",
  "cost.src.formula": "公式",
  "cost.src.manual": "手填",
  "cost.src.unset": "还没选",
  "cost.src.inUse": "客户端在用",
  "cost.src.note.formula": "新矩阵按算法总造价自动分配，随机方案随版本保存；旧版本仍以实际总造价（未填用算法）为目标。",
  "cost.src.note.formulaStar": "发布闸只查：每颗星的目标骰次为正。手填价与竞品价不参与校验。",
  "cost.src.note.manualStar": "发布闸不查大小：手填价想怎么配就怎么配。没填的星走公式价，可以填也可以不填。",
  "cost.src.note.rivalStar": "发布闸不查大小：该竞品列原值照搬，没填的星走公式价。",
  "cost.src.note.manual": "新矩阵按实际总造价自动分配（未填用算法总造价），不再逐格录入；旧版本保留逐格覆盖、递增与合计校验。",
  "cost.src.note.rival": "发布闸查：该竞品列金额随星级不减。没填的格走公式价；竞品各栋价格本就不同，不套「各栋同价」规则。",
  "cost.src.note.unset": "还没选客户端用哪套造价，发布会被拦下——选公式 / 手填 / 某一列竞品。",
  "cost.src.failed": "{n} {u}未通过，发布会被拦下",
  "cost.src.allPass": "全部 {n} {u}通过",
  "cost.src.removedInUse": "已删除竞品列「{name}」；客户端原本在用它，已改回「公式」",
  "cost.addRival": "+ 新增竞品列",
  "ps.entry": "按玩家指定",
  "ps.entryN": "按玩家指定 · {n}",
  "ps.entryHint": "让指定的玩家单独用另一套价，其余人用版本默认",
  "ps.title": "按玩家指定造价来源",
  "ps.intro": "版本默认来源（现在是「{d}」）对所有人生效；下面指定的玩家单独用另一套价，同一个版本里就能同时测公式和竞品。指定不跟版本走，改完 30 秒内生效，客户端不用改。",
  "ps.notePh": "备注，如「测竞品价」",
  "ps.add": "添加",
  "ps.col.player": "玩家",
  "ps.col.source": "用哪套价",
  "ps.col.note": "备注",
  "ps.col.updated": "更新时间",
  "ps.empty": "还没有指定任何玩家，所有人都用版本默认",
  "ps.noName": "未取到昵称",
  "ps.missing": "生效版本没有这列，按版本默认",
  "ps.midStar": "这{u}已建到第 {n} 格，换来源后剩下的格按新价扣",
  "ps.clear": "取消指定，回到版本默认",
  "ps.foot": "每个代理最多指定 {max} 人，给测试账号用；按比例分组不在这里。正在建到一半的{u}换来源后剩下的格按新价扣，这和激活新版本时一样。",
  "cost.check.formulaOk": "目标骰次已配，公式可算",
  "cost.check.formulaBad": "目标骰次待填，公式算不出这一关",
  "cost.check.formulaNoB": "新加的关还没保存：保存后系统按曲线补上 B 值",
  "cost.check.formulaNoG": "这一关还没填单掷金币 g（「总造价配置」页），算不出算法总造价",
  "cost.check.formulaNoEnv": "骰子参数里的 E / ρ 还没配，算不出算法总造价；填了实际总造价的关不受影响",
  "cost.check.formulaNoEnvG": "骰子参数里的 ρ 还没配，算不出算法总造价；填了实际总造价的关不受影响",
  "cost.check.rivalMissing": "竞品列有 {n} 格没填，公式价又算不出来（目标骰次 / B 值 / E、ρ 没配齐），这些格没价可用",
  "cost.check.rivalMissingG": "竞品列有 {n} 格没填，公式价又算不出来（目标骰次 / 单掷金币 g / ρ 没配齐），这些格没价可用",
  "cost.check.rivalDrop": "竞品列有建筑的金额随星级下降",
  "cost.check.rivalOk": "竞品列 {n} 格（没填的按公式价）随星级不减；不套「各栋同价」规则",
  "dlg.rollsDefault": "留空＝系统默认",
  "dlg.costNote": "这一关要花多少钱去「总造价配置」填实际总造价；不填就按系统默认曲线。",
  "pt.col.check": "校验",
  "pt.col.check.sub": "按在用的那套",
  "pt.col.rolls.sub": "可编辑 · 定这颗星掷几次",
  "pt.star.formula": "公式价",
  "pt.star.formula.sub": "只读",
  "pt.star.manual": "手填价",
  "pt.star.manual.sub": "可编辑 · 没填的星走公式价",
  "pt.star.rivalSub": "逐星手填 · 没填的走公式价",
  "pt.star.manualPh": "待填 · 公式 {v}",
  "pt.star.missingN": "{n} 颗没填、用公式价",
  "pt.star.head": "共 {n} 星 · 由「累计星数上限」带出 · 客户端在用的总投入 {total}",
  "pt.allStars": "全部 {n} 星",
  "pt.rolls.default": "默认",
  "pt.rolls.changed": "已改 · 默认 {v}",
  "pt.rolls.changedN": "{n} 颗改过默认",
  "pt.rolls.allDefault": "全部默认",
  "pt.sum.filled": "填了 {n} 颗，其余按公式",
  "pt.sum.none": "暂无数据",
  "pt.star.formula.hint": "目标投资次数×E×B×ρ，或已启用的分段星价曲线。统一B模式的B同时用于收入；手填和竞品价格不改变统一B。",
  "pt.star.pending": "待填",
  "pt.check.formulaOk": "骰次已配，公式可算",
  "pt.check.noDrop": "随星不降",
  "pt.check.usesFormula": "没填，用公式价",
  "u.pettownStar": "颗星",
  "cost.rollsHint": "目标通关骰次。填写起始目标骰次后按全局曲线生成，单关可覆盖，清空覆盖恢复曲线；未启用曲线时沿用原逐关值。下方小字按当前选用造价与收入 B 估算，不会反写目标。浮动范围在模拟器页。",
  "cost.rollsHintG": "目标通关骰次。填写起始目标骰次后按全局曲线生成，单关可覆盖，清空覆盖恢复曲线；未启用曲线时沿用原逐关值。下方小字按当前选用造价与本关单掷金币 g 估算，不会反写目标。浮动范围在模拟器页。",
  "cost.rollsPlaceholder": "待填",
  "u.rolls": "骰",
  "initcfg.mode.solo.title": "单人主线",
  "initcfg.mode.solo.desc": "单人掷骰、建城、挑战 NPC。",
  "initcfg.mode.match.title": "多人匹配",
  "initcfg.mode.match.desc": "2～4 人实时对战，人数不足时补 NPC。",
  "initcfg.sys.levels.title": "旧建筑",
  "initcfg.sys.levels.desc": "每关多栋建筑，全部升满后进入下一关。",
  "initcfg.sys.pettown.desc": "持续升级主建筑，逐步解锁副建筑。",
  "initcfg.buildingSet": "建筑方案",
  "initcfg.buildingSetHint": "（地图与建筑素材，目前仅此一套）",
  "initcfg.blockSoloHasMatch": "单人主线不能配置匹配玩法，请移除已配置的 {n} 个玩法。",
  "initcfg.blockMatchNone": "请先添加至少一个匹配玩法。",
  "initcfg.unpicked": "请选择",
  "initcfg.modeSection": "玩法体系",
  "initcfg.modeLocked": "已有玩家，玩法已锁定为「{active}」。如需更换，请新建代理。",
  "initcfg.modeUnset": "请选择玩法体系后再发布。",
  "initcfg.modeEffect": "每个代理只能选择一种玩法。保存后更新配置菜单，发布后对玩家生效。",
  "initcfg.sysSection": "建筑版本",
  "initcfg.sysLocked": "已有建筑进度，建筑体系已锁定为「{active}」。如需更换，请联系技术人员处理。",
  "initcfg.sysLockWarn": "玩家开始建造后，关卡与小镇体系不能互换；旧建筑与新版拼图共用关卡体系，可调整后台展示。",
  "initcfg.sysValidate": "发布时只检查所选体系的配置。切换已有建筑进度的体系需要清除进度，请联系技术人员处理。",
  "initcfg.menuSection": "查看配置菜单",
  "initcfg.menuEmpty": "请先选择玩法体系。",
  "initcfg.menuNote": "预览当前选择；保存后更新左侧菜单。",
  "initcfg.checkSection": "发布前检查",
  "initcfg.app.loadVersionFail": "读取该版本内容失败",
  "initcfg.app.backendDown": "后端不可达",
  "initcfg.err.staleTitle": "管理端刚更新过",
  "initcfg.err.staleBody": "这个页面打开得比新版本早，刷新一下就好。有未保存的改动时浏览器会先问你要不要离开。",
  "initcfg.err.reload": "刷新页面",
  "initcfg.err.crashTitle": "页面出错了",
  "initcfg.rich.styles": "样式",
  "initcfg.rich.body": "正文",
  "initcfg.rich.h1": "标题 1",
  "initcfg.rich.h2": "标题 2",
  "mmode.issueSep": "；",
  "mmode.listSep": "、",
  "mmode.issue.roomMax": "座位数要在 {min} ～ {max} 之间",
  "mmode.issue.minHumans": "开局最少真人要在 1 ～ 座位数之间",
  "mmode.issue.turnTimeout": "回合时限必须大于 7 秒（代打停顿上限）",
  "mmode.issue.dicePerRoll": "每手骰子数要在 1 ～ {max} 之间",
  "mmode.issue.maxTurns": "最多手数至少 1",
  "mmode.issue.minFinishSteps": "到终点最少骰子总和不能是负数",
  "mmode.issue.tierUncovered": "档位 {tiers} 没划进任何段位",
  "mmode.issue.needSegment": "配了档位表就要至少划一个段位",
  "mmode.issue.segmentsWithoutTiers": "没有档位表时段位也要留空",
  "mmode.issue.segmentNoId": "有段位没填段位名",
  "mmode.issue.segmentDupId": "段位名重复：{ids}",
  "mmode.issue.segmentEmpty": "段位 {ids} 一个档位都没有，点档位划进去或删掉这一段",
  "mmode.issue.segmentStray": "段位里有档位表没有的数：{tiers}。在段位表里点红色的数移除，或先把它加进体力档位",
  "mmode.issue.widenOrder": "匹配范围放宽：等待秒数要一行比一行大",
  "mmode.issue.widenNarrow": "匹配范围放宽：越等越宽，下一行的百分比不能比上一行小",
  "mmode.issue.widenRange": "匹配范围放宽：百分比要在 0～100 之间",
  "mmode.issue.ticketSplitPair": "门票比例与分成表要同时配或同时留空",
  "mmode.issue.needBasis": "配了门票就要选分法",
  "mmode.issue.ticketNoTiers": "配了门票却没有体力档位表：门票按全场最低档算，没有档位就收不到票（发布不拦，但实际不收）",
  "mmode.issue.splitRowMissing": "分成表缺 {n} 人局那一行",
  "mmode.issue.splitRowLength": "{n} 人局那一行要有 {n} 个名次",
  "mmode.issue.splitRowOver": "{n} 人局合计超过 100%",
  "mmode.issue.stageSeq": "战阶要从 1 起连号",
  "mmode.issue.stageWinsAsc": "门槛要随阶级严格升高",
  "mmode.issue.cardIdFormat": "卡编号 {id} 只能是小写字母 / 数字 / 下划线 / 连字符",
  "mmode.issue.cardIdDup": "卡编号 {id} 重复",
  "mmode.issue.unnamed": "(未命名)",
  "mmode.issue.cardNoEn": "卡 {id} 缺英文名",
  "mmode.issue.cardBadStage": "卡 {id} 指着不存在的第 {stage} 阶",
  "mmode.issue.cardNoStage": "卡 {id} 没有挂到任何战阶上（在战阶页加上去）",
  "mmode.openMode": "开放玩法 {mode}",
  "mmode.noModes": "这个代理还没开放任何多人玩法，玩家进不了匹配。点右边按钮开放。",
  "mmode.moreModes": "还有玩法没开放，需要时点右边开放。",
  "mmode.closeMode": "关闭这个玩法",
  "mmode.closeConfirm": "关闭后，这一版发布时玩家将进不了 {mode} 的匹配，这一页的参数也会清掉。确定关闭？",
  "mmode.modeTitle": "玩法 {mode}",
  "mmode.issuesTitle": "有 {n} 处问题：",
  "mmode.summaryTitle": "按现在的配置，玩家会遇到：",
  "mmode.sum.room": "{seats} 人一房，坐满立即开局；等了 {wait} 秒还没坐满时，真人 ≥ {min} 人就直接开（空座不补 NPC），不足 {min} 人就补 NPC 坐满",
  "mmode.sum.roomFull": "{seats} 人一房，坐满立即开局；等了 {wait} 秒还没坐满就补 NPC 坐满",
  "mmode.sum.turn": "每回合限时 {turn} 秒，超时由系统代掷；连续超时 {strikes} 次判弃（系统代打到结束、排名垫底）",
  "mmode.sum.board": "每手掷 {dice} 枚骰子，一局最多 {max} 手（按现在的座位和骰子数，正常走完约 {est} 手）",
  "mmode.sum.noTiers": "没有体力档位：所有人排同一个队，先到先得",
  "mmode.sum.tiers": "体力档位 {tiers}，分 {segs} 个段位，只和同段位的人匹配；刚排队时只配档位相差 {from}% 以内的对手",
  "mmode.sum.widen": "排队超过 {after} 秒后，放宽到相差 {to}% 以内",
  "mmode.sum.noEcon": "不收门票、不发名次奖",
  "mmode.sum.econ": "收门票：每人交本房最低档位 × {pct}%（例：最低 {low} 档时每人 {ticket}），奖池按名次分",
  "mmode.sum.minVer": "客户端版本低于 {v} 的玩家进不了匹配",
  "mmode.nPeople": "{n} 人",
  "mmode.nDice": "{n} 枚",
  "mmode.u.sec": "秒",
  "mmode.u.times": "次",
  "mmode.u.turns": "手",
  "mmode.u.points": "点",
  "mmode.del": "删除",
  "mmode.sec.room": "开房与补 NPC",
  "mmode.sec.room.desc": "一房几个人，没坐满时等多久、怎么补 NPC。",
  "mmode.f.roomMax": "一房座位数",
  "mmode.f.roomMax.hint": "含 NPC。",
  "mmode.f.minHumans": "开局最少真人",
  "mmode.f.minHumans.hint": "选 1 人：不匹配真人，点匹配直接和 NPC 坐满开局。选 2 人以上：先匹配真人，真人达到这个数就直接开局、不补 NPC；等满补 NPC 前等待还不够才补 NPC。",
  "mmode.f.startRange": "点击匹配到开局",
  "mmode.f.startRange.hint": "成房后到开局的时间：每次匹配在这个范围里随机取，自动准备，不再额外等机器人准备和 3 秒倒计时。等真人花掉的时间另算（见「补 NPC 前等待」）。客户端需要提前加载并及时进房；网络或收票重试仍会延后。",
  "mmode.f.startMin": "最短开局时间",
  "mmode.f.startMax": "最长开局时间",
  "mmode.start.on": "快速开局",
  "mmode.start.off": "等待全员准备",
  "mmode.issue.startRange": "开局时间下限至少 1 秒，且下限不大于上限",
  "mmode.sum.startRange": "成房后约 {from}～{to} 秒开局，自动准备，不额外叠加倒计时",
  "mmode.f.botFillWait": "补 NPC 前等待",
  "mmode.f.botFillWait.hint": "没坐满时最多等真人多久，到点补 NPC 开局。快速开局也按它等。",
  "mmode.f.readyTimeout": "准备时限",
  "mmode.f.readyTimeout.hint": "成房后这么久没点准备，算弃权。",
  "mmode.f.npcBest": "NPC 强度",
  "mmode.f.npcBest.hint": "NPC 每步选最优走法的概率，其余随机走。100 = 最强。",
  "mmode.sec.turn": "回合与超时",
  "mmode.sec.turn.desc": "玩家掉线或长时间不操作时怎么处理。",
  "mmode.f.turnTimeout": "回合时限",
  "mmode.f.turnTimeout.hint": "到时没操作由系统代掷。必须大于 7 秒。",
  "mmode.f.forfeitStrikes": "连续超时判弃",
  "mmode.f.forfeitStrikes.hint": "连续超时这么多次就判弃：系统代打到结束、排名垫底、不发名次奖。中途自己出手一次就清零。",
  "mmode.sec.board": "棋盘规则",
  "mmode.sec.board.desc": "棋盘玩法一局怎么走。规则版本只影响新开的局。",
  "mmode.f.dicePerRoll": "每手骰子数",
  "mmode.f.dicePerRoll.hint": "客户端最多演 3 枚。",
  "mmode.f.diceDist": "点数概率",
  "mmode.f.diceDist.hint": "只影响新开的局，客户端不用改。被引去副玩法格的那一手、测试面板指定的点数不受它影响；避开他色漩涡照旧。",
  "mmode.diceDist.standard": "真实骰子",
  "mmode.diceDist.uniform": "点数和均匀",
  "mmode.diceDist.balanced": "均衡（洗牌袋）",
  "mmode.diceDist.balancedNote": "每人一个袋子，装着全部 {n} 种面值组合，洗乱逐个抽、抽完再装：单手照样有大有小，但每人每 {n} 手合计都是 {total} 步，靠运气甩开别人的局明显变少。",
  "mmode.diceDist.standardNote": "每颗骰子独立随机：点数和 {lo}～{hi} 里中间多、两头少（2 枚时 7 约 16.7%，2 和 12 各约 2.8%）。",
  "mmode.diceDist.uniformNote": "点数和 {lo}～{hi} 每个都一样常见（各约 {pct}%），再拆成骰面显示；一手走得很远或很近的情况会明显变多。",
  "mmode.diceDist.oneDie": "每手 1 枚时两种一样：1～6 本来就等概率。",
  "mmode.f.maxTurns": "一局最多手数",
  "mmode.f.maxTurns.hint": "全场合计，不是每人。到了强制结束，按到家棋子数和进度排名。",
  "mmode.turnsEstimate": "参考：按现在的座位数和骰子数，一局正常走完约 {turns} 手",
  "mmode.turnsEstimateLow": "。比它小的话，多数局会打到上限才结束",
  "mmode.f.minFinishSteps": "防作弊：每枚到家最少点数",
  "mmode.f.minFinishSteps.hint": "某座位报告棋子到家时，它掷出的点数总和要 ≥ 到家枚数 × 这个值，否则不认。走一圈约 51 点；0 = 不检查。",
  "mmode.f.minClientVersion": "最低客户端版本",
  "mmode.f.minClientVersion.hint": "格式 1.2.3。低于它的客户端进不了匹配，会被提示更新。",
  "mmode.f.minClientVersion.placeholder": "不限",
  "mmode.sec.tiers": "体力档位与匹配范围",
  "mmode.sec.tiers.desc": "玩家带多少体力进场，以及和谁匹配到一起。",
  "mmode.f.tiers": "体力档位",
  "mmode.f.tiers.hint": "玩家进场时能选的体力数，自动从小到大排。一个都不加 = 不分档，所有人一起排队。",
  "mmode.tiers.none": "未设置",
  "mmode.tiers.add": "添加",
  "mmode.tiers.addPh": "如 50",
  "mmode.tiers.remove": "删除档位 {v}",
  "mmode.f.segments": "段位",
  "mmode.f.segments.hint": "只和同一段位的人匹配。点档位把它划进这一段，每个档位只属于一段；黄色虚线 = 还没划进任何段。建议 3～4 段。",
  "mmode.seg.col.id": "段位名",
  "mmode.seg.col.tiers": "包含的档位（点击切换）",
  "mmode.seg.add": "加一段",
  "mmode.seg.moveTip": "{v} 现在在 {from}，点击挪到这一段",
  "mmode.seg.strayTip": "{v} 不在体力档位里，点击从这一段移除",
  "mmode.f.widen": "匹配范围放宽",
  "mmode.f.widen.hint": "同一段位里，按档位相差的百分比找对手；等得越久范围越宽，不能变窄。",
  "mmode.widen.col.after": "排队等了",
  "mmode.widen.col.bps": "档位可相差",
  "mmode.widen.col.example": "例：{v} 档能配到",
  "mmode.widen.atJoin": "刚进队",
  "mmode.widen.add": "加一档",
  "mmode.f.downgradeAfter": "降档提示",
  "mmode.f.downgradeAfter.hint": "排队这么久后提示玩家可以降一档。0 = 不提示。",
  "mmode.sec.econ": "门票与名次奖",
  "mmode.sec.econ.desc": "开局每人交门票进奖池，结束按名次分。",
  "mmode.econ.on": "收门票",
  "mmode.econ.off": "不收",
  "mmode.econOff": "这个玩法不收门票、不发名次奖。打开右上角开关会填一套默认值（门票 80%，4 人局 55/35/10/0）。",
  "mmode.f.ticket": "门票比例",
  "mmode.f.ticket.hint": "每人门票 = 本房最低档位 × 这个比例。例：最低 {low} 档时，每人交 {ticket}。",
  "mmode.f.ticket.hintNoTier": "每人门票 = 本房最低档位 × 这个比例。没配体力档位时收不到门票。",
  "mmode.f.split": "奖池分成",
  "mmode.f.split.hint": "每种人数一行，填各名次拿走奖池的百分比。合计不能超过 100%，不足的部分销毁；NPC 和判弃者那份也销毁。",
  "mmode.split.seats": "{n} 人局",
  "mmode.split.rank": "第 {rank} 名",
  "mmode.split.totalCol": "合计",
  "mmode.split.burn": "（销毁 {p}%）",
  "mmode.f.basis": "混合档位分法",
  "mmode.f.topBonus": "最高奖励配置",
  "mmode.f.topBonus.hint": "开局按第一名的金币名次奖换算个数（金币 ÷ 比例，向下取整，不够 1 个不发），对局页「最高奖励」展示，局末只发给第一名。比例只用于换算，和真实购买价无关；留空 = 这种不展示、不发。",
  "mmode.topBonus.kind": "道具",
  "mmode.topBonus.rate": "1 个 = 多少金币",
  "mmode.topBonus.pack": "从哪个卡包抽",
  "mmode.topBonus.unit": "金币",
  "mmode.topBonus.dice": "体力",
  "mmode.topBonus.itemCard": "物品卡",
  "mmode.topBonus.wildcard": "万能卡",
  "mmode.topBonus.coin": "金币",
  "mmode.topBonus.img": "奖励图片（全局一套，所有玩法共用；对局页「最高奖励」弹窗显示。没传的客户端用默认图）",
  "mmode.topBonus.img.none": "未传",
  "mmode.topBonus.img.packCover": "没传，用所选卡包封面",
  "mmode.topBonus.img.upload": "上传",
  "mmode.topBonus.img.replace": "更换",
  "mmode.topBonus.img.clear": "清除",
  "mmode.topBonus.img.uploading": "上传中…",
  "mmode.issue.topBonusRate": "最高奖励配置的换算比例要填正整数，不配就留空",
  "mmode.issue.topBonusPack": "最高奖励配置的物品卡：比例和卡包要同时填",
  "mmode.issue.topBonusNoEcon": "最高奖励配置要按金币名次奖换算，先打开门票与名次奖",
  "mmode.f.basis.hint": "目前每人门票一样，两种分法结果相同，保持默认即可。",
  "mmode.basis.seat": "按名次分",
  "mmode.basis.stake": "按出资加权",
  "mmode.col.nameI18n": "名称（多语言）",
  "mmode.col.ops": "操作",
  "mmode.stage.col.stage": "阶",
  "mmode.stage.nth": "第 {stage} 阶",
  "mmode.stage.col.wins": "累计胜场门槛",
  "mmode.stage.col.wins.sub": "第一名且没判弃的局",
  "mmode.stage.col.cards": "道具卡",
  "mmode.stage.col.cards.sub": "升到这阶后玩家手动点解锁哪几张；每张启用的卡都要挂在某一阶上",
  "mmode.stage.cards.add": "加一张",
  "mmode.stage.cards.pickTitle": "第 {stage} 阶 · 选道具卡",
  "mmode.stage.cards.pickHint": "点一张挂到这一阶；已在这一阶的再点一下就拿掉。在别的阶的会挪过来。",
  "mmode.stage.cards.here": "已在这一阶",
  "mmode.stage.cards.atStage": "第 {stage} 阶",
  "mmode.stage.cards.atStarter": "未配置",
  "mmode.stage.rewardsBtn": "其余奖励",
  "mmode.stage.rewardsBtn.n": "其余奖励 {n}",
  "mmode.stage.rewards.none": "无",
  "mmode.stage.toolbar": "阶级按行序自动连号；卡表里「解锁战阶」引用的就是这里的阶。玩家胜场到了要手动点升阶，一次一阶。",
  "mmode.stage.add": "新增一阶",
  "mmode.stage.title": "战阶",
  "mmode.stage.empty": "还没配战阶。空表 = 本代理不开战阶，道具卡只能配「一开始就能解锁」（解锁战阶 0）的。",
  "mmode.stage.rewardDialog": "第 {stage} 阶 · 升阶奖励",
  "mmode.card.col.id": "卡编号",
  "mmode.card.col.id.sub": "小写字母 / 数字 / _ -",
  "mmode.card.col.image": "卡面图",
  "mmode.card.reupload": "重传",
  "mmode.card.upload": "上传",
  "mmode.card.clearImage": "清除",
  "mmode.card.col.effect": "效果类型",
  "mmode.card.col.effect.sub": "登记 ≠ 局内已实现",
  "mmode.card.col.params": "效果参数",
  "mmode.card.col.params.sub": "按效果类型逐项填；留空 = 用配置表默认值",
  "mmode.card.col.sort": "排序",
  "mmode.card.col.enabled": "启用",
  "mmode.card.toolbar": "解锁只影响本局基池的丰富度，不构成个人强度优势。局内使用（三选一、效果）等真玩法本体。",
  "mmode.card.add": "新增一张",
  "mmode.card.title": "道具卡",
  "mmode.card.empty": "还没配道具卡。空表 = 本代理不开道具卡。",
  "miscx.emptyValue": "空",
  "miscx.emptyParen": "（空）",
  "miscx.listSep": "、",
  "miscx.dev.damagedFloor": " 楼#{idx}",
  "miscx.adopt.tierSuffix": "{msg}（{tier}）",
  "mmon.tab.queue": "排队中",
  "mmon.tab.rooms": "对局中",
  "mmon.tab.settled": "已结算",
  "mmon.tab.exit": "未成局",
  "mmon.phase.WAITING": "等人",
  "mmon.phase.MATCHED": "已成房",
  "mmon.phase.READY": "待开局",
  "mmon.phase.PLAYING": "进行中",
  "mmon.phase.FINISHED": "已结束",
  "mmon.phase.CLOSED": "已关闭",
  "mmon.reason.room_abandoned": "成房后没人连 WS",
  "mmon.reason.report_conflict": "回报比对失败，整局作废退票",
  "mmon.reason.cancelled": "玩家自己取消",
  "mmon.reason.join_rejected": "join 被拒",
  "mmon.reason.config_dropped": "配置改档被退出队列",
  "mmon.reason.create_room_failed": "建房失败",
  "mmon.reason.all_forfeited": "开局后全员判弃",
  "mmon.loadFailed": "读取失败",
  "mmon.loadFailedWith": "读取失败：{err}",
  "mmon.loading": "载入中…",
  "mmon.intro": "队列与房间只活在 Durable Object 里，库里只有正常打完的局有行——这一页是唯一能看见它们的地方。只读，不提供踢人 / 关房 / 强制结算。",
  "mmon.autoRefresh5": "每 5 秒自动刷新",
  "mmon.autoRefresh2": "每 2 秒自动刷新",
  "mmon.refresh": "刷新",
  "mmon.lastAt": "最近 {time}",
  "mmon.col.mode": "玩法",
  "mmon.col.segment": "段位",
  "mmon.col.tiers": "含档位",
  "mmon.col.queueSize": "排队人数",
  "mmon.col.longestWait": "最长等待",
  "mmon.col.nextNpcFill": "下次补 NPC 开局",
  "mmon.col.roomRule": "成房规则",
  "mmon.col.player": "玩家",
  "mmon.col.level": "关卡",
  "mmon.col.stake": "档位",
  "mmon.col.waited": "已等",
  "mmon.col.npcCountdown": "补 NPC 倒计时",
  "mmon.col.downgrade": "可降档",
  "mmon.col.room": "房间",
  "mmon.col.modeSegment": "玩法 / 段位",
  "mmon.col.phase": "阶段",
  "mmon.col.seats": "座位",
  "mmon.col.humansOnline": "真人在线",
  "mmon.col.currentTurn": "当前回合",
  "mmon.col.elapsed": "已持续",
  "mmon.col.nextDeadline": "下一个到点",
  "mmon.col.finishedAt": "结束时刻",
  "mmon.col.duration": "时长",
  "mmon.col.endReason": "结束方式",
  "mmon.col.winner": "胜者",
  "mmon.col.poolBalance": "奖池 = 实发 + 销毁",
  "mmon.col.config": "配置",
  "mmon.col.at": "时刻",
  "mmon.col.reason": "原因",
  "mmon.col.modeStake": "玩法 / 档位",
  "mmon.col.howFar": "走到哪一步",
  "mmon.col.detail": "明细",
  "mmon.col.turn": "手",
  "mmon.col.who": "谁",
  "mmon.col.dice": "骰子",
  "mmon.col.steps": "步数",
  "mmon.col.seat": "座位",
  "mmon.col.identity": "身份",
  "mmon.col.online": "在线",
  "mmon.col.forfeited": "判弃",
  "mmon.col.score": "分数",
  "mmon.col.ticket": "门票",
  "mmon.col.rank": "名次",
  "mmon.col.vsPrev": "较上一帧",
  "mmon.col.entryStake": "进场档位",
  "mmon.col.rankReward": "名次奖",
  "mmon.queue.bucketHint": "撮合只在同一个（玩法, 段位）桶内进行；桶越碎，补 NPC 的比例越高",
  "mmon.queue.buckets": "队列分桶",
  "mmon.queue.empty": "当前没有人在排队",
  "mmon.queue.noTiers": "无档位表",
  "mmon.queue.inTime": "{s} 后",
  "mmon.queue.rule": "凑满 {max} 人立刻开；等满 {wait} 时真人够 {min} 人直接开，不够才补 NPC",
  "mmon.queue.playersHint": "一个人同时只能在一个队列或一个活跃房间里",
  "mmon.queue.players": "排队中的玩家",
  "mmon.queue.downgradeTo": "降到 {n} 档",
  "mmon.queue.stakeNote": "档位只是撮合意图，服务端没有扣过体力——门票在开局那一刻才收。",
  "mmon.rooms.stuckWarn": "{n} 间房成房超过 {grace}，还有真人没连上 WebSocket。等满 ready 超时会判弃；全部真人都没连就直接关房，这一局白排了——2026-09-10 客户端报「匹配不到」，真相就是这一格。",
  "mmon.rooms.hint": "已成房但还没落库的房间，含还没开局的",
  "mmon.rooms.title": "活跃房间",
  "mmon.rooms.empty": "当前没有活跃房间",
  "mmon.rooms.gone": "房间已不在",
  "mmon.rooms.skipped": "未拉取（超出上限）",
  "mmon.rooms.unreachable": "拉不到",
  "mmon.rooms.seatsN": "{n} 座",
  "mmon.rooms.seatsBreakdown": " （{humans} 真人 + {npc} NPC）",
  "mmon.seatN": "座位 {seat}",
  "mmon.rooms.turnLeft": " · 剩 {s}",
  "mmon.rooms.notStarted": "未开局",
  "mmon.rooms.sinceStart": "开局 {d}",
  "mmon.rooms.sinceFormed": "成房 {d}",
  "mmon.rooms.watch": "观战",
  "mmon.rooms.redRule": "标红的口径：还没开局 · 成房超过 {grace} · 还有真人没连上。刚成房那几秒客户端本来就在握手，不算异常。",
  "mmon.settled.hint": "一局一行，房间打完时由房间 DO 自己写库",
  "mmon.settled.totalN": " · 共 {n} 局",
  "mmon.filter.all": "全部",
  "mmon.end.normal": "正常结束",
  "mmon.end.abnormal": "非正常结束",
  "mmon.end.normalShort": "正常",
  "mmon.end.allForfeited": "全员判弃",
  "mmon.end.unknown": "结束方式未知",
  "mmon.unknown": "未知",
  "mmon.settled.title": "已结算的对局",
  "mmon.settled.searchPh": "搜对局编号（8 位）或完整房间号，回车",
  "mmon.settled.empty": "所选条件下没有对局记录",
  "mmon.humansN": "{n} 真人",
  "mmon.settled.npcBurned": " · NPC · 份额销毁",
  "mmon.settled.unbalanced": " ⚠ 对不平",
  "mmon.settled.noEconomy": "不做经济",
  "mmon.settled.detail": "详情",
  "mmon.settled.note1": "「奖池 = 实发 + 销毁」直接把 finance 那一行对平，对不上就是记账有问题。NPC 拿的名次奖不重分，全额销毁。",
  "mmon.settled.note2": "配置列是本局冻结的那一版：运营中途调过 NPC 强度，不按它分组算出来的胜率曲线是假的。",
  "mmon.exit.hint": "近 24 小时 · 一个玩家的一次算一条 · 点一格只看那一种",
  "mmon.exit.title": "今天为什么没打成",
  "mmon.exit.sourceNote": "「开局后全员判弃」来自 match_result（那一局真打过，有结果行），其余五种来自 match_queue_exit。",
  "mmon.exit.listHint": "按时间倒序 · 共 {n} 条",
  "mmon.exit.list": "明细",
  "mmon.exit.empty": "所选条件下没有记录",
  "mmon.exit.waited": "等了 {s}",
  "mmon.exit.afterFormed": "成房后 {s}",
  "mmon.exit.noRoom": "没成房",
  "mmon.ludo.meta": "每人 {pieces} 枚 · 每手 {dice} 枚骰子 · 最多 {max} 手",
  "mmon.ludo.acceptedTo": " · 认定到第 {n} 手",
  "mmon.ludo.minSteps": " · 每枚到家至少 {n} 步",
  "mmon.ludo.title": "棋盘：下一手是第 {n} 手",
  "mmon.ludo.conflict": "第 {n} 手回报比对失败，整局作废退票。各人报了什么见下面「回报现场」。",
  "mmon.ludo.col.order": "出手序",
  "mmon.ludo.col.home": "到家",
  "mmon.ludo.col.position": "当前这枚的位置（格序号）",
  "mmon.ludo.col.diceSum": "骰子总和",
  "mmon.ludo.col.rolls": "掷了几手",
  "mmon.ludo.col.finished": "走完",
  "mmon.yourTurn": "← 该他",
  "mmon.forfeited": "判弃",
  "mmon.ludo.done": "已走完",
  "mmon.ludo.finishedAt": "第 {rank} 名 · 第 {turn} 手",
  "mmon.ludo.positionNote": "「位置」是客户端回报、服务端比对认定后的当前格序号（起点 0、终点最大），不是服务端算的：服务端不知道棋盘。",
  "mmon.ludo.recent": "最近几手",
  "mmon.ludo.noRolls": "还没掷",
  "mmon.ludo.reports": "回报现场（客户端看不到）",
  "mmon.ludo.noRaw": "这份快照没有原样状态",
  "mmon.ludo.noPending": "没有挂着的回报：认定到第 {accepted} 手，下一手是第 {next} 手",
  "mmon.ludo.col.reporters": "谁报了",
  "mmon.ludo.col.reported": "报了什么",
  "mmon.ludo.reportLine": "{who}: 到终点 [{finished}] 位置 {progress}",
  "mmon.ludo.reportsNote": "一手要收齐在线真人的回报、内容完全一致才认定。这里一直挂着同一手，多半是某个客户端没报。",
  "mmon.drawer.room": "房间",
  "mmon.drawer.readonly": "只读观战。管端不提供踢人、关房、强制结算——那些会改变一局的结果，出了问题说不清是玩家打的还是运营干的。",
  "mmon.drawer.errMaybeClosed": "{err}（房间可能已经关掉了）",
  "mmon.drawer.seqHint": "状态版本 seq {v} · 客户端靠它丢弃迟到消息",
  "mmon.drawer.nextDeadline": " · 下一个到点：{kind} {s}",
  "mmon.mirror": "镜像 {id}",
  "mmon.human": "真人",
  "mmon.yes": "是",
  "mmon.no": "否",
  "mmon.drawer.neverConnected": "（从未连上）",
  "mmon.drawer.identityNote": "「身份」这一列只有管端看得到。客户端拿到的快照里没有 is_npc / mirror_id，NPC 必须和真人长得一模一样。",
  "mmon.drawer.gameRawHint": "GameLogic.view 的原样输出",
  "mmon.drawer.gameState": "对局状态",
  "mmon.drawer.notStartedParen": "（还没开局）",
  "mmon.drawer.gameNote": "按玩法原样展示 JSON。棋盘玩法 ludo 上面有专门的面板；格子画不出来——服务端不知道棋盘，只有骰子、进度和名次。",
  "mmon.timeline": "时间线",
  "mmon.tl.created": "建房",
  "mmon.tl.started": "开局",
  "mmon.tl.finished": "结束",
  "mmon.tl.persisted": "落库",
  "mmon.tl.closed": "关房",
  "mmon.tl.createdNote": "大厅撮合成功，建房间 DO",
  "mmon.tl.startedNote": "全员 ready，进 PLAYING",
  "mmon.tl.notStartedNote": "还没开局；等满 {s} 未 ready 的判弃",
  "mmon.tl.notFinished": "还没结束",
  "mmon.tl.settled": "已写入",
  "mmon.tl.gaveUp": "已放弃（留给 REST 兜底）",
  "mmon.tl.notSettled": "未写入（试过 {n} 次）",
  "mmon.tl.settleNote": "打完由房间 DO 自己写库；失败按 5s / 30s / 2min 退避重试",
  "mmon.tl.notClosed": "还没关",
  "mmon.theater.capItem": "{who} 用了 {kind}",
  "mmon.theater.capRoll": "{who} 掷出 {dice}（{sum} 步）",
  "mmon.theater.capChain": "{who} 连跳",
  "mmon.theater.capCell": "到第 {cell} 格",
  "mmon.theater.capCapture": "{who} 被打回",
  "mmon.theater.capFinish": "{who} 一枚棋子到家",
  "mmon.theater.capStart": "开局",
  "mmon.theater.back": "上一步",
  "mmon.theater.forward": "下一步",
  "mmon.theater.prevKey": "上一关键帧",
  "mmon.theater.nextKey": "下一关键帧",
  "mmon.theater.backToLive": "回到实况",
  "mmon.theater.live": "实况中",
  "mmon.theater.stepOf": "第 {i}/{n} 步 · 回合 {t}",
  "mmon.theater.full": "全屏",
  "mmon.theater.soundOn": "声音：开",
  "mmon.theater.soundOff": "声音：关",
  "mmon.theater.exitFull": "退出全屏",
  "mmon.theater.noEvents": "还没有吃子、用卡或到家",
  "mmon.theater.liveOneStep": "这间房开局时还没有记录走子过程，只能看到当前位置。",
  "mmon.theater.noBoard": "这一局没有棋盘数据，只能看名次回放。",
  "mmon.theater.title": "棋盘回放",
  "mmon.theater.liveTitle": "棋盘实况",
  "mmon.replay.title": "排名回放",
  "mmon.replay.none": "这一局没有过程数据，放不了回放。落过程数据是后加的，之前打完的局只有结算结果。",
  "mmon.replay.err": "回放读不出来：{err}",
  "mmon.replay.headline": "{turns} 回合 · {swaps} 次换位 · 压缩为 {frames} 帧",
  "mmon.replay.replay": "↻ 重播",
  "mmon.replay.pause": "⏸ 暂停",
  "mmon.replay.play": "▶ 播放",
  "mmon.replay.progress": "回放进度",
  "mmon.replay.turnOf": "第 {t} / {total} 手",
  "mmon.replay.endSettled": "终局 · 结算口径",
  "mmon.replay.reachedFinish": "{name} 到终点",
  "mmon.replay.frameOf": "第 {i} / {n} 帧 · 第 {t} 手",
  "mmon.replay.rankNote": "中间各帧是「对局中」的名次：到终点的人从到达那手起固定在前面，判弃的照进度排、不降级。末帧换成结算口径（判弃垫底），和上面「座位与名次」一致。",
  "mmon.replay.dice": "每手骰子",
  "mmon.replay.noHistory": "没有逐手记录",
  "mmon.replay.diceNote": "高亮的是当前这一帧走过的手。鼠标停在行上看 rand。",
  "mmon.kind.start": "开局",
  "mmon.kind.swap": "换位",
  "mmon.kind.biggest_drop": "跌得最狠",
  "mmon.kind.biggest_rise": "追得最猛",
  "mmon.kind.finish": "到终点",
  "mmon.kind.end": "终局",
  "mmon.sd.title": "对局",
  "mmon.sd.readonly": "已落库的历史对局，只读。管端不提供改分、重发奖、重算名次。",
  "mmon.sd.durationX": "时长 {d}",
  "mmon.sd.seatsRanks": "座位与名次",
  "mmon.burned": "销毁",
  "mmon.sd.finance": "这一局的账",
  "mmon.sd.noEconomy": "这个玩法不做经济：不收门票、不发名次奖。",
  "mmon.fin.pool": "奖池",
  "mmon.fin.poolNote": "全部真人的门票之和",
  "mmon.fin.paid": "实发名次奖",
  "mmon.fin.paidNote": "只发给真人",
  "mmon.fin.burnedNote": "NPC 名次奖 + 未发放额度 + 取整尾差",
  "mmon.fin.balanced": "对平了：{pool} = {paid} + {burned}。",
  "mmon.fin.unbalanced": "对不平：奖池 {pool} ≠ 实发 {paid} + 销毁 {burned}。这一行的记账有问题，要查。",
  "mmon.sd.frozenConfig": "本局冻结的配置",
  "mmon.cfg.version": "配置版本",
  "mmon.cfg.versionNote": "成房那一刻冻进 RoomState，之后运营怎么改都不影响这一局",
  "mmon.cfg.npc": "NPC 强度",
  "mmon.cfg.npcNote": "选启发式最优动作的比例；胜率统计必须按它分组",
  "mmon.cfg.ticket": "门票比例",
  "mmon.cfg.ticketNote": "进场档位里进奖池的那一部分",
  "mmon.cfg.split": "分成表",
  "mmon.cfg.splitNote": "按名次从第一名排下来，单位万分比；和小于 10000 的差额即销毁",
  "talentx.line.stake_tier": "体力倍率",
  "talentx.line.tree_growth": "体力树生长",
  "talentx.line.stamina_cap": "体力上限",
  "talentx.line.shield_slot": "盾牌位",
  "talentx.line.sr_rate": "SR 宠物卡出率",
  "talentx.line.tree_cap": "体力树容量",
  "talentx.line.friend_slots": "好友位",
  "talentx.line.match_bonus": "对战结算",
  "talentx.line.raid_yield": "偷袭奖励",
  "talentx.line.tree_count": "体力树数量",
  "talentx.line.daily_stamina": "每日登录体力",
  "talentx.bld.diceTower": "骰子塔",
  "talentx.bld.tree": "体力树",
  "talentx.bld.wall": "护墙",
  "talentx.bld.petNest": "宠物窝",
  "talentx.bld.pigeon": "信鸽塔",
  "talentx.bld.mainTent": "主帐篷",
  "talentx.bld.cannon": "大炮台",
  "talentx.unit.x": "×",
  "talentx.matchStake.tag": "（倍率）",
  "talentx.unit.minPerPt": "分钟 / 点",
  "talentx.unit.pt": "点",
  "talentx.unit.count": "个",
  "talentx.unit.pctRel": "%（相对）",
  "talentx.unit.ptPerTree": "点 / 棵",
  "talentx.unit.people": "人",
  "talentx.unit.pct": "%",
  "talentx.unit.trees": "棵",
  "talentx.base.stake_tier": "上限制：能选 1 / 2 / 3 / 5 / 10 / 20 / 50 / 100 / 200 / 500 / 1000 里不超过上限的档。起点 = 对抗与控骰页「单人倍率基础上限」",
  "talentx.base.tree_growth": "副建筑「体力树」的生长间隔",
  "talentx.base.stamina_cap": "骰子参数「体力上限（基础值）」，这条线填累计加成",
  "talentx.base.stamina_cap.levels": "关卡制原体力上限（按关卡涨、有封顶）之上的累计加成，可以超过封顶",
  "talentx.base.shield_slot": "御守页「持有上限」",
  "talentx.base.sr_rate": "棋盘掉宠物卡时 SR 的基础出率，按相对值提升",
  "talentx.base.tree_cap": "副建筑「体力树」的每棵容量",
  "talentx.base.friend_slots": "对抗与控骰页「好友上限」",
  "talentx.base.match_bonus": "对局 PvE 金币的加成，不碰排名奖金",
  "talentx.base.raid_yield": "偷袭营地所得的加成",
  "talentx.base.tree_count": "副建筑「体力树」的初始棵数",
  "talentx.base.daily_stamina": "每天首次登录额外赠送",
  "talentx.line.dice_regen": "骰子恢复",
  "talentx.line.gift_cap": "每日赠卡",
  "talentx.line.gift": "一次性礼包",
  "talentx.line.puzzle_hint": "每日拼图提示",
  "talentx.line.auto_roll": "自动掷骰",
  "talentx.bld.none": "—",
  "talentx.bld.idol": "idol",
  "talentx.unit.secPerDie": "秒 / 颗",
  "talentx.unit.cardsPerDay": "张 / 天",
  "talentx.unit.timesPerDay": "次 / 天",
  "talentx.unit.gift": "礼包",
  "talentx.unit.unlock": "1 = 解锁",
  "talentx.base.dice_regen": "骰子参数「自动恢复时间」。配了体力树的萌宠小镇不自动恢复，不能配",
  "talentx.base.gift_cap": "对抗与控骰页「每日赠卡上限」；那里填 0（不限）时不能配",
  "talentx.base.gift": "领取当场发一次，不改任何线的数值",
  "talentx.base.puzzle_hint": "起点 0",
  "talentx.base.auto_roll": "起点 0（未解锁）",
  "talentx.worse.giftEmpty": "礼包至少配一样",
  "talentx.worse.giftQty": "数量要是正整数",
  "talentx.worse.giftMax": "礼包最多 20 项",
  "talentx.worse.giftKind": "礼包里有不支持的类型",
  "talentx.issue.giftCapZero": "全局「每日赠卡上限」是 0（不限）时不能配「每日赠卡」节点",
  "talentx.future": "后续关卡未开放",
  "talentx.gift.kind.coins": "金币（固定数量）",
  "talentx.gift.unit.coins": "金币",
  "talentx.line.card_pity": "稀有卡保底",
  "talentx.base.card_pity": "起点 0；4、5 星物品卡抽卡权重在原权重上加这个值",
  "talentx.line.collection_complete": "全图鉴",
  "talentx.base.collection_complete": "终局礼包、限定外观与图鉴，效果待接入",
  "talentx.textTemplate": "描述模板用 {value}（配置值）、{delta}（本档改善量）、{qty}（礼包首项数量），各语言参数保持一致。",
  "talentx.col.benefit": "权益名 / 描述模板（{lang}）",
  "talentx.benefit.name": "权益名",
  "talentx.benefit.text": "描述模板",
  "talentx.benefit.enMissing": "缺英文权益名，发布必填：点「多语言」补",
  "talentx.icon.replace": "替换",
  "talentx.preview": "预览：{text}",
  "talentx.gift.kind.spin": "骰子",
  "talentx.gift.kind.gems": "钻石",
  "talentx.gift.kind.puzzle": "拼图碎片",
  "talentx.gift.kind.shield": "盾牌",
  "talentx.gift.kind.spinCap": "骰子 × 当前上限",
  "talentx.gift.kind.moneyStar": "金币 × 单星费用",
  "talentx.gift.unit.spin": "颗",
  "talentx.gift.unit.gems": "个",
  "talentx.gift.unit.puzzle": "片",
  "talentx.gift.unit.shield": "个",
  "talentx.gift.unit.pct": "%",
  "talentx.gift.add": "+ 加一项",
  "talentx.gift.oneShot": "领取当场发一次",
  "talentx.gift.label": "{id} · 礼包第 {n} 项",
  "p.stakeTierBase": "单人倍率基础上限",
  "p.stakeTierBase.hint": "stake_tier_base：不领天赋节点就能押的最高倍率。上限制：能选 1 / 2 / 3 / 5 / 10 / 20 / 50 / 100 … 里不超过上限的档；天赋「体力倍率」节点把上限往上抬。1 = 只能 ×1。",
  "talentx.worse.empty": "没填",
  "talentx.worse.int": "要填整数",
  "talentx.worse.decimal": "最多两位小数",
  "talentx.worse.mustUp": "要比上一个节点大",
  "talentx.worse.mustDown": "要比上一个节点小",
  "talentx.worse.positive": "要大于 0",
  "talentx.unlockLevel": "解锁天赋等级",
  "talentx.unlockStars": "解锁累计星数",
  "talentx.issue.idFormat": "节点编号 {id} 只能是 1~16 位字母 / 数字 / 下划线 / 连字符",
  "talentx.issue.idDup": "节点编号 {id} 重复",
  "talentx.issue.seqGap": "序号要从 1 连续：玩家按序号依次领取，断号后面的节点永远领不到",
  "talentx.issue.unknownLine": "节点 {id} 的升级线服务端不认识",
  "talentx.issue.treeLine": "节点 {id}：关卡制没有体力树，不能配{line}",
  "talentx.issue.unlockMin": "节点 {id} 的{unit}要 ≥ 1",
  "talentx.issue.unlockDesc": "{id} 的{unit}比前一个节点低：按序号领取，后面的节点不能先到",
  "talentx.issue.nameEn": "节点 {id} 的名称至少要有英文",
  "talentx.issue.badValues": "{n} 个节点的数值有问题（见表内红字），发布会被拦下",
  "talentx.stake.notTier": "{ids} 的值不在多人档位表里（{list}）：单人照常按这个值生效，多人领了它不会多开档位",
  "talentx.stake.unreachable": "多人档位 {list} 比天赋能开到的最高上限 {top} 还高，永远没人能选：加一个能开到它的体力倍率节点，或从档位表删掉",
  "talentx.issueSep": "；",
  "talentx.listSep": "、",
  "talentx.cityAt": "第 {city} 关升满第 {n} 栋",
  "talentx.cityOver": "超过全部升满",
  "talentx.col.seq": "序号",
  "talentx.col.line": "升级线",
  "talentx.lineNth": "{building} · 这条线第 {n} 个节点",
  "talentx.lineUnknown": "未知升级线",
  "talentx.notLive": "暂不生效",
  "talentx.live": "生效",
  "talentx.col.value": "升级前 → 升级后 / 礼包内容",
  "talentx.col.unlockLevel.sub": "累计升满几栋建筑后亮起",
  "talentx.col.unlockStars.sub": "到这颗星节点亮起",
  "talentx.fromStar": "第 {n} 星起",
  "talentx.nameLabel": "{id} · 名称",
  "talentx.col.text": "权益描述模板（多语言）",
  "talentx.textLabel": "{id} · 文案",
  "talentx.col.building": "所属建筑",
  "talentx.col.count": "格数",
  "talentx.col.seqs": "节点序号",
  "talentx.col.range": "起点 → 终点",
  "talentx.col.base": "起点从哪来",
  "talentx.col.live": "本版",
  "talentx.toolbar": "序号按行序自动连号，只许删最后一个；节点编号进资源块与领取请求。",
  "talentx.count": "{n} 条权益 · {lines} 条升级线",
  "talentx.addNode": "新增节点",
  "talentx.title": "天赋树",
  "talentx.note.levels": "天赋等级 = 升满的建筑数：前面各关的栋数之和，加上当前关升满且没被破坏的栋数（满星的那栋被打坏时等级 −1，修好恢复）。第 1 关一栋没满是 0 级。节点到了解锁等级就亮起挂红点，玩家点一下才生效；必须按序号依次领取，等级掉下来不收回已领的节点。关卡制没有体力树，生长间隔、每棵容量、棵数三条线不能配。本版本全部升满是 {max} 级。空表 = 本代理不开天赋树。",
  "talentx.note.pettown": "纯升级树：建筑按解锁星数自动解锁，树上只把已有的东西调大。节点到了解锁累计星数就亮起挂红点，玩家点一下才生效；必须按序号依次领取，积压不影响继续涨星。空表 = 本代理不开天赋树。",
  "talentx.empty": "还没配天赋节点。空表 = 本代理不开天赋树（体力倍率只能押 ×1，其余线取基础值）。",
  "talentx.summary.tag": "由节点表汇总，不单独填",
  "talentx.summary.title": "升级线汇总",
  "talentx.summary.notLive": "「暂不生效」的几条（SR 宠物卡出率、对战结算、偷袭奖励、每日拼图提示、稀有卡保底、全图鉴）玩家照常能领、状态照常记，但效果要等对应系统上线才接。对战结算只作用于 PvE 那一笔，永远不碰排名奖金。",
  "ptx.tree.err.attrs": "体力树的生长间隔、每棵容量、初始棵数都要填正整数",
  "ptx.tree.err.unlock": "体力树的解锁星数要填 0：萌宠小镇不自动恢复，树必须一开始就在",
  "ptx.col.kind": "类型 · 体力树属性",
  "ptx.kind.normal": "普通",
  "ptx.kind.tree": "体力树",
  "ptx.tree.fillTime": "单棵攒满约 {h} 小时 · 每棵独立生长，客户端一键收全部",
  "ptx.tree.one": "类型选「体力树」的那一栋（{slot}）是体力的唯一来源：散云之后按三个基础属性生长，玩家点一下把攒的体力收成骰子（收到体力上限为止，多的留在树上；树满了停止生长）。天赋树的体力树三条线在这三个基础值上加。",
  "ptx.tree.none": "没有体力树：萌宠小镇下骰子不自动恢复，玩家没有任何免费体力来源，发布会被拦下。把一个槽位的类型选成「体力树」。",
  "ptx.tree.many": "只能有一个体力树槽位（现在是 {slots}）；多棵树由天赋「体力树数量」在同一栋里加。",
  "ptx.layout.tip": "{slot} · {name} · {n} 星解锁",
  "ptx.params.noSystem": "未选",
  "nav.newbie-activity": "新手七日",
  "nav.mail": "系统邮件",
  "nav.reports": "举报管理",
  "nav.push-events": "推送事件",
  "lens.toggle": "接口视角",
  "lens.toggleTip": "给客户端开发看：每个配置项下发到哪个接口的哪个字段",
  "lens.close": "关闭接口视角面板",
  "lens.pick": "选取",
  "lens.picking": "点页面上的配置项…",
  "lens.pickHint": "鼠标移到配置项上会框出来，点一下打开；按 Esc 退出",
  "lens.altHint": "也可以按住 ⌥ Option（Windows 按 Alt）直接点页面上的配置项",
  "lens.search": "搜字段名或配置项，如 dice_cap 或 上限",
  "lens.noMatch": "登记表里没有匹配的配置项",
  "lens.onPage": "本页 {n} 项 · 下发给客户端 {m} 项",
  "lens.emptyPage": "这一页还没有登记的配置项",
  "lens.back": "本页",
  "lens.footer": "只读：看的是这一版已保存的内容，不发布、不影响线上",
  "lens.field": "字段",
  "lens.where": "在哪",
  "lens.how": "怎么拿",
  "lens.convert": "换算",
  "lens.note": "注意",
  "lens.doc": "接口文档",
  "lens.block": "资源块 {key}",
  "lens.apiPerPlayer": "按玩家返回的接口，这里不预览值",
  "lens.values": "当前值（客户端收到的原值）",
  "lens.valuesFirst": "当前值（客户端收到的原值，数组取第一项为例）",
  "lens.thisVer": "这一版 v{v}",
  "lens.liveVer": "生效 v{v}",
  "lens.noLive": "还没有生效版本",
  "lens.noBlock": "这一版没有这一块",
  "lens.notFound": "这一块里找不到这个字段",
  "lens.changed": "与生效版不同：发布后 {key} 换哈希，客户端会重拉这一块",
  "lens.same": "与生效版相同，发布后客户端不会重拉这一块",
  "lens.raw": "整块 {key} 的返回（这一版）",
  "lens.loading": "正在生成这一块…",
  "lens.error": "取不到：{msg}",
  "lens.server": "不下发给客户端。",
  "nav.econ-switch": "经济口径切换",
  "econ.g.col": "单掷金币 g",
  "econ.g.hint": "这一关每掷一次的基准金币（可带 3 位小数）。收入、纠偏、副玩法、多人奖池、算法总造价都按它算。每关必填，发布时会检查。",
  "econ.g.hintStar": "这一星每掷一次的基准金币（可带 3 位小数）。收入、纠偏、副玩法、多人奖池、公式星价都按它算。每颗星必填，发布时会检查。",
  "econ.g.curve": "待填",
  "econ.g.suggest": "推荐 {v}",
  "econ.m2.title": "这个版本按单掷金币 g 算",
  "econ.m2.body": "金币发放和造价都按每关的单掷金币 g（在「总造价配置」页的「单掷金币 g」列填）。算法总造价 = 目标骰次 × g × ρ。",
  "econ.m2.bodyStar": "金币发放和星价都按每颗星的单掷金币 g（在下面星级表的「单掷金币 g」列填）。公式星价 = 目标骰次 × g × ρ。",
  "cost.algoHintG": "按公式算出：目标骰次 × 本关单掷金币 g × ρ（g 在「总造价配置」页填，ρ 取本页参数）。选「公式」时，没填实际总造价的关按它摊价；填了就按实际总造价。只读，不能在这里改。",
  "p.rho.hintG": "玩家产出的金币里，最终投到建筑升级上的比例系数。这个版本的算法总造价 = 目标骰次 × g × ρ。不做取值限制。",
  "pt.star.formula.hintG": "目标投资次数 × 这颗星的单掷金币 g × ρ，或已启用的分段星价曲线。手填和竞品价格不受影响。",
  "econ.g.bad.format": "只能填非负数字，例如 3 或 3.1",
  "econ.g.bad.decimals": "最多 3 位小数（单位千分之一金币），不会替你四舍五入",
  "econ.g.bad.zero": "g 至少 0.001 金币；不想单独配就清空，按曲线",
  "econ.g.bad.digits": "位数超过上限（千分位最多 85 位）",
  "econ.ver.model2": "每关 / 每星配单掷金币 g，按进度缩放的金额都填几倍单掷金币。",
  "econsw.phase.legacy": "旧口径（legacy）",
  "econsw.phase.budget_on": "旧口径 + 预算旁路（budget_on）",
  "econsw.phase.switching": "切换中（switching）",
  "econsw.phase.model2": "单掷金币（model2）",
  "econsw.loading": "正在读取切换状态…",
  "econsw.intro": "按代理把金额口径在旧口径（B 数）与单掷金币 g 之间切换。切换中这个代理所有改资产 / 进度的请求都会回 60007 让客户端重试，按顺序做完、别停在中间。每一步都可以重复点，结果不变。",
  "econsw.watchdog": "看门狗：",
  "econsw.refresh": "刷新",
  "econsw.now.title": "当前状态",
  "econsw.phase": "阶段",
  "econsw.gen": "世代",
  "econsw.activeVer": "生效版本",
  "econsw.acks": "围栏确认",
  "econsw.noActive": "这个代理还没有生效配置",
  "econsw.steps": "切换步骤",
  "econsw.now": "当前这步",
  "econsw.propagate.pending": "切换 {id} 的围栏 ({gen}, {phase}) 还没推到全部已迁入玩家：{acked} / {total} 已确认。没确认的玩家会一直回 60007，先传播完再做下一步。",
  "econsw.oneBatch": "传播 / 补写一批",
  "econsw.untilDone": "继续直到完成",
  "econsw.stop": "中止",
  "econsw.batchDone": "这一批已处理",
  "econsw.prog.batches": "已跑 {n} 批。",
  "econsw.prog.acks": "确认 {acked} / {total}",
  "econsw.prog.backfill": "剩余：SQL 玩家 {sql}、已迁入玩家 {do}",
  "econsw.done.propagate": "传播完成，全部已迁入玩家都确认了",
  "econsw.done.backfill": "补写完成",
  "econsw.stuck.propagate": "这一批没有任何进展（{n} 个失败），停下了。看下面的失败原因处理后再继续。",
  "econsw.stuck.backfill": "这一批补写剩余数没有减少（{n} 个失败），停下了。看下面的失败原因处理后再继续。",
  "econsw.last.propagate": "上一批：派发 {dispatched}，确认 {acked} / {total}",
  "econsw.last.backfill": "上一批：SQL 玩家已补 {sqlDone}、剩 {sqlLeft}；已迁入玩家已补 {doDone}、剩 {doLeft}",
  "econsw.moreFailures": "还有 {n} 条失败没列出",
  "econsw.s1": "进入预算旁路（budget_on）",
  "econsw.s1.desc": "前提：游戏 Worker、player-do、房间 DO、管端的活跃版本都已是部署 1。进入后开始逐玩家建预算基线；进入后要传播到全部确认。",
  "econsw.s1.btn": "进入 budget_on",
  "econsw.s1.confirm": "进入后不能回到 legacy。确认四个运行体都已经是部署 1 之后的代码：部署 1 之前的二进制不维护预算，会让之后的切换出错。",
  "econsw.s1.check": "已核对：游戏 Worker、player-do、房间 DO、管端的活跃版本都 ≥ 部署 1",
  "econsw.s2": "开始切换",
  "econsw.s2.desc": "选目标版本开始切换：budget_on 起步选 model 2 版本，model2 起步选 model 1 版本（回到旧口径，不补写）。开始后这个代理停写，传播到全部确认才算排空。",
  "econsw.s2.pick2": "选一个 model 2 版本",
  "econsw.s2.pick1": "选一个 model 1 版本",
  "econsw.s2.btn": "开始切换",
  "econsw.s2.confirm": "从「{from}」开始切到 v{v}。确认后这个代理所有改资产 / 进度的请求立刻回 60007，直到激活或撤销并传播完。",
  "econsw.s3": "补写旧机会的依据",
  "econsw.s3.desc": "排空之后逐玩家给未结机会补写依据、建预算基线（SQL 玩家在玩家锁内，已迁入玩家经 DO 命令）。补到 SQL 与已迁入玩家都剩 0。",
  "econsw.s3.wait": "开始切换并传播完之后才能补写",
  "econsw.s3.skip": "回到旧口径不补写，直接激活",
  "econsw.s4": "激活",
  "econsw.s4.desc": "切到 model 2 时激活闸全过才会激活：可结算机会都有依据、全员建了预算基线、修复队列清空、客户端版本（生产或已配商店包版本的代理必须配 V_60289，两平台最低代码版本 ≥ 它）、附等价回放报告与数值负责人批准。回到 model 1 不要这些。激活后再传播到全部确认才算完成。",
  "econsw.gate.blocked": "激活闸还没过（回放报告与批准两条按下面填的内容在点激活时由服务端重算）：",
  "econsw.gate.ok": "激活闸已全过",
  "econsw.s4.report": "等价回放报告",
  "econsw.s4.report.hint": "回放报告的链接或结论（§6.0 第 7 步）",
  "econsw.s4.approved": "重平衡批准人",
  "econsw.s4.btn": "激活",
  "econsw.s4.confirm": "激活后生效版本换成 v{v}、阶段进入「{phase}」，这次切换封存，不能再撤销；要退回只能另发起一次切换。",
  "econsw.abort": "撤销切换",
  "econsw.abort.btn": "撤销切换",
  "econsw.abort.confirm": "撤销切换 {id}：回到 v{v}、阶段「{phase}」，世代 +1。撤销后同样要传播到全部确认，停写才解除。这次写的依据保留但不封存，下次切换会重新准备。",
  "econsw.ok": "已提交",
  "econsw.repairs": "缺依据修复队列",
  "econsw.repairs.sub": "待处理 {n} 张（最多列 200）",
  "econsw.repairs.empty": "没有待修复的机会",
  "econsw.restore": "从日志恢复",
  "econsw.restore.ok": "已恢复",
  "econsw.restore.no": "没恢复：{reason}",
  "econsw.journals": "切换记录",
  "econsw.journals.empty": "还没有切换记录",
  "econsw.col.switch": "切换 ID",
  "econsw.col.move": "从 → 到",
  "econsw.col.status": "状态",
  "econsw.col.fence": "目标围栏",
  "econsw.col.prop": "传播",
  "econsw.col.started": "开始",
  "econsw.col.finished": "结束",
  "econsw.col.actor": "操作人",
  "econsw.col.chance": "机会 ID",
  "econsw.col.user": "玩家",
  "econsw.col.reason": "原因",
  "econsw.col.created": "登记时间",
  "econsw.status.preparing": "进行中",
  "econsw.status.committed": "已提交",
  "econsw.status.aborted": "已撤销",
  "econsw.prop.done": "已完成",
  "econsw.prop.pending": "未完成",
} as const;

export type MsgKey = keyof typeof ZH;

/**
 * 英文 / 日文词条按需加载：主包里只放空壳，lang 切过去时才动态 import 填充
 * （见 i18n.en.ts 的注释）。加载完成前 translate 会回落到中文——
 * 这正是它本来就有的兜底路径，不用额外分支。
 */
const EN: Partial<Record<MsgKey, string>> = {};
const JA: Partial<Record<MsgKey, string>> = {};
const DICT: Record<UiLang, Partial<Record<MsgKey, string>>> = { zh: ZH, en: EN, ja: JA };
// 必须是两个字面量 import()，打包器才能各拆一个 chunk；拼路径的写法会把两份一起打进来
const LOADERS: Partial<Record<UiLang, () => Promise<Record<MsgKey, string>>>> = {
  en: () => import("./i18n.en").then((m) => m.EN),
  ja: () => import("./i18n.ja").then((m) => m.JA),
};
const pending: Partial<Record<UiLang, Promise<void>>> = {};
/** 这种语言里有没有这条文案（不看中文回退）。要「没有就英文兜底」而不是回退中文的地方用它 */
export const hasMsg = (lang: UiLang, key: MsgKey): boolean => DICT[lang][key] != null;
export const langLoaded = (lang: UiLang) => !LOADERS[lang] || Object.keys(DICT[lang]).length > 0;
function loadDict(lang: UiLang): Promise<void> {
  const load = LOADERS[lang];
  if (!load) return Promise.resolve();
  pending[lang] ??= load().then((d) => { Object.assign(DICT[lang], d); });
  return pending[lang];
}

export type Translate = (key: MsgKey, vars?: Record<string, string | number>) => string;

/**
 * 一份实现，provider 和 context 默认值共用。
 *
 * **默认值以前是 `(k) => ZH[k]`，它不做占位符替换。** 那个简写的代价是：
 * 一旦某棵子树没拿到 provider（少包一层、第二个 React root、模块被打成两份），
 * 界面上**没有占位符的文案全是对的**，只有带 `{}` 的那几条露出原文——
 * 「idol · {name}」就是这么来的。看起来像某一条词条写错了，其实整棵树都在用默认值。
 *
 * 默认值必须是真实现：行为要么一致，要么明显坏掉，不能「几乎一样」。
 */
const translate = (lang: UiLang): Translate => (key, vars) => {
  const raw = DICT[lang][key] ?? ZH[key] ?? key;
  return vars ? interpolateMessage(raw, vars, (value) =>
    Object.prototype.hasOwnProperty.call(DICT[lang], value) ? DICT[lang][value as MsgKey] : undefined) : raw;
};

const I18nContext = createContext<{ lang: UiLang; t: Translate }>({ lang: "zh", t: translate("zh") });

export function I18nProvider({ lang, children }: { lang: UiLang; children: ReactNode }) {
  // 英文 / 日文词条是异步来的；到货后要重算 t，否则界面停在中文兜底上不动。
  // 用一个计数器而不是布尔：Object.assign 是就地改字典，引用没变，
  // useMemo 靠依赖项判断不出「内容变了」——必须显式给它一个变化的值
  const [dictTick, setDictTick] = useState(0);
  useEffect(() => {
    if (langLoaded(lang)) return;
    let alive = true;
    void loadDict(lang).then(() => { if (alive) setDictTick((n) => n + 1); });
    return () => { alive = false; };
  }, [lang]);
  const value = useMemo(() => ({ lang, t: translate(lang) }), [lang, dictTick]);
  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export const useI18n = () => useContext(I18nContext);
export const useT = () => useContext(I18nContext).t;

/** 记住选择，刷新后不丢 */
const KEY = "aeroplane_admin_lang";
export const loadLang = (): UiLang => {
  const v = localStorage.getItem(KEY);
  return (UI_LANGS as readonly string[]).includes(v ?? "") ? (v as UiLang) : "zh";
};
export const saveLang = (l: UiLang) => localStorage.setItem(KEY, l);

/** 语言下拉里的名字：每种语言用它自己的写法，不随界面语言翻译 */
export const LANG_LABEL: Record<UiLang, string> = { zh: "简体中文", en: "English", ja: "日本語" };
/** 后台界面语言 → 配置内容多语言的语言 id（lib/locales.ts 的 LOCALE_CATALOG） */
export const contentLocaleOf = (l: UiLang): string => (l === "zh" ? "zh-CN" : l);
/** 后台界面语言 → 发给服务端 / 中台的 Accept-Language */
export const acceptLanguageOf = (l: UiLang): string => ({ zh: "zh-CN", en: "en-US", ja: "ja-JP" })[l];
